// Unified i18n source.
// All system UI, Canvas UI and runtime messages resolve through this module.
// User-authored map names/descriptions are content and intentionally remain unchanged.

export const SUPPORTED_LANGUAGES = Object.freeze([
  "en",
  "zh-CN",
  "zh-TW",
  "ja",
  "ko",
  "es",
  "fr",
  "de",
  "pt-BR",
  "pt-PT",
  "it",
  "ru",
  "ar",
  "hi",
  "th",
  "vi",
  "id",
  "tr",
  "nl",
  "pl"
]);

export const DEFAULT_LANGUAGE = "en";

export const LANGUAGE_OPTIONS = Object.freeze([
  {
    "code": "en",
    "label": "English",
    "shortLabel": "EN"
  },
  {
    "code": "zh-CN",
    "label": "简体中文",
    "shortLabel": "简中"
  },
  {
    "code": "zh-TW",
    "label": "繁體中文",
    "shortLabel": "繁中"
  },
  {
    "code": "ja",
    "label": "日本語",
    "shortLabel": "JA"
  },
  {
    "code": "ko",
    "label": "한국어",
    "shortLabel": "KO"
  },
  {
    "code": "es",
    "label": "Español",
    "shortLabel": "ES"
  },
  {
    "code": "fr",
    "label": "Français",
    "shortLabel": "FR"
  },
  {
    "code": "de",
    "label": "Deutsch",
    "shortLabel": "DE"
  },
  {
    "code": "pt-BR",
    "label": "Português (Brasil)",
    "shortLabel": "PT-BR"
  },
  {
    "code": "pt-PT",
    "label": "Português (Portugal)",
    "shortLabel": "PT-PT"
  },
  {
    "code": "it",
    "label": "Italiano",
    "shortLabel": "IT"
  },
  {
    "code": "ru",
    "label": "Русский",
    "shortLabel": "RU"
  },
  {
    "code": "ar",
    "label": "العربية",
    "shortLabel": "AR"
  },
  {
    "code": "hi",
    "label": "हिन्दी",
    "shortLabel": "HI"
  },
  {
    "code": "th",
    "label": "ไทย",
    "shortLabel": "TH"
  },
  {
    "code": "vi",
    "label": "Tiếng Việt",
    "shortLabel": "VI"
  },
  {
    "code": "id",
    "label": "Bahasa Indonesia",
    "shortLabel": "ID"
  },
  {
    "code": "tr",
    "label": "Türkçe",
    "shortLabel": "TR"
  },
  {
    "code": "nl",
    "label": "Nederlands",
    "shortLabel": "NL"
  },
  {
    "code": "pl",
    "label": "Polski",
    "shortLabel": "PL"
  }
].map(Object.freeze));

const HTML_LANGUAGE = Object.freeze(Object.fromEntries(SUPPORTED_LANGUAGES.map(code => [code, code])));


export const MESSAGES = Object.freeze({
  "en": Object.freeze({
    "allFragmentsCollected": "All fragments collected",
    "back": "Back",
    "backHome": "BACK HOME",
    "backendReady": "Backend-ready Vue frontend",
    "ballSkins": "Ball Skins",
    "ballSpeed": "BALL SPEED",
    "breakableCount": "Breakable bricks: {count}",
    "brickPalette": "Brick palette",
    "builtLevels": "Built {done}/{total}",
    "levelBuilt": "Built",
    "cancel": "Cancel",
    "cannotReadTemplate": "Unable to process this image.",
    "canvasUnsupported": "Your browser does not support Canvas.",
    "challengeMapHint": "Choose the map used by the Home PLAY button. The official map is always available.",
    "challengeMapTitle": "SELECT PLAY MAP",
    "chapter": "Chapter",
    "chapterEndFragmentNotice": " scenic fragments.",
    "chapters": "Chapters",
    "chooseChallenge": "USE THIS MAP",
    "chooseForPlay": "USE THIS MAP",
    "chooseMapPlay": "Choose this map to play",
    "clearSearch": "Clear search",
    "cleared": "Cleared",
    "close": "Close",
    "collectFragment": "Collect Fragment",
    "collectedFragments": "Collected {count} fragments",
    "comingSoon": "COMING SOON",
    "demoWorldDreamName": "Dream Around the World",
    "demoWorldDreamDesc": "Create your dream cities into a star map.",
    "demoJapanNightName": "Japan Night Journey",
    "demoJapanNightDesc": "Tokyo Kyoto Osaka and Mount Fuji travel route.",
    "demoChinaLandmarksName": "China Classic Landmarks",
    "demoChinaLandmarksDesc": "Explore famous Chinese landmarks.",
    "demoFranceTripName": "France Romantic Route",
    "demoFranceTripDesc": "Paris Lyon Nice and famous French cities.",
    "demoSevenContinentsName": "Seven Continents",
    "demoSevenContinentsDesc": "Explore seven continents.",
    "demoUsaRoadName": "USA Road Trip",
    "demoUsaRoadDesc": "Travel across American cities.",
    "demoEuropeCityName": "European City Tour",
    "demoEuropeCityDesc": "Explore European cities.",
    "completed": "Completed",
    "confirm": "Confirm",
    "confirmDeleteMap": "Delete “{name}”? Local level data will also be deleted.",
    "confirmOverwrite": "Generating from an image will overwrite the current canvas. Continue?",
    "continueGame": "Continue",
    "countryCA": "Canada",
    "countryCN": "China",
    "countryDE": "Germany",
    "countryFR": "France",
    "countryGB": "United Kingdom",
    "countryJP": "Japan",
    "countryKR": "South Korea",
    "countryUS": "United States",
    "createLevel": "Create Level",
    "createStarMap": "Create Star Map",
    "createStarMapFirst": "Create a star map in the Studio. Until then, PLAY continues the System Map.",
    "creativeWorkshop": "STUDIO",
    "creativeWorkshopHint": "Custom maps",
    "currentLevel": "CURRENT LEVEL",
    "currentLevelFormat": "Reached Level {level}",
    "currentProgress": "Continue from Level {level}",
    "currentReachedLevel": "Reached Level {level}",
    "custom": "Custom",
    "customMapCompleteSubtitle": "Congratulations! You lit every level in {name}.",
    "customMapCompleteTitle": "MAP COMPLETED!",
    "customMapCurrentProgress": "{name} · Reached Level {level}",
    "customMapDescription": "Your custom star map and custom-made levels.",
    "defeat": "Defeat",
    "deleteAction": "Delete",
    "deleteMap": "Delete Map",
    "difficulty": "DIFFICULTY",
    "dragNodesHint": "Upload an image to generate {count} level stars automatically. Drag any star to fine-tune the route, then save it as your current level-select map.",
    "editMap": "Edit Map",
    "en": "en",
    "energyFull": "Full",
    "energyInsufficient": "Not enough energy: {current}/{cost}. Wait for recovery.",
    "energyLabel": "ENERGY",
    "energyLoading": "LOADING",
    "enterMap": "Enter map",
    "reportMap": "Report",
    "confirmReportMap": "Report this map for unsafe or inappropriate content?",
    "reportSubmitted": "Report submitted. Thank you for helping keep the community safe.",
    "alreadyReported": "You have already reported this map.",
    "cannotReportOwnMap": "You cannot report your own map.",
    "dailyPublishRemaining": "Publications remaining today: {count}",
    "publishLimitReached": "Today's publication limit has been reached.",
    "workshopBanned": "Workshop publishing is currently suspended.",
    "submitAppeal": "Submit appeal",
    "appealPlaceholder": "Explain why this work should be reviewed again",
    "appealTooShort": "Please enter at least 5 characters.",
    "appealSubmitted": "Appeal submitted.",
    "pendingAppeals": "Pending appeals",
    "appealAvailabilityHint": "The appeal button appears here after a work is rejected or hidden.",
    "moderation_pending": "Pending safety review",
    "moderation_approved": "Safety review passed",
    "moderation_rejected": "Rejected by safety review",
    "moderation_hidden": "Hidden after reports",
    "contentModeration": "Content review",
    "operationLog": "Operation log",
    "operationLogEmpty": "No operation records yet.",
    "operationLogLoadFailed": "The operation log could not be loaded.",
    "operationTarget": "Target",
    "operationReason": "Reason",
    "auditActionApprove": "Approved",
    "auditActionReject": "Rejected",
    "auditActionHide": "Hidden",
    "auditActionRoleChange": "Changed a user role",
    "auditActionBan": "Suspended workshop access",
    "auditActionUnban": "Restored workshop access",
    "auditActionAppealAccept": "Accepted an appeal",
    "auditActionAppealReject": "Rejected an appeal",
    "moderatorOnly": "This page is available to moderators only.",
    "moderationQueueEmpty": "No works in this review queue.",
    "moderationReasonPrompt": "Enter the review reason (optional)",
    "reportPrompt": "Choose the main reason. Reports are confidential and the creator will not see who submitted them.",
    "selectReportReason": "Please choose a report reason.",
    "reportDetails": "Additional details (optional, up to 300 characters)",
    "submitReport": "Submit report",
    "reportReason_sexual": "Sexual or nudity content",
    "reportReason_violence": "Violence or dangerous behavior",
    "reportReason_hate": "Hate or harassment",
    "reportReason_illegal": "Illegal goods or activity",
    "reportReason_spam": "Spam or misleading content",
    "reportReason_copyright": "Copyright infringement",
    "reportReason_other": "Other inappropriate content",
    "reportCount": "{count} reports",
    "approve": "Approve",
    "reject": "Reject",
    "hide": "Hide",
    "refresh": "Refresh",
    "mapViews": "Views",
    "likeMap": "Like map",
    "unlikeMap": "Unlike map",
    "enterSpecialLevel": "Enter Special Level",
    "extreme": "EXTREME",
    "fast": "FAST",
    "finalLevel": "Final",
    "fr": "fr",
    "fragment": "Fragment",
    "fragmentCollected": "Fragment Collected",
    "fragmentProgress": "Fragment Progress",
    "fragments": "Fragments",
    "gameHelp": "A/D or ←/→ to move · Space to launch · R to restart",
    "gameFailedTitle": "Game Failed",
    "retryChallenge": "Retry Challenge",
    "generateMap": "Generate Map",
    "goCreateStarMap": "STAR MAP MAKER",
    "hard": "HARD",
    "highScore": "High Score",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Laser missile",
    "leaderboard": "LEADERBOARD",
    "leaderboardFullTitle": "Full Nation Leaderboard",
    "leaderboardHint": "Rankings",
    "leaderboardPageTitle": "🌌 GLOBAL NATION RANKINGS",
    "leaderboardPlaceholderNote": "Leaderboard route, Store and API layer are ready. The target HTML was not included in the latest message, so this page is only a temporary layout shell and can be replaced without changing backend integration code.",
    "leaderboardScore": "Glory Points",
    "resultLevelScore": "Level Score",
    "leaderboardCleared": "Progress",
    "level": "Level",
    "levelCountFormat": "{count} levels",
    "levelCountPoints": "Levels / Points",
    "levelLocked": "Level Locked",
    "levelMap": "Level Map",
    "levelPlaysFormat": "{count} levels · {plays} played",
    "levelSelect": "LEVEL SELECT",
    "litLevels": "Lit {done}/{total}",
    "loading": "Loading",
    "loadingLevel": "Loading level data...",
    "loadingSharedMap": "Loading shared map...",
    "loadingTemplate": "Analyzing image...",
    "locked": "Locked",
    "mapCompleted": "Map Completed",
    "mapDescription": "Map description",
    "mapDescriptionPlaceholder": "Enter map description",
    "mapFullyLit": "Map fully lit",
    "mapName": "MAP NAME",
    "mapPermission": "Map permission",
    "mapTemplate": "Map template",
    "mapTitle": "Echo Map",
    "mapUnlocked": "Map Unlocked",
    "mapUpdatedSaved": "Saved. This is now your current level-select map.",
    "modeGuide": "MODE GUIDE",
    "modeGuideExtreme": "Extreme: about 35% of Classic item drop rate",
    "modeGuideHard": "Hard: about 65% of Classic item drop rate",
    "modeGuideSpeed": "Faster balls increase control and clear difficulty",
    "modeGuideStandard": "Classic: original item drop rate",
    "myCustomMaps": "My Creations",
    "myStarMap": "My Star Map",
    "myWorkshop": "My Workshop",
    "next": "Next",
    "noCustomMaps": "No custom maps yet",
    "noCustomMapsHint": "Create a star map in the Studio. Until then, PLAY continues the System Map.",
    "noCustomStarMap": "No custom maps yet",
    "noMapDescription": "No map description",
    "noMatches": "No matches",
    "normal": "NORMAL",
    "off": "OFF",
    "on": "ON",
    "openStarMapEditor": "EDIT MAP",
    "originalImageTitle": "Original image",
    "paddleGrow": "Paddle enlarged",
    "paddleSkins": "Paddle Skins",
    "play": "PLAY",
    "playsLikesFormat": "{plays} played · {likes} likes",
    "previewHint": "Upload an image to generate {count} level stars automatically. Drag any star to fine-tune the route, then save it as your current level-select map.",
    "previous": "Previous",
    "privateMap": "Private",
    "publicMap": "Public",
    "visibilityChangedPublic": "Visibility changed to Public.",
    "visibilityChangedPrivate": "Visibility changed to Private.",
    "visibilityChangedShare": "Visibility changed to Shared only.",
    "publicMaps": "Gallery",
    "publicMapsDescription": "Discover maps created by other players",
    "regenerateByCount": "Trail Rebuild",
    "regenerateNodesSuccess": "Generated {count} level stars. Click Rebuild Path before dragging to fine-tune them.",
    "replayLevel": "Replay Level",
    "restart": "Restart",
    "result": "Result",
    "returnHome": "Return Home",
    "runtimeBalls": "BALLS",
    "runtimeHome": "HOME",
    "runtimeLives": "LIVES",
    "runtimePaused": "PAUSED",
    "runtimeResume": "RESUME",
    "runtimeTime": "TIME",
    "save": "Save",
    "saveAndBack": "SAVE & BACK",
    "saveFailed": "Save Failed",
    "saveMap": "Save Map",
    "saveMapAndCreateLevels": "SAVE AS CURRENT MAP",
    "saveSuccess": "Saved Successfully",
    "scenicFragment": "Scenic fragment",
    "scenicFragmentObtained": "Scenic fragment obtained! It will be saved after clearing the level.",
    "scenicFragmentProgress": "Scenic fragments {found}/{total}",
    "searchColor": "Search colors or HEX value",
    "searchMaps": "Search maps",
    "selectMapTemplate": "Map template",
    "selectTemplateDescription": "Upload an image to generate {count} level stars automatically. Drag any star to fine-tune the route, then save it as your current level-select map.",
    "selectTemplateOrUpload": "UPLOAD IMAGE",
    "selected": "Selected",
    "selectedChallenge": "SELECTED",
    "selectedMap": "SELECTED",
    "settings": "SETTINGS",
    "settingsTitle": "GAME SETTINGS",
    "share": "Share",
    "shareAction": "Share",
    "shareFailed": "Sharing failed.",
    "shareInviteText": "Light up my BricksBeater star map!",
    "shareMap": "Shared only",
    "shareReady": "Share link is ready.",
    "shareSuccess": "Shared Successfully",
    "shareUnavailable": "Private maps cannot be shared. Change visibility first.",
    "skill": "Skill",
    "skillCount": "Skill Count",
    "skins": "SKINS",
    "skinsTitle": "Skin Shop",
    "slow": "SLOW",
    "sound": "SOUND",
    "specialLevel": "Special level",
    "specialLevelLocked": "Special level locked",
    "standard": "CLASSIC",
    "starMap": "Star Map",
    "starMapClear": "CLEAR MAP",
    "starMapCleared": "Current custom star map cleared.",
    "starMapCurrentBadge": "CURRENT STAR MAP",
    "starMapDefaultName": "Enter map name",
    "starMapEdit": "EDIT MAP",
    "starMapEditorHint": "Upload an image to generate {count} level stars automatically. Drag any star to fine-tune the route, then save it as your current level-select map.",
    "starMapEditorTitle": "STAR MAP MAKER",
    "starMapGenerated": "Generated {count} level stars. Drag them to fine-tune the map.",
    "starMapImageError": "Unable to process this image.",
    "starMapLightProgress": "LIT {done} / {total}",
    "starMapName": "MAP NAME",
    "starMapPreviewInLevels": "OPEN LEVEL SELECT",
    "starMapProcessing": "Analyzing image...",
    "starMapRegenerate": "REGENERATE STARS",
    "starMapRevealHint": "Clear levels to gradually reveal the original image.",
    "starMapSaveCurrent": "SAVE AS CURRENT MAP",
    "starMapSaveError": "Unable to save the star map locally.",
    "starMapSaved": "Saved. This is now your current level-select map.",
    "starMapUpload": "UPLOAD IMAGE",
    "starMapUploadEmptyHint": "The first version uses image analysis, no AI required.",
    "starMapUploadEmptyTitle": "Upload an image to make a star map",
    "startError": "Unable to start the game.",
    "subtitle": "Break the Void · Light the Stars",
    "systemMapCurrentProgress": "System Map · Reached Level {level}",
    "systemMapDescription": "Official campaign levels, played in order.",
    "systemMapName": "ECHO MAP",
    "templateLoadFailed": "Unable to process this image.",
    "title": "Echoes of Earth",
    "totalLevels": "{count} Levels",
    "unavailable": "Level data not available yet",
    "uploadImage": "Upload Image",
    "uploadMapImage": "Upload an image to build a map",
    "uploadMapImageHint": "Upload and map-generation logic will be added in a later development stage.",
    "victory": "Victory",
    "viewCompletedMap": "VIEW MAP",
    "viewOriginal": "View original",
    "workshop": "Workshop",
    "workshopComingSoon": "CUSTOM MAP WORKSHOP",
    "workshopDescription": "A future creation space where players can upload an image and turn it into a custom brick map.",
    "workshopLevelMap": "Level Design",
    "workshopStarMap": "Map Design",
    "workshopWorkbench": "Workbench",
    "workshopToolSoon": "Editor coming soon",
    "workshopTools": "Map tools",
    "worldMap": "World Map",
    "zh": "zh",
    "skin.ball_default": "Classic White",
    "skin.ball_star": "Star",
    "skin.ball_football": "Football",
    "skin.ball_flower": "Flower",
    "skin.ball_planet": "Planet",
    "skin.ball_rainbow": "Rainbow Candy",
    "skin.ball_diamond": "Diamond",
    "skin.paddle_default": "Classic",
    "skin.paddle_stool": "Stool",
    "skin.paddle_grass": "Grass",
    "skin.paddle_rainbow": "Rainbow Bridge",
    "skin.paddle_carpet": "Magic Carpet",
    "skin.paddle_wave": "Wave",
    "skin.paddle_mecha": "Mecha Platform",
    "imageFileRequired": "UPLOAD IMAGE",
    "imageReadFailed": "Unable to process this image.",
    "imageTooLarge": "Unable to process this image.",
    "imageUnsupported": "Your browser does not support Canvas.",
    "imageNoRegion": "Unable to process this image.",
    "mapNotFound": "Level data not available yet",
    "mapCategoryWorld": "World Map",
    "mapCategoryWorldShort": "World",
    "mapCategoryCountry": "Country Map",
    "mapCategoryCountryShort": "Country",
    "mapCategoryRegion": "Region Map",
    "mapCategoryRegionShort": "Region",
    "worldClassicName": "World Classic Landmarks",
    "worldClassicDesc": "A world map made from famous landmarks.",
    "worldRouteName": "World Travel Route",
    "worldRouteDesc": "A travel route map across the world.",
    "worldContinentsName": "Seven Continents Exploration",
    "worldContinentsDesc": "Explore the seven continents.",
    "chinaClassicName": "China Classic",
    "chinaClassicDesc": "Famous Chinese landmarks.",
    "japanClassicName": "Japan Classic",
    "japanClassicDesc": "Famous Japanese landmarks.",
    "franceClassicName": "France Classic",
    "franceClassicDesc": "Famous French cities and landmarks.",
    "usaClassicName": "USA Classic",
    "usaClassicDesc": "Famous American landmarks.",
    "europeClassicName": "Europe Classic Cities",
    "europeClassicDesc": "European travel cities.",
    "eastAsiaName": "East Asia Travel",
    "eastAsiaDesc": "East Asian city landmarks.",
    "southEastAsiaName": "Southeast Asia Travel",
    "southEastAsiaDesc": "Southeast Asian travel route.",
    "chinaRegionName": "China Regions",
    "chinaRegionDesc": "Regional exploration map.",
    "scenicForbiddenCity": "Forbidden City",
    "scenicTempleOfHeaven": "Temple of Heaven",
    "scenicGreatWall": "Great Wall",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "zh-CN": Object.freeze({
    "allFragmentsCollected": "已收集全部碎片",
    "back": "返回",
    "backHome": "返回主页",
    "backendReady": "可直接接后端的 Vue 前端",
    "ballSkins": "小球皮肤",
    "ballSpeed": "小球速度",
    "breakableCount": "可击碎砖块：{count}",
    "brickPalette": "砖块调色板",
    "builtLevels": "已制作 {done}/{total}",
    "levelBuilt": "已制作",
    "cancel": "取消",
    "cannotReadTemplate": "这张图片暂时无法处理。",
    "canvasUnsupported": "当前浏览器不支持 Canvas。",
    "challengeMapHint": "选择主页“开始游戏”接下来要闯的地图；系统地图始终保留为默认选项。",
    "challengeMapTitle": "选择闯关地图",
    "chapter": "章节",
    "chapterEndFragmentNotice": "碎片。",
    "chapters": "章节",
    "chooseChallenge": "选择闯关",
    "chooseForPlay": "选择闯关",
    "chooseMapPlay": "选择此地图开始游戏",
    "clearSearch": "清空搜索",
    "cleared": "已通关",
    "close": "关闭",
    "collectFragment": "收集碎片",
    "collectedFragments": "已收集 {count} 个碎片",
    "comingSoon": "后续开放",
    "completed": "已通关",
    "confirm": "确认",
    "confirmDeleteMap": "确定删除“{name}”吗？本地关卡数据也会一起删除。",
    "confirmOverwrite": "根据图片生成会覆盖当前画布，是否继续？",
    "continueGame": "继续游戏",
    "countryCA": "加拿大",
    "countryCN": "中国",
    "countryDE": "德国",
    "countryFR": "法国",
    "countryGB": "英国",
    "countryJP": "日本",
    "countryKR": "韩国",
    "countryUS": "美国",
    "createLevel": "创建关卡",
    "createStarMap": "创建星图",
    "createStarMapFirst": "可以去创意工坊制作星图；在未制作或未选择自定义地图时，开始游戏默认继续系统地图。",
    "creativeWorkshop": "创意工坊",
    "creativeWorkshopHint": "自定义地图",
    "currentLevel": "当前关卡",
    "currentLevelFormat": "当前到达第 {level} 关",
    "currentProgress": "从第 {level} 关继续",
    "currentReachedLevel": "当前到达第 {level} 关",
    "custom": "自定义",
    "customMapCompleteSubtitle": "恭喜你，已经完成《{name}》的全部关卡。",
    "customMapCompleteTitle": "地图全部点亮！",
    "customMapCurrentProgress": "{name} · 当前到达第 {level} 关",
    "customMapDescription": "自己制作的星图地图，里面的关卡也全部使用自己的关卡。",
    "defeat": "失败",
    "deleteAction": "删除",
    "deleteMap": "删除地图",
    "difficulty": "游戏难度",
    "dragNodesHint": "上传图片后会自动生成 {count} 个关卡星点。你可以直接拖动每颗星微调位置，确认后保存为当前关卡选择地图。",
    "editMap": "编辑地图",
    "en": "en",
    "energyFull": "已满",
    "energyInsufficient": "体力不足：当前 {current} 点，每局需要 {cost} 点，请等待恢复。",
    "energyLabel": "体力",
    "energyLoading": "读取中",
    "enterMap": "进入地图",
    "reportMap": "举报",
    "confirmReportMap": "确定举报此地图含有不安全或不适当内容吗？",
    "reportSubmitted": "举报已提交，感谢你帮助维护社区安全。",
    "alreadyReported": "你已经举报过这张地图了。",
    "cannotReportOwnMap": "不能举报自己创建的地图。",
    "dailyPublishRemaining": "今日还可以发布 {count} 次",
    "publishLimitReached": "今天的发布次数已用完，请明天再试。",
    "workshopBanned": "你的创意工坊发布权限已被暂停。",
    "submitAppeal": "提交申诉",
    "appealPlaceholder": "请说明为什么需要重新审核这张作品",
    "appealTooShort": "申诉内容至少填写5个字。",
    "appealSubmitted": "申诉已提交。",
    "pendingAppeals": "待处理申诉",
    "appealAvailabilityHint": "作品被拒绝或隐藏后，这里会显示“提交申诉”按钮。",
    "moderation_pending": "等待安全审核",
    "moderation_approved": "安全审核已通过",
    "moderation_rejected": "安全审核未通过",
    "moderation_hidden": "因举报暂时隐藏",
    "contentModeration": "内容审核",
    "operationLog": "操作日志",
    "operationLogEmpty": "暂时还没有操作记录。",
    "operationLogLoadFailed": "操作日志加载失败，请稍后刷新。",
    "operationTarget": "操作对象",
    "operationReason": "原因",
    "auditActionApprove": "通过作品",
    "auditActionReject": "拒绝作品",
    "auditActionHide": "隐藏作品",
    "auditActionRoleChange": "修改用户角色",
    "auditActionBan": "暂停创意工坊权限",
    "auditActionUnban": "恢复创意工坊权限",
    "auditActionAppealAccept": "通过申诉",
    "auditActionAppealReject": "拒绝申诉",
    "moderatorOnly": "只有审核员可以进入此页面。",
    "moderationQueueEmpty": "当前审核队列中没有作品。",
    "moderationReasonPrompt": "填写审核原因（可不填）",
    "reportPrompt": "请选择最主要的举报原因。举报信息会保密，作者不会看到举报人。",
    "selectReportReason": "请选择举报原因。",
    "reportDetails": "补充说明（选填，最多300字）",
    "submitReport": "提交举报",
    "reportReason_sexual": "色情或裸露内容",
    "reportReason_violence": "暴力或危险行为",
    "reportReason_hate": "仇恨或骚扰",
    "reportReason_illegal": "违法商品或活动",
    "reportReason_spam": "垃圾广告或误导内容",
    "reportReason_copyright": "侵犯版权",
    "reportReason_other": "其他不适当内容",
    "reportCount": "收到 {count} 次举报",
    "approve": "通过",
    "reject": "拒绝",
    "hide": "隐藏",
    "refresh": "刷新",
    "mapViews": "浏览量",
    "likeMap": "点赞",
    "unlikeMap": "取消点赞",
    "enterSpecialLevel": "进入特殊关卡",
    "extreme": "极难",
    "fast": "快速",
    "finalLevel": "最终关",
    "fr": "fr",
    "fragment": "碎片",
    "fragmentCollected": "已收集碎片",
    "fragmentProgress": "碎片进度",
    "fragments": "碎片",
    "gameHelp": "A/D 或 ←/→ 移动 · 空格发射 · R 重新开始",
    "gameFailedTitle": "游戏失败",
    "retryChallenge": "重新闯关",
    "generateMap": "生成地图",
    "goCreateStarMap": "星图地图制作",
    "hard": "困难",
    "highScore": "最高分",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "激光导弹",
    "leaderboard": "排行榜",
    "leaderboardFullTitle": "完整国家排行榜",
    "leaderboardHint": "全球排名",
    "leaderboardPageTitle": "🌌 全球国家排行榜",
    "leaderboardPlaceholderNote": "排行榜的 Router、Store 和 API 接口层已经搭好。你这条消息中没有实际附上目标 HTML，所以当前只保留临时页面骨架；之后可以只替换页面布局，不动后端对接结构。",
    "leaderboardScore": "荣耀积分",
    "resultLevelScore": "本关得分",
    "leaderboardCleared": "闯关进度",
    "level": "关卡",
    "levelCountFormat": "{count} 个关卡",
    "levelCountPoints": "关卡数 / 点数",
    "levelLocked": "关卡未解锁",
    "levelMap": "关卡地图",
    "levelPlaysFormat": "{count} 个关卡 · 游玩 {plays} 次",
    "levelSelect": "关卡选择",
    "litLevels": "已点亮 {done}/{total}",
    "loading": "加载中",
    "loadingLevel": "正在加载关卡数据...",
    "loadingSharedMap": "正在加载分享地图…",
    "loadingTemplate": "正在分析图片...",
    "locked": "未解锁",
    "mapCompleted": "地图已完成",
    "mapDescription": "地图描述",
    "mapDescriptionPlaceholder": "输入地图描述",
    "mapFullyLit": "地图已全部点亮",
    "mapName": "地图名称",
    "mapPermission": "地图权限",
    "mapTemplate": "地图模板",
    "mapTitle": "回响地图",
    "mapUnlocked": "地图已解锁",
    "mapUpdatedSaved": "已保存，并设为当前关卡选择地图。",
    "modeGuide": "模式说明",
    "modeGuideExtreme": "极难：道具掉落概率约为标准的 35%",
    "modeGuideHard": "困难：道具掉落概率约为标准的 65%",
    "modeGuideSpeed": "小球速度越快，操作与通关难度越高",
    "modeGuideStandard": "标准：保持原始道具掉落概率",
    "myCustomMaps": "我的作品",
    "myStarMap": "我的星图地图",
    "myWorkshop": "我的工坊",
    "next": "下一步",
    "noCustomMaps": "还没有制作自定义地图",
    "noCustomMapsHint": "可以去创意工坊制作星图；在未制作或未选择自定义地图时，开始游戏默认继续系统地图。",
    "noCustomStarMap": "还没有制作自定义地图",
    "noMapDescription": "暂无地图描述",
    "noMatches": "没有匹配结果",
    "normal": "标准",
    "off": "关闭",
    "on": "开启",
    "openStarMapEditor": "编辑地图",
    "originalImageTitle": "原图",
    "paddleGrow": "挡板变长",
    "paddleSkins": "挡板皮肤",
    "play": "开始游戏",
    "playsLikesFormat": "游玩 {plays} 次 · {likes} 个赞",
    "previewHint": "上传图片后会自动生成 {count} 个关卡星点。你可以直接拖动每颗星微调位置，确认后保存为当前关卡选择地图。",
    "previous": "上一步",
    "privateMap": "私密",
    "publicMap": "公开",
    "visibilityChangedPublic": "已切换为公开可见。",
    "visibilityChangedPrivate": "已切换为私密。",
    "visibilityChangedShare": "已切换为仅分享可见。",
    "publicMaps": "广场",
    "publicMapsDescription": "发现其他玩家创建的地图",
    "regenerateByCount": "轨迹重构",
    "regenerateNodesSuccess": "已生成 {count} 个关卡星点，请先点击“轨迹重构”，然后再拖动微调。",
    "replayLevel": "重玩关卡",
    "restart": "重新开始",
    "result": "结果",
    "returnHome": "返回主页",
    "runtimeBalls": "小球",
    "runtimeHome": "主页",
    "runtimeLives": "生命",
    "runtimePaused": "游戏暂停",
    "runtimeResume": "继续游戏",
    "runtimeTime": "时间",
    "save": "保存",
    "saveAndBack": "保存并返回",
    "saveFailed": "保存失败",
    "saveMap": "保存地图",
    "saveMapAndCreateLevels": "保存并设为当前地图",
    "saveSuccess": "保存成功",
    "scenicFragment": "碎片",
    "scenicFragmentObtained": "获得碎片！通关后保存",
    "scenicFragmentProgress": "碎片 {found}/{total}",
    "searchColor": "搜索颜色或十六进制色值",
    "searchMaps": "搜索地图",
    "selectMapTemplate": "地图模板",
    "selectTemplateDescription": "上传图片后会自动生成 {count} 个关卡星点。你可以直接拖动每颗星微调位置，确认后保存为当前关卡选择地图。",
    "selectTemplateOrUpload": "上传图片",
    "selected": "已选择",
    "selectedChallenge": "当前选择",
    "selectedMap": "当前选择",
    "settings": "设置",
    "settingsTitle": "游戏设置",
    "share": "分享",
    "shareAction": "分享",
    "shareFailed": "分享失败。",
    "shareInviteText": "来点亮我制作的 BricksBeater 星图地图！",
    "shareMap": "仅分享人可见",
    "shareReady": "分享链接已准备好。",
    "shareSuccess": "分享成功",
    "shareUnavailable": "私密地图不能分享，请先修改地图权限。",
    "skill": "技能",
    "skillCount": "技能数量",
    "skins": "皮肤",
    "skinsTitle": "皮肤商店",
    "slow": "慢速",
    "sound": "游戏音效",
    "specialLevel": "特别关卡",
    "specialLevelLocked": "特殊关卡未解锁",
    "standard": "标准",
    "starMap": "星图",
    "starMapClear": "清除地图",
    "starMapCleared": "已清除当前自定义星图。",
    "starMapCurrentBadge": "当前星图",
    "starMapDefaultName": "输入地图名称",
    "starMapEdit": "编辑地图",
    "starMapEditorHint": "上传图片后会自动生成 {count} 个关卡星点。你可以直接拖动每颗星微调位置，确认后保存为当前关卡选择地图。",
    "starMapEditorTitle": "星图地图制作",
    "starMapGenerated": "已生成 {count} 个关卡星点，可以拖动微调。",
    "starMapImageError": "这张图片暂时无法处理。",
    "starMapLightProgress": "已点亮 {done} / {total}",
    "starMapName": "地图名称",
    "starMapPreviewInLevels": "打开关卡选择",
    "starMapProcessing": "正在分析图片...",
    "starMapRegenerate": "重新生成星点",
    "starMapRevealHint": "随着关卡通过，原图会在点亮区域逐渐显现。",
    "starMapSaveCurrent": "保存并设为当前地图",
    "starMapSaveError": "无法保存到本地，请尝试换一张更小的图片。",
    "starMapSaved": "已保存，并设为当前关卡选择地图。",
    "starMapUpload": "上传图片",
    "starMapUploadEmptyHint": "第一阶段使用普通图像分析生成，不需要 AI。",
    "starMapUploadEmptyTitle": "上传图片制作星图地图",
    "startError": "无法开始游戏。",
    "subtitle": "击碎虚空 · 点亮星辰",
    "systemMapCurrentProgress": "系统地图 · 当前到达第 {level} 关",
    "systemMapDescription": "系统制作的官方关卡，按原来的顺序逐关往下闯。",
    "systemMapName": "回响地图",
    "templateLoadFailed": "这张图片暂时无法处理。",
    "title": "地球回响",
    "totalLevels": "共 {count} 关",
    "unavailable": "该关卡资源暂未提供",
    "uploadImage": "上传图片",
    "uploadMapImage": "上传图片生成地图",
    "uploadMapImageHint": "图片上传与地图生成机制将在后续开发。",
    "victory": "胜利",
    "viewCompletedMap": "查看地图",
    "viewOriginal": "查看原图",
    "workshop": "创意工坊",
    "workshopComingSoon": "自定义地图工坊",
    "workshopDescription": "后续将在这里支持用户上传图片，并根据图片生成自己的砖块地图。",
    "workshopLevelMap": "关卡制作",
    "workshopStarMap": "地图制作",
    "workshopWorkbench": "制作台",
    "workshopToolSoon": "编辑器将在后续开发",
    "workshopTools": "地图工具",
    "worldMap": "世界地图",
    "zh": "zh",
    "skin.ball_default": "经典白",
    "skin.ball_star": "星星",
    "skin.ball_football": "足球",
    "skin.ball_flower": "花朵",
    "skin.ball_planet": "星球",
    "skin.ball_rainbow": "彩虹糖",
    "skin.ball_diamond": "钻石",
    "skin.paddle_default": "经典",
    "skin.paddle_stool": "小凳子",
    "skin.paddle_grass": "草地",
    "skin.paddle_rainbow": "彩虹桥",
    "skin.paddle_carpet": "魔法地毯",
    "skin.paddle_wave": "海浪",
    "skin.paddle_mecha": "机甲平台",
    "imageFileRequired": "上传图片",
    "imageReadFailed": "这张图片暂时无法处理。",
    "imageTooLarge": "这张图片暂时无法处理。",
    "imageUnsupported": "当前浏览器不支持 Canvas。",
    "imageNoRegion": "这张图片暂时无法处理。",
    "mapNotFound": "该关卡资源暂未提供",
    "mapCategoryWorld": "世界地图",
    "mapCategoryWorldShort": "世界",
    "mapCategoryCountry": "国家地图",
    "mapCategoryCountryShort": "国家",
    "mapCategoryRegion": "地区地图",
    "mapCategoryRegionShort": "地区",
    "worldClassicName": "世界经典地标",
    "worldClassicDesc": "由世界著名地标组成的探索地图",
    "worldRouteName": "环球旅行路线",
    "worldRouteDesc": "沿世界旅行路线设计的地图",
    "worldContinentsName": "七大洲探索",
    "worldContinentsDesc": "探索七大洲的地图",
    "chinaClassicName": "中国经典",
    "chinaClassicDesc": "包含中国著名城市和地标",
    "japanClassicName": "日本经典",
    "japanClassicDesc": "包含日本代表地点",
    "franceClassicName": "法国经典",
    "franceClassicDesc": "法国城市与地标地图",
    "usaClassicName": "美国经典",
    "usaClassicDesc": "美国代表地点地图",
    "europeClassicName": "欧洲经典城市",
    "europeClassicDesc": "欧洲旅行城市地图",
    "eastAsiaName": "东亚旅行",
    "eastAsiaDesc": "东亚城市探索地图",
    "southEastAsiaName": "东南亚旅行",
    "southEastAsiaDesc": "东南亚旅行地图",
    "chinaRegionName": "中国地区探索",
    "chinaRegionDesc": "中国地区主题地图",
    "demoWorldDreamName": "环游世界的第一站",
    "demoWorldDreamDesc": "把想去的城市做成一张星图，一关一关点亮自己的旅行清单。",
    "demoJapanNightName": "日本旅行计划",
    "demoJapanNightDesc": "东京、京都、大阪和富士山，组成一条可以游玩的旅行路线。",
    "demoChinaLandmarksName": "中国经典地标",
    "demoChinaLandmarksDesc": "从长城到西安，再一路点亮上海、成都和广州。",
    "demoFranceTripName": "法国浪漫路线",
    "demoFranceTripDesc": "巴黎、里昂、尼斯和斯特拉斯堡，适合慢慢点亮的一张地图。",
    "demoSevenContinentsName": "七大洲探索",
    "demoSevenContinentsDesc": "从亚洲出发，把七大洲都变成自己的闯关节点。",
    "demoUsaRoadName": "美国公路之旅",
    "demoUsaRoadDesc": "纽约、华盛顿、芝加哥、洛杉矶和大峡谷，一路打到西海岸。",
    "demoEuropeCityName": "欧洲城市漫游",
    "demoEuropeCityDesc": "伦敦、巴黎、柏林、罗马、雅典和巴塞罗那组成的城市闯关地图。",
    "scenicForbiddenCity": "故宫",
    "scenicTempleOfHeaven": "天坛",
    "scenicGreatWall": "长城",
    "scenicZhangjiajie": "张家界"
  }),
  "zh-TW": Object.freeze({
    "allFragmentsCollected": "已收集全部碎片",
    "back": "返回",
    "backHome": "返回首頁",
    "backendReady": "可直接串接後端的 Vue 前端",
    "ballSkins": "小球造型",
    "ballSpeed": "小球速度",
    "breakableCount": "可擊碎磚塊：{count}",
    "brickPalette": "磚塊調色盤",
    "builtLevels": "已製作 {done}/{total}",
    "levelBuilt": "已製作",
    "cancel": "取消",
    "cannotReadTemplate": "目前無法處理這張圖片。",
    "canvasUnsupported": "目前瀏覽器不支援 Canvas。",
    "challengeMapHint": "選擇首頁「開始遊戲」接下來要挑戰的地圖；系統地圖會一直保留為預設選項。",
    "challengeMapTitle": "選擇闖關地圖",
    "chapter": "章節",
    "chapterEndFragmentNotice": "碎片。",
    "chapters": "章節",
    "chooseChallenge": "選擇闖關",
    "chooseForPlay": "選擇闖關",
    "chooseMapPlay": "選擇此地圖開始遊戲",
    "clearSearch": "清除搜尋",
    "cleared": "已通關",
    "close": "關閉",
    "collectFragment": "收集碎片",
    "collectedFragments": "已收集 {count} 個碎片",
    "comingSoon": "即將開放",
    "completed": "已通關",
    "confirm": "確認",
    "confirmDeleteMap": "確定刪除「{name}」嗎？本機關卡資料也會一起刪除。",
    "confirmOverwrite": "依圖片產生會覆蓋目前畫布，是否繼續？",
    "continueGame": "繼續遊戲",
    "countryCA": "加拿大",
    "countryCN": "中國",
    "countryDE": "德國",
    "countryFR": "法國",
    "countryGB": "英國",
    "countryJP": "日本",
    "countryKR": "韓國",
    "countryUS": "美國",
    "createLevel": "建立關卡",
    "createStarMap": "建立星圖",
    "createStarMapFirst": "可以前往創意工坊製作星圖；尚未製作或選擇自訂地圖時，開始遊戲會預設繼續系統地圖。",
    "creativeWorkshop": "創意工坊",
    "creativeWorkshopHint": "自訂地圖",
    "currentLevel": "目前關卡",
    "currentLevelFormat": "目前到達第 {level} 關",
    "currentProgress": "從第 {level} 關繼續",
    "currentReachedLevel": "目前到達第 {level} 關",
    "custom": "自訂",
    "customMapCompleteSubtitle": "恭喜你，已完成《{name}》的全部關卡。",
    "customMapCompleteTitle": "地圖全部點亮！",
    "customMapCurrentProgress": "{name} · 目前到達第 {level} 關",
    "customMapDescription": "自己製作的星圖地圖，其中的關卡也都使用自製關卡。",
    "defeat": "失敗",
    "deleteAction": "刪除",
    "deleteMap": "刪除地圖",
    "difficulty": "遊戲難度",
    "dragNodesHint": "上傳圖片後會自動產生 {count} 個關卡星點。你可以直接拖曳每顆星微調位置，確認後儲存為目前的關卡選擇地圖。",
    "editMap": "編輯地圖",
    "en": "en",
    "energyFull": "已滿",
    "energyInsufficient": "體力不足：目前 {current} 點，每局需要 {cost} 點，請等待恢復。",
    "energyLabel": "體力",
    "energyLoading": "讀取中",
    "enterMap": "進入地圖",
    "mapViews": "瀏覽量",
    "likeMap": "按讚",
    "unlikeMap": "取消按讚",
    "enterSpecialLevel": "進入特殊關卡",
    "extreme": "極難",
    "fast": "快速",
    "finalLevel": "最終關",
    "fr": "fr",
    "fragment": "碎片",
    "fragmentCollected": "已收集碎片",
    "fragmentProgress": "碎片進度",
    "fragments": "碎片",
    "gameHelp": "A/D 或 ←/→ 移動 · 空白鍵發射 · R 重新開始",
    "gameFailedTitle": "遊戲失敗",
    "retryChallenge": "重新闖關",
    "generateMap": "產生地圖",
    "goCreateStarMap": "星圖地圖製作",
    "hard": "困難",
    "highScore": "最高分",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "雷射飛彈",
    "leaderboard": "排行榜",
    "leaderboardFullTitle": "完整國家排行榜",
    "leaderboardHint": "查看排名",
    "leaderboardPageTitle": "🌌 全球國家排行榜",
    "leaderboardPlaceholderNote": "排行榜的 Router、Store 與 API 介面層已準備完成。目前頁面仍是暫時版面，之後可在不變更後端串接結構的情況下替換畫面。",
    "leaderboardScore": "分數",
    "resultLevelScore": "關卡得分",
    "leaderboardCleared": "闖關進度",
    "level": "關卡",
    "levelCountFormat": "{count} 個關卡",
    "levelCountPoints": "關卡數 / 點數",
    "levelLocked": "關卡未解鎖",
    "levelMap": "關卡地圖",
    "levelPlaysFormat": "{count} 個關卡 · 遊玩 {plays} 次",
    "levelSelect": "關卡選擇",
    "litLevels": "已點亮 {done}/{total}",
    "loading": "載入中",
    "loadingLevel": "正在載入關卡資料...",
    "loadingSharedMap": "正在載入分享地圖…",
    "loadingTemplate": "正在分析圖片...",
    "locked": "未解鎖",
    "mapCompleted": "地圖已完成",
    "mapDescription": "地圖描述",
    "mapDescriptionPlaceholder": "輸入地圖描述",
    "mapFullyLit": "地圖已全部點亮",
    "mapName": "地圖名稱",
    "mapPermission": "地圖權限",
    "mapTemplate": "地圖範本",
    "mapTitle": "迴響地圖",
    "mapUnlocked": "地圖已解鎖",
    "mapUpdatedSaved": "已儲存，並設為目前的關卡選擇地圖。",
    "modeGuide": "模式說明",
    "modeGuideExtreme": "極難：道具掉落機率約為標準的 35%",
    "modeGuideHard": "困難：道具掉落機率約為標準的 65%",
    "modeGuideSpeed": "小球速度越快，操作與通關難度越高",
    "modeGuideStandard": "標準：維持原始道具掉落機率",
    "myCustomMaps": "我的作品",
    "myStarMap": "我的星圖地圖",
    "myWorkshop": "我的工坊",
    "next": "下一步",
    "noCustomMaps": "尚未製作自訂地圖",
    "noCustomMapsHint": "可以前往創意工坊製作星圖；尚未製作或選擇自訂地圖時，開始遊戲會預設繼續系統地圖。",
    "noCustomStarMap": "尚未製作自訂地圖",
    "noMapDescription": "暫無地圖描述",
    "noMatches": "沒有符合結果",
    "normal": "標準",
    "off": "關閉",
    "on": "開啟",
    "openStarMapEditor": "編輯地圖",
    "originalImageTitle": "原圖",
    "paddleGrow": "擋板加長",
    "paddleSkins": "擋板造型",
    "play": "開始遊戲",
    "playsLikesFormat": "遊玩 {plays} 次 · {likes} 個讚",
    "previewHint": "上傳圖片後會自動產生 {count} 個關卡星點。你可以直接拖曳每顆星微調位置，確認後儲存為目前的關卡選擇地圖。",
    "previous": "上一步",
    "privateMap": "私密",
    "publicMap": "公開",
    "visibilityChangedPublic": "已切換為公開可見。",
    "visibilityChangedPrivate": "已切換為私人。",
    "visibilityChangedShare": "已切換為僅分享可見。",
    "publicMaps": "廣場",
    "publicMapsDescription": "探索其他玩家建立的地圖",
    "regenerateByCount": "軌跡重構",
    "regenerateNodesSuccess": "已產生 {count} 個關卡星點，請先點擊「軌跡重構」，然後再拖曳微調。",
    "replayLevel": "重玩關卡",
    "restart": "重新開始",
    "result": "結果",
    "returnHome": "返回首頁",
    "runtimeBalls": "球數",
    "runtimeHome": "主頁",
    "runtimeLives": "生命",
    "runtimePaused": "遊戲暫停",
    "runtimeResume": "繼續遊戲",
    "runtimeTime": "時間",
    "save": "儲存",
    "saveAndBack": "儲存並返回",
    "saveFailed": "儲存失敗",
    "saveMap": "儲存地圖",
    "saveMapAndCreateLevels": "儲存並設為目前地圖",
    "saveSuccess": "儲存成功",
    "scenicFragment": "碎片",
    "scenicFragmentObtained": "獲得碎片！通關後儲存",
    "scenicFragmentProgress": "碎片 {found}/{total}",
    "searchColor": "搜尋顏色或十六進位色碼",
    "searchMaps": "搜尋地圖",
    "selectMapTemplate": "地圖範本",
    "selectTemplateDescription": "上傳圖片後會自動產生 {count} 個關卡星點。你可以直接拖曳每顆星微調位置，確認後儲存為目前的關卡選擇地圖。",
    "selectTemplateOrUpload": "上傳圖片",
    "selected": "已選擇",
    "selectedChallenge": "目前選擇",
    "selectedMap": "目前選擇",
    "settings": "設定",
    "settingsTitle": "遊戲設定",
    "share": "分享",
    "shareAction": "分享",
    "shareFailed": "分享失敗。",
    "shareInviteText": "來點亮我製作的 BricksBeater 星圖地圖！",
    "shareMap": "僅分享對象可見",
    "shareReady": "分享連結已準備好。",
    "shareSuccess": "分享成功",
    "shareUnavailable": "私密地圖不能分享，請先修改地圖權限。",
    "skill": "技能",
    "skillCount": "技能數量",
    "skins": "造型",
    "skinsTitle": "造型商店",
    "slow": "慢速",
    "sound": "遊戲音效",
    "specialLevel": "特別關卡",
    "specialLevelLocked": "特殊關卡尚未解鎖",
    "standard": "標準",
    "starMap": "星圖",
    "starMapClear": "清除地圖",
    "starMapCleared": "已清除目前的自訂星圖。",
    "starMapCurrentBadge": "目前星圖",
    "starMapDefaultName": "輸入地圖名稱",
    "starMapEdit": "編輯地圖",
    "starMapEditorHint": "上傳圖片後會自動產生 {count} 個關卡星點。你可以直接拖曳每顆星微調位置，確認後儲存為目前的關卡選擇地圖。",
    "starMapEditorTitle": "星圖地圖製作",
    "starMapGenerated": "已產生 {count} 個關卡星點，可以拖曳微調。",
    "starMapImageError": "目前無法處理這張圖片。",
    "starMapLightProgress": "已點亮 {done} / {total}",
    "starMapName": "地圖名稱",
    "starMapPreviewInLevels": "開啟關卡選擇",
    "starMapProcessing": "正在分析圖片...",
    "starMapRegenerate": "重新產生星點",
    "starMapRevealHint": "隨著關卡通過，原圖會在點亮區域逐漸顯現。",
    "starMapSaveCurrent": "儲存並設為目前地圖",
    "starMapSaveError": "無法儲存到本機，請嘗試使用較小的圖片。",
    "starMapSaved": "已儲存，並設為目前的關卡選擇地圖。",
    "starMapUpload": "上傳圖片",
    "starMapUploadEmptyHint": "第一階段使用一般影像分析產生，不需要 AI。",
    "starMapUploadEmptyTitle": "上傳圖片製作星圖地圖",
    "startError": "無法開始遊戲。",
    "subtitle": "擊碎虛空 · 點亮星辰",
    "systemMapCurrentProgress": "系統地圖 · 目前到達第 {level} 關",
    "systemMapDescription": "系統製作的官方關卡，依原本順序逐關挑戰。",
    "systemMapName": "迴響地圖",
    "templateLoadFailed": "目前無法處理這張圖片。",
    "title": "地球迴響",
    "totalLevels": "共 {count} 關",
    "unavailable": "此關卡資源尚未提供",
    "uploadImage": "上傳圖片",
    "uploadMapImage": "上傳圖片產生地圖",
    "uploadMapImageHint": "圖片上傳與地圖產生機制將於後續開發。",
    "victory": "勝利",
    "viewCompletedMap": "查看地圖",
    "viewOriginal": "查看原圖",
    "workshop": "創意工坊",
    "workshopComingSoon": "自訂地圖工坊",
    "workshopDescription": "之後將在這裡支援玩家上傳圖片，並依圖片製作自己的磚塊地圖。",
    "workshopLevelMap": "關卡製作",
    "workshopStarMap": "地圖製作",
    "workshopWorkbench": "製作台",
    "workshopToolSoon": "編輯器將於後續開放",
    "workshopTools": "地圖工具",
    "worldMap": "世界地圖",
    "zh": "zh",
    "skin.ball_default": "經典白",
    "skin.ball_star": "星星",
    "skin.ball_football": "足球",
    "skin.ball_flower": "花朵",
    "skin.ball_planet": "星球",
    "skin.ball_rainbow": "彩虹糖",
    "skin.ball_diamond": "鑽石",
    "skin.paddle_default": "經典",
    "skin.paddle_stool": "小凳子",
    "skin.paddle_grass": "草地",
    "skin.paddle_rainbow": "彩虹橋",
    "skin.paddle_carpet": "魔法地毯",
    "skin.paddle_wave": "海浪",
    "skin.paddle_mecha": "機甲平台",
    "imageFileRequired": "上傳圖片",
    "imageReadFailed": "目前無法處理這張圖片。",
    "imageTooLarge": "目前無法處理這張圖片。",
    "imageUnsupported": "目前瀏覽器不支援 Canvas。",
    "imageNoRegion": "目前無法處理這張圖片。",
    "mapNotFound": "此關卡資源尚未提供",
    "mapCategoryWorld": "世界地圖",
    "mapCategoryWorldShort": "世界",
    "mapCategoryCountry": "國家地圖",
    "mapCategoryCountryShort": "國家",
    "mapCategoryRegion": "地區地圖",
    "mapCategoryRegionShort": "地區",
    "worldClassicName": "世界經典地標",
    "worldClassicDesc": "由世界著名地標組成的探索地圖。",
    "worldRouteName": "環球旅行路線",
    "worldRouteDesc": "沿世界旅行路線設計的地圖。",
    "worldContinentsName": "七大洲探索",
    "worldContinentsDesc": "探索七大洲的地圖。",
    "chinaClassicName": "中國經典",
    "chinaClassicDesc": "包含中國著名城市與地標。",
    "japanClassicName": "日本經典",
    "japanClassicDesc": "包含日本代表性地點。",
    "franceClassicName": "法國經典",
    "franceClassicDesc": "法國城市與地標地圖。",
    "usaClassicName": "美國經典",
    "usaClassicDesc": "美國代表性地點地圖。",
    "europeClassicName": "歐洲經典城市",
    "europeClassicDesc": "歐洲旅行城市地圖。",
    "eastAsiaName": "東亞旅行",
    "eastAsiaDesc": "東亞城市探索地圖。",
    "southEastAsiaName": "東南亞旅行",
    "southEastAsiaDesc": "東南亞旅行路線地圖。",
    "chinaRegionName": "中國地區探索",
    "chinaRegionDesc": "中國地區主題探索地圖。",
    "demoWorldDreamName": "環遊世界的第一站",
    "demoWorldDreamDesc": "把想去的城市做成一張星圖，一關一關點亮自己的旅行清單。",
    "demoJapanNightName": "日本旅行計畫",
    "demoJapanNightDesc": "東京、京都、大阪和富士山，組成一條可以遊玩的旅行路線。",
    "demoChinaLandmarksName": "中國經典地標",
    "demoChinaLandmarksDesc": "從長城到西安，再一路點亮上海、成都和廣州。",
    "demoFranceTripName": "法國浪漫路線",
    "demoFranceTripDesc": "巴黎、里昂、尼斯和史特拉斯堡，適合慢慢點亮的一張地圖。",
    "demoSevenContinentsName": "七大洲探索",
    "demoSevenContinentsDesc": "從亞洲出發，把七大洲都變成自己的闖關節點。",
    "demoUsaRoadName": "美國公路之旅",
    "demoUsaRoadDesc": "紐約、華盛頓、芝加哥、洛杉磯和大峽谷，一路玩到西海岸。",
    "demoEuropeCityName": "歐洲城市漫遊",
    "demoEuropeCityDesc": "倫敦、巴黎、柏林、羅馬、雅典和巴塞隆納組成的城市闖關地圖。",
    "scenicForbiddenCity": "故宮",
    "scenicTempleOfHeaven": "天壇",
    "scenicGreatWall": "長城",
    "scenicZhangjiajie": "張家界"
  }),
  "ja": Object.freeze({
    "allFragmentsCollected": "すべてのピースを集めました",
    "back": "戻る",
    "backHome": "ホームへ",
    "backendReady": "バックエンド連携対応 Vue フロントエンド",
    "ballSkins": "ボールスキン",
    "ballSpeed": "ボール速度",
    "breakableCount": "破壊可能ブロック：{count}",
    "brickPalette": "ブロックパレット",
    "builtLevels": "制作済み {done}/{total}",
    "levelBuilt": "制作済み",
    "cancel": "キャンセル",
    "cannotReadTemplate": "画像を処理できませんでした。",
    "canvasUnsupported": "このブラウザは Canvas に対応していません。",
    "challengeMapHint": "ホームのプレイボタンで進めるマップを選びます。システムマップは常に利用できます。",
    "challengeMapTitle": "プレイマップ選択",
    "chapter": "チャプター",
    "chapterEndFragmentNotice": "名所のピース。",
    "chapters": "チャプター",
    "chooseChallenge": "このマップを選ぶ",
    "chooseForPlay": "このマップを選ぶ",
    "chooseMapPlay": "このマップでプレイ",
    "clearSearch": "検索をクリア",
    "cleared": "クリア",
    "close": "閉じる",
    "collectFragment": "ピースを集める",
    "collectedFragments": "{count} 個のピースを収集",
    "comingSoon": "準備中",
    "completed": "クリア済み",
    "confirm": "確認",
    "confirmDeleteMap": "「{name}」を削除しますか？ローカルのレベルデータも削除されます。",
    "confirmOverwrite": "画像から生成すると現在のキャンバスが上書きされます。続行しますか？",
    "continueGame": "続ける",
    "countryCA": "カナダ",
    "countryCN": "中国",
    "countryDE": "ドイツ",
    "countryFR": "フランス",
    "countryGB": "イギリス",
    "countryJP": "日本",
    "countryKR": "韓国",
    "countryUS": "アメリカ",
    "createLevel": "ステージを作成",
    "createStarMap": "スターマップを作成",
    "createStarMapFirst": "工房で星図を作成できます。それまではシステムマップが既定です。",
    "creativeWorkshop": "工房",
    "creativeWorkshopHint": "カスタムマップ",
    "currentLevel": "現在のレベル",
    "currentLevelFormat": "現在レベル {level}",
    "currentProgress": "レベル {level}から続ける",
    "currentReachedLevel": "現在レベル {level}",
    "custom": "カスタム",
    "customMapCompleteSubtitle": "おめでとうございます！{name} の全ステージを点灯しました。",
    "customMapCompleteTitle": "マップ完成！",
    "customMapCurrentProgress": "{name} · 現在レベル {level}",
    "customMapDescription": "自作の星図と自作ステージで進むマップです。",
    "defeat": "敗北",
    "deleteAction": "削除",
    "deleteMap": "マップを削除",
    "difficulty": "ゲーム難易度",
    "dragNodesHint": "画像から{count}個のレベル星を自動生成します。星をドラッグして微調整し、現在のレベル選択マップとして保存できます。",
    "editMap": "マップを編集",
    "en": "en",
    "energyFull": "満タン",
    "energyInsufficient": "エネルギー不足：{current}/{cost}。回復を待ってください。",
    "energyLabel": "エネルギー",
    "energyLoading": "読込中",
    "enterMap": "マップに入る",
    "mapViews": "閲覧数",
    "likeMap": "いいね",
    "unlikeMap": "いいねを取り消す",
    "enterSpecialLevel": "スペシャルステージへ",
    "extreme": "極限",
    "fast": "速い",
    "finalLevel": "最終",
    "fr": "fr",
    "fragment": "フラグメント",
    "fragmentCollected": "ピースを獲得",
    "fragmentProgress": "ピース進捗",
    "fragments": "ピース",
    "gameHelp": "A/D または ←/→ で移動 · Space で発射 · R でリスタート",
    "gameFailedTitle": "ゲーム失敗",
    "retryChallenge": "再挑戦",
    "generateMap": "マップを生成",
    "goCreateStarMap": "星図メーカー",
    "hard": "ハード",
    "highScore": "ハイスコア",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "レーザーミサイル",
    "leaderboard": "ランキング",
    "leaderboardFullTitle": "国別ランキング一覧",
    "leaderboardHint": "順位を見る",
    "leaderboardPageTitle": "🌌 世界の国別ランキング",
    "leaderboardPlaceholderNote": "ランキング用のRouter・Store・API層は準備済みです。今回のメッセージには対象HTMLが含まれていないため、現在は仮レイアウトのみです。後からバックエンド連携コードを変えずに画面だけ差し替えられます。",
    "leaderboardScore": "スコア",
    "resultLevelScore": "ステージスコア",
    "leaderboardCleared": "クリア進捗",
    "level": "レベル",
    "levelCountFormat": "{count} ステージ",
    "levelCountPoints": "レベル数 / ポイント数",
    "levelLocked": "ステージはロック中",
    "levelMap": "ステージマップ",
    "levelPlaysFormat": "{count} ステージ · {plays} 回プレイ",
    "levelSelect": "レベル選択",
    "litLevels": "点灯 {done}/{total}",
    "loading": "読み込み中",
    "loadingLevel": "レベルデータを読み込み中...",
    "loadingSharedMap": "共有マップを読み込み中…",
    "loadingTemplate": "画像を解析中...",
    "locked": "ロック中",
    "mapCompleted": "マップクリア",
    "mapDescription": "マップ説明",
    "mapDescriptionPlaceholder": "マップの説明を入力",
    "mapFullyLit": "マップ全点灯",
    "mapName": "マップ名",
    "mapPermission": "マップ権限",
    "mapTemplate": "マップテンプレート",
    "mapTitle": "エコーマップ",
    "mapUnlocked": "マップ解放",
    "mapUpdatedSaved": "保存しました。現在のレベル選択マップに設定されました。",
    "modeGuide": "モード説明",
    "modeGuideExtreme": "極限：標準の約35%のアイテムドロップ率",
    "modeGuideHard": "ハード：標準の約65%のアイテムドロップ率",
    "modeGuideSpeed": "ボールが速いほど操作とクリアの難易度が上がります",
    "modeGuideStandard": "標準：元のアイテムドロップ率を維持",
    "myCustomMaps": "マイ作品",
    "myStarMap": "マイ星図",
    "myWorkshop": "マイ工房",
    "next": "次へ",
    "noCustomMaps": "カスタムマップはまだありません",
    "noCustomMapsHint": "工房で星図を作成できます。それまではシステムマップが既定です。",
    "noCustomStarMap": "カスタムマップはまだありません",
    "noMapDescription": "マップの説明はありません",
    "noMatches": "一致する項目はありません",
    "normal": "普通",
    "off": "オフ",
    "on": "オン",
    "openStarMapEditor": "マップ編集",
    "originalImageTitle": "元画像",
    "paddleGrow": "パドル拡大",
    "paddleSkins": "パドルスキン",
    "play": "プレイ",
    "playsLikesFormat": "{plays} 回プレイ · {likes} いいね",
    "previewHint": "画像から{count}個のレベル星を自動生成します。星をドラッグして微調整し、現在のレベル選択マップとして保存できます。",
    "previous": "前へ",
    "privateMap": "非公開",
    "publicMap": "公開",
    "visibilityChangedPublic": "公開表示に切り替えました。",
    "visibilityChangedPrivate": "非公開に切り替えました。",
    "visibilityChangedShare": "共有リンクのみ表示に切り替えました。",
    "publicMaps": "ギャラリー",
    "publicMapsDescription": "ほかのプレイヤーが作成したマップを探す",
    "regenerateByCount": "軌跡再構成",
    "regenerateNodesSuccess": "{count}個のレベル星を生成しました。先に軌道再構築を押してからドラッグしてください。",
    "replayLevel": "もう一度プレイ",
    "restart": "リスタート",
    "result": "結果",
    "returnHome": "ホームへ戻る",
    "runtimeBalls": "ボール",
    "runtimeHome": "ホーム",
    "runtimeLives": "ライフ",
    "runtimePaused": "一時停止",
    "runtimeResume": "再開",
    "runtimeTime": "時間",
    "save": "保存",
    "saveAndBack": "保存して戻る",
    "saveFailed": "保存に失敗しました",
    "saveMap": "マップを保存",
    "saveMapAndCreateLevels": "現在のマップとして保存",
    "saveSuccess": "保存しました",
    "scenicFragment": "名所のかけら",
    "scenicFragmentObtained": "名所のかけらを獲得！クリア後に保存されます",
    "scenicFragmentProgress": "名所のかけら {found}/{total}",
    "searchColor": "色またはHEX値を検索",
    "searchMaps": "マップを検索",
    "selectMapTemplate": "マップテンプレート",
    "selectTemplateDescription": "画像から{count}個のレベル星を自動生成します。星をドラッグして微調整し、現在のレベル選択マップとして保存できます。",
    "selectTemplateOrUpload": "画像をアップロード",
    "selected": "選択中",
    "selectedChallenge": "選択中",
    "selectedMap": "選択中",
    "settings": "設定",
    "settingsTitle": "ゲーム設定",
    "share": "共有",
    "shareAction": "共有",
    "shareFailed": "共有に失敗しました。",
    "shareInviteText": "私のBricksBeaterスターマップを一緒に点灯しよう！",
    "shareMap": "共有相手のみ",
    "shareReady": "共有リンクを準備しました。",
    "shareSuccess": "共有しました",
    "shareUnavailable": "非公開マップは共有できません。先に公開範囲を変更してください。",
    "skill": "スキル",
    "skillCount": "スキル数",
    "skins": "スキン",
    "skinsTitle": "スキンショップ",
    "slow": "遅い",
    "sound": "ゲーム音",
    "specialLevel": "特別レベル",
    "specialLevelLocked": "特殊ステージは未解放です",
    "standard": "標準",
    "starMap": "スターマップ",
    "starMapClear": "マップ削除",
    "starMapCleared": "現在のカスタム星図を削除しました。",
    "starMapCurrentBadge": "現在の星図",
    "starMapDefaultName": "マップ名を入力",
    "starMapEdit": "マップ編集",
    "starMapEditorHint": "画像から{count}個のレベル星を自動生成します。星をドラッグして微調整し、現在のレベル選択マップとして保存できます。",
    "starMapEditorTitle": "星図メーカー",
    "starMapGenerated": "{count}個のレベル星を生成しました。ドラッグして調整できます。",
    "starMapImageError": "画像を処理できませんでした。",
    "starMapLightProgress": "点灯 {done} / {total}",
    "starMapName": "マップ名",
    "starMapPreviewInLevels": "レベル選択を開く",
    "starMapProcessing": "画像を解析中...",
    "starMapRegenerate": "星を再生成",
    "starMapRevealHint": "クリアするほど元画像が少しずつ現れます。",
    "starMapSaveCurrent": "現在のマップとして保存",
    "starMapSaveError": "星図をローカル保存できませんでした。",
    "starMapSaved": "保存しました。現在のレベル選択マップに設定されました。",
    "starMapUpload": "画像をアップロード",
    "starMapUploadEmptyHint": "初期版はAIを使わず画像解析で生成します。",
    "starMapUploadEmptyTitle": "画像をアップロードして星図を作成",
    "startError": "ゲームを開始できません。",
    "subtitle": "虚空を砕き · 星を灯せ",
    "systemMapCurrentProgress": "システムマップ · 現在レベル {level}",
    "systemMapDescription": "公式ステージを順番に進めます。",
    "systemMapName": "エコーマップ",
    "templateLoadFailed": "画像を処理できませんでした。",
    "title": "地球のこだま",
    "totalLevels": "全 {count} レベル",
    "unavailable": "このレベルのデータはまだありません",
    "uploadImage": "画像をアップロード",
    "uploadMapImage": "画像をアップロードしてマップを作成",
    "uploadMapImageHint": "アップロードとマップ生成機能は今後の開発で追加します。",
    "victory": "勝利",
    "viewCompletedMap": "マップを見る",
    "viewOriginal": "元画像を見る",
    "workshop": "ワークショップ",
    "workshopComingSoon": "カスタムマップ工房",
    "workshopDescription": "画像をアップロードしてオリジナルのブロックマップを作成できる機能を今後追加します。",
    "workshopLevelMap": "ステージ制作",
    "workshopStarMap": "マップ制作",
    "workshopWorkbench": "制作台",
    "workshopToolSoon": "エディターは今後追加",
    "workshopTools": "マップツール",
    "worldMap": "世界地図",
    "zh": "zh",
    "skin.ball_default": "クラシックホワイト",
    "skin.ball_star": "スター",
    "skin.ball_football": "サッカーボール",
    "skin.ball_flower": "花",
    "skin.ball_planet": "惑星",
    "skin.ball_rainbow": "レインボーキャンディ",
    "skin.ball_diamond": "ダイヤモンド",
    "skin.paddle_default": "クラシック",
    "skin.paddle_stool": "スツール",
    "skin.paddle_grass": "芝生",
    "skin.paddle_rainbow": "虹の橋",
    "skin.paddle_carpet": "魔法のじゅうたん",
    "skin.paddle_wave": "波",
    "skin.paddle_mecha": "メカプラットフォーム",
    "imageFileRequired": "画像をアップロード",
    "imageReadFailed": "画像を処理できませんでした。",
    "imageTooLarge": "画像を処理できませんでした。",
    "imageUnsupported": "このブラウザは Canvas に対応していません。",
    "imageNoRegion": "画像を処理できませんでした。",
    "mapNotFound": "このレベルのデータはまだありません",
    "mapCategoryWorld": "世界地図",
    "mapCategoryWorldShort": "世界",
    "mapCategoryCountry": "国別地図",
    "mapCategoryCountryShort": "国",
    "mapCategoryRegion": "地域地図",
    "mapCategoryRegionShort": "地域",
    "worldClassicName": "世界の定番ランドマーク",
    "worldClassicDesc": "世界の有名なランドマークで構成された探索マップ。",
    "worldRouteName": "世界旅行ルート",
    "worldRouteDesc": "世界を巡る旅行ルートをテーマにしたマップ。",
    "worldContinentsName": "七大陸探索",
    "worldContinentsDesc": "七大陸を探索するマップ。",
    "chinaClassicName": "中国クラシック",
    "chinaClassicDesc": "中国の有名都市とランドマークを収録。",
    "japanClassicName": "日本クラシック",
    "japanClassicDesc": "日本を代表する場所を収録。",
    "franceClassicName": "フランスクラシック",
    "franceClassicDesc": "フランスの都市とランドマークを巡るマップ。",
    "usaClassicName": "アメリカクラシック",
    "usaClassicDesc": "アメリカを代表するランドマークのマップ。",
    "europeClassicName": "ヨーロッパの定番都市",
    "europeClassicDesc": "ヨーロッパの旅行都市を巡るマップ。",
    "eastAsiaName": "東アジア旅行",
    "eastAsiaDesc": "東アジアの都市とランドマークを巡るマップ。",
    "southEastAsiaName": "東南アジア旅行",
    "southEastAsiaDesc": "東南アジアを巡る旅行ルート。",
    "chinaRegionName": "中国地域探索",
    "chinaRegionDesc": "中国の各地域をテーマにした探索マップ。",
    "demoWorldDreamName": "世界一周への第一歩",
    "demoWorldDreamDesc": "行きたい都市を星図にして、自分だけの旅行リストをステージごとに輝かせよう。",
    "demoJapanNightName": "日本旅行プラン",
    "demoJapanNightDesc": "東京、京都、大阪、富士山を巡るプレイ可能な旅行ルート。",
    "demoChinaLandmarksName": "中国の名所",
    "demoChinaLandmarksDesc": "万里の長城、西安、上海、成都、広州を巡るルート。",
    "demoFranceTripName": "フランス・ロマンチックルート",
    "demoFranceTripDesc": "パリ、リヨン、ニース、ストラスブールをゆっくり巡るマップ。",
    "demoSevenContinentsName": "七大陸探索",
    "demoSevenContinentsDesc": "アジアから出発し、七大陸を自分のステージにしよう。",
    "demoUsaRoadName": "アメリカ横断ロードトリップ",
    "demoUsaRoadDesc": "ニューヨーク、ワシントン、シカゴ、ロサンゼルス、グランドキャニオンを巡る旅。",
    "demoEuropeCityName": "ヨーロッパ都市巡り",
    "demoEuropeCityDesc": "ロンドン、パリ、ベルリン、ローマ、アテネ、バルセロナを巡る都市マップ。",
    "scenicForbiddenCity": "故宮",
    "scenicTempleOfHeaven": "天壇",
    "scenicGreatWall": "万里の長城",
    "scenicZhangjiajie": "張家界"
  }),
  "ko": Object.freeze({
    "allFragmentsCollected": "모든 조각을 수집했습니다",
    "back": "뒤로",
    "backHome": "홈으로",
    "backendReady": "백엔드 연동 준비 완료 Vue 프론트엔드",
    "ballSkins": "공 스킨",
    "ballSpeed": "공 속도",
    "breakableCount": "파괴 가능한 벽돌: {count}",
    "brickPalette": "블록 팔레트",
    "builtLevels": "제작 {done}/{total}",
    "levelBuilt": "제작 완료",
    "cancel": "취소",
    "cannotReadTemplate": "이미지를 처리할 수 없습니다.",
    "canvasUnsupported": "이 브라우저는 Canvas를 지원하지 않습니다.",
    "challengeMapHint": "홈의 플레이 버튼으로 이어서 할 지도를 선택하세요. 시스템 지도는 항상 사용할 수 있습니다.",
    "challengeMapTitle": "플레이 지도 선택",
    "chapter": "챕터",
    "chapterEndFragmentNotice": "명소 조각.",
    "chapters": "챕터",
    "chooseChallenge": "이 지도 선택",
    "chooseForPlay": "이 지도 선택",
    "chooseMapPlay": "이 맵으로 플레이",
    "clearSearch": "검색 지우기",
    "cleared": "클리어",
    "close": "닫기",
    "collectFragment": "조각 수집",
    "collectedFragments": "조각 {count}개 수집",
    "comingSoon": "준비 중",
    "completed": "완료",
    "confirm": "확인",
    "confirmDeleteMap": "“{name}”을(를) 삭제할까요? 로컬 레벨 데이터도 함께 삭제됩니다.",
    "confirmOverwrite": "이미지에서 생성하면 현재 캔버스를 덮어씁니다. 계속할까요?",
    "continueGame": "계속하기",
    "countryCA": "캐나다",
    "countryCN": "중국",
    "countryDE": "독일",
    "countryFR": "프랑스",
    "countryGB": "영국",
    "countryJP": "일본",
    "countryKR": "대한민국",
    "countryUS": "미국",
    "createLevel": "레벨 만들기",
    "createStarMap": "스타 맵 만들기",
    "createStarMapFirst": "공방에서 별지도를 만들어 보세요. 만들기 전에는 시스템 지도가 기본으로 진행됩니다.",
    "creativeWorkshop": "공방",
    "creativeWorkshopHint": "커스텀 맵",
    "currentLevel": "현재 레벨",
    "currentLevelFormat": "현재 레벨 {level}",
    "currentProgress": "레벨 {level}부터 계속",
    "currentReachedLevel": "현재 레벨 {level}",
    "custom": "사용자 지정",
    "customMapCompleteSubtitle": "축하합니다! {name}의 모든 레벨을 밝혔습니다.",
    "customMapCompleteTitle": "지도 완성!",
    "customMapCurrentProgress": "{name} · 현재 레벨 {level}",
    "customMapDescription": "직접 만든 별지도와 커스텀 스테이지입니다.",
    "defeat": "패배",
    "deleteAction": "삭제",
    "deleteMap": "맵 삭제",
    "difficulty": "게임 난이도",
    "dragNodesHint": "이미지를 업로드하면 {count}개의 레벨 별이 자동 생성됩니다. 별을 드래그해 위치를 조정한 뒤 현재 레벨 선택 지도로 저장하세요.",
    "editMap": "맵 편집",
    "en": "en",
    "energyFull": "가득 참",
    "energyInsufficient": "에너지가 부족합니다: {current}/{cost}. 회복을 기다려 주세요.",
    "energyLabel": "에너지",
    "energyLoading": "불러오는 중",
    "enterMap": "맵 들어가기",
    "mapViews": "조회수",
    "likeMap": "좋아요",
    "unlikeMap": "좋아요 취소",
    "enterSpecialLevel": "특별 레벨 입장",
    "extreme": "극한",
    "fast": "빠름",
    "finalLevel": "최종",
    "fr": "fr",
    "fragment": "조각",
    "fragmentCollected": "조각 획득",
    "fragmentProgress": "조각 진행도",
    "fragments": "조각",
    "gameHelp": "A/D 또는 ←/→ 이동 · Space 발사 · R 재시작",
    "gameFailedTitle": "게임 실패",
    "retryChallenge": "다시 도전",
    "generateMap": "맵 생성",
    "goCreateStarMap": "별지도 제작",
    "hard": "어려움",
    "highScore": "최고 점수",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "레이저 미사일",
    "leaderboard": "랭킹",
    "leaderboardFullTitle": "전체 국가 랭킹",
    "leaderboardHint": "순위 보기",
    "leaderboardPageTitle": "🌌 세계 국가 랭킹",
    "leaderboardPlaceholderNote": "랭킹 페이지의 라우터·Store·API 구조는 준비되어 있습니다. 이번 메시지에 목표 HTML이 포함되지 않아 현재는 임시 화면 골격만 제공하며, 이후 백엔드 연동 구조를 바꾸지 않고 화면만 교체할 수 있습니다.",
    "leaderboardScore": "점수",
    "resultLevelScore": "스테이지 점수",
    "leaderboardCleared": "클리어 진행도",
    "level": "레벨",
    "levelCountFormat": "레벨 {count}개",
    "levelCountPoints": "레벨 수 / 포인트 수",
    "levelLocked": "레벨 잠김",
    "levelMap": "레벨 맵",
    "levelPlaysFormat": "레벨 {count}개 · {plays}회 플레이",
    "levelSelect": "레벨 선택",
    "litLevels": "점등 {done}/{total}",
    "loading": "불러오는 중",
    "loadingLevel": "레벨 데이터를 불러오는 중...",
    "loadingSharedMap": "공유 지도를 불러오는 중…",
    "loadingTemplate": "이미지 분석 중...",
    "locked": "잠김",
    "mapCompleted": "맵 완료",
    "mapDescription": "맵 설명",
    "mapDescriptionPlaceholder": "맵 설명 입력",
    "mapFullyLit": "지도 전체 점등",
    "mapName": "지도 이름",
    "mapPermission": "지도 권한",
    "mapTemplate": "지도 템플릿",
    "mapTitle": "에코 맵",
    "mapUnlocked": "맵 잠금 해제",
    "mapUpdatedSaved": "저장했습니다. 현재 레벨 선택 지도로 설정되었습니다.",
    "modeGuide": "모드 안내",
    "modeGuideExtreme": "극한: 표준의 약 35% 아이템 드롭 확률",
    "modeGuideHard": "어려움: 표준의 약 65% 아이템 드롭 확률",
    "modeGuideSpeed": "공이 빠를수록 조작과 클리어 난도가 높아집니다",
    "modeGuideStandard": "표준: 기본 아이템 드롭 확률 유지",
    "myCustomMaps": "내 작품",
    "myStarMap": "나의 별지도",
    "myWorkshop": "내 작업실",
    "next": "다음",
    "noCustomMaps": "아직 커스텀 지도가 없습니다",
    "noCustomMapsHint": "공방에서 별지도를 만들어 보세요. 만들기 전에는 시스템 지도가 기본으로 진행됩니다.",
    "noCustomStarMap": "아직 커스텀 지도가 없습니다",
    "noMapDescription": "지도 설명 없음",
    "noMatches": "일치하는 결과 없음",
    "normal": "보통",
    "off": "끄기",
    "on": "켜기",
    "openStarMapEditor": "지도 편집",
    "originalImageTitle": "원본 이미지",
    "paddleGrow": "패들 확대",
    "paddleSkins": "패들 스킨",
    "play": "플레이",
    "playsLikesFormat": "{plays}회 플레이 · 좋아요 {likes}개",
    "previewHint": "이미지를 업로드하면 {count}개의 레벨 별이 자동 생성됩니다. 별을 드래그해 위치를 조정한 뒤 현재 레벨 선택 지도로 저장하세요.",
    "previous": "이전",
    "privateMap": "비공개",
    "publicMap": "공개",
    "visibilityChangedPublic": "공개로 전환했습니다.",
    "visibilityChangedPrivate": "비공개로 전환했습니다.",
    "visibilityChangedShare": "공유 링크 전용으로 전환했습니다.",
    "publicMaps": "광장",
    "publicMapsDescription": "다른 플레이어가 만든 맵을 찾아보세요",
    "regenerateByCount": "궤적 재구성",
    "regenerateNodesSuccess": "{count}개의 레벨 별을 생성했습니다. 먼저 경로 재구성을 누른 후 드래그하세요.",
    "replayLevel": "레벨 다시 플레이",
    "restart": "다시 시작",
    "result": "결과",
    "returnHome": "홈으로 돌아가기",
    "runtimeBalls": "공",
    "runtimeHome": "홈",
    "runtimeLives": "생명",
    "runtimePaused": "일시정지",
    "runtimeResume": "계속하기",
    "runtimeTime": "시간",
    "save": "저장",
    "saveAndBack": "저장 후 돌아가기",
    "saveFailed": "저장 실패",
    "saveMap": "맵 저장",
    "saveMapAndCreateLevels": "현재 지도로 저장",
    "saveSuccess": "저장 완료",
    "scenicFragment": "명소 조각",
    "scenicFragmentObtained": "명소 조각 획득! 클리어 후 저장됩니다",
    "scenicFragmentProgress": "명소 조각 {found}/{total}",
    "searchColor": "색상 또는 HEX 값 검색",
    "searchMaps": "지도 검색",
    "selectMapTemplate": "지도 템플릿",
    "selectTemplateDescription": "이미지를 업로드하면 {count}개의 레벨 별이 자동 생성됩니다. 별을 드래그해 위치를 조정한 뒤 현재 레벨 선택 지도로 저장하세요.",
    "selectTemplateOrUpload": "이미지 업로드",
    "selected": "선택됨",
    "selectedChallenge": "선택됨",
    "selectedMap": "선택됨",
    "settings": "설정",
    "settingsTitle": "게임 설정",
    "share": "공유",
    "shareAction": "공유",
    "shareFailed": "공유에 실패했습니다.",
    "shareInviteText": "제가 만든 BricksBeater 별 지도를 함께 밝혀 주세요!",
    "shareMap": "공유 대상만",
    "shareReady": "공유 링크가 준비되었습니다.",
    "shareSuccess": "공유 완료",
    "shareUnavailable": "비공개 지도는 공유할 수 없습니다. 먼저 공개 범위를 변경하세요.",
    "skill": "스킬",
    "skillCount": "스킬 수",
    "skins": "스킨",
    "skinsTitle": "스킨 상점",
    "slow": "느림",
    "sound": "게임 사운드",
    "specialLevel": "특별 레벨",
    "specialLevelLocked": "특수 레벨이 잠겨 있습니다",
    "standard": "표준",
    "starMap": "스타 맵",
    "starMapClear": "지도 삭제",
    "starMapCleared": "현재 커스텀 별지도를 삭제했습니다.",
    "starMapCurrentBadge": "현재 별지도",
    "starMapDefaultName": "맵 이름 입력",
    "starMapEdit": "지도 편집",
    "starMapEditorHint": "이미지를 업로드하면 {count}개의 레벨 별이 자동 생성됩니다. 별을 드래그해 위치를 조정한 뒤 현재 레벨 선택 지도로 저장하세요.",
    "starMapEditorTitle": "별지도 제작",
    "starMapGenerated": "{count}개의 레벨 별을 생성했습니다. 드래그해 조정할 수 있습니다.",
    "starMapImageError": "이미지를 처리할 수 없습니다.",
    "starMapLightProgress": "점등 {done} / {total}",
    "starMapName": "지도 이름",
    "starMapPreviewInLevels": "레벨 선택 열기",
    "starMapProcessing": "이미지 분석 중...",
    "starMapRegenerate": "별 다시 생성",
    "starMapRevealHint": "레벨을 클리어할수록 원본 이미지가 점차 나타납니다.",
    "starMapSaveCurrent": "현재 지도로 저장",
    "starMapSaveError": "별지도를 로컬에 저장할 수 없습니다.",
    "starMapSaved": "저장했습니다. 현재 레벨 선택 지도로 설정되었습니다.",
    "starMapUpload": "이미지 업로드",
    "starMapUploadEmptyHint": "첫 버전은 AI 없이 이미지 분석으로 생성합니다.",
    "starMapUploadEmptyTitle": "이미지를 업로드해 별지도 만들기",
    "startError": "게임을 시작할 수 없습니다.",
    "subtitle": "공허를 깨고 · 별을 밝혀라",
    "systemMapCurrentProgress": "시스템 지도 · 현재 레벨 {level}",
    "systemMapDescription": "공식 스테이지를 순서대로 진행합니다.",
    "systemMapName": "에코 맵",
    "templateLoadFailed": "이미지를 처리할 수 없습니다.",
    "title": "지구의 메아리",
    "totalLevels": "총 {count}레벨",
    "unavailable": "아직 레벨 데이터가 없습니다",
    "uploadImage": "이미지 업로드",
    "uploadMapImage": "이미지를 업로드해 맵 만들기",
    "uploadMapImageHint": "업로드 및 맵 생성 기능은 추후 개발됩니다.",
    "victory": "승리",
    "viewCompletedMap": "지도 보기",
    "viewOriginal": "원본 보기",
    "workshop": "워크숍",
    "workshopComingSoon": "커스텀 맵 공방",
    "workshopDescription": "사용자가 이미지를 업로드해 자신만의 벽돌 맵을 만들 수 있는 기능을 이후 개발 단계에서 추가합니다.",
    "workshopLevelMap": "레벨 제작",
    "workshopStarMap": "맵 제작",
    "workshopWorkbench": "제작대",
    "workshopToolSoon": "편집기는 추후 추가",
    "workshopTools": "맵 도구",
    "worldMap": "세계 지도",
    "zh": "zh",
    "skin.ball_default": "클래식 화이트",
    "skin.ball_star": "별",
    "skin.ball_football": "축구공",
    "skin.ball_flower": "꽃",
    "skin.ball_planet": "행성",
    "skin.ball_rainbow": "무지개 캔디",
    "skin.ball_diamond": "다이아몬드",
    "skin.paddle_default": "클래식",
    "skin.paddle_stool": "작은 의자",
    "skin.paddle_grass": "잔디",
    "skin.paddle_rainbow": "무지개 다리",
    "skin.paddle_carpet": "마법 양탄자",
    "skin.paddle_wave": "파도",
    "skin.paddle_mecha": "메카 플랫폼",
    "imageFileRequired": "이미지 업로드",
    "imageReadFailed": "이미지를 처리할 수 없습니다.",
    "imageTooLarge": "이미지를 처리할 수 없습니다.",
    "imageUnsupported": "이 브라우저는 Canvas를 지원하지 않습니다.",
    "imageNoRegion": "이미지를 처리할 수 없습니다.",
    "mapNotFound": "아직 레벨 데이터가 없습니다",
    "mapCategoryWorld": "세계 지도",
    "mapCategoryWorldShort": "세계",
    "mapCategoryCountry": "국가 지도",
    "mapCategoryCountryShort": "국가",
    "mapCategoryRegion": "지역 지도",
    "mapCategoryRegionShort": "지역",
    "worldClassicName": "세계 대표 랜드마크",
    "worldClassicDesc": "세계의 유명 랜드마크로 구성된 탐험 지도입니다.",
    "worldRouteName": "세계 여행 루트",
    "worldRouteDesc": "전 세계 여행 경로를 따라가는 지도입니다.",
    "worldContinentsName": "7대륙 탐험",
    "worldContinentsDesc": "7대륙을 탐험하는 지도입니다.",
    "chinaClassicName": "중국 클래식",
    "chinaClassicDesc": "중국의 유명 도시와 랜드마크를 담았습니다.",
    "japanClassicName": "일본 클래식",
    "japanClassicDesc": "일본의 대표적인 장소를 담았습니다.",
    "franceClassicName": "프랑스 클래식",
    "franceClassicDesc": "프랑스의 도시와 랜드마크를 담은 지도입니다.",
    "usaClassicName": "미국 클래식",
    "usaClassicDesc": "미국의 대표적인 랜드마크 지도입니다.",
    "europeClassicName": "유럽 대표 도시",
    "europeClassicDesc": "유럽의 여행 도시를 둘러보는 지도입니다.",
    "eastAsiaName": "동아시아 여행",
    "eastAsiaDesc": "동아시아의 도시와 랜드마크를 탐험합니다.",
    "southEastAsiaName": "동남아시아 여행",
    "southEastAsiaDesc": "동남아시아 여행 루트를 따라가는 지도입니다.",
    "chinaRegionName": "중국 지역 탐험",
    "chinaRegionDesc": "중국의 여러 지역을 주제로 한 탐험 지도입니다.",
    "demoWorldDreamName": "세계 일주의 첫 번째 정거장",
    "demoWorldDreamDesc": "가고 싶은 도시를 별 지도로 만들고 여행 목록을 하나씩 밝혀 보세요.",
    "demoJapanNightName": "일본 여행 계획",
    "demoJapanNightDesc": "도쿄, 교토, 오사카와 후지산을 잇는 플레이 가능한 여행 코스입니다.",
    "demoChinaLandmarksName": "중국 대표 명소",
    "demoChinaLandmarksDesc": "만리장성에서 시안, 상하이, 청두, 광저우까지 이어지는 여행입니다.",
    "demoFranceTripName": "프랑스 로맨틱 루트",
    "demoFranceTripDesc": "파리, 리옹, 니스와 스트라스부르를 천천히 밝혀 가는 지도입니다.",
    "demoSevenContinentsName": "7대륙 탐험",
    "demoSevenContinentsDesc": "아시아에서 출발해 7대륙을 나만의 스테이지로 만들어 보세요.",
    "demoUsaRoadName": "미국 로드 트립",
    "demoUsaRoadDesc": "뉴욕, 워싱턴, 시카고, 로스앤젤레스와 그랜드캐니언을 여행합니다.",
    "demoEuropeCityName": "유럽 도시 여행",
    "demoEuropeCityDesc": "런던, 파리, 베를린, 로마, 아테네와 바르셀로나를 잇는 도시 지도입니다.",
    "scenicForbiddenCity": "자금성",
    "scenicTempleOfHeaven": "천단",
    "scenicGreatWall": "만리장성",
    "scenicZhangjiajie": "장자제"
  }),
  "es": Object.freeze({
    "allFragmentsCollected": "Todos los fragmentos recogidos",
    "back": "Volver",
    "backHome": "VOLVER AL INICIO",
    "backendReady": "Frontend Vue preparado para el backend",
    "ballSkins": "Aspectos de bola",
    "ballSpeed": "VELOCIDAD DE LA BOLA",
    "breakableCount": "Bloques rompibles: {count}",
    "brickPalette": "Paleta de ladrillos",
    "builtLevels": "Creados {done}/{total}",
    "levelBuilt": "Creado",
    "cancel": "Cancelar",
    "cannotReadTemplate": "No se puede procesar esta imagen.",
    "canvasUnsupported": "Tu navegador no es compatible con Canvas.",
    "challengeMapHint": "Elige el mapa que continuará el botón JUGAR de la pantalla principal. El mapa oficial siempre estará disponible.",
    "challengeMapTitle": "SELECCIONAR MAPA",
    "chapter": "Capítulo",
    "chapterEndFragmentNotice": "fragmentos de lugares emblemáticos.",
    "chapters": "Capítulos",
    "chooseChallenge": "USAR ESTE MAPA",
    "chooseForPlay": "USAR ESTE MAPA",
    "chooseMapPlay": "Jugar en este mapa",
    "clearSearch": "Borrar búsqueda",
    "cleared": "Completado",
    "close": "Cerrar",
    "collectFragment": "Recoger fragmento",
    "collectedFragments": "{count} fragmentos recogidos",
    "comingSoon": "PRÓXIMAMENTE",
    "completed": "Completado",
    "confirm": "Confirmar",
    "confirmDeleteMap": "¿Eliminar «{name}»? También se eliminarán los datos locales de los niveles.",
    "confirmOverwrite": "Generar desde una imagen sobrescribirá el lienzo actual. ¿Continuar?",
    "continueGame": "Continuar",
    "countryCA": "Canadá",
    "countryCN": "China",
    "countryDE": "Alemania",
    "countryFR": "Francia",
    "countryGB": "Reino Unido",
    "countryJP": "Japón",
    "countryKR": "Corea del Sur",
    "countryUS": "Estados Unidos",
    "createLevel": "Crear nivel",
    "createStarMap": "Crear mapa estelar",
    "createStarMapFirst": "Crea un mapa estelar en el Taller. Hasta entonces, JUGAR continuará con el mapa del sistema.",
    "creativeWorkshop": "TALLER",
    "creativeWorkshopHint": "Mapas personalizados",
    "currentLevel": "NIVEL ACTUAL",
    "currentLevelFormat": "Nivel alcanzado: {level}",
    "currentProgress": "Continuar desde el nivel {level}",
    "currentReachedLevel": "Nivel alcanzado: {level}",
    "custom": "Personalizado",
    "customMapCompleteSubtitle": "¡Enhorabuena! Has iluminado todos los niveles de {name}.",
    "customMapCompleteTitle": "¡MAPA COMPLETADO!",
    "customMapCurrentProgress": "{name} · Nivel alcanzado {level}",
    "customMapDescription": "Tu mapa estelar y tus niveles personalizados.",
    "defeat": "Derrota",
    "deleteAction": "Eliminar",
    "deleteMap": "Eliminar mapa",
    "difficulty": "DIFICULTAD",
    "dragNodesHint": "Sube una imagen para generar automáticamente {count} estrellas de nivel. Arrastra cada estrella para ajustar la ruta y guarda el resultado como mapa actual de selección de niveles.",
    "editMap": "Editar mapa",
    "en": "en",
    "energyFull": "Lleno",
    "energyInsufficient": "Energía insuficiente: {current}/{cost}. Espera a que se recupere.",
    "energyLabel": "ENERGÍA",
    "energyLoading": "CARGANDO",
    "enterMap": "Entrar al mapa",
    "mapViews": "Visualizaciones",
    "likeMap": "Me gusta",
    "unlikeMap": "Quitar Me gusta",
    "enterSpecialLevel": "Entrar al nivel especial",
    "extreme": "EXTREMO",
    "fast": "RÁPIDA",
    "finalLevel": "Nivel final",
    "fr": "fr",
    "fragment": "Fragmento",
    "fragmentCollected": "Fragmento recogido",
    "fragmentProgress": "Progreso de fragmentos",
    "fragments": "Fragmentos",
    "gameHelp": "A/D o ←/→ para mover · Espacio para lanzar · R para reiniciar",
    "gameFailedTitle": "Partida fallida",
    "retryChallenge": "Reintentar",
    "generateMap": "Generar mapa",
    "goCreateStarMap": "CREADOR DE MAPAS ESTELARES",
    "hard": "DIFÍCIL",
    "highScore": "Mejor puntuación",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Misil láser",
    "leaderboard": "CLASIFICACIÓN",
    "leaderboardFullTitle": "Clasificación completa por países",
    "leaderboardHint": "Ver posiciones",
    "leaderboardPageTitle": "🌌 CLASIFICACIÓN MUNDIAL POR PAÍSES",
    "leaderboardPlaceholderNote": "La ruta, el Store y la capa de API de la clasificación ya están preparados. Esta pantalla sigue siendo una estructura temporal y podrá sustituirse sin cambiar la integración con el backend.",
    "leaderboardScore": "Puntuación",
    "resultLevelScore": "Puntuación del nivel",
    "leaderboardCleared": "Progreso",
    "level": "Nivel",
    "levelCountFormat": "{count} niveles",
    "levelCountPoints": "Niveles / Puntos",
    "levelLocked": "Nivel bloqueado",
    "levelMap": "Mapa de niveles",
    "levelPlaysFormat": "{count} niveles · {plays} partidas",
    "levelSelect": "SELECCIÓN DE NIVELES",
    "litLevels": "Iluminados {done}/{total}",
    "loading": "Cargando",
    "loadingLevel": "Cargando datos del nivel...",
    "loadingSharedMap": "Cargando mapa compartido…",
    "loadingTemplate": "Analizando imagen...",
    "locked": "Bloqueado",
    "mapCompleted": "Mapa completado",
    "mapDescription": "Descripción del mapa",
    "mapDescriptionPlaceholder": "Introduce la descripción del mapa",
    "mapFullyLit": "Mapa completamente iluminado",
    "mapName": "NOMBRE DEL MAPA",
    "mapPermission": "Permisos del mapa",
    "mapTemplate": "Plantilla de mapa",
    "mapTitle": "Mapa de Ecos",
    "mapUnlocked": "Mapa desbloqueado",
    "mapUpdatedSaved": "Guardado. Ahora es tu mapa actual de selección de niveles.",
    "modeGuide": "GUÍA DE MODOS",
    "modeGuideExtreme": "Extremo: alrededor del 35% de la probabilidad de objetos del modo Clásico",
    "modeGuideHard": "Difícil: alrededor del 65% de la probabilidad de objetos del modo Clásico",
    "modeGuideSpeed": "Una bola más rápida aumenta la dificultad de control y de completar el nivel",
    "modeGuideStandard": "Clásico: mantiene la probabilidad original de aparición de objetos",
    "myCustomMaps": "Mis Creaciones",
    "myStarMap": "Mi mapa estelar",
    "myWorkshop": "Mi taller",
    "next": "Siguiente",
    "noCustomMaps": "Aún no hay mapas personalizados",
    "noCustomMapsHint": "Crea un mapa estelar en el Taller. Hasta entonces, JUGAR continuará con el mapa del sistema.",
    "noCustomStarMap": "Aún no hay mapas personalizados",
    "noMapDescription": "Sin descripción del mapa",
    "noMatches": "Sin coincidencias",
    "normal": "NORMAL",
    "off": "DESACTIVADO",
    "on": "ACTIVADO",
    "openStarMapEditor": "EDITAR MAPA",
    "originalImageTitle": "Imagen original",
    "paddleGrow": "Plataforma ampliada",
    "paddleSkins": "Aspectos de pala",
    "play": "JUGAR",
    "playsLikesFormat": "{plays} partidas · {likes} Me gusta",
    "previewHint": "Sube una imagen para generar automáticamente {count} estrellas de nivel. Arrastra cada estrella para ajustar la ruta y guarda el resultado como mapa actual de selección de niveles.",
    "previous": "Anterior",
    "privateMap": "Privado",
    "publicMap": "Público",
    "visibilityChangedPublic": "La visibilidad se cambió a Pública.",
    "visibilityChangedPrivate": "La visibilidad se cambió a Privada.",
    "visibilityChangedShare": "La visibilidad se cambió a Solo compartido.",
    "publicMaps": "Galería",
    "publicMapsDescription": "Descubre mapas creados por otros jugadores",
    "regenerateByCount": "Reconstrucción de Ruta",
    "regenerateNodesSuccess": "Se generaron {count} estrellas. Pulsa Reconstruir ruta antes de arrastrarlas.",
    "replayLevel": "Repetir nivel",
    "restart": "Reiniciar",
    "result": "Resultado",
    "returnHome": "Volver al inicio",
    "runtimeBalls": "BOLAS",
    "runtimeHome": "INICIO",
    "runtimeLives": "VIDAS",
    "runtimePaused": "PAUSA",
    "runtimeResume": "CONTINUAR",
    "runtimeTime": "TIEMPO",
    "save": "Guardar",
    "saveAndBack": "GUARDAR Y VOLVER",
    "saveFailed": "Error al guardar",
    "saveMap": "Guardar mapa",
    "saveMapAndCreateLevels": "GUARDAR COMO MAPA ACTUAL",
    "saveSuccess": "Guardado correctamente",
    "scenicFragment": "Fragmento de lugar",
    "scenicFragmentObtained": "¡Fragmento conseguido! Se guardará al superar el nivel.",
    "scenicFragmentProgress": "Fragmentos de lugares {found}/{total}",
    "searchColor": "Buscar colores o valor HEX",
    "searchMaps": "Buscar mapas",
    "selectMapTemplate": "Plantilla de mapa",
    "selectTemplateDescription": "Sube una imagen para generar automáticamente {count} estrellas de nivel. Arrastra cada estrella para ajustar la ruta y guarda el resultado como mapa actual de selección de niveles.",
    "selectTemplateOrUpload": "SUBIR IMAGEN",
    "selected": "Seleccionado",
    "selectedChallenge": "SELECCIONADO",
    "selectedMap": "SELECCIONADO",
    "settings": "AJUSTES",
    "settingsTitle": "AJUSTES DEL JUEGO",
    "share": "Compartir",
    "shareAction": "Compartir",
    "shareFailed": "No se pudo compartir.",
    "shareInviteText": "¡Ayúdame a iluminar mi mapa estelar de BricksBeater!",
    "shareMap": "Solo con enlace",
    "shareReady": "El enlace para compartir está listo.",
    "shareSuccess": "Compartido correctamente",
    "shareUnavailable": "Los mapas privados no se pueden compartir. Cambia primero la visibilidad.",
    "skill": "Habilidad",
    "skillCount": "Número de habilidades",
    "skins": "ASPECTOS",
    "skinsTitle": "Tienda de aspectos",
    "slow": "LENTA",
    "sound": "SONIDO",
    "specialLevel": "Nivel especial",
    "specialLevelLocked": "Nivel especial bloqueado",
    "standard": "CLÁSICO",
    "starMap": "Mapa estelar",
    "starMapClear": "BORRAR MAPA",
    "starMapCleared": "Se eliminó el mapa estelar personalizado actual.",
    "starMapCurrentBadge": "MAPA ESTELAR ACTUAL",
    "starMapDefaultName": "Introduce el nombre del mapa",
    "starMapEdit": "EDITAR MAPA",
    "starMapEditorHint": "Sube una imagen para generar automáticamente {count} estrellas de nivel. Arrastra cada estrella para ajustar la ruta y guarda el resultado como mapa actual de selección de niveles.",
    "starMapEditorTitle": "CREADOR DE MAPAS ESTELARES",
    "starMapGenerated": "Se generaron {count} estrellas de nivel. Arrástralas para ajustar el mapa.",
    "starMapImageError": "No se puede procesar esta imagen.",
    "starMapLightProgress": "ILUMINADOS {done} / {total}",
    "starMapName": "NOMBRE DEL MAPA",
    "starMapPreviewInLevels": "ABRIR SELECCIÓN DE NIVELES",
    "starMapProcessing": "Analizando imagen...",
    "starMapRegenerate": "REGENERAR ESTRELLAS",
    "starMapRevealHint": "Completa niveles para revelar poco a poco la imagen original.",
    "starMapSaveCurrent": "GUARDAR COMO MAPA ACTUAL",
    "starMapSaveError": "No se pudo guardar el mapa estelar localmente.",
    "starMapSaved": "Guardado. Ahora es tu mapa actual de selección de niveles.",
    "starMapUpload": "SUBIR IMAGEN",
    "starMapUploadEmptyHint": "La primera versión usa análisis de imagen y no necesita IA.",
    "starMapUploadEmptyTitle": "Sube una imagen para crear un mapa estelar",
    "startError": "No se puede iniciar la partida.",
    "subtitle": "Rompe el vacío · Enciende las estrellas",
    "systemMapCurrentProgress": "Mapa del sistema · Nivel alcanzado {level}",
    "systemMapDescription": "Campaña oficial con niveles que se juegan en orden.",
    "systemMapName": "MAPA DE ECOS",
    "templateLoadFailed": "No se puede procesar esta imagen.",
    "title": "Ecos de la Tierra",
    "totalLevels": "{count} niveles",
    "unavailable": "Los datos de este nivel aún no están disponibles",
    "uploadImage": "Subir imagen",
    "uploadMapImage": "Subir una imagen para crear un mapa",
    "uploadMapImageHint": "La carga y la generación de mapas se añadirán en una fase posterior.",
    "victory": "Victoria",
    "viewCompletedMap": "VER MAPA",
    "viewOriginal": "Ver original",
    "workshop": "Taller",
    "workshopComingSoon": "TALLER DE MAPAS PERSONALIZADOS",
    "workshopDescription": "Un futuro espacio de creación donde los jugadores podrán subir una imagen y convertirla en un mapa de bloques personalizado.",
    "workshopLevelMap": "Diseño de Niveles",
    "workshopStarMap": "Diseño de Mapas",
    "workshopWorkbench": "Mesa de Trabajo",
    "workshopToolSoon": "Editor próximamente",
    "workshopTools": "Herramientas del mapa",
    "worldMap": "Mapa mundial",
    "zh": "zh",
    "skin.ball_default": "Blanco clásico",
    "skin.ball_star": "Estrella",
    "skin.ball_football": "Balón de fútbol",
    "skin.ball_flower": "Flor",
    "skin.ball_planet": "Planeta",
    "skin.ball_rainbow": "Caramelo arcoíris",
    "skin.ball_diamond": "Diamante",
    "skin.paddle_default": "Clásica",
    "skin.paddle_stool": "Taburete",
    "skin.paddle_grass": "Césped",
    "skin.paddle_rainbow": "Puente arcoíris",
    "skin.paddle_carpet": "Alfombra mágica",
    "skin.paddle_wave": "Ola",
    "skin.paddle_mecha": "Plataforma mecánica",
    "imageFileRequired": "SUBIR IMAGEN",
    "imageReadFailed": "No se puede procesar esta imagen.",
    "imageTooLarge": "No se puede procesar esta imagen.",
    "imageUnsupported": "Tu navegador no es compatible con Canvas.",
    "imageNoRegion": "No se puede procesar esta imagen.",
    "mapNotFound": "Los datos de este nivel aún no están disponibles",
    "mapCategoryWorld": "Mapa mundial",
    "mapCategoryWorldShort": "Mundo",
    "mapCategoryCountry": "Mapa de país",
    "mapCategoryCountryShort": "País",
    "mapCategoryRegion": "Mapa regional",
    "mapCategoryRegionShort": "Región",
    "worldClassicName": "Lugares emblemáticos del mundo",
    "worldClassicDesc": "Un mapa de exploración formado por lugares emblemáticos del mundo.",
    "worldRouteName": "Ruta de viaje mundial",
    "worldRouteDesc": "Un mapa diseñado como una ruta de viaje alrededor del mundo.",
    "worldContinentsName": "Exploración de los siete continentes",
    "worldContinentsDesc": "Explora los siete continentes.",
    "chinaClassicName": "China clásica",
    "chinaClassicDesc": "Ciudades y lugares emblemáticos famosos de China.",
    "japanClassicName": "Japón clásico",
    "japanClassicDesc": "Lugares representativos de Japón.",
    "franceClassicName": "Francia clásica",
    "franceClassicDesc": "Ciudades y lugares emblemáticos de Francia.",
    "usaClassicName": "Estados Unidos clásico",
    "usaClassicDesc": "Lugares emblemáticos representativos de Estados Unidos.",
    "europeClassicName": "Ciudades clásicas de Europa",
    "europeClassicDesc": "Ciudades europeas para recorrer.",
    "eastAsiaName": "Viaje por Asia Oriental",
    "eastAsiaDesc": "Ciudades y lugares emblemáticos de Asia Oriental.",
    "southEastAsiaName": "Viaje por el Sudeste Asiático",
    "southEastAsiaDesc": "Una ruta de viaje por el Sudeste Asiático.",
    "chinaRegionName": "Regiones de China",
    "chinaRegionDesc": "Mapa de exploración temática por regiones de China.",
    "demoWorldDreamName": "Primera parada: vuelta al mundo",
    "demoWorldDreamDesc": "Convierte las ciudades que quieres visitar en un mapa estelar y completa tu lista de viaje.",
    "demoJapanNightName": "Plan de viaje por Japón",
    "demoJapanNightDesc": "Una ruta jugable por Tokio, Kioto, Osaka y el monte Fuji.",
    "demoChinaLandmarksName": "Lugares clásicos de China",
    "demoChinaLandmarksDesc": "Viaja desde la Gran Muralla hasta Xi'an, Shanghái, Chengdu y Guangzhou.",
    "demoFranceTripName": "Ruta romántica por Francia",
    "demoFranceTripDesc": "Descubre París, Lyon, Niza y Estrasburgo en un mapa para recorrer con calma.",
    "demoSevenContinentsName": "Exploración de los siete continentes",
    "demoSevenContinentsDesc": "Parte de Asia y convierte los siete continentes en etapas de tu aventura.",
    "demoUsaRoadName": "Viaje por carretera en EE. UU.",
    "demoUsaRoadDesc": "Recorre Nueva York, Washington, Chicago, Los Ángeles y el Gran Cañón.",
    "demoEuropeCityName": "Recorrido por ciudades europeas",
    "demoEuropeCityDesc": "Un mapa urbano por Londres, París, Berlín, Roma, Atenas y Barcelona.",
    "scenicForbiddenCity": "Ciudad Prohibida",
    "scenicTempleOfHeaven": "Templo del Cielo",
    "scenicGreatWall": "Gran Muralla China",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "fr": Object.freeze({
    "allFragmentsCollected": "Tous les fragments ont été collectés",
    "back": "Retour",
    "backHome": "ACCUEIL",
    "backendReady": "Frontend Vue prêt pour le backend",
    "ballSkins": "Skins de balle",
    "ballSpeed": "VITESSE DE LA BALLE",
    "breakableCount": "Briques destructibles : {count}",
    "brickPalette": "Palette de briques",
    "builtLevels": "Créés {done}/{total}",
    "levelBuilt": "Créé",
    "cancel": "Annuler",
    "cannotReadTemplate": "Impossible de traiter cette image.",
    "canvasUnsupported": "Votre navigateur ne prend pas en charge Canvas.",
    "challengeMapHint": "Choisissez la carte poursuivie par le bouton JOUER. La carte système reste toujours disponible.",
    "challengeMapTitle": "CHOISIR LA CARTE",
    "chapter": "Chapitre",
    "chapterEndFragmentNotice": "fragments de sites remarquables.",
    "chapters": "Chapitres",
    "chooseChallenge": "CHOISIR",
    "chooseForPlay": "CHOISIR",
    "chooseMapPlay": "Jouer sur cette carte",
    "clearSearch": "Effacer la recherche",
    "cleared": "Terminés",
    "close": "Fermer",
    "collectFragment": "Collecter le fragment",
    "collectedFragments": "{count} fragments collectés",
    "comingSoon": "BIENTÔT",
    "completed": "Terminé",
    "confirm": "Confirmer",
    "confirmDeleteMap": "Supprimer « {name} » ? Les données locales des niveaux seront aussi supprimées.",
    "confirmOverwrite": "Générer depuis une image remplacera le canevas actuel. Continuer ?",
    "continueGame": "Continuer",
    "countryCA": "Canada",
    "countryCN": "Chine",
    "countryDE": "Allemagne",
    "countryFR": "France",
    "countryGB": "Royaume-Uni",
    "countryJP": "Japon",
    "countryKR": "Corée du Sud",
    "countryUS": "États-Unis",
    "createLevel": "Créer un niveau",
    "createStarMap": "Créer une carte étoilée",
    "createStarMapFirst": "Créez une carte étoilée dans l’Atelier. Sinon, JOUER continue la carte système.",
    "creativeWorkshop": "ATELIER",
    "creativeWorkshopHint": "Cartes perso",
    "currentLevel": "NIVEAU ACTUEL",
    "currentLevelFormat": "Niveau atteint {level}",
    "currentProgress": "Continuer au niveau {level}",
    "currentReachedLevel": "Niveau atteint {level}",
    "custom": "Personnalisé",
    "customMapCompleteSubtitle": "Félicitations ! Tous les niveaux de {name} sont allumés.",
    "customMapCompleteTitle": "CARTE TERMINÉE !",
    "customMapCurrentProgress": "{name} · Niveau atteint {level}",
    "customMapDescription": "Votre carte étoilée et vos niveaux personnalisés.",
    "defeat": "Défaite",
    "deleteAction": "Supprimer",
    "deleteMap": "Supprimer la carte",
    "difficulty": "DIFFICULTÉ",
    "dragNodesHint": "Importez une image pour générer automatiquement {count} étoiles de niveaux. Faites glisser les étoiles pour ajuster le tracé, puis enregistrez la carte.",
    "editMap": "Modifier la carte",
    "en": "en",
    "energyFull": "Plein",
    "energyInsufficient": "Énergie insuffisante : {current}/{cost}. Attendez la récupération.",
    "energyLabel": "ÉNERGIE",
    "energyLoading": "CHARGEMENT",
    "enterMap": "Entrer dans la carte",
    "mapViews": "Vues",
    "likeMap": "J’aime",
    "unlikeMap": "Retirer J’aime",
    "enterSpecialLevel": "Entrer dans le niveau spécial",
    "extreme": "EXTRÊME",
    "fast": "RAPIDE",
    "finalLevel": "Niveau final",
    "fr": "fr",
    "fragment": "Fragment",
    "fragmentCollected": "Fragment collecté",
    "fragmentProgress": "Progression des fragments",
    "fragments": "Fragments",
    "gameHelp": "A/D ou ←/→ pour bouger · Espace pour lancer · R pour recommencer",
    "gameFailedTitle": "Échec de la partie",
    "retryChallenge": "Réessayer",
    "generateMap": "Générer la carte",
    "goCreateStarMap": "CRÉATEUR DE CARTE ÉTOILÉE",
    "hard": "DIFFICILE",
    "highScore": "Meilleur score",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Missile laser",
    "leaderboard": "CLASSEMENT",
    "leaderboardFullTitle": "Classement complet des pays",
    "leaderboardHint": "Voir les rangs",
    "leaderboardPageTitle": "🌌 CLASSEMENT MONDIAL DES PAYS",
    "leaderboardPlaceholderNote": "Le Router, le Store et la couche API du classement sont prêts. Le HTML cible n’était pas joint au dernier message : cette page est donc seulement une structure temporaire et pourra être remplacée sans modifier l’intégration backend.",
    "leaderboardScore": "Points",
    "resultLevelScore": "Score du niveau",
    "leaderboardCleared": "Progression",
    "level": "Niveau",
    "levelCountFormat": "{count} niveaux",
    "levelCountPoints": "Niveaux / Points",
    "levelLocked": "Niveau verrouillé",
    "levelMap": "Carte des niveaux",
    "levelPlaysFormat": "{count} niveaux · {plays} parties",
    "levelSelect": "NIVEAUX",
    "litLevels": "Allumés {done}/{total}",
    "loading": "Chargement",
    "loadingLevel": "Chargement du niveau...",
    "loadingSharedMap": "Chargement de la carte partagée…",
    "loadingTemplate": "Analyse de l’image...",
    "locked": "Verrouillé",
    "mapCompleted": "Carte terminée",
    "mapDescription": "Description de la carte",
    "mapDescriptionPlaceholder": "Saisissez la description de la carte",
    "mapFullyLit": "Carte entièrement illuminée",
    "mapName": "NOM DE LA CARTE",
    "mapPermission": "Visibilité de la carte",
    "mapTemplate": "Modèle de carte",
    "mapTitle": "Carte des Échos",
    "mapUnlocked": "Carte déverrouillée",
    "mapUpdatedSaved": "Enregistrée comme carte actuelle de sélection des niveaux.",
    "modeGuide": "GUIDE DES MODES",
    "modeGuideExtreme": "Extrême : environ 35 % du taux Standard",
    "modeGuideHard": "Difficile : environ 65 % du taux Standard",
    "modeGuideSpeed": "Une balle plus rapide augmente la difficulté de contrôle",
    "modeGuideStandard": "Standard : taux de chute des objets d’origine",
    "myCustomMaps": "Mes Créations",
    "myStarMap": "Ma carte étoilée",
    "myWorkshop": "Mon atelier",
    "next": "Suivant",
    "noCustomMaps": "Aucune carte personnalisée",
    "noCustomMapsHint": "Créez une carte étoilée dans l’Atelier. Sinon, JOUER continue la carte système.",
    "noCustomStarMap": "Aucune carte personnalisée",
    "noMapDescription": "Aucune description",
    "noMatches": "Aucun résultat",
    "normal": "NORMALE",
    "off": "DÉSACTIVÉ",
    "on": "ACTIVÉ",
    "openStarMapEditor": "MODIFIER",
    "originalImageTitle": "Image originale",
    "paddleGrow": "Raquette agrandie",
    "paddleSkins": "Skins de raquette",
    "play": "JOUER",
    "playsLikesFormat": "{plays} parties · {likes} J’aime",
    "previewHint": "Importez une image pour générer automatiquement {count} étoiles de niveaux. Faites glisser les étoiles pour ajuster le tracé, puis enregistrez la carte.",
    "previous": "Précédent",
    "privateMap": "Privé",
    "publicMap": "Publique",
    "visibilityChangedPublic": "Visibilité définie sur Publique.",
    "visibilityChangedPrivate": "Visibilité définie sur Privée.",
    "visibilityChangedShare": "Visibilité définie sur Partage uniquement.",
    "publicMaps": "Galerie",
    "publicMapsDescription": "Découvrez les cartes créées par d’autres joueurs",
    "regenerateByCount": "Reconstruction du Tracé",
    "regenerateNodesSuccess": "{count} étoiles générées. Appuyez sur Reconstruire le trajet avant de les déplacer.",
    "replayLevel": "Rejouer le niveau",
    "restart": "Recommencer",
    "result": "Résultat",
    "returnHome": "Retour à l’accueil",
    "runtimeBalls": "BALLES",
    "runtimeHome": "ACCUEIL",
    "runtimeLives": "VIES",
    "runtimePaused": "PAUSE",
    "runtimeResume": "REPRENDRE",
    "runtimeTime": "TEMPS",
    "save": "Enregistrer",
    "saveAndBack": "ENREGISTRER ET RETOUR",
    "saveFailed": "Échec de l’enregistrement",
    "saveMap": "Enregistrer la carte",
    "saveMapAndCreateLevels": "ENREGISTRER COMME CARTE",
    "saveSuccess": "Enregistré avec succès",
    "scenicFragment": "Fragment de site",
    "scenicFragmentObtained": "Fragment obtenu ! Il sera enregistré après avoir terminé le niveau.",
    "scenicFragmentProgress": "Fragments de sites {found}/{total}",
    "searchColor": "Rechercher une couleur ou une valeur HEX",
    "searchMaps": "Rechercher des cartes",
    "selectMapTemplate": "Modèle de carte",
    "selectTemplateDescription": "Importez une image pour générer automatiquement {count} étoiles de niveaux. Faites glisser les étoiles pour ajuster le tracé, puis enregistrez la carte.",
    "selectTemplateOrUpload": "IMPORTER UNE IMAGE",
    "selected": "Sélectionné",
    "selectedChallenge": "SÉLECTIONNÉE",
    "selectedMap": "SÉLECTIONNÉE",
    "settings": "RÉGLAGES",
    "settingsTitle": "RÉGLAGES DU JEU",
    "share": "Partager",
    "shareAction": "Partager",
    "shareFailed": "Échec du partage.",
    "shareInviteText": "Venez illuminer ma carte étoilée BricksBeater !",
    "shareMap": "Lien uniquement",
    "shareReady": "Le lien de partage est prêt.",
    "shareSuccess": "Partagé avec succès",
    "shareUnavailable": "Une carte privée ne peut pas être partagée. Modifiez d’abord sa visibilité.",
    "skill": "Compétence",
    "skillCount": "Nombre de compétences",
    "skins": "APPARENCES",
    "skinsTitle": "Boutique de skins",
    "slow": "LENTE",
    "sound": "SON",
    "specialLevel": "Niveau spécial",
    "specialLevelLocked": "Niveau spécial verrouillé",
    "standard": "STANDARD",
    "starMap": "Carte étoilée",
    "starMapClear": "EFFACER",
    "starMapCleared": "Carte étoilée personnalisée supprimée.",
    "starMapCurrentBadge": "CARTE ACTUELLE",
    "starMapDefaultName": "Saisissez le nom de la carte",
    "starMapEdit": "MODIFIER",
    "starMapEditorHint": "Importez une image pour générer automatiquement {count} étoiles de niveaux. Faites glisser les étoiles pour ajuster le tracé, puis enregistrez la carte.",
    "starMapEditorTitle": "CRÉATEUR DE CARTE ÉTOILÉE",
    "starMapGenerated": "{count} étoiles générées. Faites-les glisser pour ajuster la carte.",
    "starMapImageError": "Impossible de traiter cette image.",
    "starMapLightProgress": "ALLUMÉES {done} / {total}",
    "starMapName": "NOM DE LA CARTE",
    "starMapPreviewInLevels": "OUVRIR LES NIVEAUX",
    "starMapProcessing": "Analyse de l’image...",
    "starMapRegenerate": "RÉGÉNÉRER LES ÉTOILES",
    "starMapRevealHint": "Terminez les niveaux pour révéler progressivement l’image d’origine.",
    "starMapSaveCurrent": "ENREGISTRER COMME CARTE",
    "starMapSaveError": "Impossible d’enregistrer la carte localement.",
    "starMapSaved": "Enregistrée comme carte actuelle de sélection des niveaux.",
    "starMapUpload": "IMPORTER UNE IMAGE",
    "starMapUploadEmptyHint": "La première version utilise l’analyse d’image, sans IA.",
    "starMapUploadEmptyTitle": "Importez une image pour créer une carte étoilée",
    "startError": "Impossible de démarrer la partie.",
    "subtitle": "Brisez le vide · Allumez les étoiles",
    "systemMapCurrentProgress": "Carte système · Niveau atteint {level}",
    "systemMapDescription": "Campagne officielle jouée niveau après niveau.",
    "systemMapName": "CARTE DES ÉCHOS",
    "templateLoadFailed": "Impossible de traiter cette image.",
    "title": "Échos de la Terre",
    "totalLevels": "{count} niveaux",
    "unavailable": "Données de niveau indisponibles",
    "uploadImage": "Importer une image",
    "uploadMapImage": "Importer une image pour créer une carte",
    "uploadMapImageHint": "L’import et la génération de cartes seront ajoutés ultérieurement.",
    "victory": "Victoire",
    "viewCompletedMap": "VOIR LA CARTE",
    "viewOriginal": "Voir l’original",
    "workshop": "Atelier",
    "workshopComingSoon": "ATELIER DE CARTES",
    "workshopDescription": "Une future fonction permettra d’importer une image afin de créer une carte de briques personnalisée.",
    "workshopLevelMap": "Création de Niveau",
    "workshopStarMap": "Création de Carte",
    "workshopWorkbench": "Établi",
    "workshopToolSoon": "Éditeur à venir",
    "workshopTools": "Outils de carte",
    "worldMap": "Carte du monde",
    "zh": "zh",
    "skin.ball_default": "Blanc classique",
    "skin.ball_star": "Étoile",
    "skin.ball_football": "Ballon",
    "skin.ball_flower": "Fleur",
    "skin.ball_planet": "Planète",
    "skin.ball_rainbow": "Bonbon arc-en-ciel",
    "skin.ball_diamond": "Diamant",
    "skin.paddle_default": "Classique",
    "skin.paddle_stool": "Tabouret",
    "skin.paddle_grass": "Gazon",
    "skin.paddle_rainbow": "Pont arc-en-ciel",
    "skin.paddle_carpet": "Tapis magique",
    "skin.paddle_wave": "Vague",
    "skin.paddle_mecha": "Plateforme méca",
    "imageFileRequired": "IMPORTER UNE IMAGE",
    "imageReadFailed": "Impossible de traiter cette image.",
    "imageTooLarge": "Impossible de traiter cette image.",
    "imageUnsupported": "Votre navigateur ne prend pas en charge Canvas.",
    "imageNoRegion": "Impossible de traiter cette image.",
    "mapNotFound": "Données de niveau indisponibles",
    "mapCategoryWorld": "Carte du monde",
    "mapCategoryWorldShort": "Monde",
    "mapCategoryCountry": "Carte nationale",
    "mapCategoryCountryShort": "Pays",
    "mapCategoryRegion": "Carte régionale",
    "mapCategoryRegionShort": "Région",
    "worldClassicName": "Grands sites du monde",
    "worldClassicDesc": "Une carte d’exploration composée de sites célèbres du monde entier.",
    "worldRouteName": "Tour du monde",
    "worldRouteDesc": "Une carte conçue comme un itinéraire de voyage autour du monde.",
    "worldContinentsName": "Exploration des sept continents",
    "worldContinentsDesc": "Explorez les sept continents.",
    "chinaClassicName": "Chine classique",
    "chinaClassicDesc": "Villes et sites emblématiques de Chine.",
    "japanClassicName": "Japon classique",
    "japanClassicDesc": "Lieux emblématiques du Japon.",
    "franceClassicName": "France classique",
    "franceClassicDesc": "Villes et sites emblématiques de France.",
    "usaClassicName": "États-Unis classiques",
    "usaClassicDesc": "Sites emblématiques des États-Unis.",
    "europeClassicName": "Villes classiques d’Europe",
    "europeClassicDesc": "Villes européennes à découvrir.",
    "eastAsiaName": "Voyage en Asie de l’Est",
    "eastAsiaDesc": "Villes et sites emblématiques d’Asie de l’Est.",
    "southEastAsiaName": "Voyage en Asie du Sud-Est",
    "southEastAsiaDesc": "Un itinéraire de voyage en Asie du Sud-Est.",
    "chinaRegionName": "Régions de Chine",
    "chinaRegionDesc": "Carte d’exploration thématique des régions de Chine.",
    "demoWorldDreamName": "Première étape autour du monde",
    "demoWorldDreamDesc": "Transformez les villes que vous rêvez de visiter en carte étoilée et illuminez votre itinéraire.",
    "demoJapanNightName": "Voyage au Japon",
    "demoJapanNightDesc": "Un itinéraire jouable entre Tokyo, Kyoto, Osaka et le mont Fuji.",
    "demoChinaLandmarksName": "Sites emblématiques de Chine",
    "demoChinaLandmarksDesc": "De la Grande Muraille à Xi'an, puis Shanghai, Chengdu et Guangzhou.",
    "demoFranceTripName": "Route romantique en France",
    "demoFranceTripDesc": "Paris, Lyon, Nice et Strasbourg dans une carte à explorer tranquillement.",
    "demoSevenContinentsName": "Exploration des sept continents",
    "demoSevenContinentsDesc": "Partez d'Asie et transformez les sept continents en étapes de votre aventure.",
    "demoUsaRoadName": "Road trip aux États-Unis",
    "demoUsaRoadDesc": "New York, Washington, Chicago, Los Angeles et le Grand Canyon jusqu'à la côte Ouest.",
    "demoEuropeCityName": "Escapade dans les villes d'Europe",
    "demoEuropeCityDesc": "Une carte urbaine reliant Londres, Paris, Berlin, Rome, Athènes et Barcelone.",
    "scenicForbiddenCity": "Cité interdite",
    "scenicTempleOfHeaven": "Temple du Ciel",
    "scenicGreatWall": "Grande Muraille de Chine",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "de": Object.freeze({
    "allFragmentsCollected": "Alle Fragmente gesammelt",
    "back": "Zurück",
    "backHome": "ZUR STARTSEITE",
    "backendReady": "Vue-Frontend für Backend-Anbindung vorbereitet",
    "ballSkins": "Ball-Skins",
    "ballSpeed": "BALLGESCHWINDIGKEIT",
    "breakableCount": "Zerstörbare Blöcke: {count}",
    "brickPalette": "Steinpalette",
    "builtLevels": "Erstellt {done}/{total}",
    "levelBuilt": "Erstellt",
    "cancel": "Abbrechen",
    "cannotReadTemplate": "Dieses Bild kann nicht verarbeitet werden.",
    "canvasUnsupported": "Dein Browser unterstützt Canvas nicht.",
    "challengeMapHint": "Wähle die Karte, die über den SPIELEN-Button auf der Startseite fortgesetzt wird. Die offizielle Karte bleibt immer verfügbar.",
    "challengeMapTitle": "SPIELKARTE WÄHLEN",
    "chapter": "Kapitel",
    "chapterEndFragmentNotice": "Sehenswürdigkeiten-Fragmente.",
    "chapters": "Kapitel",
    "chooseChallenge": "DIESE KARTE NUTZEN",
    "chooseForPlay": "DIESE KARTE NUTZEN",
    "chooseMapPlay": "Auf dieser Karte spielen",
    "clearSearch": "Suche löschen",
    "cleared": "Abgeschlossen",
    "close": "Schließen",
    "collectFragment": "Fragment sammeln",
    "collectedFragments": "{count} Fragmente gesammelt",
    "comingSoon": "DEMNÄCHST",
    "completed": "Abgeschlossen",
    "confirm": "Bestätigen",
    "confirmDeleteMap": "„{name}“ löschen? Lokale Leveldaten werden ebenfalls gelöscht.",
    "confirmOverwrite": "Das Erstellen aus einem Bild überschreibt die aktuelle Fläche. Fortfahren?",
    "continueGame": "Fortsetzen",
    "countryCA": "Kanada",
    "countryCN": "China",
    "countryDE": "Deutschland",
    "countryFR": "Frankreich",
    "countryGB": "Vereinigtes Königreich",
    "countryJP": "Japan",
    "countryKR": "Südkorea",
    "countryUS": "Vereinigte Staaten",
    "createLevel": "Level erstellen",
    "createStarMap": "Sternenkarte erstellen",
    "createStarMapFirst": "Erstelle im Studio eine Sternkarte. Bis dahin setzt SPIELEN die Systemkarte fort.",
    "creativeWorkshop": "WERKSTATT",
    "creativeWorkshopHint": "Eigene Karten",
    "currentLevel": "AKTUELLES LEVEL",
    "currentLevelFormat": "Erreichtes Level {level}",
    "currentProgress": "Ab Level {level} fortsetzen",
    "currentReachedLevel": "Erreichtes Level {level}",
    "custom": "Benutzerdefiniert",
    "customMapCompleteSubtitle": "Glückwunsch! Du hast alle Levels von {name} erleuchtet.",
    "customMapCompleteTitle": "KARTE ABGESCHLOSSEN!",
    "customMapCurrentProgress": "{name} · Level {level} erreicht",
    "customMapDescription": "Deine eigene Sternkarte mit selbst erstellten Levels.",
    "defeat": "Niederlage",
    "deleteAction": "Löschen",
    "deleteMap": "Karte löschen",
    "difficulty": "SCHWIERIGKEIT",
    "dragNodesHint": "Lade ein Bild hoch, um automatisch {count} Levelsterne zu erzeugen. Ziehe die Sterne, um die Route anzupassen, und speichere sie anschließend als aktuelle Levelauswahlkarte.",
    "editMap": "Karte bearbeiten",
    "en": "en",
    "energyFull": "Voll",
    "energyInsufficient": "Nicht genug Energie: {current}/{cost}. Warte auf die Regeneration.",
    "energyLabel": "ENERGIE",
    "energyLoading": "LÄDT",
    "enterMap": "Karte betreten",
    "mapViews": "Aufrufe",
    "likeMap": "Gefällt mir",
    "unlikeMap": "Gefällt mir entfernen",
    "enterSpecialLevel": "Speziallevel betreten",
    "extreme": "EXTREM",
    "fast": "SCHNELL",
    "finalLevel": "Finale",
    "fr": "fr",
    "fragment": "Fragment",
    "fragmentCollected": "Fragment gesammelt",
    "fragmentProgress": "Fragmentfortschritt",
    "fragments": "Fragmente",
    "gameHelp": "A/D oder ←/→ zum Bewegen · Leertaste zum Starten · R zum Neustart",
    "gameFailedTitle": "Spiel fehlgeschlagen",
    "retryChallenge": "Erneut versuchen",
    "generateMap": "Karte generieren",
    "goCreateStarMap": "STERNKARTEN-EDITOR",
    "hard": "SCHWER",
    "highScore": "Highscore",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Laserrakete",
    "leaderboard": "BESTENLISTE",
    "leaderboardFullTitle": "Vollständige Länder-Rangliste",
    "leaderboardHint": "Rangliste ansehen",
    "leaderboardPageTitle": "🌌 WELTWEITE LÄNDER-RANGLISTE",
    "leaderboardPlaceholderNote": "Route, Store und API-Schicht der Bestenliste sind vorbereitet. Diese Seite ist derzeit nur ein vorläufiges Layout und kann später ersetzt werden, ohne die Backend-Anbindung zu ändern.",
    "leaderboardScore": "Punktzahl",
    "resultLevelScore": "Levelpunktzahl",
    "leaderboardCleared": "Fortschritt",
    "level": "Stufe",
    "levelCountFormat": "{count} Level",
    "levelCountPoints": "Level / Punkte",
    "levelLocked": "Level gesperrt",
    "levelMap": "Levelkarte",
    "levelPlaysFormat": "{count} Level · {plays} Spiele",
    "levelSelect": "LEVELAUSWAHL",
    "litLevels": "Erleuchtet {done}/{total}",
    "loading": "Wird geladen",
    "loadingLevel": "Leveldaten werden geladen...",
    "loadingSharedMap": "Geteilte Karte wird geladen…",
    "loadingTemplate": "Bild wird analysiert...",
    "locked": "Gesperrt",
    "mapCompleted": "Karte abgeschlossen",
    "mapDescription": "Kartenbeschreibung",
    "mapDescriptionPlaceholder": "Kartenbeschreibung eingeben",
    "mapFullyLit": "Karte vollständig erleuchtet",
    "mapName": "KARTENNAME",
    "mapPermission": "Kartenberechtigung",
    "mapTemplate": "Kartenvorlage",
    "mapTitle": "Echo-Karte",
    "mapUnlocked": "Karte freigeschaltet",
    "mapUpdatedSaved": "Gespeichert. Dies ist jetzt deine aktuelle Levelauswahlkarte.",
    "modeGuide": "MODUS-INFO",
    "modeGuideExtreme": "Extrem: etwa 35% der Gegenstands-Droprate von Klassisch",
    "modeGuideHard": "Schwer: etwa 65% der Gegenstands-Droprate von Klassisch",
    "modeGuideSpeed": "Schnellere Bälle erhöhen die Steuerungs- und Abschluss-Schwierigkeit",
    "modeGuideStandard": "Klassisch: ursprüngliche Gegenstands-Droprate",
    "myCustomMaps": "Meine Werke",
    "myStarMap": "Meine Sternkarte",
    "myWorkshop": "Meine Werkstatt",
    "next": "Weiter",
    "noCustomMaps": "Noch keine eigenen Karten",
    "noCustomMapsHint": "Erstelle im Studio eine Sternkarte. Bis dahin setzt SPIELEN die Systemkarte fort.",
    "noCustomStarMap": "Noch keine eigenen Karten",
    "noMapDescription": "Keine Kartenbeschreibung",
    "noMatches": "Keine Treffer",
    "normal": "STANDARD",
    "off": "AUS",
    "on": "AN",
    "openStarMapEditor": "KARTE BEARBEITEN",
    "originalImageTitle": "Originalbild",
    "paddleGrow": "Schläger vergrößert",
    "paddleSkins": "Schläger-Skins",
    "play": "SPIELEN",
    "playsLikesFormat": "{plays} Spiele · {likes} Likes",
    "previewHint": "Lade ein Bild hoch, um automatisch {count} Levelsterne zu erzeugen. Ziehe die Sterne, um die Route anzupassen, und speichere sie anschließend als aktuelle Levelauswahlkarte.",
    "previous": "Zurück",
    "privateMap": "Privat",
    "publicMap": "Öffentlich",
    "visibilityChangedPublic": "Sichtbarkeit auf Öffentlich geändert.",
    "visibilityChangedPrivate": "Sichtbarkeit auf Privat geändert.",
    "visibilityChangedShare": "Sichtbarkeit auf Nur per Link geändert.",
    "publicMaps": "Galerie",
    "publicMapsDescription": "Entdecke Karten anderer Spieler",
    "regenerateByCount": "Pfadrekonstruktion",
    "regenerateNodesSuccess": "{count} Levelsterne wurden erzeugt. Klicke vor dem Ziehen auf Pfad neu erstellen.",
    "replayLevel": "Level wiederholen",
    "restart": "Neu starten",
    "result": "Ergebnis",
    "returnHome": "Zur Startseite",
    "runtimeBalls": "BÄLLE",
    "runtimeHome": "START",
    "runtimeLives": "LEBEN",
    "runtimePaused": "PAUSE",
    "runtimeResume": "FORTSETZEN",
    "runtimeTime": "ZEIT",
    "save": "Speichern",
    "saveAndBack": "SPEICHERN & ZURÜCK",
    "saveFailed": "Speichern fehlgeschlagen",
    "saveMap": "Karte speichern",
    "saveMapAndCreateLevels": "ALS AKTUELLE KARTE SPEICHERN",
    "saveSuccess": "Erfolgreich gespeichert",
    "scenicFragment": "Sehenswürdigkeitsfragment",
    "scenicFragmentObtained": "Fragment erhalten! Es wird nach Abschluss des Levels gespeichert.",
    "scenicFragmentProgress": "Sehenswürdigkeitsfragmente {found}/{total}",
    "searchColor": "Farbe oder HEX-Wert suchen",
    "searchMaps": "Karten suchen",
    "selectMapTemplate": "Kartenvorlage",
    "selectTemplateDescription": "Lade ein Bild hoch, um automatisch {count} Levelsterne zu erzeugen. Ziehe die Sterne, um die Route anzupassen, und speichere sie anschließend als aktuelle Levelauswahlkarte.",
    "selectTemplateOrUpload": "BILD HOCHLADEN",
    "selected": "Ausgewählt",
    "selectedChallenge": "AUSGEWÄHLT",
    "selectedMap": "AUSGEWÄHLT",
    "settings": "EINSTELLUNGEN",
    "settingsTitle": "SPIELEINSTELLUNGEN",
    "share": "Teilen",
    "shareAction": "Teilen",
    "shareFailed": "Teilen fehlgeschlagen.",
    "shareInviteText": "Hilf mir, meine BricksBeater-Sternkarte zu erleuchten!",
    "shareMap": "Nur per Link",
    "shareReady": "Freigabelink ist bereit.",
    "shareSuccess": "Erfolgreich geteilt",
    "shareUnavailable": "Private Karten können nicht geteilt werden. Ändere zuerst die Sichtbarkeit.",
    "skill": "Fähigkeit",
    "skillCount": "Anzahl der Fähigkeiten",
    "skins": "DESIGNS",
    "skinsTitle": "Skin-Shop",
    "slow": "LANGSAM",
    "sound": "TON",
    "specialLevel": "Speziallevel",
    "specialLevelLocked": "Speziallevel gesperrt",
    "standard": "KLASSISCH",
    "starMap": "Sternenkarte",
    "starMapClear": "KARTE LÖSCHEN",
    "starMapCleared": "Die aktuelle eigene Sternkarte wurde gelöscht.",
    "starMapCurrentBadge": "AKTUELLE STERNKARTE",
    "starMapDefaultName": "Kartennamen eingeben",
    "starMapEdit": "KARTE BEARBEITEN",
    "starMapEditorHint": "Lade ein Bild hoch, um automatisch {count} Levelsterne zu erzeugen. Ziehe die Sterne, um die Route anzupassen, und speichere sie anschließend als aktuelle Levelauswahlkarte.",
    "starMapEditorTitle": "STERNKARTEN-EDITOR",
    "starMapGenerated": "{count} Levelsterne wurden erzeugt. Ziehe sie, um die Karte anzupassen.",
    "starMapImageError": "Dieses Bild kann nicht verarbeitet werden.",
    "starMapLightProgress": "ERLEUCHTET {done} / {total}",
    "starMapName": "KARTENNAME",
    "starMapPreviewInLevels": "LEVELAUSWAHL ÖFFNEN",
    "starMapProcessing": "Bild wird analysiert...",
    "starMapRegenerate": "STERNE NEU ERZEUGEN",
    "starMapRevealHint": "Schließe Levels ab, um das Originalbild nach und nach sichtbar zu machen.",
    "starMapSaveCurrent": "ALS AKTUELLE KARTE SPEICHERN",
    "starMapSaveError": "Die Sternkarte konnte nicht lokal gespeichert werden.",
    "starMapSaved": "Gespeichert. Dies ist jetzt deine aktuelle Levelauswahlkarte.",
    "starMapUpload": "BILD HOCHLADEN",
    "starMapUploadEmptyHint": "Die erste Version nutzt Bildanalyse und benötigt keine KI.",
    "starMapUploadEmptyTitle": "Bild hochladen und Sternkarte erstellen",
    "startError": "Das Spiel kann nicht gestartet werden.",
    "subtitle": "Durchbrich die Leere · Entfache die Sterne",
    "systemMapCurrentProgress": "Systemkarte · Level {level} erreicht",
    "systemMapDescription": "Offizielle Kampagne mit Levels in fester Reihenfolge.",
    "systemMapName": "ECHO-KARTE",
    "templateLoadFailed": "Dieses Bild kann nicht verarbeitet werden.",
    "title": "Echos der Erde",
    "totalLevels": "{count} Stufen",
    "unavailable": "Leveldaten sind noch nicht verfügbar",
    "uploadImage": "Bild hochladen",
    "uploadMapImage": "Bild hochladen und Karte erstellen",
    "uploadMapImageHint": "Upload und Kartengenerierung werden in einer späteren Entwicklungsphase ergänzt.",
    "victory": "Sieg",
    "viewCompletedMap": "KARTE ANSEHEN",
    "viewOriginal": "Original ansehen",
    "workshop": "Werkstatt",
    "workshopComingSoon": "WERKSTATT FÜR EIGENE KARTEN",
    "workshopDescription": "Ein zukünftiger Kreativbereich, in dem Spieler Bilder hochladen und daraus eigene Blockkarten erstellen können.",
    "workshopLevelMap": "Leveldesign",
    "workshopStarMap": "Kartendesign",
    "workshopWorkbench": "Werkbank",
    "workshopToolSoon": "Editor folgt später",
    "workshopTools": "Kartenwerkzeuge",
    "worldMap": "Weltkarte",
    "zh": "zh",
    "skin.ball_default": "Klassisches Weiß",
    "skin.ball_star": "Stern",
    "skin.ball_football": "Fußball",
    "skin.ball_flower": "Blume",
    "skin.ball_planet": "Planet",
    "skin.ball_rainbow": "Regenbogenbonbon",
    "skin.ball_diamond": "Diamant",
    "skin.paddle_default": "Klassisch",
    "skin.paddle_stool": "Hocker",
    "skin.paddle_grass": "Gras",
    "skin.paddle_rainbow": "Regenbogenbrücke",
    "skin.paddle_carpet": "Fliegender Teppich",
    "skin.paddle_wave": "Welle",
    "skin.paddle_mecha": "Mecha-Plattform",
    "imageFileRequired": "BILD HOCHLADEN",
    "imageReadFailed": "Dieses Bild kann nicht verarbeitet werden.",
    "imageTooLarge": "Dieses Bild kann nicht verarbeitet werden.",
    "imageUnsupported": "Dein Browser unterstützt Canvas nicht.",
    "imageNoRegion": "Dieses Bild kann nicht verarbeitet werden.",
    "mapNotFound": "Leveldaten sind noch nicht verfügbar",
    "mapCategoryWorld": "Weltkarte",
    "mapCategoryWorldShort": "Welt",
    "mapCategoryCountry": "Länderkarte",
    "mapCategoryCountryShort": "Land",
    "mapCategoryRegion": "Regionskarte",
    "mapCategoryRegionShort": "Region",
    "worldClassicName": "Klassische Wahrzeichen der Welt",
    "worldClassicDesc": "Eine Erkundungskarte mit berühmten Wahrzeichen aus aller Welt.",
    "worldRouteName": "Weltreise-Route",
    "worldRouteDesc": "Eine Karte entlang einer Reiseroute rund um die Welt.",
    "worldContinentsName": "Sieben Kontinente erkunden",
    "worldContinentsDesc": "Erkunde die sieben Kontinente.",
    "chinaClassicName": "China-Klassiker",
    "chinaClassicDesc": "Berühmte chinesische Städte und Wahrzeichen.",
    "japanClassicName": "Japan-Klassiker",
    "japanClassicDesc": "Bekannte Orte in Japan.",
    "franceClassicName": "Frankreich-Klassiker",
    "franceClassicDesc": "Französische Städte und Wahrzeichen.",
    "usaClassicName": "USA-Klassiker",
    "usaClassicDesc": "Bekannte Wahrzeichen der USA.",
    "europeClassicName": "Klassische Städte Europas",
    "europeClassicDesc": "Europäische Reisestädte zum Entdecken.",
    "eastAsiaName": "Ostasien-Reise",
    "eastAsiaDesc": "Städte und Wahrzeichen Ostasiens.",
    "southEastAsiaName": "Südostasien-Reise",
    "southEastAsiaDesc": "Eine Reiseroute durch Südostasien.",
    "chinaRegionName": "Regionen Chinas",
    "chinaRegionDesc": "Eine thematische Erkundungskarte der Regionen Chinas.",
    "demoWorldDreamName": "Erster Halt auf der Weltreise",
    "demoWorldDreamDesc": "Verwandle deine Wunschstädte in eine Sternkarte und erleuchte deine Reiseliste Schritt für Schritt.",
    "demoJapanNightName": "Japan-Reiseplan",
    "demoJapanNightDesc": "Eine spielbare Route durch Tokio, Kyoto, Osaka und zum Fuji.",
    "demoChinaLandmarksName": "Klassische Sehenswürdigkeiten Chinas",
    "demoChinaLandmarksDesc": "Von der Großen Mauer über Xi'an bis Shanghai, Chengdu und Guangzhou.",
    "demoFranceTripName": "Romantische Frankreich-Route",
    "demoFranceTripDesc": "Paris, Lyon, Nizza und Straßburg auf einer entspannten Reisekarte.",
    "demoSevenContinentsName": "Erkundung der sieben Kontinente",
    "demoSevenContinentsDesc": "Starte in Asien und mache alle sieben Kontinente zu deinen Spielstationen.",
    "demoUsaRoadName": "USA-Roadtrip",
    "demoUsaRoadDesc": "New York, Washington, Chicago, Los Angeles und der Grand Canyon bis zur Westküste.",
    "demoEuropeCityName": "Europäische Städtereise",
    "demoEuropeCityDesc": "Eine Städtereise durch London, Paris, Berlin, Rom, Athen und Barcelona.",
    "scenicForbiddenCity": "Verbotene Stadt",
    "scenicTempleOfHeaven": "Himmelstempel",
    "scenicGreatWall": "Chinesische Mauer",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "pt-BR": Object.freeze({
    "allFragmentsCollected": "Todos os fragmentos foram coletados",
    "back": "Voltar",
    "backHome": "VOLTAR AO INÍCIO",
    "backendReady": "Frontend Vue pronto para integração com o backend",
    "ballSkins": "Visuais da bola",
    "ballSpeed": "VELOCIDADE DA BOLA",
    "breakableCount": "Blocos quebráveis: {count}",
    "brickPalette": "Paleta de blocos",
    "builtLevels": "Criadas {done}/{total}",
    "levelBuilt": "Criado",
    "cancel": "Cancelar",
    "cannotReadTemplate": "Não foi possível processar esta imagem.",
    "canvasUnsupported": "Seu navegador não é compatível com Canvas.",
    "challengeMapHint": "Escolha o mapa que será continuado pelo botão JOGAR da tela inicial. O mapa oficial estará sempre disponível.",
    "challengeMapTitle": "SELECIONAR MAPA",
    "chapter": "Capítulo",
    "chapterEndFragmentNotice": "fragmentos de pontos turísticos.",
    "chapters": "Capítulos",
    "chooseChallenge": "USAR ESTE MAPA",
    "chooseForPlay": "USAR ESTE MAPA",
    "chooseMapPlay": "Jogar neste mapa",
    "clearSearch": "Limpar busca",
    "cleared": "Concluído",
    "close": "Fechar",
    "collectFragment": "Coletar fragmento",
    "collectedFragments": "{count} fragmentos coletados",
    "comingSoon": "EM BREVE",
    "completed": "Concluído",
    "confirm": "Confirmar",
    "confirmDeleteMap": "Excluir “{name}”? Os dados locais das fases também serão excluídos.",
    "confirmOverwrite": "Gerar a partir de uma imagem substituirá a tela atual. Continuar?",
    "continueGame": "Continuar",
    "countryCA": "Canadá",
    "countryCN": "China",
    "countryDE": "Alemanha",
    "countryFR": "França",
    "countryGB": "Reino Unido",
    "countryJP": "Japão",
    "countryKR": "Coreia do Sul",
    "countryUS": "Estados Unidos",
    "createLevel": "Criar fase",
    "createStarMap": "Criar mapa estelar",
    "createStarMapFirst": "Crie um mapa estelar no Estúdio. Até lá, JOGAR continuará pelo mapa do sistema.",
    "creativeWorkshop": "ESTÚDIO",
    "creativeWorkshopHint": "Mapas personalizados",
    "currentLevel": "FASE ATUAL",
    "currentLevelFormat": "Fase alcançada {level}",
    "currentProgress": "Continuar da fase {level}",
    "currentReachedLevel": "Fase alcançada {level}",
    "custom": "Personalizado",
    "customMapCompleteSubtitle": "Parabéns! Você acendeu todas as fases de {name}.",
    "customMapCompleteTitle": "MAPA CONCLUÍDO!",
    "customMapCurrentProgress": "{name} · Fase {level} alcançada",
    "customMapDescription": "Seu mapa estelar e suas fases personalizadas.",
    "defeat": "Derrota",
    "deleteAction": "Excluir",
    "deleteMap": "Excluir mapa",
    "difficulty": "DIFICULDADE",
    "dragNodesHint": "Envie uma imagem para gerar automaticamente {count} estrelas de fase. Arraste cada estrela para ajustar a rota e salve o resultado como mapa atual de seleção de fases.",
    "editMap": "Editar mapa",
    "en": "en",
    "energyFull": "Cheio",
    "energyInsufficient": "Energia insuficiente: {current}/{cost}. Aguarde a recuperação.",
    "energyLabel": "ENERGIA",
    "energyLoading": "CARREGANDO",
    "enterMap": "Entrar no mapa",
    "mapViews": "Visualizações",
    "likeMap": "Curtir",
    "unlikeMap": "Descurtir",
    "enterSpecialLevel": "Entrar na fase especial",
    "extreme": "EXTREMO",
    "fast": "RÁPIDA",
    "finalLevel": "Fase final",
    "fr": "fr",
    "fragment": "Fragmento",
    "fragmentCollected": "Fragmento coletado",
    "fragmentProgress": "Progresso dos fragmentos",
    "fragments": "Fragmentos",
    "gameHelp": "A/D ou ←/→ para mover · Espaço para lançar · R para reiniciar",
    "gameFailedTitle": "Falha na partida",
    "retryChallenge": "Tentar novamente",
    "generateMap": "Gerar mapa",
    "goCreateStarMap": "CRIADOR DE MAPA ESTELAR",
    "hard": "DIFÍCIL",
    "highScore": "Recorde",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Míssil laser",
    "leaderboard": "RANKING",
    "leaderboardFullTitle": "Ranking completo por países",
    "leaderboardHint": "Ver posições",
    "leaderboardPageTitle": "🌌 RANKING MUNDIAL POR PAÍSES",
    "leaderboardPlaceholderNote": "A rota, a Store e a camada de API do ranking já estão preparadas. Esta página ainda é um layout temporário e poderá ser substituída sem alterar a integração com o backend.",
    "leaderboardScore": "Pontuação",
    "resultLevelScore": "Pontuação da fase",
    "leaderboardCleared": "Progresso",
    "level": "Fase",
    "levelCountFormat": "{count} fases",
    "levelCountPoints": "Fases / Pontos",
    "levelLocked": "Fase bloqueada",
    "levelMap": "Mapa de fases",
    "levelPlaysFormat": "{count} fases · {plays} partidas",
    "levelSelect": "SELEÇÃO DE FASES",
    "litLevels": "Acesas {done}/{total}",
    "loading": "Carregando",
    "loadingLevel": "Carregando dados da fase...",
    "loadingSharedMap": "Carregando mapa compartilhado…",
    "loadingTemplate": "Analisando imagem...",
    "locked": "Bloqueado",
    "mapCompleted": "Mapa concluído",
    "mapDescription": "Descrição do mapa",
    "mapDescriptionPlaceholder": "Digite a descrição do mapa",
    "mapFullyLit": "Mapa totalmente iluminado",
    "mapName": "NOME DO MAPA",
    "mapPermission": "Permissão do mapa",
    "mapTemplate": "Modelo de mapa",
    "mapTitle": "Mapa de Ecos",
    "mapUnlocked": "Mapa desbloqueado",
    "mapUpdatedSaved": "Salvo. Este agora é seu mapa atual de seleção de fases.",
    "modeGuide": "GUIA DOS MODOS",
    "modeGuideExtreme": "Extremo: cerca de 35% da taxa de itens do modo Clássico",
    "modeGuideHard": "Difícil: cerca de 65% da taxa de itens do modo Clássico",
    "modeGuideSpeed": "Bolas mais rápidas aumentam a dificuldade de controle e conclusão",
    "modeGuideStandard": "Clássico: taxa original de queda de itens",
    "myCustomMaps": "Minhas Criações",
    "myStarMap": "Meu mapa estelar",
    "myWorkshop": "Minha oficina",
    "next": "Próximo",
    "noCustomMaps": "Ainda não há mapas personalizados",
    "noCustomMapsHint": "Crie um mapa estelar no Estúdio. Até lá, JOGAR continuará pelo mapa do sistema.",
    "noCustomStarMap": "Ainda não há mapas personalizados",
    "noMapDescription": "Sem descrição do mapa",
    "noMatches": "Nenhum resultado",
    "normal": "NORMAL",
    "off": "DESLIGADO",
    "on": "LIGADO",
    "openStarMapEditor": "EDITAR MAPA",
    "originalImageTitle": "Imagem original",
    "paddleGrow": "Raquete ampliada",
    "paddleSkins": "Visuais da barra",
    "play": "JOGAR",
    "playsLikesFormat": "{plays} partidas · {likes} curtidas",
    "previewHint": "Envie uma imagem para gerar automaticamente {count} estrelas de fase. Arraste cada estrela para ajustar a rota e salve o resultado como mapa atual de seleção de fases.",
    "previous": "Anterior",
    "privateMap": "Privado",
    "publicMap": "Público",
    "visibilityChangedPublic": "Visibilidade alterada para Pública.",
    "visibilityChangedPrivate": "Visibilidade alterada para Privada.",
    "visibilityChangedShare": "Visibilidade alterada para Somente compartilhamento.",
    "publicMaps": "Galeria",
    "publicMapsDescription": "Descubra mapas criados por outros jogadores",
    "regenerateByCount": "Reconstrução de Trajeto",
    "regenerateNodesSuccess": "Foram geradas {count} estrelas. Toque em Reconstruir rota antes de arrastá-las.",
    "replayLevel": "Jogar fase novamente",
    "restart": "Reiniciar",
    "result": "Resultado",
    "returnHome": "Voltar ao início",
    "runtimeBalls": "BOLAS",
    "runtimeHome": "INÍCIO",
    "runtimeLives": "VIDAS",
    "runtimePaused": "PAUSADO",
    "runtimeResume": "CONTINUAR",
    "runtimeTime": "TEMPO",
    "save": "Salvar",
    "saveAndBack": "SALVAR E VOLTAR",
    "saveFailed": "Falha ao salvar",
    "saveMap": "Salvar mapa",
    "saveMapAndCreateLevels": "SALVAR COMO MAPA ATUAL",
    "saveSuccess": "Salvo com sucesso",
    "scenicFragment": "Fragmento de ponto turístico",
    "scenicFragmentObtained": "Fragmento obtido! Será salvo após concluir a fase.",
    "scenicFragmentProgress": "Fragmentos de pontos turísticos {found}/{total}",
    "searchColor": "Buscar cor ou valor HEX",
    "searchMaps": "Buscar mapas",
    "selectMapTemplate": "Modelo de mapa",
    "selectTemplateDescription": "Envie uma imagem para gerar automaticamente {count} estrelas de fase. Arraste cada estrela para ajustar a rota e salve o resultado como mapa atual de seleção de fases.",
    "selectTemplateOrUpload": "ENVIAR IMAGEM",
    "selected": "Selecionado",
    "selectedChallenge": "SELECIONADO",
    "selectedMap": "SELECIONADO",
    "settings": "CONFIGURAÇÕES",
    "settingsTitle": "CONFIGURAÇÕES DO JOGO",
    "share": "Compartilhar",
    "shareAction": "Compartilhar",
    "shareFailed": "Falha ao compartilhar.",
    "shareInviteText": "Venha iluminar meu mapa estelar do BricksBeater!",
    "shareMap": "Somente por link",
    "shareReady": "Link de compartilhamento pronto.",
    "shareSuccess": "Compartilhado com sucesso",
    "shareUnavailable": "Mapas privados não podem ser compartilhados. Altere a visibilidade primeiro.",
    "skill": "Habilidade",
    "skillCount": "Quantidade de habilidades",
    "skins": "VISUAIS",
    "skinsTitle": "Loja de visuais",
    "slow": "LENTA",
    "sound": "SOM",
    "specialLevel": "Fase especial",
    "specialLevelLocked": "Fase especial bloqueada",
    "standard": "CLÁSSICO",
    "starMap": "Mapa estelar",
    "starMapClear": "LIMPAR MAPA",
    "starMapCleared": "O mapa estelar personalizado atual foi removido.",
    "starMapCurrentBadge": "MAPA ESTELAR ATUAL",
    "starMapDefaultName": "Digite o nome do mapa",
    "starMapEdit": "EDITAR MAPA",
    "starMapEditorHint": "Envie uma imagem para gerar automaticamente {count} estrelas de fase. Arraste cada estrela para ajustar a rota e salve o resultado como mapa atual de seleção de fases.",
    "starMapEditorTitle": "CRIADOR DE MAPA ESTELAR",
    "starMapGenerated": "Foram geradas {count} estrelas de fase. Arraste-as para ajustar o mapa.",
    "starMapImageError": "Não foi possível processar esta imagem.",
    "starMapLightProgress": "ACESAS {done} / {total}",
    "starMapName": "NOME DO MAPA",
    "starMapPreviewInLevels": "ABRIR SELEÇÃO DE FASES",
    "starMapProcessing": "Analisando imagem...",
    "starMapRegenerate": "GERAR ESTRELAS NOVAMENTE",
    "starMapRevealHint": "Conclua fases para revelar gradualmente a imagem original.",
    "starMapSaveCurrent": "SALVAR COMO MAPA ATUAL",
    "starMapSaveError": "Não foi possível salvar o mapa estelar localmente.",
    "starMapSaved": "Salvo. Este agora é seu mapa atual de seleção de fases.",
    "starMapUpload": "ENVIAR IMAGEM",
    "starMapUploadEmptyHint": "A primeira versão usa análise de imagem e não precisa de IA.",
    "starMapUploadEmptyTitle": "Envie uma imagem para criar um mapa estelar",
    "startError": "Não foi possível iniciar o jogo.",
    "subtitle": "Quebre o vazio · Acenda as estrelas",
    "systemMapCurrentProgress": "Mapa do sistema · Fase {level} alcançada",
    "systemMapDescription": "Campanha oficial com fases jogadas em sequência.",
    "systemMapName": "MAPA DE ECOS",
    "templateLoadFailed": "Não foi possível processar esta imagem.",
    "title": "Ecos da Terra",
    "totalLevels": "{count} fases",
    "unavailable": "Os dados desta fase ainda não estão disponíveis",
    "uploadImage": "Enviar imagem",
    "uploadMapImage": "Enviar imagem para criar mapa",
    "uploadMapImageHint": "O envio e a geração de mapas serão adicionados em uma etapa futura.",
    "victory": "Vitória",
    "viewCompletedMap": "VER MAPA",
    "viewOriginal": "Ver original",
    "workshop": "Oficina",
    "workshopComingSoon": "OFICINA DE MAPAS PERSONALIZADOS",
    "workshopDescription": "Um futuro espaço de criação onde jogadores poderão enviar uma imagem e transformá-la em um mapa de blocos personalizado.",
    "workshopLevelMap": "Criação de Fase",
    "workshopStarMap": "Criação de Mapa",
    "workshopWorkbench": "Bancada",
    "workshopToolSoon": "Editor em breve",
    "workshopTools": "Ferramentas do mapa",
    "worldMap": "Mapa-múndi",
    "zh": "zh",
    "skin.ball_default": "Branco clássico",
    "skin.ball_star": "Estrela",
    "skin.ball_football": "Bola de futebol",
    "skin.ball_flower": "Flor",
    "skin.ball_planet": "Planeta",
    "skin.ball_rainbow": "Doce arco-íris",
    "skin.ball_diamond": "Diamante",
    "skin.paddle_default": "Clássica",
    "skin.paddle_stool": "Banquinho",
    "skin.paddle_grass": "Grama",
    "skin.paddle_rainbow": "Ponte arco-íris",
    "skin.paddle_carpet": "Tapete mágico",
    "skin.paddle_wave": "Onda",
    "skin.paddle_mecha": "Plataforma mecânica",
    "imageFileRequired": "ENVIAR IMAGEM",
    "imageReadFailed": "Não foi possível processar esta imagem.",
    "imageTooLarge": "Não foi possível processar esta imagem.",
    "imageUnsupported": "Seu navegador não é compatível com Canvas.",
    "imageNoRegion": "Não foi possível processar esta imagem.",
    "mapNotFound": "Os dados desta fase ainda não estão disponíveis",
    "mapCategoryWorld": "Mapa-múndi",
    "mapCategoryWorldShort": "Mundo",
    "mapCategoryCountry": "Mapa de país",
    "mapCategoryCountryShort": "País",
    "mapCategoryRegion": "Mapa regional",
    "mapCategoryRegionShort": "Região",
    "worldClassicName": "Marcos clássicos do mundo",
    "worldClassicDesc": "Um mapa de exploração formado por marcos famosos do mundo inteiro.",
    "worldRouteName": "Rota de viagem mundial",
    "worldRouteDesc": "Um mapa baseado em uma rota de viagem ao redor do mundo.",
    "worldContinentsName": "Exploração dos sete continentes",
    "worldContinentsDesc": "Explore os sete continentes.",
    "chinaClassicName": "China clássica",
    "chinaClassicDesc": "Cidades e marcos famosos da China.",
    "japanClassicName": "Japão clássico",
    "japanClassicDesc": "Locais representativos do Japão.",
    "franceClassicName": "França clássica",
    "franceClassicDesc": "Cidades e marcos da França.",
    "usaClassicName": "Estados Unidos clássico",
    "usaClassicDesc": "Marcos representativos dos Estados Unidos.",
    "europeClassicName": "Cidades clássicas da Europa",
    "europeClassicDesc": "Cidades europeias para viajar e explorar.",
    "eastAsiaName": "Viagem pela Ásia Oriental",
    "eastAsiaDesc": "Cidades e marcos da Ásia Oriental.",
    "southEastAsiaName": "Viagem pelo Sudeste Asiático",
    "southEastAsiaDesc": "Uma rota de viagem pelo Sudeste Asiático.",
    "chinaRegionName": "Regiões da China",
    "chinaRegionDesc": "Mapa de exploração temática das regiões da China.",
    "demoWorldDreamName": "Primeira parada pelo mundo",
    "demoWorldDreamDesc": "Transforme as cidades que deseja visitar em um mapa estelar e ilumine sua lista de viagens.",
    "demoJapanNightName": "Plano de viagem pelo Japão",
    "demoJapanNightDesc": "Uma rota jogável por Tóquio, Kyoto, Osaka e Monte Fuji.",
    "demoChinaLandmarksName": "Pontos clássicos da China",
    "demoChinaLandmarksDesc": "Da Grande Muralha a Xi'an, Xangai, Chengdu e Guangzhou.",
    "demoFranceTripName": "Rota romântica pela França",
    "demoFranceTripDesc": "Paris, Lyon, Nice e Estrasburgo em um mapa para explorar com calma.",
    "demoSevenContinentsName": "Exploração dos sete continentes",
    "demoSevenContinentsDesc": "Parta da Ásia e transforme os sete continentes em fases da sua aventura.",
    "demoUsaRoadName": "Viagem de carro pelos EUA",
    "demoUsaRoadDesc": "Nova York, Washington, Chicago, Los Angeles e Grand Canyon até a costa oeste.",
    "demoEuropeCityName": "Passeio pelas cidades europeias",
    "demoEuropeCityDesc": "Um mapa por Londres, Paris, Berlim, Roma, Atenas e Barcelona.",
    "scenicForbiddenCity": "Cidade Proibida",
    "scenicTempleOfHeaven": "Templo do Céu",
    "scenicGreatWall": "Grande Muralha da China",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "pt-PT": Object.freeze({
    "allFragmentsCollected": "Todos os fragmentos foram recolhidos",
    "back": "Voltar",
    "backHome": "VOLTAR AO INÍCIO",
    "backendReady": "Frontend Vue preparado para integração com o backend",
    "ballSkins": "Visuais da bola",
    "ballSpeed": "VELOCIDADE DA BOLA",
    "breakableCount": "Blocos destrutíveis: {count}",
    "brickPalette": "Paleta de blocos",
    "builtLevels": "Criados {done}/{total}",
    "levelBuilt": "Criado",
    "cancel": "Cancelar",
    "cannotReadTemplate": "Não foi possível processar esta imagem.",
    "canvasUnsupported": "O teu navegador não suporta Canvas.",
    "challengeMapHint": "Escolhe o mapa que será continuado pelo botão JOGAR no ecrã inicial. O mapa oficial estará sempre disponível.",
    "challengeMapTitle": "SELECIONAR MAPA",
    "chapter": "Capítulo",
    "chapterEndFragmentNotice": "fragmentos de pontos turísticos.",
    "chapters": "Capítulos",
    "chooseChallenge": "USAR ESTE MAPA",
    "chooseForPlay": "USAR ESTE MAPA",
    "chooseMapPlay": "Jogar neste mapa",
    "clearSearch": "Limpar pesquisa",
    "cleared": "Concluído",
    "close": "Fechar",
    "collectFragment": "Recolher fragmento",
    "collectedFragments": "{count} fragmentos recolhidos",
    "comingSoon": "EM BREVE",
    "completed": "Concluído",
    "confirm": "Confirmar",
    "confirmDeleteMap": "Eliminar “{name}”? Os dados locais dos níveis também serão eliminados.",
    "confirmOverwrite": "Gerar a partir de uma imagem substituirá a área atual. Continuar?",
    "continueGame": "Continuar",
    "countryCA": "Canadá",
    "countryCN": "China",
    "countryDE": "Alemanha",
    "countryFR": "França",
    "countryGB": "Reino Unido",
    "countryJP": "Japão",
    "countryKR": "Coreia do Sul",
    "countryUS": "Estados Unidos",
    "createLevel": "Criar nível",
    "createStarMap": "Criar mapa estelar",
    "createStarMapFirst": "Cria um mapa estelar no Estúdio. Até lá, JOGAR continuará no mapa do sistema.",
    "creativeWorkshop": "ESTÚDIO",
    "creativeWorkshopHint": "Mapas personalizados",
    "currentLevel": "NÍVEL ATUAL",
    "currentLevelFormat": "Nível alcançado {level}",
    "currentProgress": "Continuar a partir do nível {level}",
    "currentReachedLevel": "Nível alcançado {level}",
    "custom": "Personalizado",
    "customMapCompleteSubtitle": "Parabéns! Iluminaste todos os níveis de {name}.",
    "customMapCompleteTitle": "MAPA CONCLUÍDO!",
    "customMapCurrentProgress": "{name} · Nível {level} alcançado",
    "customMapDescription": "O teu mapa estelar e os teus níveis personalizados.",
    "defeat": "Derrota",
    "deleteAction": "Eliminar",
    "deleteMap": "Eliminar mapa",
    "difficulty": "DIFICULDADE",
    "dragNodesHint": "Carrega uma imagem para gerar automaticamente {count} estrelas de nível. Arrasta cada estrela para ajustar o percurso e guarda o resultado como mapa atual de seleção de níveis.",
    "editMap": "Editar mapa",
    "en": "en",
    "energyFull": "Cheio",
    "energyInsufficient": "Energia insuficiente: {current}/{cost}. Aguarda a recuperação.",
    "energyLabel": "ENERGIA",
    "energyLoading": "A CARREGAR",
    "enterMap": "Entrar no mapa",
    "mapViews": "Visualizações",
    "likeMap": "Gostar",
    "unlikeMap": "Remover gosto",
    "enterSpecialLevel": "Entrar no nível especial",
    "extreme": "EXTREMO",
    "fast": "RÁPIDA",
    "finalLevel": "Nível final",
    "fr": "fr",
    "fragment": "Fragmento",
    "fragmentCollected": "Fragmento recolhido",
    "fragmentProgress": "Progresso dos fragmentos",
    "fragments": "Fragmentos",
    "gameHelp": "A/D ou ←/→ para mover · Espaço para lançar · R para reiniciar",
    "gameFailedTitle": "Jogo falhado",
    "retryChallenge": "Tentar novamente",
    "generateMap": "Gerar mapa",
    "goCreateStarMap": "CRIADOR DE MAPA ESTELAR",
    "hard": "DIFÍCIL",
    "highScore": "Melhor pontuação",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Míssil laser",
    "leaderboard": "CLASSIFICAÇÃO",
    "leaderboardFullTitle": "Classificação completa por países",
    "leaderboardHint": "Ver posições",
    "leaderboardPageTitle": "🌌 CLASSIFICAÇÃO MUNDIAL POR PAÍSES",
    "leaderboardPlaceholderNote": "A rota, a Store e a camada de API da classificação já estão preparadas. Esta página continua a ser um esquema temporário e poderá ser substituída sem alterar a integração com o backend.",
    "leaderboardScore": "Pontuação",
    "resultLevelScore": "Pontuação do nível",
    "leaderboardCleared": "Progresso",
    "level": "Nível",
    "levelCountFormat": "{count} níveis",
    "levelCountPoints": "Níveis / Pontos",
    "levelLocked": "Nível bloqueado",
    "levelMap": "Mapa de níveis",
    "levelPlaysFormat": "{count} níveis · {plays} partidas",
    "levelSelect": "SELEÇÃO DE NÍVEIS",
    "litLevels": "Iluminados {done}/{total}",
    "loading": "A carregar",
    "loadingLevel": "A carregar dados do nível...",
    "loadingSharedMap": "A carregar mapa partilhado…",
    "loadingTemplate": "A analisar a imagem...",
    "locked": "Bloqueado",
    "mapCompleted": "Mapa concluído",
    "mapDescription": "Descrição do mapa",
    "mapDescriptionPlaceholder": "Introduza a descrição do mapa",
    "mapFullyLit": "Mapa totalmente iluminado",
    "mapName": "NOME DO MAPA",
    "mapPermission": "Permissão do mapa",
    "mapTemplate": "Modelo de mapa",
    "mapTitle": "Mapa de Ecos",
    "mapUnlocked": "Mapa desbloqueado",
    "mapUpdatedSaved": "Guardado. Este é agora o teu mapa atual de seleção de níveis.",
    "modeGuide": "GUIA DOS MODOS",
    "modeGuideExtreme": "Extremo: cerca de 35% da taxa de itens do modo Clássico",
    "modeGuideHard": "Difícil: cerca de 65% da taxa de itens do modo Clássico",
    "modeGuideSpeed": "Bolas mais rápidas aumentam a dificuldade de controlo e conclusão",
    "modeGuideStandard": "Clássico: taxa original de queda de itens",
    "myCustomMaps": "As Minhas Criações",
    "myStarMap": "O meu mapa estelar",
    "myWorkshop": "A minha oficina",
    "next": "Seguinte",
    "noCustomMaps": "Ainda não existem mapas personalizados",
    "noCustomMapsHint": "Cria um mapa estelar no Estúdio. Até lá, JOGAR continuará no mapa do sistema.",
    "noCustomStarMap": "Ainda não existem mapas personalizados",
    "noMapDescription": "Sem descrição do mapa",
    "noMatches": "Sem resultados",
    "normal": "NORMAL",
    "off": "DESLIGADO",
    "on": "LIGADO",
    "openStarMapEditor": "EDITAR MAPA",
    "originalImageTitle": "Imagem original",
    "paddleGrow": "Raquete ampliada",
    "paddleSkins": "Visuais da raquete",
    "play": "JOGAR",
    "playsLikesFormat": "{plays} partidas · {likes} gostos",
    "previewHint": "Carrega uma imagem para gerar automaticamente {count} estrelas de nível. Arrasta cada estrela para ajustar o percurso e guarda o resultado como mapa atual de seleção de níveis.",
    "previous": "Anterior",
    "privateMap": "Privado",
    "publicMap": "Público",
    "visibilityChangedPublic": "Visibilidade alterada para Pública.",
    "visibilityChangedPrivate": "Visibilidade alterada para Privada.",
    "visibilityChangedShare": "Visibilidade alterada para Apenas partilha.",
    "publicMaps": "Galeria",
    "publicMapsDescription": "Descubra mapas criados por outros jogadores",
    "regenerateByCount": "Reconstrução de Percurso",
    "regenerateNodesSuccess": "Foram geradas {count} estrelas. Toca em Reconstruir trajetória antes de as arrastares.",
    "replayLevel": "Repetir nível",
    "restart": "Reiniciar",
    "result": "Resultado",
    "returnHome": "Voltar ao início",
    "runtimeBalls": "BOLAS",
    "runtimeHome": "INÍCIO",
    "runtimeLives": "VIDAS",
    "runtimePaused": "PAUSA",
    "runtimeResume": "CONTINUAR",
    "runtimeTime": "TEMPO",
    "save": "Guardar",
    "saveAndBack": "GUARDAR E VOLTAR",
    "saveFailed": "Falha ao guardar",
    "saveMap": "Guardar mapa",
    "saveMapAndCreateLevels": "GUARDAR COMO MAPA ATUAL",
    "saveSuccess": "Guardado com sucesso",
    "scenicFragment": "Fragmento de ponto turístico",
    "scenicFragmentObtained": "Fragmento obtido! Será guardado após concluir o nível.",
    "scenicFragmentProgress": "Fragmentos de pontos turísticos {found}/{total}",
    "searchColor": "Procurar cor ou valor HEX",
    "searchMaps": "Procurar mapas",
    "selectMapTemplate": "Modelo de mapa",
    "selectTemplateDescription": "Carrega uma imagem para gerar automaticamente {count} estrelas de nível. Arrasta cada estrela para ajustar o percurso e guarda o resultado como mapa atual de seleção de níveis.",
    "selectTemplateOrUpload": "CARREGAR IMAGEM",
    "selected": "Selecionado",
    "selectedChallenge": "SELECIONADO",
    "selectedMap": "SELECIONADO",
    "settings": "DEFINIÇÕES",
    "settingsTitle": "DEFINIÇÕES DO JOGO",
    "share": "Partilhar",
    "shareAction": "Partilhar",
    "shareFailed": "Falha ao partilhar.",
    "shareInviteText": "Vem iluminar o meu mapa estelar do BricksBeater!",
    "shareMap": "Apenas por ligação",
    "shareReady": "Ligação de partilha pronta.",
    "shareSuccess": "Partilhado com sucesso",
    "shareUnavailable": "Mapas privados não podem ser partilhados. Altere primeiro a visibilidade.",
    "skill": "Habilidade",
    "skillCount": "Número de habilidades",
    "skins": "VISUAIS",
    "skinsTitle": "Loja de visuais",
    "slow": "LENTA",
    "sound": "SOM",
    "specialLevel": "Nível especial",
    "specialLevelLocked": "Nível especial bloqueado",
    "standard": "CLÁSSICO",
    "starMap": "Mapa estelar",
    "starMapClear": "LIMPAR MAPA",
    "starMapCleared": "O mapa estelar personalizado atual foi removido.",
    "starMapCurrentBadge": "MAPA ESTELAR ATUAL",
    "starMapDefaultName": "Introduza o nome do mapa",
    "starMapEdit": "EDITAR MAPA",
    "starMapEditorHint": "Carrega uma imagem para gerar automaticamente {count} estrelas de nível. Arrasta cada estrela para ajustar o percurso e guarda o resultado como mapa atual de seleção de níveis.",
    "starMapEditorTitle": "CRIADOR DE MAPA ESTELAR",
    "starMapGenerated": "Foram geradas {count} estrelas de nível. Arrasta-as para ajustar o mapa.",
    "starMapImageError": "Não foi possível processar esta imagem.",
    "starMapLightProgress": "ILUMINADOS {done} / {total}",
    "starMapName": "NOME DO MAPA",
    "starMapPreviewInLevels": "ABRIR SELEÇÃO DE NÍVEIS",
    "starMapProcessing": "A analisar a imagem...",
    "starMapRegenerate": "GERAR ESTRELAS NOVAMENTE",
    "starMapRevealHint": "Conclui níveis para revelar gradualmente a imagem original.",
    "starMapSaveCurrent": "GUARDAR COMO MAPA ATUAL",
    "starMapSaveError": "Não foi possível guardar o mapa estelar localmente.",
    "starMapSaved": "Guardado. Este é agora o teu mapa atual de seleção de níveis.",
    "starMapUpload": "CARREGAR IMAGEM",
    "starMapUploadEmptyHint": "A primeira versão usa análise de imagem e não necessita de IA.",
    "starMapUploadEmptyTitle": "Carrega uma imagem para criar um mapa estelar",
    "startError": "Não foi possível iniciar o jogo.",
    "subtitle": "Quebra o vazio · Acende as estrelas",
    "systemMapCurrentProgress": "Mapa do sistema · Nível {level} alcançado",
    "systemMapDescription": "Campanha oficial com níveis jogados por ordem.",
    "systemMapName": "MAPA DE ECOS",
    "templateLoadFailed": "Não foi possível processar esta imagem.",
    "title": "Ecos da Terra",
    "totalLevels": "{count} níveis",
    "unavailable": "Os dados deste nível ainda não estão disponíveis",
    "uploadImage": "Carregar imagem",
    "uploadMapImage": "Carregar imagem para criar mapa",
    "uploadMapImageHint": "O carregamento e a geração de mapas serão adicionados numa fase posterior.",
    "victory": "Vitória",
    "viewCompletedMap": "VER MAPA",
    "viewOriginal": "Ver original",
    "workshop": "Oficina",
    "workshopComingSoon": "OFICINA DE MAPAS PERSONALIZADOS",
    "workshopDescription": "Um futuro espaço de criação onde os jogadores poderão carregar uma imagem e transformá-la num mapa de blocos personalizado.",
    "workshopLevelMap": "Criação de Nível",
    "workshopStarMap": "Criação de Mapa",
    "workshopWorkbench": "Bancada",
    "workshopToolSoon": "Editor brevemente",
    "workshopTools": "Ferramentas do mapa",
    "worldMap": "Mapa-múndi",
    "zh": "zh",
    "skin.ball_default": "Branco clássico",
    "skin.ball_star": "Estrela",
    "skin.ball_football": "Bola de futebol",
    "skin.ball_flower": "Flor",
    "skin.ball_planet": "Planeta",
    "skin.ball_rainbow": "Doce arco-íris",
    "skin.ball_diamond": "Diamante",
    "skin.paddle_default": "Clássica",
    "skin.paddle_stool": "Banco",
    "skin.paddle_grass": "Relva",
    "skin.paddle_rainbow": "Ponte arco-íris",
    "skin.paddle_carpet": "Tapete mágico",
    "skin.paddle_wave": "Onda",
    "skin.paddle_mecha": "Plataforma mecânica",
    "imageFileRequired": "CARREGAR IMAGEM",
    "imageReadFailed": "Não foi possível processar esta imagem.",
    "imageTooLarge": "Não foi possível processar esta imagem.",
    "imageUnsupported": "O teu navegador não suporta Canvas.",
    "imageNoRegion": "Não foi possível processar esta imagem.",
    "mapNotFound": "Os dados deste nível ainda não estão disponíveis",
    "mapCategoryWorld": "Mapa-múndi",
    "mapCategoryWorldShort": "Mundo",
    "mapCategoryCountry": "Mapa de país",
    "mapCategoryCountryShort": "País",
    "mapCategoryRegion": "Mapa regional",
    "mapCategoryRegionShort": "Região",
    "worldClassicName": "Marcos clássicos do mundo",
    "worldClassicDesc": "Um mapa de exploração composto por marcos famosos de todo o mundo.",
    "worldRouteName": "Rota de viagem mundial",
    "worldRouteDesc": "Um mapa baseado numa rota de viagem à volta do mundo.",
    "worldContinentsName": "Exploração dos sete continentes",
    "worldContinentsDesc": "Explore os sete continentes.",
    "chinaClassicName": "China clássica",
    "chinaClassicDesc": "Cidades e marcos famosos da China.",
    "japanClassicName": "Japão clássico",
    "japanClassicDesc": "Locais representativos do Japão.",
    "franceClassicName": "França clássica",
    "franceClassicDesc": "Cidades e marcos de França.",
    "usaClassicName": "Estados Unidos clássico",
    "usaClassicDesc": "Marcos representativos dos Estados Unidos.",
    "europeClassicName": "Cidades clássicas da Europa",
    "europeClassicDesc": "Cidades europeias para viajar e explorar.",
    "eastAsiaName": "Viagem pela Ásia Oriental",
    "eastAsiaDesc": "Cidades e marcos da Ásia Oriental.",
    "southEastAsiaName": "Viagem pelo Sudeste Asiático",
    "southEastAsiaDesc": "Uma rota de viagem pelo Sudeste Asiático.",
    "chinaRegionName": "Regiões da China",
    "chinaRegionDesc": "Mapa de exploração temática das regiões da China.",
    "demoWorldDreamName": "Primeira paragem à volta do mundo",
    "demoWorldDreamDesc": "Transforma as cidades que queres visitar num mapa estelar e ilumina a tua lista de viagens.",
    "demoJapanNightName": "Plano de viagem pelo Japão",
    "demoJapanNightDesc": "Uma rota jogável por Tóquio, Quioto, Osaka e Monte Fuji.",
    "demoChinaLandmarksName": "Marcos clássicos da China",
    "demoChinaLandmarksDesc": "Da Grande Muralha a Xi'an, Xangai, Chengdu e Guangzhou.",
    "demoFranceTripName": "Rota romântica por França",
    "demoFranceTripDesc": "Paris, Lyon, Nice e Estrasburgo num mapa para explorar com calma.",
    "demoSevenContinentsName": "Exploração dos sete continentes",
    "demoSevenContinentsDesc": "Parte da Ásia e transforma os sete continentes em etapas da tua aventura.",
    "demoUsaRoadName": "Viagem de estrada pelos EUA",
    "demoUsaRoadDesc": "Nova Iorque, Washington, Chicago, Los Angeles e Grand Canyon até à costa oeste.",
    "demoEuropeCityName": "Passeio pelas cidades europeias",
    "demoEuropeCityDesc": "Um mapa por Londres, Paris, Berlim, Roma, Atenas e Barcelona.",
    "scenicForbiddenCity": "Cidade Proibida",
    "scenicTempleOfHeaven": "Templo do Céu",
    "scenicGreatWall": "Grande Muralha da China",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "it": Object.freeze({
    "allFragmentsCollected": "Tutti i frammenti raccolti",
    "back": "Indietro",
    "backHome": "TORNA ALLA HOME",
    "backendReady": "Frontend Vue pronto per il backend",
    "ballSkins": "Skin della pallina",
    "ballSpeed": "VELOCITÀ PALLA",
    "breakableCount": "Mattoni distruttibili: {count}",
    "brickPalette": "Tavolozza mattoni",
    "builtLevels": "Creati {done}/{total}",
    "levelBuilt": "Creato",
    "cancel": "Annulla",
    "cannotReadTemplate": "Impossibile elaborare questa immagine.",
    "canvasUnsupported": "Il browser non supporta Canvas.",
    "challengeMapHint": "Scegli la mappa usata dal pulsante GIOCA nella Home. La mappa ufficiale è sempre disponibile.",
    "challengeMapTitle": "SELEZIONA MAPPA",
    "chapter": "Capitolo",
    "chapterEndFragmentNotice": "frammenti dei luoghi d’interesse.",
    "chapters": "Capitoli",
    "chooseChallenge": "USA QUESTA MAPPA",
    "chooseForPlay": "USA QUESTA MAPPA",
    "chooseMapPlay": "Gioca su questa mappa",
    "clearSearch": "Cancella ricerca",
    "cleared": "Superato",
    "close": "Chiudi",
    "collectFragment": "Raccogli frammento",
    "collectedFragments": "{count} frammenti raccolti",
    "comingSoon": "PROSSIMAMENTE",
    "completed": "Completato",
    "confirm": "Conferma",
    "confirmDeleteMap": "Eliminare “{name}”? Verranno eliminati anche i dati locali dei livelli.",
    "confirmOverwrite": "La generazione da un’immagine sovrascriverà la tela attuale. Continuare?",
    "continueGame": "Continua",
    "countryCA": "Canada",
    "countryCN": "Cina",
    "countryDE": "Germania",
    "countryFR": "Francia",
    "countryGB": "Regno Unito",
    "countryJP": "Giappone",
    "countryKR": "Corea del Sud",
    "countryUS": "Stati Uniti",
    "createLevel": "Crea livello",
    "createStarMap": "Crea mappa stellare",
    "createStarMapFirst": "Crea una mappa stellare nel Laboratorio. Fino ad allora, GIOCA continuerà a usare la mappa di sistema.",
    "creativeWorkshop": "LABORATORIO",
    "creativeWorkshopHint": "Mappe personalizzate",
    "currentLevel": "LIVELLO ATTUALE",
    "currentLevelFormat": "Livello raggiunto {level}",
    "currentProgress": "Continua dal livello {level}",
    "currentReachedLevel": "Livello raggiunto {level}",
    "custom": "Personalizzato",
    "customMapCompleteSubtitle": "Congratulazioni! Hai illuminato tutti i livelli di {name}.",
    "customMapCompleteTitle": "MAPPA COMPLETATA!",
    "customMapCurrentProgress": "{name} · Raggiunto il livello {level}",
    "customMapDescription": "La tua mappa stellare personalizzata e i livelli creati da te.",
    "defeat": "Sconfitta",
    "deleteAction": "Elimina",
    "deleteMap": "Elimina mappa",
    "difficulty": "DIFFICOLTÀ",
    "dragNodesHint": "Carica un'immagine per generare automaticamente {count} stelle-livello. Trascina le stelle per perfezionare il percorso, quindi salva la mappa come selezione livelli corrente.",
    "editMap": "Modifica mappa",
    "en": "en",
    "energyFull": "Pieno",
    "energyInsufficient": "Energia insufficiente: {current}/{cost}. Attendi il recupero.",
    "energyLabel": "ENERGIA",
    "energyLoading": "CARICAMENTO",
    "enterMap": "Entra nella mappa",
    "mapViews": "Visualizzazioni",
    "likeMap": "Mi piace",
    "unlikeMap": "Rimuovi Mi piace",
    "enterSpecialLevel": "Entra nel livello speciale",
    "extreme": "ESTREMO",
    "fast": "VELOCE",
    "finalLevel": "Finale",
    "fr": "fr",
    "fragment": "Frammento",
    "fragmentCollected": "Frammento raccolto",
    "fragmentProgress": "Progresso frammenti",
    "fragments": "Frammenti",
    "gameHelp": "A/D o ←/→ per muovere · Spazio per lanciare · R per riavviare",
    "gameFailedTitle": "Partita fallita",
    "retryChallenge": "Riprova",
    "generateMap": "Genera mappa",
    "goCreateStarMap": "CREA MAPPA STELLARE",
    "hard": "DIFFICILE",
    "highScore": "Punteggio migliore",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Missile laser",
    "leaderboard": "CLASSIFICA",
    "leaderboardFullTitle": "Classifica nazionale completa",
    "leaderboardHint": "Classifiche",
    "leaderboardPageTitle": "🌍 CLASSIFICA GLOBALE PER NAZIONI",
    "leaderboardPlaceholderNote": "Il percorso della classifica, lo store e il livello API sono pronti. Il layout attuale è temporaneo e può essere sostituito senza modificare il codice di integrazione backend.",
    "leaderboardScore": "Punteggio",
    "resultLevelScore": "Punteggio livello",
    "leaderboardCleared": "Progresso",
    "level": "Livello",
    "levelCountFormat": "{count} livelli",
    "levelCountPoints": "Livelli / Punti",
    "levelLocked": "Livello bloccato",
    "levelMap": "Mappa dei livelli",
    "levelPlaysFormat": "{count} livelli · {plays} partite",
    "levelSelect": "SELEZIONE LIVELLI",
    "litLevels": "Illuminati {done}/{total}",
    "loading": "Caricamento",
    "loadingLevel": "Caricamento livello...",
    "loadingSharedMap": "Caricamento mappa condivisa…",
    "loadingTemplate": "Analisi dell'immagine...",
    "locked": "Bloccato",
    "mapCompleted": "Mappa completata",
    "mapDescription": "Descrizione mappa",
    "mapDescriptionPlaceholder": "Inserisci la descrizione della mappa",
    "mapFullyLit": "Mappa completamente illuminata",
    "mapName": "NOME MAPPA",
    "mapPermission": "Permessi mappa",
    "mapTemplate": "Modello mappa",
    "mapTitle": "Mappa degli Echi",
    "mapUnlocked": "Mappa sbloccata",
    "mapUpdatedSaved": "Salvata. Ora questa è la mappa corrente per la selezione dei livelli.",
    "modeGuide": "GUIDA MODALITÀ",
    "modeGuideExtreme": "Estrema: circa il 35% del tasso di rilascio oggetti della modalità Classica",
    "modeGuideHard": "Difficile: circa il 65% del tasso di rilascio oggetti della modalità Classica",
    "modeGuideSpeed": "Una pallina più veloce aumenta la difficoltà di controllo e completamento",
    "modeGuideStandard": "Classica: tasso di rilascio oggetti originale",
    "myCustomMaps": "Le Mie Creazioni",
    "myStarMap": "La mia mappa stellare",
    "myWorkshop": "La mia officina",
    "next": "Avanti",
    "noCustomMaps": "Nessuna mappa personalizzata",
    "noCustomMapsHint": "Crea una mappa stellare nel Laboratorio. Fino ad allora, GIOCA continuerà a usare la mappa di sistema.",
    "noCustomStarMap": "Nessuna mappa personalizzata",
    "noMapDescription": "Nessuna descrizione della mappa",
    "noMatches": "Nessun risultato",
    "normal": "NORMALE",
    "off": "DISATTIVATO",
    "on": "ON",
    "openStarMapEditor": "MODIFICA MAPPA",
    "originalImageTitle": "Immagine originale",
    "paddleGrow": "Racchetta ingrandita",
    "paddleSkins": "Skin della barra",
    "play": "GIOCA",
    "playsLikesFormat": "{plays} partite · {likes} Mi piace",
    "previewHint": "Carica un'immagine per generare automaticamente {count} stelle-livello. Trascinale per perfezionare il percorso, quindi salva la mappa.",
    "previous": "Indietro",
    "privateMap": "Privata",
    "publicMap": "Pubblica",
    "visibilityChangedPublic": "Visibilità impostata su Pubblica.",
    "visibilityChangedPrivate": "Visibilità impostata su Privata.",
    "visibilityChangedShare": "Visibilità impostata su Solo tramite link.",
    "publicMaps": "Galleria",
    "publicMapsDescription": "Scopri le mappe create da altri giocatori",
    "regenerateByCount": "Ricostruzione Percorso",
    "regenerateNodesSuccess": "Generate {count} stelle. Premi Ricostruisci percorso prima di trascinarle.",
    "replayLevel": "Rigioca livello",
    "restart": "Riavvia",
    "result": "Risultato",
    "returnHome": "Torna alla home",
    "runtimeBalls": "PALLE",
    "runtimeHome": "INIZIO",
    "runtimeLives": "VITE",
    "runtimePaused": "PAUSA",
    "runtimeResume": "RIPRENDI",
    "runtimeTime": "TEMPO",
    "save": "Salva",
    "saveAndBack": "SALVA E TORNA",
    "saveFailed": "Salvataggio non riuscito",
    "saveMap": "Salva mappa",
    "saveMapAndCreateLevels": "SALVA COME MAPPA CORRENTE",
    "saveSuccess": "Salvato correttamente",
    "scenicFragment": "Frammento luogo",
    "scenicFragmentObtained": "Frammento ottenuto! Verrà salvato dopo aver completato il livello.",
    "scenicFragmentProgress": "Frammenti dei luoghi {found}/{total}",
    "searchColor": "Cerca colore o valore HEX",
    "searchMaps": "Cerca mappe",
    "selectMapTemplate": "Modello mappa",
    "selectTemplateDescription": "Carica un'immagine per generare automaticamente {count} stelle-livello. Trascinale per perfezionare il percorso, quindi salva la mappa.",
    "selectTemplateOrUpload": "CARICA IMMAGINE",
    "selected": "Selezionato",
    "selectedChallenge": "SELEZIONATA",
    "selectedMap": "SELEZIONATA",
    "settings": "IMPOSTAZIONI",
    "settingsTitle": "IMPOSTAZIONI DI GIOCO",
    "share": "Condividi",
    "shareAction": "Condividi",
    "shareFailed": "Condivisione non riuscita.",
    "shareInviteText": "Vieni a illuminare la mia mappa stellare BricksBeater!",
    "shareMap": "Solo tramite link",
    "shareReady": "Link di condivisione pronto.",
    "shareSuccess": "Condiviso correttamente",
    "shareUnavailable": "Le mappe private non possono essere condivise. Modifica prima la visibilità.",
    "skill": "Abilità",
    "skillCount": "Numero di abilità",
    "skins": "SKIN",
    "skinsTitle": "Negozio skin",
    "slow": "LENTA",
    "sound": "AUDIO",
    "specialLevel": "Livello speciale",
    "specialLevelLocked": "Livello speciale bloccato",
    "standard": "CLASSICO",
    "starMap": "Mappa stellare",
    "starMapClear": "CANCELLA MAPPA",
    "starMapCleared": "La mappa stellare personalizzata corrente è stata cancellata.",
    "starMapCurrentBadge": "MAPPA STELLARE CORRENTE",
    "starMapDefaultName": "Inserisci il nome della mappa",
    "starMapEdit": "MODIFICA MAPPA",
    "starMapEditorHint": "Carica un'immagine per generare automaticamente {count} stelle-livello. Trascinale per perfezionare il percorso, quindi salva la mappa.",
    "starMapEditorTitle": "CREATORE MAPPA STELLARE",
    "starMapGenerated": "Generate {count} stelle-livello. Trascinale per perfezionare la mappa.",
    "starMapImageError": "Impossibile elaborare questa immagine.",
    "starMapLightProgress": "ILLUMINATI {done} / {total}",
    "starMapName": "NOME MAPPA",
    "starMapPreviewInLevels": "APRI SELEZIONE LIVELLI",
    "starMapProcessing": "Analisi dell'immagine...",
    "starMapRegenerate": "RIGENERA STELLE",
    "starMapRevealHint": "Completa i livelli per rivelare gradualmente l'immagine originale.",
    "starMapSaveCurrent": "SALVA COME MAPPA CORRENTE",
    "starMapSaveError": "Impossibile salvare localmente la mappa stellare.",
    "starMapSaved": "Salvata. Ora questa è la mappa corrente per la selezione dei livelli.",
    "starMapUpload": "CARICA IMMAGINE",
    "starMapUploadEmptyHint": "La prima versione usa l'analisi dell'immagine e non richiede IA.",
    "starMapUploadEmptyTitle": "Carica un'immagine per creare una mappa stellare",
    "startError": "Impossibile avviare il gioco.",
    "subtitle": "Spezza il vuoto · Accendi le stelle",
    "systemMapCurrentProgress": "Mappa di sistema · Raggiunto il livello {level}",
    "systemMapDescription": "Campagna ufficiale, livello dopo livello.",
    "systemMapName": "MAPPA DEGLI ECHI",
    "templateLoadFailed": "Impossibile elaborare questa immagine.",
    "title": "Echi della Terra",
    "totalLevels": "{count} livelli",
    "unavailable": "Dati del livello non disponibili",
    "uploadImage": "Carica immagine",
    "uploadMapImage": "Carica un'immagine per creare una mappa",
    "uploadMapImageHint": "La logica di caricamento e generazione della mappa verrà aggiunta in una fase successiva.",
    "victory": "Vittoria",
    "viewCompletedMap": "VEDI MAPPA",
    "viewOriginal": "Vedi originale",
    "workshop": "Laboratorio",
    "workshopComingSoon": "LABORATORIO MAPPE PERSONALIZZATE",
    "workshopDescription": "Uno spazio creativo in cui i giocatori potranno caricare un'immagine e trasformarla in una mappa di mattoni personalizzata.",
    "workshopLevelMap": "Creazione Livello",
    "workshopStarMap": "Creazione Mappa",
    "workshopWorkbench": "Banco di Lavoro",
    "workshopToolSoon": "Editor in arrivo",
    "workshopTools": "Strumenti mappa",
    "worldMap": "Mappa del mondo",
    "zh": "zh",
    "skin.ball_default": "Bianco classico",
    "skin.ball_star": "Stella",
    "skin.ball_football": "Pallone da calcio",
    "skin.ball_flower": "Fiore",
    "skin.ball_planet": "Pianeta",
    "skin.ball_rainbow": "Caramella arcobaleno",
    "skin.ball_diamond": "Diamante",
    "skin.paddle_default": "Classica",
    "skin.paddle_stool": "Sgabello",
    "skin.paddle_grass": "Erba",
    "skin.paddle_rainbow": "Ponte arcobaleno",
    "skin.paddle_carpet": "Tappeto magico",
    "skin.paddle_wave": "Onda",
    "skin.paddle_mecha": "Piattaforma mecha",
    "imageFileRequired": "CARICA IMMAGINE",
    "imageReadFailed": "Impossibile leggere questa immagine.",
    "imageTooLarge": "L'immagine è troppo grande.",
    "imageUnsupported": "Il browser non supporta Canvas.",
    "imageNoRegion": "Nell'immagine non è stata rilevata alcuna area utilizzabile.",
    "mapNotFound": "Dati del livello non disponibili",
    "mapCategoryWorld": "Mappa del mondo",
    "mapCategoryWorldShort": "Mondo",
    "mapCategoryCountry": "Mappa nazionale",
    "mapCategoryCountryShort": "Paese",
    "mapCategoryRegion": "Mappa regionale",
    "mapCategoryRegionShort": "Regione",
    "worldClassicName": "Luoghi simbolo del mondo",
    "worldClassicDesc": "Una mappa di esplorazione composta da celebri luoghi simbolo del mondo.",
    "worldRouteName": "Itinerario intorno al mondo",
    "worldRouteDesc": "Una mappa progettata come itinerario di viaggio intorno al mondo.",
    "worldContinentsName": "Esplorazione dei sette continenti",
    "worldContinentsDesc": "Esplora i sette continenti.",
    "chinaClassicName": "Cina classica",
    "chinaClassicDesc": "Famose città e luoghi simbolo della Cina.",
    "japanClassicName": "Giappone classico",
    "japanClassicDesc": "Luoghi rappresentativi del Giappone.",
    "franceClassicName": "Francia classica",
    "franceClassicDesc": "Città e luoghi simbolo della Francia.",
    "usaClassicName": "Stati Uniti classici",
    "usaClassicDesc": "Luoghi simbolo degli Stati Uniti.",
    "europeClassicName": "Città classiche d’Europa",
    "europeClassicDesc": "Città europee da visitare ed esplorare.",
    "eastAsiaName": "Viaggio in Asia orientale",
    "eastAsiaDesc": "Città e luoghi simbolo dell’Asia orientale.",
    "southEastAsiaName": "Viaggio nel Sud-est asiatico",
    "southEastAsiaDesc": "Un itinerario di viaggio nel Sud-est asiatico.",
    "chinaRegionName": "Regioni della Cina",
    "chinaRegionDesc": "Mappa di esplorazione tematica delle regioni della Cina.",
    "demoWorldDreamName": "Prima tappa intorno al mondo",
    "demoWorldDreamDesc": "Trasforma le città che vuoi visitare in una mappa stellare e illumina il tuo itinerario.",
    "demoJapanNightName": "Viaggio in Giappone",
    "demoJapanNightDesc": "Un percorso giocabile tra Tokyo, Kyoto, Osaka e il Monte Fuji.",
    "demoChinaLandmarksName": "Luoghi classici della Cina",
    "demoChinaLandmarksDesc": "Dalla Grande Muraglia a Xi'an, Shanghai, Chengdu e Guangzhou.",
    "demoFranceTripName": "Percorso romantico in Francia",
    "demoFranceTripDesc": "Parigi, Lione, Nizza e Strasburgo in una mappa da esplorare con calma.",
    "demoSevenContinentsName": "Esplorazione dei sette continenti",
    "demoSevenContinentsDesc": "Parti dall'Asia e trasforma i sette continenti nelle tappe della tua avventura.",
    "demoUsaRoadName": "Viaggio on the road negli USA",
    "demoUsaRoadDesc": "New York, Washington, Chicago, Los Angeles e Grand Canyon fino alla costa occidentale.",
    "demoEuropeCityName": "Tour delle città europee",
    "demoEuropeCityDesc": "Una mappa tra Londra, Parigi, Berlino, Roma, Atene e Barcellona.",
    "scenicForbiddenCity": "Città Proibita",
    "scenicTempleOfHeaven": "Tempio del Cielo",
    "scenicGreatWall": "Grande Muraglia Cinese",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "ru": Object.freeze({
    "allFragmentsCollected": "Все фрагменты собраны",
    "back": "Назад",
    "backHome": "НА ГЛАВНУЮ",
    "backendReady": "Vue-интерфейс готов к работе с сервером",
    "ballSkins": "Скины мяча",
    "ballSpeed": "СКОРОСТЬ МЯЧА",
    "breakableCount": "Разрушаемые блоки: {count}",
    "brickPalette": "Палитра блоков",
    "builtLevels": "Создано {done}/{total}",
    "levelBuilt": "Создано",
    "cancel": "Отмена",
    "cannotReadTemplate": "Не удалось обработать это изображение.",
    "canvasUnsupported": "Ваш браузер не поддерживает Canvas.",
    "challengeMapHint": "Выберите карту для кнопки «ИГРАТЬ» на главной странице. Официальная карта доступна всегда.",
    "challengeMapTitle": "ВЫБРАТЬ КАРТУ",
    "chapter": "Глава",
    "chapterEndFragmentNotice": "фрагментов достопримечательностей.",
    "chapters": "Главы",
    "chooseChallenge": "ИГРАТЬ НА ЭТОЙ КАРТЕ",
    "chooseForPlay": "ИГРАТЬ НА ЭТОЙ КАРТЕ",
    "chooseMapPlay": "Играть на этой карте",
    "clearSearch": "Очистить поиск",
    "cleared": "Пройдено",
    "close": "Закрыть",
    "collectFragment": "Собрать фрагмент",
    "collectedFragments": "Собрано фрагментов: {count}",
    "comingSoon": "СКОРО",
    "completed": "Завершено",
    "confirm": "Подтвердить",
    "confirmDeleteMap": "Удалить «{name}»? Локальные данные уровней также будут удалены.",
    "confirmOverwrite": "Создание из изображения перезапишет текущий холст. Продолжить?",
    "continueGame": "Продолжить",
    "countryCA": "Канада",
    "countryCN": "Китай",
    "countryDE": "Германия",
    "countryFR": "Франция",
    "countryGB": "Великобритания",
    "countryJP": "Япония",
    "countryKR": "Южная Корея",
    "countryUS": "США",
    "createLevel": "Создать уровень",
    "createStarMap": "Создать звёздную карту",
    "createStarMapFirst": "Создайте звёздную карту в Мастерской. До этого кнопка «ИГРАТЬ» будет открывать системную карту.",
    "creativeWorkshop": "МАСТЕРСКАЯ",
    "creativeWorkshopHint": "Пользовательские карты",
    "currentLevel": "ТЕКУЩИЙ УРОВЕНЬ",
    "currentLevelFormat": "Достигнут уровень {level}",
    "currentProgress": "Продолжить с уровня {level}",
    "currentReachedLevel": "Достигнут уровень {level}",
    "custom": "Пользовательский",
    "customMapCompleteSubtitle": "Поздравляем! Вы прошли все уровни карты «{name}».",
    "customMapCompleteTitle": "КАРТА ПРОЙДЕНА!",
    "customMapCurrentProgress": "{name} · Достигнут уровень {level}",
    "customMapDescription": "Ваша пользовательская звёздная карта и созданные вами уровни.",
    "defeat": "Поражение",
    "deleteAction": "Удалить",
    "deleteMap": "Удалить карту",
    "difficulty": "СЛОЖНОСТЬ",
    "dragNodesHint": "Загрузите изображение, чтобы автоматически создать {count} звёзд уровней. Перетаскивайте звёзды для настройки маршрута, затем сохраните карту как текущую карту выбора уровней.",
    "editMap": "Редактировать карту",
    "en": "en",
    "energyFull": "Полная",
    "energyInsufficient": "Недостаточно энергии: {current}/{cost}. Дождитесь восстановления.",
    "energyLabel": "ЭНЕРГИЯ",
    "energyLoading": "ЗАГРУЗКА",
    "enterMap": "Войти на карту",
    "mapViews": "Просмотры",
    "likeMap": "Нравится",
    "unlikeMap": "Убрать отметку «Нравится»",
    "enterSpecialLevel": "Войти в особый уровень",
    "extreme": "ЭКСТРИМ",
    "fast": "БЫСТРО",
    "finalLevel": "Финал",
    "fr": "fr",
    "fragment": "Фрагмент",
    "fragmentCollected": "Фрагмент собран",
    "fragmentProgress": "Прогресс фрагментов",
    "fragments": "Фрагменты",
    "gameHelp": "A/D или ←/→ — движение · Пробел — запуск · R — перезапуск",
    "gameFailedTitle": "Игра проиграна",
    "retryChallenge": "Повторить",
    "generateMap": "Создать карту",
    "goCreateStarMap": "СОЗДАТЬ ЗВЁЗДНУЮ КАРТУ",
    "hard": "СЛОЖНО",
    "highScore": "Рекорд",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Лазерная ракета",
    "leaderboard": "РЕЙТИНГ",
    "leaderboardFullTitle": "Полный рейтинг по странам",
    "leaderboardHint": "Рейтинг",
    "leaderboardPageTitle": "🌍 МИРОВОЙ РЕЙТИНГ СТРАН",
    "leaderboardPlaceholderNote": "Маршрут рейтинга, хранилище и слой API готовы. Текущий макет временный и может быть заменён без изменений интеграции с сервером.",
    "leaderboardScore": "Очки",
    "resultLevelScore": "Очки за уровень",
    "leaderboardCleared": "Прогресс",
    "level": "Уровень",
    "levelCountFormat": "Уровней: {count}",
    "levelCountPoints": "Уровни / Точки",
    "levelLocked": "Уровень заблокирован",
    "levelMap": "Карта уровней",
    "levelPlaysFormat": "Уровней: {count} · игр: {plays}",
    "levelSelect": "ВЫБОР УРОВНЯ",
    "litLevels": "Открыто {done}/{total}",
    "loading": "Загрузка",
    "loadingLevel": "Загрузка уровня...",
    "loadingSharedMap": "Загрузка общей карты…",
    "loadingTemplate": "Анализ изображения...",
    "locked": "Закрыто",
    "mapCompleted": "Карта завершена",
    "mapDescription": "Описание карты",
    "mapDescriptionPlaceholder": "Введите описание карты",
    "mapFullyLit": "Карта полностью освещена",
    "mapName": "НАЗВАНИЕ КАРТЫ",
    "mapPermission": "Доступ к карте",
    "mapTemplate": "Шаблон карты",
    "mapTitle": "Карта Эха",
    "mapUnlocked": "Карта разблокирована",
    "mapUpdatedSaved": "Сохранено. Теперь это текущая карта выбора уровней.",
    "modeGuide": "ОПИСАНИЕ РЕЖИМОВ",
    "modeGuideExtreme": "Экстрим: около 35% от частоты выпадения предметов в классическом режиме",
    "modeGuideHard": "Сложно: около 65% от частоты выпадения предметов в классическом режиме",
    "modeGuideSpeed": "Более быстрый мяч усложняет управление и прохождение",
    "modeGuideStandard": "Классика: исходная частота выпадения предметов",
    "myCustomMaps": "Мои Работы",
    "myStarMap": "Моя звёздная карта",
    "myWorkshop": "Моя мастерская",
    "next": "Далее",
    "noCustomMaps": "Пользовательских карт пока нет",
    "noCustomMapsHint": "Создайте звёздную карту в Мастерской. До этого кнопка «ИГРАТЬ» будет открывать системную карту.",
    "noCustomStarMap": "Пользовательских карт пока нет",
    "noMapDescription": "Описание карты отсутствует",
    "noMatches": "Совпадений нет",
    "normal": "НОРМАЛЬНО",
    "off": "ВЫКЛ",
    "on": "ВКЛ",
    "openStarMapEditor": "РЕДАКТИРОВАТЬ КАРТУ",
    "originalImageTitle": "Исходное изображение",
    "paddleGrow": "Платформа увеличена",
    "paddleSkins": "Скины платформы",
    "play": "ИГРАТЬ",
    "playsLikesFormat": "Игр: {plays} · отметок «Нравится»: {likes}",
    "previewHint": "Загрузите изображение, чтобы создать {count} звёзд уровней. Перетаскивайте их для настройки маршрута, затем сохраните карту.",
    "previous": "Назад",
    "privateMap": "Приватная",
    "publicMap": "Публичная",
    "visibilityChangedPublic": "Видимость изменена на публичную.",
    "visibilityChangedPrivate": "Видимость изменена на приватную.",
    "visibilityChangedShare": "Видимость изменена на доступ только по ссылке.",
    "publicMaps": "Галерея",
    "publicMapsDescription": "Откройте карты, созданные другими игроками",
    "regenerateByCount": "Перестройка Маршрута",
    "regenerateNodesSuccess": "Создано точек: {count}. Перед перетаскиванием нажмите «Перестроить маршрут».",
    "replayLevel": "Пройти уровень снова",
    "restart": "Начать заново",
    "result": "Результат",
    "returnHome": "На главную",
    "runtimeBalls": "МЯЧИ",
    "runtimeHome": "ДОМОЙ",
    "runtimeLives": "ЖИЗНИ",
    "runtimePaused": "ПАУЗА",
    "runtimeResume": "ПРОДОЛЖИТЬ",
    "runtimeTime": "ВРЕМЯ",
    "save": "Сохранить",
    "saveAndBack": "СОХРАНИТЬ И НАЗАД",
    "saveFailed": "Не удалось сохранить",
    "saveMap": "Сохранить карту",
    "saveMapAndCreateLevels": "СОХРАНИТЬ КАК ТЕКУЩУЮ КАРТУ",
    "saveSuccess": "Успешно сохранено",
    "scenicFragment": "Фрагмент достопримечательности",
    "scenicFragmentObtained": "Фрагмент получен! Он сохранится после прохождения уровня.",
    "scenicFragmentProgress": "Фрагменты достопримечательностей {found}/{total}",
    "searchColor": "Поиск цвета или HEX-кода",
    "searchMaps": "Поиск карт",
    "selectMapTemplate": "Шаблон карты",
    "selectTemplateDescription": "Загрузите изображение, чтобы автоматически создать {count} звёзд уровней. Настройте маршрут и сохраните карту.",
    "selectTemplateOrUpload": "ЗАГРУЗИТЬ ИЗОБРАЖЕНИЕ",
    "selected": "Выбрано",
    "selectedChallenge": "ВЫБРАНО",
    "selectedMap": "ВЫБРАНО",
    "settings": "НАСТРОЙКИ",
    "settingsTitle": "НАСТРОЙКИ ИГРЫ",
    "share": "Поделиться",
    "shareAction": "Поделиться",
    "shareFailed": "Не удалось поделиться.",
    "shareInviteText": "Помоги зажечь мою звёздную карту BricksBeater!",
    "shareMap": "Только по ссылке",
    "shareReady": "Ссылка для общего доступа готова.",
    "shareSuccess": "Успешно опубликовано",
    "shareUnavailable": "Приватную карту нельзя поделиться. Сначала измените видимость.",
    "skill": "Навык",
    "skillCount": "Количество навыков",
    "skins": "СКИНЫ",
    "skinsTitle": "Магазин скинов",
    "slow": "МЕДЛЕННО",
    "sound": "ЗВУК",
    "specialLevel": "Особый уровень",
    "specialLevelLocked": "Особый уровень заблокирован",
    "standard": "КЛАССИКА",
    "starMap": "Звёздная карта",
    "starMapClear": "ОЧИСТИТЬ КАРТУ",
    "starMapCleared": "Текущая пользовательская звёздная карта очищена.",
    "starMapCurrentBadge": "ТЕКУЩАЯ ЗВЁЗДНАЯ КАРТА",
    "starMapDefaultName": "Введите название карты",
    "starMapEdit": "РЕДАКТИРОВАТЬ КАРТУ",
    "starMapEditorHint": "Загрузите изображение, чтобы автоматически создать {count} звёзд уровней. Настройте маршрут и сохраните карту.",
    "starMapEditorTitle": "РЕДАКТОР ЗВЁЗДНОЙ КАРТЫ",
    "starMapGenerated": "Создано звёзд уровней: {count}. Перетаскивайте их для настройки карты.",
    "starMapImageError": "Не удалось обработать это изображение.",
    "starMapLightProgress": "ОТКРЫТО {done} / {total}",
    "starMapName": "НАЗВАНИЕ КАРТЫ",
    "starMapPreviewInLevels": "ОТКРЫТЬ ВЫБОР УРОВНЕЙ",
    "starMapProcessing": "Анализ изображения...",
    "starMapRegenerate": "СОЗДАТЬ ЗВЁЗДЫ ЗАНОВО",
    "starMapRevealHint": "Проходите уровни, чтобы постепенно открыть исходное изображение.",
    "starMapSaveCurrent": "СОХРАНИТЬ КАК ТЕКУЩУЮ КАРТУ",
    "starMapSaveError": "Не удалось сохранить звёздную карту локально.",
    "starMapSaved": "Сохранено. Теперь это текущая карта выбора уровней.",
    "starMapUpload": "ЗАГРУЗИТЬ ИЗОБРАЖЕНИЕ",
    "starMapUploadEmptyHint": "Первая версия использует анализ изображения и не требует ИИ.",
    "starMapUploadEmptyTitle": "Загрузите изображение, чтобы создать звёздную карту",
    "startError": "Не удалось начать игру.",
    "subtitle": "Разбей пустоту · Зажги звёзды",
    "systemMapCurrentProgress": "Системная карта · Достигнут уровень {level}",
    "systemMapDescription": "Официальная кампания по порядку уровней.",
    "systemMapName": "КАРТА ЭХА",
    "templateLoadFailed": "Не удалось обработать это изображение.",
    "title": "Эхо Земли",
    "totalLevels": "Уровней: {count}",
    "unavailable": "Данные уровня недоступны",
    "uploadImage": "Загрузить изображение",
    "uploadMapImage": "Загрузите изображение, чтобы создать карту",
    "uploadMapImageHint": "Логика загрузки и генерации карты будет добавлена на следующем этапе разработки.",
    "victory": "Победа",
    "viewCompletedMap": "ОТКРЫТЬ КАРТУ",
    "viewOriginal": "Посмотреть оригинал",
    "workshop": "Мастерская",
    "workshopComingSoon": "МАСТЕРСКАЯ ПОЛЬЗОВАТЕЛЬСКИХ КАРТ",
    "workshopDescription": "Пространство для творчества, где игроки смогут загрузить изображение и превратить его в пользовательскую карту кирпичей.",
    "workshopLevelMap": "Создание Уровня",
    "workshopStarMap": "Создание Карты",
    "workshopWorkbench": "Мастерская",
    "workshopToolSoon": "Редактор скоро появится",
    "workshopTools": "Инструменты карты",
    "worldMap": "Карта мира",
    "zh": "zh",
    "skin.ball_default": "Классический белый",
    "skin.ball_star": "Звезда",
    "skin.ball_football": "Футбольный мяч",
    "skin.ball_flower": "Цветок",
    "skin.ball_planet": "Планета",
    "skin.ball_rainbow": "Радужная конфета",
    "skin.ball_diamond": "Бриллиант",
    "skin.paddle_default": "Классическая",
    "skin.paddle_stool": "Табурет",
    "skin.paddle_grass": "Трава",
    "skin.paddle_rainbow": "Радужный мост",
    "skin.paddle_carpet": "Волшебный ковёр",
    "skin.paddle_wave": "Волна",
    "skin.paddle_mecha": "Меха-платформа",
    "imageFileRequired": "ЗАГРУЗИТЬ ИЗОБРАЖЕНИЕ",
    "imageReadFailed": "Не удалось прочитать изображение.",
    "imageTooLarge": "Изображение слишком большое.",
    "imageUnsupported": "Ваш браузер не поддерживает Canvas.",
    "imageNoRegion": "На изображении не найдено подходящей области.",
    "mapNotFound": "Данные уровня недоступны",
    "mapCategoryWorld": "Карта мира",
    "mapCategoryWorldShort": "Мир",
    "mapCategoryCountry": "Карта страны",
    "mapCategoryCountryShort": "Страна",
    "mapCategoryRegion": "Региональная карта",
    "mapCategoryRegionShort": "Регион",
    "worldClassicName": "Знаменитые места мира",
    "worldClassicDesc": "Карта для исследования известных достопримечательностей со всего мира.",
    "worldRouteName": "Маршрут кругосветного путешествия",
    "worldRouteDesc": "Карта, построенная как маршрут путешествия вокруг света.",
    "worldContinentsName": "Исследование семи континентов",
    "worldContinentsDesc": "Исследуйте семь континентов.",
    "chinaClassicName": "Классический Китай",
    "chinaClassicDesc": "Известные города и достопримечательности Китая.",
    "japanClassicName": "Классическая Япония",
    "japanClassicDesc": "Знаковые места Японии.",
    "franceClassicName": "Классическая Франция",
    "franceClassicDesc": "Города и достопримечательности Франции.",
    "usaClassicName": "Классические США",
    "usaClassicDesc": "Знаковые достопримечательности США.",
    "europeClassicName": "Классические города Европы",
    "europeClassicDesc": "Европейские города для путешествий.",
    "eastAsiaName": "Путешествие по Восточной Азии",
    "eastAsiaDesc": "Города и достопримечательности Восточной Азии.",
    "southEastAsiaName": "Путешествие по Юго-Восточной Азии",
    "southEastAsiaDesc": "Маршрут путешествия по Юго-Восточной Азии.",
    "chinaRegionName": "Регионы Китая",
    "chinaRegionDesc": "Тематическая карта исследования регионов Китая.",
    "demoWorldDreamName": "Первая остановка кругосветного путешествия",
    "demoWorldDreamDesc": "Превратите города мечты в звёздную карту и постепенно зажигайте свой маршрут.",
    "demoJapanNightName": "Путешествие по Японии",
    "demoJapanNightDesc": "Игровой маршрут через Токио, Киото, Осаку и гору Фудзи.",
    "demoChinaLandmarksName": "Классические достопримечательности Китая",
    "demoChinaLandmarksDesc": "От Великой Китайской стены до Сианя, Шанхая, Чэнду и Гуанчжоу.",
    "demoFranceTripName": "Романтический маршрут по Франции",
    "demoFranceTripDesc": "Париж, Лион, Ницца и Страсбург на карте для неспешного путешествия.",
    "demoSevenContinentsName": "Исследование семи континентов",
    "demoSevenContinentsDesc": "Начните с Азии и превратите семь континентов в этапы своего приключения.",
    "demoUsaRoadName": "Автопутешествие по США",
    "demoUsaRoadDesc": "Нью-Йорк, Вашингтон, Чикаго, Лос-Анджелес и Гранд-Каньон до западного побережья.",
    "demoEuropeCityName": "Путешествие по городам Европы",
    "demoEuropeCityDesc": "Карта городов: Лондон, Париж, Берлин, Рим, Афины и Барселона.",
    "scenicForbiddenCity": "Запретный город",
    "scenicTempleOfHeaven": "Храм Неба",
    "scenicGreatWall": "Великая Китайская стена",
    "scenicZhangjiajie": "Чжанцзяцзе"
  }),
  "ar": Object.freeze({
    "allFragmentsCollected": "تم جمع جميع الشظايا",
    "back": "رجوع",
    "backHome": "العودة للرئيسية",
    "backendReady": "واجهة Vue جاهزة للاتصال بالخادم",
    "ballSkins": "مظاهر الكرة",
    "ballSpeed": "سرعة الكرة",
    "breakableCount": "الطوب القابل للكسر: {count}",
    "brickPalette": "لوحة الطوب",
    "builtLevels": "تم إنشاء {done}/{total}",
    "levelBuilt": "تم الإنشاء",
    "cancel": "إلغاء",
    "cannotReadTemplate": "تعذرت معالجة هذه الصورة.",
    "canvasUnsupported": "متصفحك لا يدعم Canvas.",
    "challengeMapHint": "اختر الخريطة التي يستخدمها زر «العب» في الصفحة الرئيسية. الخريطة الرسمية متاحة دائمًا.",
    "challengeMapTitle": "اختيار الخريطة",
    "chapter": "الفصل",
    "chapterEndFragmentNotice": "شظايا المعالم.",
    "chapters": "الفصول",
    "chooseChallenge": "استخدم هذه الخريطة",
    "chooseForPlay": "استخدم هذه الخريطة",
    "chooseMapPlay": "العب على هذه الخريطة",
    "clearSearch": "مسح البحث",
    "cleared": "مكتمل",
    "close": "إغلاق",
    "collectFragment": "جمع الشظية",
    "collectedFragments": "تم جمع {count} شظية",
    "comingSoon": "قريبًا",
    "completed": "مكتمل",
    "confirm": "تأكيد",
    "confirmDeleteMap": "هل تريد حذف «{name}»؟ سيتم حذف بيانات المراحل المحلية أيضًا.",
    "confirmOverwrite": "سيؤدي الإنشاء من صورة إلى استبدال اللوحة الحالية. هل تريد المتابعة؟",
    "continueGame": "متابعة",
    "countryCA": "كندا",
    "countryCN": "الصين",
    "countryDE": "ألمانيا",
    "countryFR": "فرنسا",
    "countryGB": "المملكة المتحدة",
    "countryJP": "اليابان",
    "countryKR": "كوريا الجنوبية",
    "countryUS": "الولايات المتحدة",
    "createLevel": "إنشاء مرحلة",
    "createStarMap": "إنشاء خريطة النجوم",
    "createStarMapFirst": "أنشئ خريطة نجوم في ورشة الإبداع. وحتى ذلك الحين سيواصل زر «العب» استخدام خريطة النظام.",
    "creativeWorkshop": "ورشة الإبداع",
    "creativeWorkshopHint": "خرائط مخصصة",
    "currentLevel": "المستوى الحالي",
    "currentLevelFormat": "وصلت إلى المستوى {level}",
    "currentProgress": "المتابعة من المستوى {level}",
    "currentReachedLevel": "وصلت إلى المستوى {level}",
    "custom": "مخصص",
    "customMapCompleteSubtitle": "تهانينا! لقد أضأت جميع مراحل {name}.",
    "customMapCompleteTitle": "اكتملت الخريطة!",
    "customMapCurrentProgress": "{name} · وصلت إلى المرحلة {level}",
    "customMapDescription": "خريطة النجوم المخصصة والمراحل التي أنشأتها.",
    "defeat": "هزيمة",
    "deleteAction": "حذف",
    "deleteMap": "حذف الخريطة",
    "difficulty": "الصعوبة",
    "dragNodesHint": "ارفع صورة لإنشاء {count} نجمة مراحل تلقائيًا. اسحب النجوم لضبط المسار ثم احفظها كخريطة اختيار المراحل الحالية.",
    "editMap": "تعديل الخريطة",
    "en": "en",
    "energyFull": "ممتلئة",
    "energyInsufficient": "الطاقة غير كافية: {current}/{cost}. انتظر الاستعادة.",
    "energyLabel": "الطاقة",
    "energyLoading": "جارٍ التحميل",
    "enterMap": "دخول الخريطة",
    "mapViews": "المشاهدات",
    "likeMap": "إعجاب",
    "unlikeMap": "إلغاء الإعجاب",
    "enterSpecialLevel": "دخول المرحلة الخاصة",
    "extreme": "متطرف",
    "fast": "سريعة",
    "finalLevel": "النهاية",
    "fr": "fr",
    "fragment": "جزء",
    "fragmentCollected": "تم جمع الشظية",
    "fragmentProgress": "تقدم الشظايا",
    "fragments": "الشظايا",
    "gameHelp": "A/D أو ←/→ للحركة · Space للإطلاق · R لإعادة البدء",
    "gameFailedTitle": "فشلت اللعبة",
    "retryChallenge": "إعادة المحاولة",
    "generateMap": "إنشاء الخريطة",
    "goCreateStarMap": "إنشاء خريطة نجوم",
    "hard": "صعب",
    "highScore": "أعلى نتيجة",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "صاروخ ليزري",
    "leaderboard": "التصنيف",
    "leaderboardFullTitle": "الترتيب الكامل حسب الدول",
    "leaderboardHint": "الترتيب",
    "leaderboardPageTitle": "🌍 الترتيب العالمي للدول",
    "leaderboardPlaceholderNote": "مسار لوحة الصدارة والمخزن وطبقة API جاهزة. التخطيط الحالي مؤقت ويمكن استبداله دون تغيير تكامل الخادم.",
    "leaderboardScore": "النقاط",
    "resultLevelScore": "نقاط المرحلة",
    "leaderboardCleared": "التقدم",
    "level": "المستوى",
    "levelCountFormat": "{count} مرحلة",
    "levelCountPoints": "المراحل / النقاط",
    "levelLocked": "المرحلة مقفلة",
    "levelMap": "خريطة المراحل",
    "levelPlaysFormat": "{count} مرحلة · {plays} مرة لعب",
    "levelSelect": "اختيار المستوى",
    "litLevels": "تمت إضاءة {done}/{total}",
    "loading": "جارٍ التحميل",
    "loadingLevel": "جارٍ تحميل المستوى...",
    "loadingSharedMap": "جارٍ تحميل الخريطة المشتركة…",
    "loadingTemplate": "جارٍ تحليل الصورة...",
    "locked": "مغلق",
    "mapCompleted": "اكتملت الخريطة",
    "mapDescription": "وصف الخريطة",
    "mapDescriptionPlaceholder": "أدخل وصف الخريطة",
    "mapFullyLit": "تمت إضاءة الخريطة بالكامل",
    "mapName": "اسم الخريطة",
    "mapPermission": "صلاحية الخريطة",
    "mapTemplate": "قالب الخريطة",
    "mapTitle": "خريطة الأصداء",
    "mapUnlocked": "تم فتح الخريطة",
    "mapUpdatedSaved": "تم الحفظ. هذه الآن خريطة اختيار المراحل الحالية.",
    "modeGuide": "دليل الأوضاع",
    "modeGuideExtreme": "متطرف: نحو 35% من معدل سقوط العناصر في الوضع الكلاسيكي",
    "modeGuideHard": "صعب: نحو 65% من معدل سقوط العناصر في الوضع الكلاسيكي",
    "modeGuideSpeed": "زيادة سرعة الكرة تجعل التحكم وإنهاء المرحلة أكثر صعوبة",
    "modeGuideStandard": "كلاسيكي: معدل سقوط العناصر الأصلي",
    "myCustomMaps": "أعمالي",
    "myStarMap": "خريطة النجوم الخاصة بي",
    "myWorkshop": "ورشة عملي",
    "next": "التالي",
    "noCustomMaps": "لا توجد خرائط مخصصة بعد",
    "noCustomMapsHint": "أنشئ خريطة نجوم في ورشة الإبداع. وحتى ذلك الحين سيواصل زر «العب» استخدام خريطة النظام.",
    "noCustomStarMap": "لا توجد خرائط مخصصة بعد",
    "noMapDescription": "لا يوجد وصف للخريطة",
    "noMatches": "لا توجد نتائج",
    "normal": "عادية",
    "off": "إيقاف",
    "on": "تشغيل",
    "openStarMapEditor": "تعديل الخريطة",
    "originalImageTitle": "الصورة الأصلية",
    "paddleGrow": "تم تكبير المضرب",
    "paddleSkins": "مظاهر المضرب",
    "play": "العب",
    "playsLikesFormat": "{plays} مرة لعب · {likes} إعجاب",
    "previewHint": "ارفع صورة لإنشاء {count} نجمة مراحل تلقائيًا، ثم اسحبها لضبط المسار واحفظ الخريطة.",
    "previous": "السابق",
    "privateMap": "خاص",
    "publicMap": "عام",
    "visibilityChangedPublic": "تم التبديل إلى العرض العام.",
    "visibilityChangedPrivate": "تم التبديل إلى الوضع الخاص.",
    "visibilityChangedShare": "تم التبديل إلى العرض عبر رابط المشاركة فقط.",
    "publicMaps": "المعرض",
    "publicMapsDescription": "اكتشف خرائط أنشأها لاعبون آخرون",
    "regenerateByCount": "إعادة بناء المسار",
    "regenerateNodesSuccess": "تم إنشاء {count} نقطة. اضغط إعادة بناء المسار قبل سحبها.",
    "replayLevel": "إعادة المرحلة",
    "restart": "إعادة التشغيل",
    "result": "النتيجة",
    "returnHome": "العودة للرئيسية",
    "runtimeBalls": "الكرات",
    "runtimeHome": "الرئيسية",
    "runtimeLives": "الأرواح",
    "runtimePaused": "إيقاف مؤقت",
    "runtimeResume": "متابعة",
    "runtimeTime": "الوقت",
    "save": "حفظ",
    "saveAndBack": "حفظ والعودة",
    "saveFailed": "فشل الحفظ",
    "saveMap": "حفظ الخريطة",
    "saveMapAndCreateLevels": "حفظ كخريطة حالية",
    "saveSuccess": "تم الحفظ بنجاح",
    "scenicFragment": "جزء من المعلم",
    "scenicFragmentObtained": "تم الحصول على جزء من المعلم! سيُحفظ بعد إنهاء المرحلة.",
    "scenicFragmentProgress": "أجزاء المعالم {found}/{total}",
    "searchColor": "ابحث عن لون أو قيمة HEX",
    "searchMaps": "البحث في الخرائط",
    "selectMapTemplate": "قالب الخريطة",
    "selectTemplateDescription": "ارفع صورة لإنشاء {count} نجمة مراحل تلقائيًا. اضبط المسار ثم احفظ الخريطة.",
    "selectTemplateOrUpload": "رفع صورة",
    "selected": "محدد",
    "selectedChallenge": "محدد",
    "selectedMap": "محدد",
    "settings": "الإعدادات",
    "settingsTitle": "إعدادات اللعبة",
    "share": "مشاركة",
    "shareAction": "مشاركة",
    "shareFailed": "فشلت المشاركة.",
    "shareInviteText": "ساعدني في إضاءة خريطة النجوم الخاصة بي في BricksBeater!",
    "shareMap": "بالرابط فقط",
    "shareReady": "رابط المشاركة جاهز.",
    "shareSuccess": "تمت المشاركة بنجاح",
    "shareUnavailable": "لا يمكن مشاركة الخريطة الخاصة. غيّر مستوى الظهور أولًا.",
    "skill": "مهارة",
    "skillCount": "عدد المهارات",
    "skins": "المظاهر",
    "skinsTitle": "متجر المظاهر",
    "slow": "بطيئة",
    "sound": "الصوت",
    "specialLevel": "مرحلة خاصة",
    "specialLevelLocked": "المستوى الخاص مقفل",
    "standard": "كلاسيكي",
    "starMap": "خريطة النجوم",
    "starMapClear": "مسح الخريطة",
    "starMapCleared": "تم مسح خريطة النجوم المخصصة الحالية.",
    "starMapCurrentBadge": "خريطة النجوم الحالية",
    "starMapDefaultName": "أدخل اسم الخريطة",
    "starMapEdit": "تعديل الخريطة",
    "starMapEditorHint": "ارفع صورة لإنشاء {count} نجمة مراحل تلقائيًا. اضبط المسار ثم احفظ الخريطة.",
    "starMapEditorTitle": "منشئ خريطة النجوم",
    "starMapGenerated": "تم إنشاء {count} نجمة مراحل. اسحبها لضبط الخريطة.",
    "starMapImageError": "تعذرت معالجة هذه الصورة.",
    "starMapLightProgress": "مضاء {done} / {total}",
    "starMapName": "اسم الخريطة",
    "starMapPreviewInLevels": "فتح اختيار المراحل",
    "starMapProcessing": "جارٍ تحليل الصورة...",
    "starMapRegenerate": "إعادة إنشاء النجوم",
    "starMapRevealHint": "أنه المراحل لكشف الصورة الأصلية تدريجيًا.",
    "starMapSaveCurrent": "حفظ كخريطة حالية",
    "starMapSaveError": "تعذر حفظ خريطة النجوم محليًا.",
    "starMapSaved": "تم الحفظ. هذه الآن خريطة اختيار المراحل الحالية.",
    "starMapUpload": "رفع صورة",
    "starMapUploadEmptyHint": "الإصدار الأول يستخدم تحليل الصورة ولا يحتاج إلى ذكاء اصطناعي.",
    "starMapUploadEmptyTitle": "ارفع صورة لإنشاء خريطة نجوم",
    "startError": "تعذر بدء اللعبة.",
    "subtitle": "حطّم الفراغ · أضئ النجوم",
    "systemMapCurrentProgress": "خريطة النظام · وصلت إلى المرحلة {level}",
    "systemMapDescription": "الحملة الرسمية مستوى بعد مستوى.",
    "systemMapName": "خريطة الأصداء",
    "templateLoadFailed": "تعذرت معالجة هذه الصورة.",
    "title": "أصداء الأرض",
    "totalLevels": "{count} مستوى",
    "unavailable": "بيانات المستوى غير متاحة",
    "uploadImage": "رفع صورة",
    "uploadMapImage": "ارفع صورة لإنشاء خريطة",
    "uploadMapImageHint": "ستُضاف آلية الرفع وإنشاء الخريطة في مرحلة تطوير لاحقة.",
    "victory": "فوز",
    "viewCompletedMap": "عرض الخريطة",
    "viewOriginal": "عرض الأصل",
    "workshop": "ورشة الإبداع",
    "workshopComingSoon": "ورشة الخرائط المخصصة",
    "workshopDescription": "مساحة إبداعية تتيح للاعبين رفع صورة وتحويلها إلى خريطة طوب مخصصة.",
    "workshopLevelMap": "تصميم المرحلة",
    "workshopStarMap": "تصميم الخريطة",
    "workshopWorkbench": "منصة العمل",
    "workshopToolSoon": "المحرر قادم قريبًا",
    "workshopTools": "أدوات الخريطة",
    "worldMap": "خريطة العالم",
    "zh": "zh",
    "skin.ball_default": "أبيض كلاسيكي",
    "skin.ball_star": "نجمة",
    "skin.ball_football": "كرة قدم",
    "skin.ball_flower": "زهرة",
    "skin.ball_planet": "كوكب",
    "skin.ball_rainbow": "حلوى قوس قزح",
    "skin.ball_diamond": "ألماس",
    "skin.paddle_default": "كلاسيكي",
    "skin.paddle_stool": "مقعد",
    "skin.paddle_grass": "عشب",
    "skin.paddle_rainbow": "جسر قوس قزح",
    "skin.paddle_carpet": "بساط سحري",
    "skin.paddle_wave": "موجة",
    "skin.paddle_mecha": "منصة ميكا",
    "imageFileRequired": "رفع صورة",
    "imageReadFailed": "تعذرت قراءة الصورة.",
    "imageTooLarge": "حجم الصورة كبير جدًا.",
    "imageUnsupported": "متصفحك لا يدعم Canvas.",
    "imageNoRegion": "لم يتم العثور على منطقة صالحة في الصورة.",
    "mapNotFound": "بيانات المستوى غير متاحة",
    "mapCategoryWorld": "خريطة العالم",
    "mapCategoryWorldShort": "العالم",
    "mapCategoryCountry": "خريطة دولة",
    "mapCategoryCountryShort": "دولة",
    "mapCategoryRegion": "خريطة إقليم",
    "mapCategoryRegionShort": "إقليم",
    "worldClassicName": "معالم العالم الكلاسيكية",
    "worldClassicDesc": "خريطة استكشاف تضم معالم شهيرة من أنحاء العالم.",
    "worldRouteName": "مسار رحلة حول العالم",
    "worldRouteDesc": "خريطة مصممة كمسار سفر حول العالم.",
    "worldContinentsName": "استكشاف القارات السبع",
    "worldContinentsDesc": "استكشف القارات السبع.",
    "chinaClassicName": "الصين الكلاسيكية",
    "chinaClassicDesc": "مدن ومعالم صينية شهيرة.",
    "japanClassicName": "اليابان الكلاسيكية",
    "japanClassicDesc": "أماكن بارزة في اليابان.",
    "franceClassicName": "فرنسا الكلاسيكية",
    "franceClassicDesc": "مدن ومعالم فرنسية شهيرة.",
    "usaClassicName": "الولايات المتحدة الكلاسيكية",
    "usaClassicDesc": "معالم بارزة في الولايات المتحدة.",
    "europeClassicName": "مدن أوروبا الكلاسيكية",
    "europeClassicDesc": "مدن أوروبية للسفر والاستكشاف.",
    "eastAsiaName": "رحلة شرق آسيا",
    "eastAsiaDesc": "مدن ومعالم شرق آسيا.",
    "southEastAsiaName": "رحلة جنوب شرق آسيا",
    "southEastAsiaDesc": "مسار سفر عبر جنوب شرق آسيا.",
    "chinaRegionName": "مناطق الصين",
    "chinaRegionDesc": "خريطة استكشاف لمناطق الصين.",
    "demoWorldDreamName": "المحطة الأولى حول العالم",
    "demoWorldDreamDesc": "حوّل المدن التي تحلم بزيارتها إلى خريطة نجوم وأضئ قائمة رحلاتك مرحلة بعد مرحلة.",
    "demoJapanNightName": "خطة رحلة إلى اليابان",
    "demoJapanNightDesc": "مسار قابل للعب عبر طوكيو وكيوتو وأوساكا وجبل فوجي.",
    "demoChinaLandmarksName": "معالم الصين الكلاسيكية",
    "demoChinaLandmarksDesc": "من سور الصين العظيم إلى شيآن وشنغهاي وتشنغدو وقوانغتشو.",
    "demoFranceTripName": "المسار الرومانسي في فرنسا",
    "demoFranceTripDesc": "باريس وليون ونيس وستراسبورغ في خريطة للاستكشاف بهدوء.",
    "demoSevenContinentsName": "استكشاف القارات السبع",
    "demoSevenContinentsDesc": "ابدأ من آسيا واجعل القارات السبع مراحل في مغامرتك.",
    "demoUsaRoadName": "رحلة برية عبر الولايات المتحدة",
    "demoUsaRoadDesc": "نيويورك وواشنطن وشيكاغو ولوس أنجلوس وغراند كانيون حتى الساحل الغربي.",
    "demoEuropeCityName": "جولة في مدن أوروبا",
    "demoEuropeCityDesc": "خريطة مدن تشمل لندن وباريس وبرلين وروما وأثينا وبرشلونة.",
    "scenicForbiddenCity": "المدينة المحرمة",
    "scenicTempleOfHeaven": "معبد السماء",
    "scenicGreatWall": "سور الصين العظيم",
    "scenicZhangjiajie": "تشانغجياجيه"
  }),
  "hi": Object.freeze({
    "allFragmentsCollected": "सभी टुकड़े एकत्र हो गए",
    "back": "वापस",
    "backHome": "होम पर जाएँ",
    "backendReady": "बैकएंड के लिए तैयार Vue फ्रंटएंड",
    "ballSkins": "गेंद की स्किन",
    "ballSpeed": "गेंद की गति",
    "breakableCount": "तोड़ने योग्य ईंटें: {count}",
    "brickPalette": "ईंट पैलेट",
    "builtLevels": "{done}/{total} बनाए गए",
    "levelBuilt": "बनाया गया",
    "cancel": "रद्द करें",
    "cannotReadTemplate": "इस चित्र को संसाधित नहीं किया जा सका।",
    "canvasUnsupported": "आपका ब्राउज़र Canvas का समर्थन नहीं करता।",
    "challengeMapHint": "होम के 'खेलें' बटन के लिए मानचित्र चुनें। आधिकारिक मानचित्र हमेशा उपलब्ध रहता है।",
    "challengeMapTitle": "मैप चुनें",
    "chapter": "अध्याय",
    "chapterEndFragmentNotice": "दर्शनीय स्थल के टुकड़े।",
    "chapters": "अध्याय",
    "chooseChallenge": "यह मैप चुनें",
    "chooseForPlay": "यह मैप चुनें",
    "chooseMapPlay": "इस मानचित्र पर खेलें",
    "clearSearch": "खोज साफ़ करें",
    "cleared": "पूरा",
    "close": "बंद करें",
    "collectFragment": "टुकड़ा एकत्र करें",
    "collectedFragments": "{count} टुकड़े एकत्र किए",
    "comingSoon": "जल्द आ रहा है",
    "completed": "पूरा",
    "confirm": "पुष्टि करें",
    "confirmDeleteMap": "“{name}” हटाएँ? स्थानीय स्तर डेटा भी हट जाएगा।",
    "confirmOverwrite": "चित्र से बनाने पर वर्तमान कैनवास बदल जाएगा। जारी रखें?",
    "continueGame": "जारी रखें",
    "countryCA": "कनाडा",
    "countryCN": "चीन",
    "countryDE": "जर्मनी",
    "countryFR": "फ्रांस",
    "countryGB": "यूनाइटेड किंगडम",
    "countryJP": "जापान",
    "countryKR": "दक्षिण कोरिया",
    "countryUS": "संयुक्त राज्य अमेरिका",
    "createLevel": "स्तर बनाएँ",
    "createStarMap": "स्टार मैप बनाएँ",
    "createStarMapFirst": "वर्कशॉप में स्टार मैप बनाएँ। तब तक 'खेलें' बटन सिस्टम मैप का उपयोग करेगा।",
    "creativeWorkshop": "वर्कशॉप",
    "creativeWorkshopHint": "कस्टम मानचित्र",
    "currentLevel": "वर्तमान लेवल",
    "currentLevelFormat": "लेवल {level} तक पहुँचे",
    "currentProgress": "लेवल {level} से जारी रखें",
    "currentReachedLevel": "लेवल {level} तक पहुँचे",
    "custom": "कस्टम",
    "customMapCompleteSubtitle": "बधाई! आपने {name} के सभी स्तर रोशन कर दिए।",
    "customMapCompleteTitle": "मानचित्र पूरा हुआ!",
    "customMapCurrentProgress": "{name} · स्तर {level} तक पहुँचे",
    "customMapDescription": "आपका कस्टम स्टार मैप और आपके बनाए स्तर।",
    "defeat": "हार",
    "deleteAction": "हटाएँ",
    "deleteMap": "मानचित्र हटाएँ",
    "difficulty": "कठिनाई",
    "dragNodesHint": "{count} स्तर-तारे अपने आप बनाने के लिए चित्र अपलोड करें। रास्ता ठीक करने के लिए तारों को खींचें, फिर इसे मौजूदा स्तर-चयन मानचित्र के रूप में सहेजें।",
    "editMap": "मानचित्र संपादित करें",
    "en": "en",
    "energyFull": "पूरा",
    "energyInsufficient": "ऊर्जा कम है: {current}/{cost}. पुनर्प्राप्ति की प्रतीक्षा करें।",
    "energyLabel": "ऊर्जा",
    "energyLoading": "लोड हो रहा है",
    "enterMap": "मैप में जाएँ",
    "mapViews": "व्यूज़",
    "likeMap": "पसंद करें",
    "unlikeMap": "पसंद हटाएँ",
    "enterSpecialLevel": "विशेष स्तर में जाएँ",
    "extreme": "अत्यंत कठिन",
    "fast": "तेज़",
    "finalLevel": "अंतिम",
    "fr": "fr",
    "fragment": "टुकड़ा",
    "fragmentCollected": "टुकड़ा एकत्र हुआ",
    "fragmentProgress": "टुकड़ों की प्रगति",
    "fragments": "टुकड़े",
    "gameHelp": "A/D या ←/→ से चलें · Space से लॉन्च करें · R से रीस्टार्ट करें",
    "gameFailedTitle": "गेम विफल",
    "retryChallenge": "फिर से प्रयास करें",
    "generateMap": "मानचित्र बनाएँ",
    "goCreateStarMap": "स्टार मैप बनाएँ",
    "hard": "कठिन",
    "highScore": "सर्वोच्च स्कोर",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "लेज़र मिसाइल",
    "leaderboard": "लीडरबोर्ड",
    "leaderboardFullTitle": "पूर्ण राष्ट्रीय लीडरबोर्ड",
    "leaderboardHint": "रैंकिंग",
    "leaderboardPageTitle": "🌍 वैश्विक देश रैंकिंग",
    "leaderboardPlaceholderNote": "लीडरबोर्ड रूट, स्टोर और API परत तैयार हैं। मौजूदा लेआउट अस्थायी है और बैकएंड एकीकरण बदले बिना बदला जा सकता है।",
    "leaderboardScore": "स्कोर",
    "resultLevelScore": "स्तर स्कोर",
    "leaderboardCleared": "प्रगति",
    "level": "लेवल",
    "levelCountFormat": "{count} स्तर",
    "levelCountPoints": "स्तर / बिंदु",
    "levelLocked": "स्तर लॉक है",
    "levelMap": "स्तर मानचित्र",
    "levelPlaysFormat": "{count} स्तर · {plays} बार खेला गया",
    "levelSelect": "लेवल चुनें",
    "litLevels": "{done}/{total} रोशन",
    "loading": "लोड हो रहा है",
    "loadingLevel": "लेवल लोड हो रहा है...",
    "loadingSharedMap": "साझा मानचित्र लोड हो रहा है…",
    "loadingTemplate": "चित्र का विश्लेषण हो रहा है...",
    "locked": "लॉक",
    "mapCompleted": "मानचित्र पूरा हुआ",
    "mapDescription": "मानचित्र विवरण",
    "mapDescriptionPlaceholder": "मानचित्र का विवरण दर्ज करें",
    "mapFullyLit": "पूरा मैप रोशन",
    "mapName": "मानचित्र का नाम",
    "mapPermission": "मानचित्र अनुमति",
    "mapTemplate": "मानचित्र टेम्पलेट",
    "mapTitle": "प्रतिध्वनि मानचित्र",
    "mapUnlocked": "मानचित्र अनलॉक हुआ",
    "mapUpdatedSaved": "सहेजा गया। अब यही मौजूदा स्तर-चयन मानचित्र है।",
    "modeGuide": "मोड गाइड",
    "modeGuideExtreme": "एक्सट्रीम: क्लासिक की तुलना में लगभग 35% आइटम ड्रॉप दर",
    "modeGuideHard": "हार्ड: क्लासिक की तुलना में लगभग 65% आइटम ड्रॉप दर",
    "modeGuideSpeed": "तेज़ गेंद से नियंत्रण और स्तर पूरा करना कठिन होता है",
    "modeGuideStandard": "क्लासिक: मूल आइटम ड्रॉप दर",
    "myCustomMaps": "मेरी रचनाएँ",
    "myStarMap": "मेरा स्टार मैप",
    "myWorkshop": "मेरी कार्यशाला",
    "next": "अगला",
    "noCustomMaps": "अभी कोई कस्टम मानचित्र नहीं",
    "noCustomMapsHint": "वर्कशॉप में स्टार मैप बनाएँ। तब तक 'खेलें' बटन सिस्टम मैप का उपयोग करेगा।",
    "noCustomStarMap": "अभी कोई कस्टम मानचित्र नहीं",
    "noMapDescription": "मानचित्र का कोई विवरण नहीं",
    "noMatches": "कोई परिणाम नहीं",
    "normal": "सामान्य",
    "off": "बंद",
    "on": "चालू",
    "openStarMapEditor": "मानचित्र संपादित करें",
    "originalImageTitle": "मूल चित्र",
    "paddleGrow": "पैडल बड़ा हुआ",
    "paddleSkins": "पैडल स्किन",
    "play": "खेलें",
    "playsLikesFormat": "{plays} बार खेला · {likes} पसंद",
    "previewHint": "{count} स्तर-तारे बनाने के लिए चित्र अपलोड करें। रास्ता ठीक करने के लिए उन्हें खींचें, फिर मानचित्र सहेजें।",
    "previous": "पिछला",
    "privateMap": "निजी",
    "publicMap": "सार्वजनिक",
    "visibilityChangedPublic": "दृश्यता सार्वजनिक कर दी गई है।",
    "visibilityChangedPrivate": "दृश्यता निजी कर दी गई है।",
    "visibilityChangedShare": "दृश्यता केवल साझा लिंक कर दी गई है।",
    "publicMaps": "गैलरी",
    "publicMapsDescription": "अन्य खिलाड़ियों द्वारा बनाए गए मानचित्र खोजें",
    "regenerateByCount": "पथ पुनर्निर्माण",
    "regenerateNodesSuccess": "{count} स्तर बिंदु बने। खींचने से पहले पथ पुनर्निर्माण दबाएँ।",
    "replayLevel": "स्तर फिर खेलें",
    "restart": "फिर शुरू करें",
    "result": "परिणाम",
    "returnHome": "होम पर लौटें",
    "runtimeBalls": "गेंदें",
    "runtimeHome": "होम",
    "runtimeLives": "जीवन",
    "runtimePaused": "विराम",
    "runtimeResume": "जारी रखें",
    "runtimeTime": "समय",
    "save": "सहेजें",
    "saveAndBack": "सेव करें और वापस जाएँ",
    "saveFailed": "सहेजना विफल",
    "saveMap": "मानचित्र सहेजें",
    "saveMapAndCreateLevels": "मौजूदा मानचित्र के रूप में सहेजें",
    "saveSuccess": "सफलतापूर्वक सहेजा गया",
    "scenicFragment": "स्थल का टुकड़ा",
    "scenicFragmentObtained": "स्थल का टुकड़ा मिला! लेवल पूरा करने के बाद सहेजा जाएगा।",
    "scenicFragmentProgress": "स्थल के टुकड़े {found}/{total}",
    "searchColor": "रंग या HEX मान खोजें",
    "searchMaps": "मानचित्र खोजें",
    "selectMapTemplate": "मानचित्र टेम्पलेट",
    "selectTemplateDescription": "{count} स्तर-तारे अपने आप बनाने के लिए चित्र अपलोड करें। रास्ता ठीक करें और मानचित्र सहेजें।",
    "selectTemplateOrUpload": "चित्र अपलोड करें",
    "selected": "चयनित",
    "selectedChallenge": "चुना गया",
    "selectedMap": "चुना गया",
    "settings": "सेटिंग्स",
    "settingsTitle": "गेम सेटिंग्स",
    "share": "साझा करें",
    "shareAction": "साझा करें",
    "shareFailed": "साझा करना विफल रहा।",
    "shareInviteText": "मेरे BricksBeater स्टार मैप को रोशन करने में मदद करें!",
    "shareMap": "केवल लिंक से",
    "shareReady": "साझा लिंक तैयार है।",
    "shareSuccess": "सफलतापूर्वक साझा किया गया",
    "shareUnavailable": "निजी मानचित्र साझा नहीं किए जा सकते। पहले दृश्यता बदलें।",
    "skill": "कौशल",
    "skillCount": "कौशल संख्या",
    "skins": "स्किन",
    "skinsTitle": "स्किन शॉप",
    "slow": "धीमी",
    "sound": "ध्वनि",
    "specialLevel": "विशेष स्तर",
    "specialLevelLocked": "विशेष स्तर लॉक है",
    "standard": "क्लासिक",
    "starMap": "स्टार मैप",
    "starMapClear": "मानचित्र साफ करें",
    "starMapCleared": "मौजूदा कस्टम स्टार मैप साफ कर दिया गया।",
    "starMapCurrentBadge": "मौजूदा स्टार मैप",
    "starMapDefaultName": "मानचित्र का नाम दर्ज करें",
    "starMapEdit": "मानचित्र संपादित करें",
    "starMapEditorHint": "{count} स्तर-तारे अपने आप बनाने के लिए चित्र अपलोड करें। रास्ता ठीक करें और मानचित्र सहेजें।",
    "starMapEditorTitle": "स्टार मैप निर्माता",
    "starMapGenerated": "{count} स्तर-तारे बनाए गए। मानचित्र ठीक करने के लिए उन्हें खींचें।",
    "starMapImageError": "इस चित्र को संसाधित नहीं किया जा सका।",
    "starMapLightProgress": "रोशन {done} / {total}",
    "starMapName": "मानचित्र का नाम",
    "starMapPreviewInLevels": "स्तर चयन खोलें",
    "starMapProcessing": "चित्र का विश्लेषण हो रहा है...",
    "starMapRegenerate": "तारे फिर बनाएँ",
    "starMapRevealHint": "मूल चित्र को धीरे-धीरे दिखाने के लिए स्तर पूरे करें।",
    "starMapSaveCurrent": "मौजूदा मानचित्र के रूप में सहेजें",
    "starMapSaveError": "स्टार मैप स्थानीय रूप से सहेजा नहीं जा सका।",
    "starMapSaved": "सहेजा गया। अब यही मौजूदा स्तर-चयन मानचित्र है।",
    "starMapUpload": "चित्र अपलोड करें",
    "starMapUploadEmptyHint": "पहला संस्करण चित्र विश्लेषण का उपयोग करता है और AI की आवश्यकता नहीं है।",
    "starMapUploadEmptyTitle": "स्टार मैप बनाने के लिए चित्र अपलोड करें",
    "startError": "गेम शुरू नहीं हो सका।",
    "subtitle": "शून्य को तोड़ो · तारों को रोशन करो",
    "systemMapCurrentProgress": "सिस्टम मैप · स्तर {level} तक पहुँचे",
    "systemMapDescription": "आधिकारिक अभियान, एक-एक लेवल करके।",
    "systemMapName": "प्रतिध्वनि मानचित्र",
    "templateLoadFailed": "इस चित्र को संसाधित नहीं किया जा सका।",
    "title": "पृथ्वी की गूंज",
    "totalLevels": "{count} लेवल",
    "unavailable": "लेवल डेटा उपलब्ध नहीं",
    "uploadImage": "चित्र अपलोड करें",
    "uploadMapImage": "मानचित्र बनाने के लिए चित्र अपलोड करें",
    "uploadMapImageHint": "अपलोड और मानचित्र निर्माण की सुविधा बाद के विकास चरण में जोड़ी जाएगी।",
    "victory": "जीत",
    "viewCompletedMap": "मानचित्र देखें",
    "viewOriginal": "मूल देखें",
    "workshop": "वर्कशॉप",
    "workshopComingSoon": "कस्टम मैप वर्कशॉप",
    "workshopDescription": "एक रचनात्मक स्थान जहाँ खिलाड़ी चित्र अपलोड करके उसे कस्टम ईंट मानचित्र में बदल सकेंगे।",
    "workshopLevelMap": "स्तर निर्माण",
    "workshopStarMap": "मानचित्र निर्माण",
    "workshopWorkbench": "कार्यपीठ",
    "workshopToolSoon": "संपादक जल्द आ रहा है",
    "workshopTools": "मानचित्र उपकरण",
    "worldMap": "विश्व मानचित्र",
    "zh": "zh",
    "skin.ball_default": "क्लासिक सफेद",
    "skin.ball_star": "तारा",
    "skin.ball_football": "फुटबॉल",
    "skin.ball_flower": "फूल",
    "skin.ball_planet": "ग्रह",
    "skin.ball_rainbow": "इंद्रधनुष कैंडी",
    "skin.ball_diamond": "हीरा",
    "skin.paddle_default": "क्लासिक",
    "skin.paddle_stool": "स्टूल",
    "skin.paddle_grass": "घास",
    "skin.paddle_rainbow": "इंद्रधनुष पुल",
    "skin.paddle_carpet": "जादुई कालीन",
    "skin.paddle_wave": "लहर",
    "skin.paddle_mecha": "मेका प्लेटफ़ॉर्म",
    "imageFileRequired": "चित्र अपलोड करें",
    "imageReadFailed": "चित्र पढ़ा नहीं जा सका।",
    "imageTooLarge": "चित्र बहुत बड़ा है।",
    "imageUnsupported": "आपका ब्राउज़र Canvas का समर्थन नहीं करता।",
    "imageNoRegion": "चित्र में कोई उपयोगी क्षेत्र नहीं मिला।",
    "mapNotFound": "लेवल डेटा उपलब्ध नहीं",
    "mapCategoryWorld": "विश्व मानचित्र",
    "mapCategoryWorldShort": "विश्व",
    "mapCategoryCountry": "देश मानचित्र",
    "mapCategoryCountryShort": "देश",
    "mapCategoryRegion": "क्षेत्रीय मानचित्र",
    "mapCategoryRegionShort": "क्षेत्र",
    "worldClassicName": "विश्व के प्रसिद्ध स्थल",
    "worldClassicDesc": "दुनिया के प्रसिद्ध स्थलों से बना अन्वेषण मानचित्र।",
    "worldRouteName": "विश्व यात्रा मार्ग",
    "worldRouteDesc": "दुनिया भर की यात्रा के मार्ग पर आधारित मानचित्र।",
    "worldContinentsName": "सात महाद्वीपों की खोज",
    "worldContinentsDesc": "सातों महाद्वीपों का अन्वेषण करें।",
    "chinaClassicName": "क्लासिक चीन",
    "chinaClassicDesc": "चीन के प्रसिद्ध शहर और प्रमुख स्थल।",
    "japanClassicName": "क्लासिक जापान",
    "japanClassicDesc": "जापान के प्रमुख स्थान।",
    "franceClassicName": "क्लासिक फ्रांस",
    "franceClassicDesc": "फ्रांस के शहर और प्रमुख स्थल।",
    "usaClassicName": "क्लासिक अमेरिका",
    "usaClassicDesc": "अमेरिका के प्रमुख स्थल।",
    "europeClassicName": "यूरोप के क्लासिक शहर",
    "europeClassicDesc": "यूरोप के यात्रा शहरों का मानचित्र।",
    "eastAsiaName": "पूर्वी एशिया यात्रा",
    "eastAsiaDesc": "पूर्वी एशिया के शहर और प्रमुख स्थल।",
    "southEastAsiaName": "दक्षिण-पूर्व एशिया यात्रा",
    "southEastAsiaDesc": "दक्षिण-पूर्व एशिया का यात्रा मार्ग।",
    "chinaRegionName": "चीन के क्षेत्र",
    "chinaRegionDesc": "चीन के क्षेत्रों पर आधारित अन्वेषण मानचित्र।",
    "demoWorldDreamName": "दुनिया की पहली यात्रा",
    "demoWorldDreamDesc": "जिन शहरों में जाना चाहते हैं उन्हें स्टार मैप में बदलें और अपनी यात्रा सूची को चरण-दर-चरण रोशन करें।",
    "demoJapanNightName": "जापान यात्रा योजना",
    "demoJapanNightDesc": "टोक्यो, क्योटो, ओसाका और माउंट फ़ूजी से होकर जाने वाला खेलने योग्य मार्ग।",
    "demoChinaLandmarksName": "चीन के प्रसिद्ध स्थल",
    "demoChinaLandmarksDesc": "ग्रेट वॉल से शीआन, शंघाई, चेंगदू और ग्वांगझोउ तक यात्रा करें।",
    "demoFranceTripName": "फ्रांस रोमांटिक मार्ग",
    "demoFranceTripDesc": "पेरिस, लियोन, नीस और स्ट्रासबर्ग को आराम से खोजें।",
    "demoSevenContinentsName": "सात महाद्वीपों की खोज",
    "demoSevenContinentsDesc": "एशिया से शुरू करें और सातों महाद्वीपों को अपनी यात्रा के चरण बनाएं।",
    "demoUsaRoadName": "अमेरिका रोड ट्रिप",
    "demoUsaRoadDesc": "न्यूयॉर्क, वॉशिंगटन, शिकागो, लॉस एंजेलिस और ग्रैंड कैन्यन से पश्चिमी तट तक।",
    "demoEuropeCityName": "यूरोपीय शहरों की यात्रा",
    "demoEuropeCityDesc": "लंदन, पेरिस, बर्लिन, रोम, एथेंस और बार्सिलोना का शहर मानचित्र।",
    "scenicForbiddenCity": "निषिद्ध नगर",
    "scenicTempleOfHeaven": "स्वर्ग का मंदिर",
    "scenicGreatWall": "चीन की महान दीवार",
    "scenicZhangjiajie": "झांगजियाजी"
  }),
  "th": Object.freeze({
    "allFragmentsCollected": "เก็บชิ้นส่วนครบทั้งหมดแล้ว",
    "back": "กลับ",
    "backHome": "กลับหน้าหลัก",
    "backendReady": "ฟรอนต์เอนด์ Vue พร้อมเชื่อมต่อแบ็กเอนด์",
    "ballSkins": "สกินลูกบอล",
    "ballSpeed": "ความเร็วลูกบอล",
    "breakableCount": "อิฐที่ทำลายได้: {count}",
    "brickPalette": "ชุดสีอิฐ",
    "builtLevels": "สร้างแล้ว {done}/{total}",
    "levelBuilt": "สร้างแล้ว",
    "cancel": "ยกเลิก",
    "cannotReadTemplate": "ไม่สามารถประมวลผลรูปภาพนี้ได้",
    "canvasUnsupported": "เบราว์เซอร์ของคุณไม่รองรับ Canvas",
    "challengeMapHint": "เลือกแผนที่สำหรับปุ่ม เล่น ที่หน้าแรก แผนที่ทางการพร้อมใช้งานเสมอ",
    "challengeMapTitle": "เลือกแผนที่",
    "chapter": "บท",
    "chapterEndFragmentNotice": "ชิ้นส่วนสถานที่สำคัญ",
    "chapters": "บท",
    "chooseChallenge": "ใช้แผนที่นี้",
    "chooseForPlay": "ใช้แผนที่นี้",
    "chooseMapPlay": "เล่นแผนที่นี้",
    "clearSearch": "ล้างการค้นหา",
    "cleared": "ผ่านแล้ว",
    "close": "ปิด",
    "collectFragment": "เก็บชิ้นส่วน",
    "collectedFragments": "เก็บชิ้นส่วนแล้ว {count} ชิ้น",
    "comingSoon": "เร็ว ๆ นี้",
    "completed": "เสร็จสิ้น",
    "confirm": "ยืนยัน",
    "confirmDeleteMap": "ลบ “{name}” หรือไม่? ข้อมูลด่านในเครื่องจะถูกลบด้วย",
    "confirmOverwrite": "การสร้างจากรูปภาพจะเขียนทับแคนวาสปัจจุบัน ดำเนินการต่อหรือไม่?",
    "continueGame": "เล่นต่อ",
    "countryCA": "แคนาดา",
    "countryCN": "จีน",
    "countryDE": "เยอรมนี",
    "countryFR": "ฝรั่งเศส",
    "countryGB": "สหราชอาณาจักร",
    "countryJP": "ญี่ปุ่น",
    "countryKR": "เกาหลีใต้",
    "countryUS": "สหรัฐอเมริกา",
    "createLevel": "สร้างด่าน",
    "createStarMap": "สร้างแผนที่ดาว",
    "createStarMapFirst": "สร้างแผนที่ดาวในเวิร์กช็อป ก่อนหน้านั้นปุ่ม เล่น จะยังใช้แผนที่ระบบ",
    "creativeWorkshop": "เวิร์กช็อป",
    "creativeWorkshopHint": "แผนที่กำหนดเอง",
    "currentLevel": "ด่านปัจจุบัน",
    "currentLevelFormat": "ถึงด่าน {level}",
    "currentProgress": "เล่นต่อจากด่าน {level}",
    "currentReachedLevel": "ถึงด่าน {level}",
    "custom": "กำหนดเอง",
    "customMapCompleteSubtitle": "ยินดีด้วย! คุณเปิดครบทุกด่านใน {name} แล้ว",
    "customMapCompleteTitle": "ผ่านแผนที่แล้ว!",
    "customMapCurrentProgress": "{name} · ถึงด่าน {level}",
    "customMapDescription": "แผนที่ดาวแบบกำหนดเองและด่านที่คุณสร้าง",
    "defeat": "พ่ายแพ้",
    "deleteAction": "ลบ",
    "deleteMap": "ลบแผนที่",
    "difficulty": "ความยาก",
    "dragNodesHint": "อัปโหลดรูปเพื่อสร้างดาวด่าน {count} จุดโดยอัตโนมัติ ลากดาวเพื่อปรับเส้นทาง แล้วบันทึกเป็นแผนที่เลือกด่านปัจจุบัน",
    "editMap": "แก้ไขแผนที่",
    "en": "en",
    "energyFull": "เต็ม",
    "energyInsufficient": "พลังงานไม่พอ: {current}/{cost} โปรดรอการฟื้นฟู",
    "energyLabel": "พลังงาน",
    "energyLoading": "กำลังโหลด",
    "enterMap": "เข้าสู่แผนที่",
    "mapViews": "ยอดดู",
    "likeMap": "ถูกใจ",
    "unlikeMap": "ยกเลิกถูกใจ",
    "enterSpecialLevel": "เข้าสู่ด่านพิเศษ",
    "extreme": "สุดขีด",
    "fast": "เร็ว",
    "finalLevel": "สุดท้าย",
    "fr": "fr",
    "fragment": "ชิ้นส่วน",
    "fragmentCollected": "เก็บชิ้นส่วนแล้ว",
    "fragmentProgress": "ความคืบหน้าชิ้นส่วน",
    "fragments": "ชิ้นส่วน",
    "gameHelp": "A/D หรือ ←/→ เพื่อขยับ · Space เพื่อยิง · R เพื่อเริ่มใหม่",
    "gameFailedTitle": "เกมล้มเหลว",
    "retryChallenge": "ลองอีกครั้ง",
    "generateMap": "สร้างแผนที่",
    "goCreateStarMap": "สร้างแผนที่ดาว",
    "hard": "ยาก",
    "highScore": "คะแนนสูงสุด",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "ขีปนาวุธเลเซอร์",
    "leaderboard": "อันดับ",
    "leaderboardFullTitle": "อันดับประเทศแบบเต็ม",
    "leaderboardHint": "อันดับ",
    "leaderboardPageTitle": "🌍 อันดับประเทศทั่วโลก",
    "leaderboardPlaceholderNote": "เส้นทางอันดับ สโตร์ และชั้น API พร้อมแล้ว เลย์เอาต์ปัจจุบันเป็นแบบชั่วคราวและสามารถเปลี่ยนได้โดยไม่กระทบการเชื่อมต่อแบ็กเอนด์",
    "leaderboardScore": "คะแนน",
    "resultLevelScore": "คะแนนด่าน",
    "leaderboardCleared": "ความคืบหน้า",
    "level": "ด่าน",
    "levelCountFormat": "{count} ด่าน",
    "levelCountPoints": "ด่าน / จุด",
    "levelLocked": "ด่านถูกล็อก",
    "levelMap": "แผนที่ด่าน",
    "levelPlaysFormat": "{count} ด่าน · เล่น {plays} ครั้ง",
    "levelSelect": "เลือกด่าน",
    "litLevels": "เปิดแล้ว {done}/{total}",
    "loading": "กำลังโหลด",
    "loadingLevel": "กำลังโหลดด่าน...",
    "loadingSharedMap": "กำลังโหลดแผนที่ที่แชร์…",
    "loadingTemplate": "กำลังวิเคราะห์รูปภาพ...",
    "locked": "ล็อก",
    "mapCompleted": "ผ่านแผนที่แล้ว",
    "mapDescription": "คำอธิบายแผนที่",
    "mapDescriptionPlaceholder": "ป้อนคำอธิบายแผนที่",
    "mapFullyLit": "แผนที่สว่างครบแล้ว",
    "mapName": "ชื่อแผนที่",
    "mapPermission": "สิทธิ์ของแผนที่",
    "mapTemplate": "เทมเพลตแผนที่",
    "mapTitle": "แผนที่เสียงสะท้อน",
    "mapUnlocked": "ปลดล็อกแผนที่แล้ว",
    "mapUpdatedSaved": "บันทึกแล้ว ตอนนี้นี่คือแผนที่เลือกด่านปัจจุบัน",
    "modeGuide": "คำอธิบายโหมด",
    "modeGuideExtreme": "เอ็กซ์ตรีม: อัตราดรอปไอเท็มประมาณ 35% ของโหมดคลาสสิก",
    "modeGuideHard": "ยาก: อัตราดรอปไอเท็มประมาณ 65% ของโหมดคลาสสิก",
    "modeGuideSpeed": "ลูกบอลที่เร็วขึ้นทำให้ควบคุมและผ่านด่านยากขึ้น",
    "modeGuideStandard": "คลาสสิก: อัตราดรอปไอเท็มดั้งเดิม",
    "myCustomMaps": "ผลงานของฉัน",
    "myStarMap": "แผนที่ดาวของฉัน",
    "myWorkshop": "เวิร์กช็อปของฉัน",
    "next": "ถัดไป",
    "noCustomMaps": "ยังไม่มีแผนที่กำหนดเอง",
    "noCustomMapsHint": "สร้างแผนที่ดาวในเวิร์กช็อป ก่อนหน้านั้นปุ่ม เล่น จะยังใช้แผนที่ระบบ",
    "noCustomStarMap": "ยังไม่มีแผนที่กำหนดเอง",
    "noMapDescription": "ไม่มีคำอธิบายแผนที่",
    "noMatches": "ไม่พบผลลัพธ์",
    "normal": "ปกติ",
    "off": "ปิด",
    "on": "เปิด",
    "openStarMapEditor": "แก้ไขแผนที่",
    "originalImageTitle": "รูปต้นฉบับ",
    "paddleGrow": "ขยายแป้นตี",
    "paddleSkins": "สกินแพดเดิล",
    "play": "เล่น",
    "playsLikesFormat": "เล่น {plays} ครั้ง · ถูกใจ {likes}",
    "previewHint": "อัปโหลดรูปเพื่อสร้างดาวด่าน {count} จุด แล้วลากเพื่อปรับเส้นทางและบันทึกแผนที่",
    "previous": "ก่อนหน้า",
    "privateMap": "ส่วนตัว",
    "publicMap": "สาธารณะ",
    "visibilityChangedPublic": "เปลี่ยนเป็นสาธารณะแล้ว",
    "visibilityChangedPrivate": "เปลี่ยนเป็นส่วนตัวแล้ว",
    "visibilityChangedShare": "เปลี่ยนเป็นเฉพาะลิงก์แชร์แล้ว",
    "publicMaps": "แกลเลอรี",
    "publicMapsDescription": "ค้นพบแผนที่ที่ผู้เล่นคนอื่นสร้าง",
    "regenerateByCount": "สร้างเส้นทางใหม่",
    "regenerateNodesSuccess": "สร้างจุดด่าน {count} จุดแล้ว กดสร้างเส้นทางใหม่ก่อนลากปรับ",
    "replayLevel": "เล่นด่านอีกครั้ง",
    "restart": "เริ่มใหม่",
    "result": "ผลลัพธ์",
    "returnHome": "กลับหน้าหลัก",
    "runtimeBalls": "ลูกบอล",
    "runtimeHome": "หน้าหลัก",
    "runtimeLives": "ชีวิต",
    "runtimePaused": "หยุดชั่วคราว",
    "runtimeResume": "เล่นต่อ",
    "runtimeTime": "เวลา",
    "save": "บันทึก",
    "saveAndBack": "บันทึกและกลับ",
    "saveFailed": "บันทึกไม่สำเร็จ",
    "saveMap": "บันทึกแผนที่",
    "saveMapAndCreateLevels": "บันทึกเป็นแผนที่ปัจจุบัน",
    "saveSuccess": "บันทึกสำเร็จ",
    "scenicFragment": "ชิ้นส่วนสถานที่",
    "scenicFragmentObtained": "ได้รับชิ้นส่วนสถานที่แล้ว! จะบันทึกหลังผ่านด่าน",
    "scenicFragmentProgress": "ชิ้นส่วนสถานที่ {found}/{total}",
    "searchColor": "ค้นหาสีหรือค่า HEX",
    "searchMaps": "ค้นหาแผนที่",
    "selectMapTemplate": "เทมเพลตแผนที่",
    "selectTemplateDescription": "อัปโหลดรูปเพื่อสร้างดาวด่าน {count} จุดโดยอัตโนมัติ ปรับเส้นทางแล้วบันทึกแผนที่",
    "selectTemplateOrUpload": "อัปโหลดรูปภาพ",
    "selected": "เลือกแล้ว",
    "selectedChallenge": "เลือกแล้ว",
    "selectedMap": "เลือกแล้ว",
    "settings": "ตั้งค่า",
    "settingsTitle": "ตั้งค่าเกม",
    "share": "แชร์",
    "shareAction": "แชร์",
    "shareFailed": "แชร์ไม่สำเร็จ",
    "shareInviteText": "มาช่วยจุดแผนที่ดาว BricksBeater ของฉันกัน!",
    "shareMap": "เฉพาะลิงก์",
    "shareReady": "ลิงก์แชร์พร้อมแล้ว",
    "shareSuccess": "แชร์สำเร็จ",
    "shareUnavailable": "ไม่สามารถแชร์แผนที่ส่วนตัวได้ โปรดเปลี่ยนการมองเห็นก่อน",
    "skill": "สกิล",
    "skillCount": "จำนวนสกิล",
    "skins": "สกิน",
    "skinsTitle": "ร้านสกิน",
    "slow": "ช้า",
    "sound": "เสียง",
    "specialLevel": "ด่านพิเศษ",
    "specialLevelLocked": "ด่านพิเศษยังล็อกอยู่",
    "standard": "คลาสสิก",
    "starMap": "แผนที่ดาว",
    "starMapClear": "ล้างแผนที่",
    "starMapCleared": "ล้างแผนที่ดาวแบบกำหนดเองปัจจุบันแล้ว",
    "starMapCurrentBadge": "แผนที่ดาวปัจจุบัน",
    "starMapDefaultName": "ป้อนชื่อแผนที่",
    "starMapEdit": "แก้ไขแผนที่",
    "starMapEditorHint": "อัปโหลดรูปเพื่อสร้างดาวด่าน {count} จุดโดยอัตโนมัติ ปรับเส้นทางแล้วบันทึกแผนที่",
    "starMapEditorTitle": "ตัวสร้างแผนที่ดาว",
    "starMapGenerated": "สร้างดาวด่าน {count} จุดแล้ว ลากเพื่อปรับแผนที่",
    "starMapImageError": "ไม่สามารถประมวลผลรูปภาพนี้ได้",
    "starMapLightProgress": "เปิดแล้ว {done} / {total}",
    "starMapName": "ชื่อแผนที่",
    "starMapPreviewInLevels": "เปิดหน้าเลือกด่าน",
    "starMapProcessing": "กำลังวิเคราะห์รูปภาพ...",
    "starMapRegenerate": "สร้างดาวใหม่",
    "starMapRevealHint": "ผ่านด่านเพื่อค่อย ๆ เปิดเผยภาพต้นฉบับ",
    "starMapSaveCurrent": "บันทึกเป็นแผนที่ปัจจุบัน",
    "starMapSaveError": "ไม่สามารถบันทึกแผนที่ดาวไว้ในเครื่องได้",
    "starMapSaved": "บันทึกแล้ว ตอนนี้นี่คือแผนที่เลือกด่านปัจจุบัน",
    "starMapUpload": "อัปโหลดรูปภาพ",
    "starMapUploadEmptyHint": "เวอร์ชันแรกใช้การวิเคราะห์รูปภาพและไม่ต้องใช้ AI",
    "starMapUploadEmptyTitle": "อัปโหลดรูปเพื่อสร้างแผนที่ดาว",
    "startError": "ไม่สามารถเริ่มเกมได้",
    "subtitle": "ทำลายความว่างเปล่า · จุดดาวให้สว่าง",
    "systemMapCurrentProgress": "แผนที่ระบบ · ถึงด่าน {level}",
    "systemMapDescription": "แคมเปญทางการ เล่นตามลำดับด่าน",
    "systemMapName": "แผนที่เสียงสะท้อน",
    "templateLoadFailed": "ไม่สามารถประมวลผลรูปภาพนี้ได้",
    "title": "เสียงสะท้อนแห่งโลก",
    "totalLevels": "{count} ด่าน",
    "unavailable": "ยังไม่มีข้อมูลด่าน",
    "uploadImage": "อัปโหลดรูปภาพ",
    "uploadMapImage": "อัปโหลดรูปเพื่อสร้างแผนที่",
    "uploadMapImageHint": "ระบบอัปโหลดและสร้างแผนที่จะเพิ่มในขั้นพัฒนาถัดไป",
    "victory": "ชนะ",
    "viewCompletedMap": "ดูแผนที่",
    "viewOriginal": "ดูต้นฉบับ",
    "workshop": "เวิร์กช็อป",
    "workshopComingSoon": "เวิร์กช็อปแผนที่กำหนดเอง",
    "workshopDescription": "พื้นที่สร้างสรรค์ที่ผู้เล่นสามารถอัปโหลดรูปและแปลงเป็นแผนที่อิฐแบบกำหนดเอง",
    "workshopLevelMap": "สร้างด่าน",
    "workshopStarMap": "สร้างแผนที่",
    "workshopWorkbench": "โต๊ะสร้าง",
    "workshopToolSoon": "ตัวแก้ไขจะมาเร็ว ๆ นี้",
    "workshopTools": "เครื่องมือแผนที่",
    "worldMap": "แผนที่โลก",
    "zh": "zh",
    "skin.ball_default": "ขาวคลาสสิก",
    "skin.ball_star": "ดาว",
    "skin.ball_football": "ลูกฟุตบอล",
    "skin.ball_flower": "ดอกไม้",
    "skin.ball_planet": "ดาวเคราะห์",
    "skin.ball_rainbow": "ลูกอมสายรุ้ง",
    "skin.ball_diamond": "เพชร",
    "skin.paddle_default": "คลาสสิก",
    "skin.paddle_stool": "ม้านั่ง",
    "skin.paddle_grass": "สนามหญ้า",
    "skin.paddle_rainbow": "สะพานสายรุ้ง",
    "skin.paddle_carpet": "พรมวิเศษ",
    "skin.paddle_wave": "คลื่น",
    "skin.paddle_mecha": "แท่นเมคา",
    "imageFileRequired": "อัปโหลดรูปภาพ",
    "imageReadFailed": "ไม่สามารถอ่านรูปภาพได้",
    "imageTooLarge": "รูปภาพมีขนาดใหญ่เกินไป",
    "imageUnsupported": "เบราว์เซอร์ของคุณไม่รองรับ Canvas",
    "imageNoRegion": "ไม่พบพื้นที่ที่ใช้งานได้ในรูปภาพ",
    "mapNotFound": "ยังไม่มีข้อมูลด่าน",
    "mapCategoryWorld": "แผนที่โลก",
    "mapCategoryWorldShort": "โลก",
    "mapCategoryCountry": "แผนที่ประเทศ",
    "mapCategoryCountryShort": "ประเทศ",
    "mapCategoryRegion": "แผนที่ภูมิภาค",
    "mapCategoryRegionShort": "ภูมิภาค",
    "worldClassicName": "แลนด์มาร์กคลาสสิกทั่วโลก",
    "worldClassicDesc": "แผนที่สำรวจที่รวมแลนด์มาร์กชื่อดังจากทั่วโลก",
    "worldRouteName": "เส้นทางท่องเที่ยวรอบโลก",
    "worldRouteDesc": "แผนที่ที่ออกแบบตามเส้นทางท่องเที่ยวรอบโลก",
    "worldContinentsName": "สำรวจเจ็ดทวีป",
    "worldContinentsDesc": "สำรวจทั้งเจ็ดทวีป",
    "chinaClassicName": "จีนคลาสสิก",
    "chinaClassicDesc": "เมืองและแลนด์มาร์กชื่อดังของจีน",
    "japanClassicName": "ญี่ปุ่นคลาสสิก",
    "japanClassicDesc": "สถานที่สำคัญของญี่ปุ่น",
    "franceClassicName": "ฝรั่งเศสคลาสสิก",
    "franceClassicDesc": "เมืองและแลนด์มาร์กของฝรั่งเศส",
    "usaClassicName": "สหรัฐอเมริกาคลาสสิก",
    "usaClassicDesc": "แลนด์มาร์กสำคัญของสหรัฐอเมริกา",
    "europeClassicName": "เมืองคลาสสิกแห่งยุโรป",
    "europeClassicDesc": "เมืองท่องเที่ยวในยุโรป",
    "eastAsiaName": "ท่องเที่ยวเอเชียตะวันออก",
    "eastAsiaDesc": "เมืองและแลนด์มาร์กในเอเชียตะวันออก",
    "southEastAsiaName": "ท่องเที่ยวเอเชียตะวันออกเฉียงใต้",
    "southEastAsiaDesc": "เส้นทางท่องเที่ยวในเอเชียตะวันออกเฉียงใต้",
    "chinaRegionName": "ภูมิภาคของจีน",
    "chinaRegionDesc": "แผนที่สำรวจตามภูมิภาคต่าง ๆ ของจีน",
    "demoWorldDreamName": "จุดแวะแรกของการเดินทางรอบโลก",
    "demoWorldDreamDesc": "เปลี่ยนเมืองที่อยากไปให้เป็นแผนที่ดาว และค่อย ๆ ทำรายการเดินทางของคุณให้สว่างขึ้น",
    "demoJapanNightName": "แผนเที่ยวญี่ปุ่น",
    "demoJapanNightDesc": "เส้นทางเล่นผ่านโตเกียว เกียวโต โอซาก้า และภูเขาไฟฟูจิ",
    "demoChinaLandmarksName": "สถานที่สำคัญของจีน",
    "demoChinaLandmarksDesc": "เดินทางจากกำแพงเมืองจีนไปซีอาน เซี่ยงไฮ้ เฉิงตู และกว่างโจว",
    "demoFranceTripName": "เส้นทางโรแมนติกในฝรั่งเศส",
    "demoFranceTripDesc": "ปารีส ลียง นีซ และสตราสบูร์กในแผนที่สำหรับเดินทางแบบสบาย ๆ",
    "demoSevenContinentsName": "สำรวจเจ็ดทวีป",
    "demoSevenContinentsDesc": "เริ่มจากเอเชียและเปลี่ยนทั้งเจ็ดทวีปให้เป็นด่านผจญภัยของคุณ",
    "demoUsaRoadName": "โรดทริปสหรัฐอเมริกา",
    "demoUsaRoadDesc": "นิวยอร์ก วอชิงตัน ชิคาโก ลอสแอนเจลิส และแกรนด์แคนยอนไปจนถึงฝั่งตะวันตก",
    "demoEuropeCityName": "ท่องเมืองยุโรป",
    "demoEuropeCityDesc": "แผนที่เมืองลอนดอน ปารีส เบอร์ลิน โรม เอเธนส์ และบาร์เซโลนา",
    "scenicForbiddenCity": "พระราชวังต้องห้าม",
    "scenicTempleOfHeaven": "หอสักการะฟ้า",
    "scenicGreatWall": "กำแพงเมืองจีน",
    "scenicZhangjiajie": "จางเจียเจี้ย"
  }),
  "vi": Object.freeze({
    "allFragmentsCollected": "Đã thu thập tất cả mảnh ghép",
    "back": "Quay lại",
    "backHome": "VỀ TRANG CHỦ",
    "backendReady": "Giao diện Vue đã sẵn sàng kết nối backend",
    "ballSkins": "Giao diện bóng",
    "ballSpeed": "TỐC ĐỘ BÓNG",
    "breakableCount": "Gạch có thể phá: {count}",
    "brickPalette": "Bảng màu gạch",
    "builtLevels": "Đã tạo {done}/{total}",
    "levelBuilt": "Đã tạo",
    "cancel": "Hủy",
    "cannotReadTemplate": "Không thể xử lý hình ảnh này.",
    "canvasUnsupported": "Trình duyệt của bạn không hỗ trợ Canvas.",
    "challengeMapHint": "Chọn bản đồ dùng cho nút CHƠI ở trang chủ. Bản đồ chính thức luôn khả dụng.",
    "challengeMapTitle": "CHỌN BẢN ĐỒ",
    "chapter": "Chương",
    "chapterEndFragmentNotice": "mảnh ghép danh thắng.",
    "chapters": "Chương",
    "chooseChallenge": "DÙNG BẢN ĐỒ NÀY",
    "chooseForPlay": "DÙNG BẢN ĐỒ NÀY",
    "chooseMapPlay": "Chơi trên bản đồ này",
    "clearSearch": "Xóa tìm kiếm",
    "cleared": "Đã qua",
    "close": "Đóng",
    "collectFragment": "Thu thập mảnh ghép",
    "collectedFragments": "Đã thu thập {count} mảnh ghép",
    "comingSoon": "SẮP RA MẮT",
    "completed": "Hoàn thành",
    "confirm": "Xác nhận",
    "confirmDeleteMap": "Xóa “{name}”? Dữ liệu màn chơi cục bộ cũng sẽ bị xóa.",
    "confirmOverwrite": "Tạo từ ảnh sẽ ghi đè canvas hiện tại. Tiếp tục?",
    "continueGame": "Tiếp tục",
    "countryCA": "Canada",
    "countryCN": "Trung Quốc",
    "countryDE": "Đức",
    "countryFR": "Pháp",
    "countryGB": "Vương quốc Anh",
    "countryJP": "Nhật Bản",
    "countryKR": "Hàn Quốc",
    "countryUS": "Hoa Kỳ",
    "createLevel": "Tạo màn chơi",
    "createStarMap": "Tạo bản đồ sao",
    "createStarMapFirst": "Hãy tạo bản đồ sao trong Xưởng sáng tạo. Trước đó, nút CHƠI vẫn dùng Bản đồ hệ thống.",
    "creativeWorkshop": "XƯỞNG SÁNG TẠO",
    "creativeWorkshopHint": "Bản đồ tùy chỉnh",
    "currentLevel": "MÀN HIỆN TẠI",
    "currentLevelFormat": "Đã đến màn {level}",
    "currentProgress": "Tiếp tục từ màn {level}",
    "currentReachedLevel": "Đã đến màn {level}",
    "custom": "Tùy chỉnh",
    "customMapCompleteSubtitle": "Chúc mừng! Bạn đã thắp sáng mọi màn trong {name}.",
    "customMapCompleteTitle": "HOÀN THÀNH BẢN ĐỒ!",
    "customMapCurrentProgress": "{name} · Đã đến màn {level}",
    "customMapDescription": "Bản đồ sao tùy chỉnh và các màn chơi do bạn tạo.",
    "defeat": "Thất bại",
    "deleteAction": "Xóa",
    "deleteMap": "Xóa bản đồ",
    "difficulty": "ĐỘ KHÓ",
    "dragNodesHint": "Tải ảnh lên để tự động tạo {count} ngôi sao màn chơi. Kéo các ngôi sao để tinh chỉnh tuyến đường, rồi lưu làm bản đồ chọn màn hiện tại.",
    "editMap": "Chỉnh sửa bản đồ",
    "en": "en",
    "energyFull": "Đầy",
    "energyInsufficient": "Không đủ năng lượng: {current}/{cost}. Hãy chờ hồi phục.",
    "energyLabel": "NĂNG LƯỢNG",
    "energyLoading": "ĐANG TẢI",
    "enterMap": "Vào bản đồ",
    "mapViews": "Lượt xem",
    "likeMap": "Thích",
    "unlikeMap": "Bỏ thích",
    "enterSpecialLevel": "Vào màn đặc biệt",
    "extreme": "CỰC KHÓ",
    "fast": "NHANH",
    "finalLevel": "Cuối",
    "fr": "fr",
    "fragment": "Mảnh ghép",
    "fragmentCollected": "Đã thu thập mảnh ghép",
    "fragmentProgress": "Tiến độ mảnh ghép",
    "fragments": "Mảnh ghép",
    "gameHelp": "A/D hoặc ←/→ để di chuyển · Space để phóng · R để chơi lại",
    "gameFailedTitle": "Thất bại",
    "retryChallenge": "Thử lại",
    "generateMap": "Tạo bản đồ",
    "goCreateStarMap": "TẠO BẢN ĐỒ SAO",
    "hard": "KHÓ",
    "highScore": "Điểm cao nhất",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Tên lửa laser",
    "leaderboard": "BẢNG XẾP HẠNG",
    "leaderboardFullTitle": "Bảng xếp hạng quốc gia đầy đủ",
    "leaderboardHint": "Xếp hạng",
    "leaderboardPageTitle": "🌍 XẾP HẠNG QUỐC GIA TOÀN CẦU",
    "leaderboardPlaceholderNote": "Tuyến bảng xếp hạng, store và lớp API đã sẵn sàng. Bố cục hiện tại chỉ là tạm thời và có thể thay thế mà không ảnh hưởng tích hợp backend.",
    "leaderboardScore": "Điểm",
    "resultLevelScore": "Điểm màn chơi",
    "leaderboardCleared": "Tiến độ",
    "level": "Màn",
    "levelCountFormat": "{count} màn",
    "levelCountPoints": "Màn / Điểm",
    "levelLocked": "Màn chơi bị khóa",
    "levelMap": "Bản đồ màn chơi",
    "levelPlaysFormat": "{count} màn · {plays} lượt chơi",
    "levelSelect": "CHỌN MÀN",
    "litLevels": "Đã sáng {done}/{total}",
    "loading": "Đang tải",
    "loadingLevel": "Đang tải màn...",
    "loadingSharedMap": "Đang tải bản đồ được chia sẻ…",
    "loadingTemplate": "Đang phân tích hình ảnh...",
    "locked": "Đã khóa",
    "mapCompleted": "Đã hoàn thành bản đồ",
    "mapDescription": "Mô tả bản đồ",
    "mapDescriptionPlaceholder": "Nhập mô tả bản đồ",
    "mapFullyLit": "Bản đồ đã sáng hoàn toàn",
    "mapName": "TÊN BẢN ĐỒ",
    "mapPermission": "Quyền bản đồ",
    "mapTemplate": "Mẫu bản đồ",
    "mapTitle": "Bản Đồ Vọng Âm",
    "mapUnlocked": "Đã mở khóa bản đồ",
    "mapUpdatedSaved": "Đã lưu. Đây hiện là bản đồ chọn màn hiện tại của bạn.",
    "modeGuide": "HƯỚNG DẪN CHẾ ĐỘ",
    "modeGuideExtreme": "Cực khó: khoảng 35% tỷ lệ rơi vật phẩm của chế độ Cổ điển",
    "modeGuideHard": "Khó: khoảng 65% tỷ lệ rơi vật phẩm của chế độ Cổ điển",
    "modeGuideSpeed": "Bóng nhanh hơn khiến việc điều khiển và vượt màn khó hơn",
    "modeGuideStandard": "Cổ điển: tỷ lệ rơi vật phẩm gốc",
    "myCustomMaps": "Tác Phẩm Của Tôi",
    "myStarMap": "Bản đồ sao của tôi",
    "myWorkshop": "Xưởng của tôi",
    "next": "Tiếp theo",
    "noCustomMaps": "Chưa có bản đồ tùy chỉnh",
    "noCustomMapsHint": "Hãy tạo bản đồ sao trong Xưởng sáng tạo. Trước đó, nút CHƠI vẫn dùng Bản đồ hệ thống.",
    "noCustomStarMap": "Chưa có bản đồ tùy chỉnh",
    "noMapDescription": "Chưa có mô tả bản đồ",
    "noMatches": "Không có kết quả",
    "normal": "BÌNH THƯỜNG",
    "off": "TẮT",
    "on": "BẬT",
    "openStarMapEditor": "CHỈNH SỬA BẢN ĐỒ",
    "originalImageTitle": "Ảnh gốc",
    "paddleGrow": "Thanh đỡ được mở rộng",
    "paddleSkins": "Giao diện thanh đỡ",
    "play": "CHƠI",
    "playsLikesFormat": "{plays} lượt chơi · {likes} lượt thích",
    "previewHint": "Tải ảnh lên để tạo {count} ngôi sao màn chơi. Kéo chúng để tinh chỉnh tuyến đường rồi lưu bản đồ.",
    "previous": "Trước",
    "privateMap": "Riêng tư",
    "publicMap": "Công khai",
    "visibilityChangedPublic": "Đã chuyển sang Công khai.",
    "visibilityChangedPrivate": "Đã chuyển sang Riêng tư.",
    "visibilityChangedShare": "Đã chuyển sang Chỉ qua liên kết chia sẻ.",
    "publicMaps": "Quảng Trường",
    "publicMapsDescription": "Khám phá bản đồ do người chơi khác tạo",
    "regenerateByCount": "Tái Cấu Trúc Lộ Trình",
    "regenerateNodesSuccess": "Đã tạo {count} điểm màn chơi. Hãy nhấn Tái tạo quỹ đạo trước khi kéo.",
    "replayLevel": "Chơi lại màn",
    "restart": "Chơi lại",
    "result": "Kết quả",
    "returnHome": "Về trang chủ",
    "runtimeBalls": "BÓNG",
    "runtimeHome": "TRANG CHỦ",
    "runtimeLives": "MẠNG",
    "runtimePaused": "TẠM DỪNG",
    "runtimeResume": "TIẾP TỤC",
    "runtimeTime": "THỜI GIAN",
    "save": "Lưu",
    "saveAndBack": "LƯU & QUAY LẠI",
    "saveFailed": "Lưu thất bại",
    "saveMap": "Lưu bản đồ",
    "saveMapAndCreateLevels": "LƯU LÀM BẢN ĐỒ HIỆN TẠI",
    "saveSuccess": "Đã lưu thành công",
    "scenicFragment": "Mảnh địa danh",
    "scenicFragmentObtained": "Đã nhận mảnh địa danh! Sẽ được lưu sau khi vượt màn.",
    "scenicFragmentProgress": "Mảnh địa danh {found}/{total}",
    "searchColor": "Tìm màu hoặc mã HEX",
    "searchMaps": "Tìm bản đồ",
    "selectMapTemplate": "Mẫu bản đồ",
    "selectTemplateDescription": "Tải ảnh lên để tự động tạo {count} ngôi sao màn chơi. Tinh chỉnh tuyến đường rồi lưu bản đồ.",
    "selectTemplateOrUpload": "TẢI ẢNH LÊN",
    "selected": "Đã chọn",
    "selectedChallenge": "ĐÃ CHỌN",
    "selectedMap": "ĐÃ CHỌN",
    "settings": "CÀI ĐẶT",
    "settingsTitle": "CÀI ĐẶT TRÒ CHƠI",
    "share": "Chia sẻ",
    "shareAction": "Chia sẻ",
    "shareFailed": "Chia sẻ thất bại.",
    "shareInviteText": "Hãy cùng thắp sáng bản đồ sao BricksBeater của tôi!",
    "shareMap": "Chỉ qua liên kết",
    "shareReady": "Liên kết chia sẻ đã sẵn sàng.",
    "shareSuccess": "Đã chia sẻ thành công",
    "shareUnavailable": "Không thể chia sẻ bản đồ riêng tư. Hãy đổi quyền hiển thị trước.",
    "skill": "Kỹ năng",
    "skillCount": "Số kỹ năng",
    "skins": "TRANG PHỤC",
    "skinsTitle": "Cửa hàng giao diện",
    "slow": "CHẬM",
    "sound": "ÂM THANH",
    "specialLevel": "Màn đặc biệt",
    "specialLevelLocked": "Màn đặc biệt đang bị khóa",
    "standard": "CỔ ĐIỂN",
    "starMap": "Bản đồ sao",
    "starMapClear": "XÓA BẢN ĐỒ",
    "starMapCleared": "Đã xóa bản đồ sao tùy chỉnh hiện tại.",
    "starMapCurrentBadge": "BẢN ĐỒ SAO HIỆN TẠI",
    "starMapDefaultName": "Nhập tên bản đồ",
    "starMapEdit": "CHỈNH SỬA BẢN ĐỒ",
    "starMapEditorHint": "Tải ảnh lên để tự động tạo {count} ngôi sao màn chơi. Tinh chỉnh tuyến đường rồi lưu bản đồ.",
    "starMapEditorTitle": "TRÌNH TẠO BẢN ĐỒ SAO",
    "starMapGenerated": "Đã tạo {count} ngôi sao màn chơi. Kéo chúng để tinh chỉnh bản đồ.",
    "starMapImageError": "Không thể xử lý hình ảnh này.",
    "starMapLightProgress": "ĐÃ SÁNG {done} / {total}",
    "starMapName": "TÊN BẢN ĐỒ",
    "starMapPreviewInLevels": "MỞ CHỌN MÀN",
    "starMapProcessing": "Đang phân tích hình ảnh...",
    "starMapRegenerate": "TẠO LẠI CÁC SAO",
    "starMapRevealHint": "Vượt các màn để dần hé lộ hình ảnh gốc.",
    "starMapSaveCurrent": "LƯU LÀM BẢN ĐỒ HIỆN TẠI",
    "starMapSaveError": "Không thể lưu bản đồ sao cục bộ.",
    "starMapSaved": "Đã lưu. Đây hiện là bản đồ chọn màn hiện tại của bạn.",
    "starMapUpload": "TẢI ẢNH LÊN",
    "starMapUploadEmptyHint": "Phiên bản đầu sử dụng phân tích hình ảnh và không cần AI.",
    "starMapUploadEmptyTitle": "Tải ảnh lên để tạo bản đồ sao",
    "startError": "Không thể bắt đầu trò chơi.",
    "subtitle": "Phá vỡ hư không · Thắp sáng các vì sao",
    "systemMapCurrentProgress": "Bản đồ hệ thống · Đã đến màn {level}",
    "systemMapDescription": "Chiến dịch chính thức, chơi theo thứ tự màn.",
    "systemMapName": "BẢN ĐỒ VỌNG ÂM",
    "templateLoadFailed": "Không thể xử lý hình ảnh này.",
    "title": "Vọng Âm Trái Đất",
    "totalLevels": "{count} màn",
    "unavailable": "Chưa có dữ liệu màn",
    "uploadImage": "Tải ảnh lên",
    "uploadMapImage": "Tải ảnh lên để tạo bản đồ",
    "uploadMapImageHint": "Tính năng tải lên và tạo bản đồ sẽ được bổ sung ở giai đoạn phát triển sau.",
    "victory": "Chiến thắng",
    "viewCompletedMap": "XEM BẢN ĐỒ",
    "viewOriginal": "Xem ảnh gốc",
    "workshop": "Xưởng sáng tạo",
    "workshopComingSoon": "XƯỞNG BẢN ĐỒ TÙY CHỈNH",
    "workshopDescription": "Không gian sáng tạo nơi người chơi có thể tải ảnh lên và biến ảnh thành bản đồ gạch tùy chỉnh.",
    "workshopLevelMap": "Thiết Kế Màn Chơi",
    "workshopStarMap": "Thiết Kế Bản Đồ",
    "workshopWorkbench": "Bàn Chế Tác",
    "workshopToolSoon": "Trình chỉnh sửa sắp ra mắt",
    "workshopTools": "Công cụ bản đồ",
    "worldMap": "Bản đồ thế giới",
    "zh": "zh",
    "skin.ball_default": "Trắng cổ điển",
    "skin.ball_star": "Ngôi sao",
    "skin.ball_football": "Bóng đá",
    "skin.ball_flower": "Hoa",
    "skin.ball_planet": "Hành tinh",
    "skin.ball_rainbow": "Kẹo cầu vồng",
    "skin.ball_diamond": "Kim cương",
    "skin.paddle_default": "Cổ điển",
    "skin.paddle_stool": "Ghế đẩu",
    "skin.paddle_grass": "Cỏ",
    "skin.paddle_rainbow": "Cầu vồng",
    "skin.paddle_carpet": "Thảm thần",
    "skin.paddle_wave": "Sóng",
    "skin.paddle_mecha": "Bệ cơ khí",
    "imageFileRequired": "TẢI ẢNH LÊN",
    "imageReadFailed": "Không thể đọc hình ảnh.",
    "imageTooLarge": "Hình ảnh quá lớn.",
    "imageUnsupported": "Trình duyệt của bạn không hỗ trợ Canvas.",
    "imageNoRegion": "Không phát hiện vùng phù hợp trong hình ảnh.",
    "mapNotFound": "Chưa có dữ liệu màn",
    "mapCategoryWorld": "Bản đồ thế giới",
    "mapCategoryWorldShort": "Thế giới",
    "mapCategoryCountry": "Bản đồ quốc gia",
    "mapCategoryCountryShort": "Quốc gia",
    "mapCategoryRegion": "Bản đồ khu vực",
    "mapCategoryRegionShort": "Khu vực",
    "worldClassicName": "Danh thắng kinh điển thế giới",
    "worldClassicDesc": "Bản đồ khám phá gồm các danh thắng nổi tiếng trên thế giới.",
    "worldRouteName": "Hành trình vòng quanh thế giới",
    "worldRouteDesc": "Bản đồ được thiết kế theo một hành trình vòng quanh thế giới.",
    "worldContinentsName": "Khám phá bảy châu lục",
    "worldContinentsDesc": "Khám phá bảy châu lục.",
    "chinaClassicName": "Trung Quốc kinh điển",
    "chinaClassicDesc": "Các thành phố và danh thắng nổi tiếng của Trung Quốc.",
    "japanClassicName": "Nhật Bản kinh điển",
    "japanClassicDesc": "Những địa điểm tiêu biểu của Nhật Bản.",
    "franceClassicName": "Pháp kinh điển",
    "franceClassicDesc": "Các thành phố và danh thắng của Pháp.",
    "usaClassicName": "Hoa Kỳ kinh điển",
    "usaClassicDesc": "Các danh thắng tiêu biểu của Hoa Kỳ.",
    "europeClassicName": "Thành phố kinh điển châu Âu",
    "europeClassicDesc": "Các thành phố du lịch ở châu Âu.",
    "eastAsiaName": "Du lịch Đông Á",
    "eastAsiaDesc": "Các thành phố và danh thắng Đông Á.",
    "southEastAsiaName": "Du lịch Đông Nam Á",
    "southEastAsiaDesc": "Hành trình du lịch qua Đông Nam Á.",
    "chinaRegionName": "Các vùng của Trung Quốc",
    "chinaRegionDesc": "Bản đồ khám phá theo chủ đề các vùng của Trung Quốc.",
    "demoWorldDreamName": "Điểm dừng đầu tiên vòng quanh thế giới",
    "demoWorldDreamDesc": "Biến những thành phố bạn muốn đến thành bản đồ sao và thắp sáng hành trình từng chặng.",
    "demoJapanNightName": "Kế hoạch du lịch Nhật Bản",
    "demoJapanNightDesc": "Tuyến đường có thể chơi qua Tokyo, Kyoto, Osaka và núi Phú Sĩ.",
    "demoChinaLandmarksName": "Danh thắng kinh điển Trung Quốc",
    "demoChinaLandmarksDesc": "Từ Vạn Lý Trường Thành đến Tây An, Thượng Hải, Thành Đô và Quảng Châu.",
    "demoFranceTripName": "Hành trình lãng mạn nước Pháp",
    "demoFranceTripDesc": "Paris, Lyon, Nice và Strasbourg trong một bản đồ thư thả.",
    "demoSevenContinentsName": "Khám phá bảy châu lục",
    "demoSevenContinentsDesc": "Khởi hành từ châu Á và biến bảy châu lục thành các chặng phiêu lưu.",
    "demoUsaRoadName": "Chuyến đi đường bộ nước Mỹ",
    "demoUsaRoadDesc": "New York, Washington, Chicago, Los Angeles và Grand Canyon đến bờ Tây.",
    "demoEuropeCityName": "Du ngoạn các thành phố châu Âu",
    "demoEuropeCityDesc": "Bản đồ qua London, Paris, Berlin, Rome, Athens và Barcelona.",
    "scenicForbiddenCity": "Tử Cấm Thành",
    "scenicTempleOfHeaven": "Thiên Đàn",
    "scenicGreatWall": "Vạn Lý Trường Thành",
    "scenicZhangjiajie": "Trương Gia Giới"
  }),
  "id": Object.freeze({
    "allFragmentsCollected": "Semua fragmen telah dikumpulkan",
    "back": "Kembali",
    "backHome": "KEMBALI KE BERANDA",
    "backendReady": "Frontend Vue siap terhubung ke backend",
    "ballSkins": "Skin bola",
    "ballSpeed": "KECEPATAN BOLA",
    "breakableCount": "Bata yang dapat dihancurkan: {count}",
    "brickPalette": "Palet bata",
    "builtLevels": "Dibuat {done}/{total}",
    "levelBuilt": "Sudah dibuat",
    "cancel": "Batal",
    "cannotReadTemplate": "Gambar ini tidak dapat diproses.",
    "canvasUnsupported": "Browser Anda tidak mendukung Canvas.",
    "challengeMapHint": "Pilih peta untuk tombol MAIN di Beranda. Peta resmi selalu tersedia.",
    "challengeMapTitle": "PILIH PETA",
    "chapter": "Bab",
    "chapterEndFragmentNotice": "fragmen landmark.",
    "chapters": "Bab",
    "chooseChallenge": "PAKAI PETA INI",
    "chooseForPlay": "PAKAI PETA INI",
    "chooseMapPlay": "Mainkan peta ini",
    "clearSearch": "Hapus pencarian",
    "cleared": "Selesai",
    "close": "Tutup",
    "collectFragment": "Kumpulkan fragmen",
    "collectedFragments": "{count} fragmen dikumpulkan",
    "comingSoon": "SEGERA HADIR",
    "completed": "Selesai",
    "confirm": "Konfirmasi",
    "confirmDeleteMap": "Hapus “{name}”? Data level lokal juga akan dihapus.",
    "confirmOverwrite": "Membuat dari gambar akan menimpa kanvas saat ini. Lanjutkan?",
    "continueGame": "Lanjutkan",
    "countryCA": "Kanada",
    "countryCN": "Tiongkok",
    "countryDE": "Jerman",
    "countryFR": "Prancis",
    "countryGB": "Britania Raya",
    "countryJP": "Jepang",
    "countryKR": "Korea Selatan",
    "countryUS": "Amerika Serikat",
    "createLevel": "Buat level",
    "createStarMap": "Buat peta bintang",
    "createStarMapFirst": "Buat peta bintang di Lokakarya. Sebelum itu, tombol MAIN akan tetap menggunakan Peta Sistem.",
    "creativeWorkshop": "BENGKEL",
    "creativeWorkshopHint": "Peta kustom",
    "currentLevel": "LEVEL SAAT INI",
    "currentLevelFormat": "Mencapai level {level}",
    "currentProgress": "Lanjut dari level {level}",
    "currentReachedLevel": "Mencapai level {level}",
    "custom": "Kustom",
    "customMapCompleteSubtitle": "Selamat! Semua level di {name} telah dinyalakan.",
    "customMapCompleteTitle": "PETA SELESAI!",
    "customMapCurrentProgress": "{name} · Mencapai level {level}",
    "customMapDescription": "Peta bintang kustom dan level buatanmu.",
    "defeat": "Kalah",
    "deleteAction": "Hapus",
    "deleteMap": "Hapus peta",
    "difficulty": "KESULITAN",
    "dragNodesHint": "Unggah gambar untuk membuat {count} bintang level secara otomatis. Seret bintang untuk menyempurnakan rute, lalu simpan sebagai peta pemilihan level saat ini.",
    "editMap": "Edit peta",
    "en": "en",
    "energyFull": "Penuh",
    "energyInsufficient": "Energi tidak cukup: {current}/{cost}. Tunggu pemulihan.",
    "energyLabel": "ENERGI",
    "energyLoading": "MEMUAT",
    "enterMap": "Masuk peta",
    "mapViews": "Dilihat",
    "likeMap": "Suka",
    "unlikeMap": "Batal suka",
    "enterSpecialLevel": "Masuk level khusus",
    "extreme": "EKSTREM",
    "fast": "CEPAT",
    "finalLevel": "Level terakhir",
    "fr": "fr",
    "fragment": "Fragmen",
    "fragmentCollected": "Fragmen dikumpulkan",
    "fragmentProgress": "Progres fragmen",
    "fragments": "Fragmen",
    "gameHelp": "A/D atau ←/→ untuk bergerak · Space untuk meluncurkan · R untuk mulai ulang",
    "gameFailedTitle": "Permainan gagal",
    "retryChallenge": "Coba lagi",
    "generateMap": "Buat peta",
    "goCreateStarMap": "BUAT PETA BINTANG",
    "hard": "SULIT",
    "highScore": "Skor tertinggi",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Rudal laser",
    "leaderboard": "PERINGKAT",
    "leaderboardFullTitle": "Papan peringkat negara lengkap",
    "leaderboardHint": "Peringkat",
    "leaderboardPageTitle": "🌍 PERINGKAT NEGARA GLOBAL",
    "leaderboardPlaceholderNote": "Rute papan peringkat, store, dan lapisan API sudah siap. Tata letak saat ini bersifat sementara dan dapat diganti tanpa mengubah integrasi backend.",
    "leaderboardScore": "Skor",
    "resultLevelScore": "Skor level",
    "leaderboardCleared": "Progres",
    "level": "Level",
    "levelCountFormat": "{count} level",
    "levelCountPoints": "Level / Titik",
    "levelLocked": "Level terkunci",
    "levelMap": "Peta level",
    "levelPlaysFormat": "{count} level · dimainkan {plays} kali",
    "levelSelect": "PILIH LEVEL",
    "litLevels": "Menyala {done}/{total}",
    "loading": "Memuat",
    "loadingLevel": "Memuat level...",
    "loadingSharedMap": "Memuat peta bersama…",
    "loadingTemplate": "Menganalisis gambar...",
    "locked": "Terkunci",
    "mapCompleted": "Peta selesai",
    "mapDescription": "Deskripsi peta",
    "mapDescriptionPlaceholder": "Masukkan deskripsi peta",
    "mapFullyLit": "Peta menyala sepenuhnya",
    "mapName": "NAMA PETA",
    "mapPermission": "Izin peta",
    "mapTemplate": "Templat peta",
    "mapTitle": "Peta Gema",
    "mapUnlocked": "Peta terbuka",
    "mapUpdatedSaved": "Tersimpan. Ini sekarang menjadi peta pemilihan level saat ini.",
    "modeGuide": "PANDUAN MODE",
    "modeGuideExtreme": "Ekstrem: sekitar 35% dari tingkat drop item mode Klasik",
    "modeGuideHard": "Sulit: sekitar 65% dari tingkat drop item mode Klasik",
    "modeGuideSpeed": "Bola yang lebih cepat membuat kontrol dan penyelesaian level lebih sulit",
    "modeGuideStandard": "Klasik: tingkat drop item asli",
    "myCustomMaps": "Karya Saya",
    "myStarMap": "Peta Bintang Saya",
    "myWorkshop": "Bengkel saya",
    "next": "Berikutnya",
    "noCustomMaps": "Belum ada peta kustom",
    "noCustomMapsHint": "Buat peta bintang di Lokakarya. Sebelum itu, tombol MAIN akan tetap menggunakan Peta Sistem.",
    "noCustomStarMap": "Belum ada peta kustom",
    "noMapDescription": "Tidak ada deskripsi peta",
    "noMatches": "Tidak ada hasil",
    "normal": "NORMAL",
    "off": "MATI",
    "on": "NYALA",
    "openStarMapEditor": "EDIT PETA",
    "originalImageTitle": "Gambar asli",
    "paddleGrow": "Papan pemantul diperbesar",
    "paddleSkins": "Skin paddle",
    "play": "MAIN",
    "playsLikesFormat": "Dimainkan {plays} kali · {likes} suka",
    "previewHint": "Unggah gambar untuk membuat {count} bintang level. Seret untuk menyempurnakan rute, lalu simpan peta.",
    "previous": "Sebelumnya",
    "privateMap": "Privat",
    "publicMap": "Publik",
    "visibilityChangedPublic": "Visibilitas diubah menjadi Publik.",
    "visibilityChangedPrivate": "Visibilitas diubah menjadi Pribadi.",
    "visibilityChangedShare": "Visibilitas diubah menjadi Hanya melalui tautan.",
    "publicMaps": "Galeri",
    "publicMapsDescription": "Temukan peta buatan pemain lain",
    "regenerateByCount": "Rekonstruksi Jalur",
    "regenerateNodesSuccess": "{count} titik level dibuat. Tekan Bangun ulang jalur sebelum menyeret.",
    "replayLevel": "Mainkan ulang level",
    "restart": "Mulai ulang",
    "result": "Hasil",
    "returnHome": "Kembali ke beranda",
    "runtimeBalls": "BOLA",
    "runtimeHome": "BERANDA",
    "runtimeLives": "NYAWA",
    "runtimePaused": "DIJEDA",
    "runtimeResume": "LANJUTKAN",
    "runtimeTime": "WAKTU",
    "save": "Simpan",
    "saveAndBack": "SIMPAN & KEMBALI",
    "saveFailed": "Gagal menyimpan",
    "saveMap": "Simpan peta",
    "saveMapAndCreateLevels": "SIMPAN SEBAGAI PETA SAAT INI",
    "saveSuccess": "Berhasil disimpan",
    "scenicFragment": "Fragmen landmark",
    "scenicFragmentObtained": "Fragmen landmark diperoleh! Akan disimpan setelah level selesai.",
    "scenicFragmentProgress": "Fragmen landmark {found}/{total}",
    "searchColor": "Cari warna atau nilai HEX",
    "searchMaps": "Cari peta",
    "selectMapTemplate": "Templat peta",
    "selectTemplateDescription": "Unggah gambar untuk membuat {count} bintang level secara otomatis. Sempurnakan rute lalu simpan peta.",
    "selectTemplateOrUpload": "UNGGAH GAMBAR",
    "selected": "Dipilih",
    "selectedChallenge": "DIPILIH",
    "selectedMap": "DIPILIH",
    "settings": "PENGATURAN",
    "settingsTitle": "PENGATURAN GAME",
    "share": "Bagikan",
    "shareAction": "Bagikan",
    "shareFailed": "Gagal membagikan.",
    "shareInviteText": "Ayo nyalakan peta bintang BricksBeater buatanku!",
    "shareMap": "Hanya lewat tautan",
    "shareReady": "Tautan berbagi siap.",
    "shareSuccess": "Berhasil dibagikan",
    "shareUnavailable": "Peta privat tidak dapat dibagikan. Ubah visibilitas terlebih dahulu.",
    "skill": "Keterampilan",
    "skillCount": "Jumlah keterampilan",
    "skins": "SKIN",
    "skinsTitle": "Toko skin",
    "slow": "LAMBAT",
    "sound": "SUARA",
    "specialLevel": "Level khusus",
    "specialLevelLocked": "Level khusus terkunci",
    "standard": "KLASIK",
    "starMap": "Peta bintang",
    "starMapClear": "HAPUS PETA",
    "starMapCleared": "Peta bintang kustom saat ini telah dihapus.",
    "starMapCurrentBadge": "PETA BINTANG SAAT INI",
    "starMapDefaultName": "Masukkan nama peta",
    "starMapEdit": "EDIT PETA",
    "starMapEditorHint": "Unggah gambar untuk membuat {count} bintang level secara otomatis. Sempurnakan rute lalu simpan peta.",
    "starMapEditorTitle": "PEMBUAT PETA BINTANG",
    "starMapGenerated": "{count} bintang level dibuat. Seret untuk menyempurnakan peta.",
    "starMapImageError": "Gambar ini tidak dapat diproses.",
    "starMapLightProgress": "MENYALA {done} / {total}",
    "starMapName": "NAMA PETA",
    "starMapPreviewInLevels": "BUKA PILIH LEVEL",
    "starMapProcessing": "Menganalisis gambar...",
    "starMapRegenerate": "BUAT ULANG BINTANG",
    "starMapRevealHint": "Selesaikan level untuk perlahan menampilkan gambar asli.",
    "starMapSaveCurrent": "SIMPAN SEBAGAI PETA SAAT INI",
    "starMapSaveError": "Peta bintang tidak dapat disimpan secara lokal.",
    "starMapSaved": "Tersimpan. Ini sekarang menjadi peta pemilihan level saat ini.",
    "starMapUpload": "UNGGAH GAMBAR",
    "starMapUploadEmptyHint": "Versi pertama menggunakan analisis gambar dan tidak memerlukan AI.",
    "starMapUploadEmptyTitle": "Unggah gambar untuk membuat peta bintang",
    "startError": "Game tidak dapat dimulai.",
    "subtitle": "Hancurkan kehampaan · Nyalakan bintang",
    "systemMapCurrentProgress": "Peta Sistem · Mencapai level {level}",
    "systemMapDescription": "Kampanye resmi, dimainkan berurutan.",
    "systemMapName": "PETA GEMA",
    "templateLoadFailed": "Gambar ini tidak dapat diproses.",
    "title": "Gema Bumi",
    "totalLevels": "{count} level",
    "unavailable": "Data level belum tersedia",
    "uploadImage": "Unggah gambar",
    "uploadMapImage": "Unggah gambar untuk membuat peta",
    "uploadMapImageHint": "Logika unggah dan pembuatan peta akan ditambahkan pada tahap pengembangan berikutnya.",
    "victory": "Menang",
    "viewCompletedMap": "LIHAT PETA",
    "viewOriginal": "Lihat asli",
    "workshop": "Lokakarya",
    "workshopComingSoon": "LOKAKARYA PETA KUSTOM",
    "workshopDescription": "Ruang kreatif tempat pemain dapat mengunggah gambar dan mengubahnya menjadi peta bata kustom.",
    "workshopLevelMap": "Pembuatan Level",
    "workshopStarMap": "Pembuatan Peta",
    "workshopWorkbench": "Meja Kerja",
    "workshopToolSoon": "Editor segera hadir",
    "workshopTools": "Alat peta",
    "worldMap": "Peta dunia",
    "zh": "zh",
    "skin.ball_default": "Putih klasik",
    "skin.ball_star": "Bintang",
    "skin.ball_football": "Bola sepak",
    "skin.ball_flower": "Bunga",
    "skin.ball_planet": "Planet",
    "skin.ball_rainbow": "Permen pelangi",
    "skin.ball_diamond": "Berlian",
    "skin.paddle_default": "Klasik",
    "skin.paddle_stool": "Bangku",
    "skin.paddle_grass": "Rumput",
    "skin.paddle_rainbow": "Jembatan pelangi",
    "skin.paddle_carpet": "Karpet ajaib",
    "skin.paddle_wave": "Ombak",
    "skin.paddle_mecha": "Platform meka",
    "imageFileRequired": "UNGGAH GAMBAR",
    "imageReadFailed": "Gambar tidak dapat dibaca.",
    "imageTooLarge": "Ukuran gambar terlalu besar.",
    "imageUnsupported": "Browser Anda tidak mendukung Canvas.",
    "imageNoRegion": "Tidak ada area yang dapat digunakan terdeteksi pada gambar.",
    "mapNotFound": "Data level belum tersedia",
    "mapCategoryWorld": "Peta dunia",
    "mapCategoryWorldShort": "Dunia",
    "mapCategoryCountry": "Peta negara",
    "mapCategoryCountryShort": "Negara",
    "mapCategoryRegion": "Peta wilayah",
    "mapCategoryRegionShort": "Wilayah",
    "worldClassicName": "Landmark klasik dunia",
    "worldClassicDesc": "Peta eksplorasi yang berisi landmark terkenal dari seluruh dunia.",
    "worldRouteName": "Rute perjalanan dunia",
    "worldRouteDesc": "Peta yang dirancang sebagai rute perjalanan keliling dunia.",
    "worldContinentsName": "Eksplorasi tujuh benua",
    "worldContinentsDesc": "Jelajahi tujuh benua.",
    "chinaClassicName": "Tiongkok klasik",
    "chinaClassicDesc": "Kota dan landmark terkenal di Tiongkok.",
    "japanClassicName": "Jepang klasik",
    "japanClassicDesc": "Lokasi khas Jepang.",
    "franceClassicName": "Prancis klasik",
    "franceClassicDesc": "Kota dan landmark di Prancis.",
    "usaClassicName": "Amerika Serikat klasik",
    "usaClassicDesc": "Landmark khas Amerika Serikat.",
    "europeClassicName": "Kota klasik Eropa",
    "europeClassicDesc": "Kota-kota wisata di Eropa.",
    "eastAsiaName": "Perjalanan Asia Timur",
    "eastAsiaDesc": "Kota dan landmark di Asia Timur.",
    "southEastAsiaName": "Perjalanan Asia Tenggara",
    "southEastAsiaDesc": "Rute perjalanan melalui Asia Tenggara.",
    "chinaRegionName": "Wilayah Tiongkok",
    "chinaRegionDesc": "Peta eksplorasi bertema wilayah-wilayah Tiongkok.",
    "demoWorldDreamName": "Pemberhentian pertama keliling dunia",
    "demoWorldDreamDesc": "Ubah kota impian menjadi peta bintang dan nyalakan daftar perjalananmu tahap demi tahap.",
    "demoJapanNightName": "Rencana perjalanan Jepang",
    "demoJapanNightDesc": "Rute permainan melalui Tokyo, Kyoto, Osaka, dan Gunung Fuji.",
    "demoChinaLandmarksName": "Landmark klasik Tiongkok",
    "demoChinaLandmarksDesc": "Dari Tembok Besar ke Xi'an, Shanghai, Chengdu, dan Guangzhou.",
    "demoFranceTripName": "Rute romantis Prancis",
    "demoFranceTripDesc": "Paris, Lyon, Nice, dan Strasbourg dalam peta perjalanan santai.",
    "demoSevenContinentsName": "Penjelajahan tujuh benua",
    "demoSevenContinentsDesc": "Mulai dari Asia dan jadikan tujuh benua sebagai tahap petualanganmu.",
    "demoUsaRoadName": "Road trip Amerika Serikat",
    "demoUsaRoadDesc": "New York, Washington, Chicago, Los Angeles, dan Grand Canyon hingga Pantai Barat.",
    "demoEuropeCityName": "Jelajah kota Eropa",
    "demoEuropeCityDesc": "Peta kota London, Paris, Berlin, Roma, Athena, dan Barcelona.",
    "scenicForbiddenCity": "Kota Terlarang",
    "scenicTempleOfHeaven": "Kuil Surga",
    "scenicGreatWall": "Tembok Besar Tiongkok",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "tr": Object.freeze({
    "allFragmentsCollected": "Tüm parçalar toplandı",
    "back": "Geri",
    "backHome": "ANA SAYFAYA DÖN",
    "backendReady": "Backend'e hazır Vue arayüzü",
    "ballSkins": "Top görünümleri",
    "ballSpeed": "TOP HIZI",
    "breakableCount": "Kırılabilir tuğlalar: {count}",
    "brickPalette": "Tuğla paleti",
    "builtLevels": "Oluşturulan {done}/{total}",
    "levelBuilt": "Oluşturuldu",
    "cancel": "İptal",
    "cannotReadTemplate": "Bu görsel işlenemedi.",
    "canvasUnsupported": "Tarayıcınız Canvas desteklemiyor.",
    "challengeMapHint": "Ana sayfadaki OYNA düğmesi için kullanılacak haritayı seç. Resmî harita her zaman kullanılabilir.",
    "challengeMapTitle": "HARİTA SEÇ",
    "chapter": "Bölüm",
    "chapterEndFragmentNotice": "simge yapı parçaları.",
    "chapters": "Bölümler",
    "chooseChallenge": "BU HARİTAYI KULLAN",
    "chooseForPlay": "BU HARİTAYI KULLAN",
    "chooseMapPlay": "Bu haritada oyna",
    "clearSearch": "Aramayı temizle",
    "cleared": "Tamamlandı",
    "close": "Kapat",
    "collectFragment": "Parçayı topla",
    "collectedFragments": "{count} parça toplandı",
    "comingSoon": "YAKINDA",
    "completed": "Tamamlandı",
    "confirm": "Onayla",
    "confirmDeleteMap": "“{name}” silinsin mi? Yerel bölüm verileri de silinecek.",
    "confirmOverwrite": "Görselden oluşturmak mevcut tuvali üzerine yazacak. Devam edilsin mi?",
    "continueGame": "Devam et",
    "countryCA": "Kanada",
    "countryCN": "Çin",
    "countryDE": "Almanya",
    "countryFR": "Fransa",
    "countryGB": "Birleşik Krallık",
    "countryJP": "Japonya",
    "countryKR": "Güney Kore",
    "countryUS": "Amerika Birleşik Devletleri",
    "createLevel": "Bölüm oluştur",
    "createStarMap": "Yıldız haritası oluştur",
    "createStarMapFirst": "Atölyede bir yıldız haritası oluştur. O zamana kadar OYNA düğmesi Sistem Haritasını kullanır.",
    "creativeWorkshop": "ATÖLYE",
    "creativeWorkshopHint": "Özel haritalar",
    "currentLevel": "MEVCUT BÖLÜM",
    "currentLevelFormat": "Ulaşılan bölüm {level}",
    "currentProgress": "Bölüm {level} seviyesinden devam et",
    "currentReachedLevel": "Ulaşılan bölüm {level}",
    "custom": "Özel",
    "customMapCompleteSubtitle": "Tebrikler! {name} içindeki tüm bölümleri aydınlattın.",
    "customMapCompleteTitle": "HARİTA TAMAMLANDI!",
    "customMapCurrentProgress": "{name} · {level}. bölüme ulaşıldı",
    "customMapDescription": "Özel yıldız haritan ve kendi oluşturduğun bölümler.",
    "defeat": "Yenilgi",
    "deleteAction": "Sil",
    "deleteMap": "Haritayı sil",
    "difficulty": "ZORLUK",
    "dragNodesHint": "Otomatik olarak {count} bölüm yıldızı oluşturmak için bir görsel yükle. Rotayı ayarlamak için yıldızları sürükle, sonra mevcut bölüm seçme haritası olarak kaydet.",
    "editMap": "Haritayı düzenle",
    "en": "en",
    "energyFull": "Dolu",
    "energyInsufficient": "Yetersiz enerji: {current}/{cost}. Yenilenmeyi bekleyin.",
    "energyLabel": "ENERJİ",
    "energyLoading": "YÜKLENİYOR",
    "enterMap": "Haritaya gir",
    "mapViews": "Görüntülenme",
    "likeMap": "Beğen",
    "unlikeMap": "Beğeniyi kaldır",
    "enterSpecialLevel": "Özel bölüme gir",
    "extreme": "AŞIRI",
    "fast": "HIZLI",
    "finalLevel": "Son Bölüm",
    "fr": "fr",
    "fragment": "Parça",
    "fragmentCollected": "Parça toplandı",
    "fragmentProgress": "Parça ilerlemesi",
    "fragments": "Parçalar",
    "gameHelp": "A/D veya ←/→ hareket · Space fırlat · R yeniden başlat",
    "gameFailedTitle": "Oyun başarısız",
    "retryChallenge": "Tekrar dene",
    "generateMap": "Harita oluştur",
    "goCreateStarMap": "YILDIZ HARİTASI OLUŞTUR",
    "hard": "ZOR",
    "highScore": "En yüksek skor",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Lazer füzesi",
    "leaderboard": "LİDERLİK",
    "leaderboardFullTitle": "Tam ülke sıralaması",
    "leaderboardHint": "Sıralamalar",
    "leaderboardPageTitle": "🌍 KÜRESEL ÜLKE SIRALAMASI",
    "leaderboardPlaceholderNote": "Sıralama rotası, store ve API katmanı hazır. Mevcut düzen geçicidir ve backend entegrasyonu değiştirilmeden yenilenebilir.",
    "leaderboardScore": "Puan",
    "resultLevelScore": "Bölüm puanı",
    "leaderboardCleared": "İlerleme",
    "level": "Seviye",
    "levelCountFormat": "{count} bölüm",
    "levelCountPoints": "Bölüm / Nokta",
    "levelLocked": "Bölüm kilitli",
    "levelMap": "Bölüm haritası",
    "levelPlaysFormat": "{count} bölüm · {plays} oynama",
    "levelSelect": "BÖLÜM SEÇ",
    "litLevels": "Aydınlatıldı {done}/{total}",
    "loading": "Yükleniyor",
    "loadingLevel": "Bölüm yükleniyor...",
    "loadingSharedMap": "Paylaşılan harita yükleniyor…",
    "loadingTemplate": "Görsel analiz ediliyor...",
    "locked": "Kilitli",
    "mapCompleted": "Harita tamamlandı",
    "mapDescription": "Harita açıklaması",
    "mapDescriptionPlaceholder": "Harita açıklamasını girin",
    "mapFullyLit": "Harita tamamen aydınlatıldı",
    "mapName": "HARİTA ADI",
    "mapPermission": "Harita izni",
    "mapTemplate": "Harita şablonu",
    "mapTitle": "Yankı Haritası",
    "mapUnlocked": "Haritanın kilidi açıldı",
    "mapUpdatedSaved": "Kaydedildi. Artık mevcut bölüm seçme haritan bu.",
    "modeGuide": "MOD REHBERİ",
    "modeGuideExtreme": "Ekstrem: Klasik mod eşya düşme oranının yaklaşık %35'i",
    "modeGuideHard": "Zor: Klasik mod eşya düşme oranının yaklaşık %65'i",
    "modeGuideSpeed": "Daha hızlı top kontrolü ve bölümü tamamlamayı zorlaştırır",
    "modeGuideStandard": "Klasik: orijinal eşya düşme oranı",
    "myCustomMaps": "Çalışmalarım",
    "myStarMap": "Yıldız Haritam",
    "myWorkshop": "Atölyem",
    "next": "İleri",
    "noCustomMaps": "Henüz özel harita yok",
    "noCustomMapsHint": "Atölyede bir yıldız haritası oluştur. O zamana kadar OYNA düğmesi Sistem Haritasını kullanır.",
    "noCustomStarMap": "Henüz özel harita yok",
    "noMapDescription": "Harita açıklaması yok",
    "noMatches": "Sonuç yok",
    "normal": "NORMAL",
    "off": "KAPALI",
    "on": "AÇIK",
    "openStarMapEditor": "HARİTAYI DÜZENLE",
    "originalImageTitle": "Orijinal görsel",
    "paddleGrow": "Raket büyütüldü",
    "paddleSkins": "Raket görünümleri",
    "play": "OYNA",
    "playsLikesFormat": "{plays} oynama · {likes} beğeni",
    "previewHint": "{count} bölüm yıldızı oluşturmak için bir görsel yükle. Rotayı ayarlamak için sürükle ve haritayı kaydet.",
    "previous": "Geri",
    "privateMap": "Gizli",
    "publicMap": "Herkese açık",
    "visibilityChangedPublic": "Görünürlük Herkese Açık olarak değiştirildi.",
    "visibilityChangedPrivate": "Görünürlük Özel olarak değiştirildi.",
    "visibilityChangedShare": "Görünürlük Yalnızca paylaşım bağlantısı olarak değiştirildi.",
    "publicMaps": "Galeri",
    "publicMapsDescription": "Diğer oyuncuların oluşturduğu haritaları keşfet",
    "regenerateByCount": "Rota Yeniden Yapılandırma",
    "regenerateNodesSuccess": "{count} bölüm noktası oluşturuldu. Sürüklemeden önce Rotayı yeniden oluştur'a basın.",
    "replayLevel": "Bölümü tekrar oyna",
    "restart": "Yeniden başlat",
    "result": "Sonuç",
    "returnHome": "Ana sayfaya dön",
    "runtimeBalls": "TOP",
    "runtimeHome": "ANA SAYFA",
    "runtimeLives": "CAN",
    "runtimePaused": "DURAKLATILDI",
    "runtimeResume": "DEVAM ET",
    "runtimeTime": "SÜRE",
    "save": "Kaydet",
    "saveAndBack": "KAYDET & DÖN",
    "saveFailed": "Kaydetme başarısız",
    "saveMap": "Haritayı kaydet",
    "saveMapAndCreateLevels": "MEVCUT HARİTA OLARAK KAYDET",
    "saveSuccess": "Başarıyla kaydedildi",
    "scenicFragment": "Simge parçası",
    "scenicFragmentObtained": "Simge parçası alındı! Bölüm tamamlandıktan sonra kaydedilecek.",
    "scenicFragmentProgress": "Simge parçaları {found}/{total}",
    "searchColor": "Renk veya HEX değeri ara",
    "searchMaps": "Harita ara",
    "selectMapTemplate": "Harita şablonu",
    "selectTemplateDescription": "Otomatik olarak {count} bölüm yıldızı oluşturmak için görsel yükle. Rotayı ayarla ve haritayı kaydet.",
    "selectTemplateOrUpload": "GÖRSEL YÜKLE",
    "selected": "Seçildi",
    "selectedChallenge": "SEÇİLİ",
    "selectedMap": "SEÇİLİ",
    "settings": "AYARLAR",
    "settingsTitle": "OYUN AYARLARI",
    "share": "Paylaş",
    "shareAction": "Paylaş",
    "shareFailed": "Paylaşım başarısız.",
    "shareInviteText": "BricksBeater yıldız haritamı birlikte aydınlatalım!",
    "shareMap": "Yalnızca bağlantıyla",
    "shareReady": "Paylaşım bağlantısı hazır.",
    "shareSuccess": "Başarıyla paylaşıldı",
    "shareUnavailable": "Gizli haritalar paylaşılamaz. Önce görünürlüğü değiştirin.",
    "skill": "Yetenek",
    "skillCount": "Yetenek sayısı",
    "skins": "GÖRÜNÜMLER",
    "skinsTitle": "Görünüm mağazası",
    "slow": "YAVAŞ",
    "sound": "SES",
    "specialLevel": "Özel bölüm",
    "specialLevelLocked": "Özel bölüm kilitli",
    "standard": "KLASİK",
    "starMap": "Yıldız haritası",
    "starMapClear": "HARİTAYI TEMİZLE",
    "starMapCleared": "Mevcut özel yıldız haritası temizlendi.",
    "starMapCurrentBadge": "MEVCUT YILDIZ HARİTASI",
    "starMapDefaultName": "Harita adını girin",
    "starMapEdit": "HARİTAYI DÜZENLE",
    "starMapEditorHint": "Otomatik olarak {count} bölüm yıldızı oluşturmak için görsel yükle. Rotayı ayarla ve haritayı kaydet.",
    "starMapEditorTitle": "YILDIZ HARİTASI OLUŞTURUCU",
    "starMapGenerated": "{count} bölüm yıldızı oluşturuldu. Haritayı ayarlamak için sürükle.",
    "starMapImageError": "Bu görsel işlenemedi.",
    "starMapLightProgress": "AYDINLATILDI {done} / {total}",
    "starMapName": "HARİTA ADI",
    "starMapPreviewInLevels": "BÖLÜM SEÇİMİNİ AÇ",
    "starMapProcessing": "Görsel analiz ediliyor...",
    "starMapRegenerate": "YILDIZLARI YENİDEN OLUŞTUR",
    "starMapRevealHint": "Orijinal görseli yavaş yavaş ortaya çıkarmak için bölümleri tamamla.",
    "starMapSaveCurrent": "MEVCUT HARİTA OLARAK KAYDET",
    "starMapSaveError": "Yıldız haritası yerel olarak kaydedilemedi.",
    "starMapSaved": "Kaydedildi. Artık mevcut bölüm seçme haritan bu.",
    "starMapUpload": "GÖRSEL YÜKLE",
    "starMapUploadEmptyHint": "İlk sürüm görsel analizi kullanır; yapay zekâ gerekmez.",
    "starMapUploadEmptyTitle": "Yıldız haritası oluşturmak için görsel yükle",
    "startError": "Oyun başlatılamadı.",
    "subtitle": "Boşluğu kır · Yıldızları yak",
    "systemMapCurrentProgress": "Sistem Haritası · {level}. bölüme ulaşıldı",
    "systemMapDescription": "Resmî senaryo, bölümler sırayla oynanır.",
    "systemMapName": "YANKI HARİTASI",
    "templateLoadFailed": "Bu görsel işlenemedi.",
    "title": "Dünyanın Yankıları",
    "totalLevels": "{count} bölüm",
    "unavailable": "Bölüm verisi henüz yok",
    "uploadImage": "Görsel yükle",
    "uploadMapImage": "Harita oluşturmak için görsel yükle",
    "uploadMapImageHint": "Yükleme ve harita oluşturma mantığı sonraki geliştirme aşamasında eklenecek.",
    "victory": "Zafer",
    "viewCompletedMap": "HARİTAYI GÖR",
    "viewOriginal": "Orijinali görüntüle",
    "workshop": "Atölye",
    "workshopComingSoon": "ÖZEL HARİTA ATÖLYESİ",
    "workshopDescription": "Oyuncuların bir görsel yükleyip onu özel bir tuğla haritasına dönüştürebileceği yaratıcı alan.",
    "workshopLevelMap": "Bölüm Tasarımı",
    "workshopStarMap": "Harita Tasarımı",
    "workshopWorkbench": "Çalışma Tezgahı",
    "workshopToolSoon": "Düzenleyici yakında",
    "workshopTools": "Harita araçları",
    "worldMap": "Dünya haritası",
    "zh": "zh",
    "skin.ball_default": "Klasik beyaz",
    "skin.ball_star": "Yıldız",
    "skin.ball_football": "Futbol topu",
    "skin.ball_flower": "Çiçek",
    "skin.ball_planet": "Gezegen",
    "skin.ball_rainbow": "Gökkuşağı şekeri",
    "skin.ball_diamond": "Elmas",
    "skin.paddle_default": "Klasik",
    "skin.paddle_stool": "Tabure",
    "skin.paddle_grass": "Çim",
    "skin.paddle_rainbow": "Gökkuşağı köprüsü",
    "skin.paddle_carpet": "Sihirli halı",
    "skin.paddle_wave": "Dalga",
    "skin.paddle_mecha": "Meka platformu",
    "imageFileRequired": "GÖRSEL YÜKLE",
    "imageReadFailed": "Görsel okunamadı.",
    "imageTooLarge": "Görsel çok büyük.",
    "imageUnsupported": "Tarayıcınız Canvas desteklemiyor.",
    "imageNoRegion": "Görselde kullanılabilir bir alan algılanmadı.",
    "mapNotFound": "Bölüm verisi henüz yok",
    "mapCategoryWorld": "Dünya haritası",
    "mapCategoryWorldShort": "Dünya",
    "mapCategoryCountry": "Ülke haritası",
    "mapCategoryCountryShort": "Ülke",
    "mapCategoryRegion": "Bölge haritası",
    "mapCategoryRegionShort": "Bölge",
    "worldClassicName": "Dünyanın klasik simge yapıları",
    "worldClassicDesc": "Dünyanın ünlü simge yapılarından oluşan keşif haritası.",
    "worldRouteName": "Dünya seyahat rotası",
    "worldRouteDesc": "Dünya çevresindeki bir seyahat rotasına göre tasarlanmış harita.",
    "worldContinentsName": "Yedi kıta keşfi",
    "worldContinentsDesc": "Yedi kıtayı keşfet.",
    "chinaClassicName": "Klasik Çin",
    "chinaClassicDesc": "Çin’in ünlü şehirleri ve simge yapıları.",
    "japanClassicName": "Klasik Japonya",
    "japanClassicDesc": "Japonya’nın öne çıkan yerleri.",
    "franceClassicName": "Klasik Fransa",
    "franceClassicDesc": "Fransa’nın şehirleri ve simge yapıları.",
    "usaClassicName": "Klasik ABD",
    "usaClassicDesc": "ABD’nin öne çıkan simge yapıları.",
    "europeClassicName": "Avrupa’nın klasik şehirleri",
    "europeClassicDesc": "Avrupa’nın seyahat şehirleri.",
    "eastAsiaName": "Doğu Asya seyahati",
    "eastAsiaDesc": "Doğu Asya şehirleri ve simge yapıları.",
    "southEastAsiaName": "Güneydoğu Asya seyahati",
    "southEastAsiaDesc": "Güneydoğu Asya boyunca bir seyahat rotası.",
    "chinaRegionName": "Çin bölgeleri",
    "chinaRegionDesc": "Çin bölgelerini temel alan keşif haritası.",
    "demoWorldDreamName": "Dünya turunun ilk durağı",
    "demoWorldDreamDesc": "Gitmek istediğin şehirleri yıldız haritasına dönüştür ve seyahat listesini adım adım aydınlat.",
    "demoJapanNightName": "Japonya seyahat planı",
    "demoJapanNightDesc": "Tokyo, Kyoto, Osaka ve Fuji Dağı üzerinden oynanabilir bir rota.",
    "demoChinaLandmarksName": "Çin'in klasik simgeleri",
    "demoChinaLandmarksDesc": "Çin Seddi'nden Xi'an, Şanghay, Chengdu ve Guangzhou'ya uzanan yolculuk.",
    "demoFranceTripName": "Fransa romantik rotası",
    "demoFranceTripDesc": "Paris, Lyon, Nice ve Strasbourg'u sakin bir tempoda keşfet.",
    "demoSevenContinentsName": "Yedi kıtayı keşfet",
    "demoSevenContinentsDesc": "Asya'dan başla ve yedi kıtayı maceranın bölümlerine dönüştür.",
    "demoUsaRoadName": "ABD yolculuğu",
    "demoUsaRoadDesc": "New York, Washington, Chicago, Los Angeles ve Büyük Kanyon üzerinden Batı Yakası'na.",
    "demoEuropeCityName": "Avrupa şehir turu",
    "demoEuropeCityDesc": "Londra, Paris, Berlin, Roma, Atina ve Barselona'yı kapsayan şehir haritası.",
    "scenicForbiddenCity": "Yasak Şehir",
    "scenicTempleOfHeaven": "Cennet Tapınağı",
    "scenicGreatWall": "Çin Seddi",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "nl": Object.freeze({
    "allFragmentsCollected": "Alle fragmenten verzameld",
    "back": "Terug",
    "backHome": "TERUG NAAR HOME",
    "backendReady": "Vue-frontend klaar voor de backend",
    "ballSkins": "Bal-skins",
    "ballSpeed": "BALSNELHEID",
    "breakableCount": "Breekbare stenen: {count}",
    "brickPalette": "Blokkenpalet",
    "builtLevels": "Gebouwd {done}/{total}",
    "levelBuilt": "Gemaakt",
    "cancel": "Annuleren",
    "cannotReadTemplate": "Deze afbeelding kan niet worden verwerkt.",
    "canvasUnsupported": "Je browser ondersteunt Canvas niet.",
    "challengeMapHint": "Kies de kaart voor de SPELEN-knop op Home. De officiële kaart is altijd beschikbaar.",
    "challengeMapTitle": "KAART KIEZEN",
    "chapter": "Hoofdstuk",
    "chapterEndFragmentNotice": "fragmenten van bezienswaardigheden.",
    "chapters": "Hoofdstukken",
    "chooseChallenge": "DEZE KAART GEBRUIKEN",
    "chooseForPlay": "DEZE KAART GEBRUIKEN",
    "chooseMapPlay": "Speel op deze kaart",
    "clearSearch": "Zoekopdracht wissen",
    "cleared": "Voltooid",
    "close": "Sluiten",
    "collectFragment": "Fragment verzamelen",
    "collectedFragments": "{count} fragmenten verzameld",
    "comingSoon": "BINNENKORT",
    "completed": "Voltooid",
    "confirm": "Bevestigen",
    "confirmDeleteMap": "‘{name}’ verwijderen? Lokale levelgegevens worden ook verwijderd.",
    "confirmOverwrite": "Genereren vanuit een afbeelding overschrijft het huidige canvas. Doorgaan?",
    "continueGame": "Doorgaan",
    "countryCA": "Canada",
    "countryCN": "China",
    "countryDE": "Duitsland",
    "countryFR": "Frankrijk",
    "countryGB": "Verenigd Koninkrijk",
    "countryJP": "Japan",
    "countryKR": "Zuid-Korea",
    "countryUS": "Verenigde Staten",
    "createLevel": "Level maken",
    "createStarMap": "Sterrenkaart maken",
    "createStarMapFirst": "Maak een sterrenkaart in de Werkplaats. Tot die tijd gebruikt SPELEN de systeemkaart.",
    "creativeWorkshop": "WERKPLAATS",
    "creativeWorkshopHint": "Aangepaste kaarten",
    "currentLevel": "HUIDIG LEVEL",
    "currentLevelFormat": "Level {level} bereikt",
    "currentProgress": "Doorgaan vanaf level {level}",
    "currentReachedLevel": "Level {level} bereikt",
    "custom": "Aangepast",
    "customMapCompleteSubtitle": "Gefeliciteerd! Je hebt elk level in {name} verlicht.",
    "customMapCompleteTitle": "KAART VOLTOOID!",
    "customMapCurrentProgress": "{name} · Level {level} bereikt",
    "customMapDescription": "Je aangepaste sterrenkaart en zelfgemaakte levels.",
    "defeat": "Nederlaag",
    "deleteAction": "Verwijderen",
    "deleteMap": "Kaart verwijderen",
    "difficulty": "MOEILIJKHEID",
    "dragNodesHint": "Upload een afbeelding om automatisch {count} levelsterren te maken. Sleep de sterren om de route af te stemmen en sla de kaart op als huidige levelselectie.",
    "editMap": "Kaart bewerken",
    "en": "en",
    "energyFull": "Vol",
    "energyInsufficient": "Niet genoeg energie: {current}/{cost}. Wacht op herstel.",
    "energyLabel": "ENERGIE",
    "energyLoading": "LADEN",
    "enterMap": "Kaart openen",
    "mapViews": "Weergaven",
    "likeMap": "Leuk vinden",
    "unlikeMap": "Vind ik niet meer leuk",
    "enterSpecialLevel": "Speciaal level openen",
    "extreme": "EXTREEM",
    "fast": "SNEL",
    "finalLevel": "Finale",
    "fr": "fr",
    "fragment": "Fragment",
    "fragmentCollected": "Fragment verzameld",
    "fragmentProgress": "Fragmentvoortgang",
    "fragments": "Fragmenten",
    "gameHelp": "A/D of ←/→ bewegen · Spatie lanceren · R opnieuw starten",
    "gameFailedTitle": "Spel mislukt",
    "retryChallenge": "Opnieuw proberen",
    "generateMap": "Kaart genereren",
    "goCreateStarMap": "STERRENKAART MAKEN",
    "hard": "MOEILIJK",
    "highScore": "Hoogste score",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Laserraket",
    "leaderboard": "RANGLIJST",
    "leaderboardFullTitle": "Volledige landenranglijst",
    "leaderboardHint": "Ranglijst",
    "leaderboardPageTitle": "🌍 WERELDWIJDE LANDENRANGLIJST",
    "leaderboardPlaceholderNote": "De ranglijstroute, store en API-laag zijn klaar. De huidige lay-out is tijdelijk en kan worden vervangen zonder de backendintegratie te wijzigen.",
    "leaderboardScore": "Score",
    "resultLevelScore": "Levelscore",
    "leaderboardCleared": "Voortgang",
    "level": "Level",
    "levelCountFormat": "{count} levels",
    "levelCountPoints": "Levels / Punten",
    "levelLocked": "Level vergrendeld",
    "levelMap": "Levelkaart",
    "levelPlaysFormat": "{count} levels · {plays} keer gespeeld",
    "levelSelect": "LEVEL KIEZEN",
    "litLevels": "Verlicht {done}/{total}",
    "loading": "Laden",
    "loadingLevel": "Level laden...",
    "loadingSharedMap": "Gedeelde kaart laden…",
    "loadingTemplate": "Afbeelding analyseren...",
    "locked": "Vergrendeld",
    "mapCompleted": "Kaart voltooid",
    "mapDescription": "Kaartbeschrijving",
    "mapDescriptionPlaceholder": "Voer kaartbeschrijving in",
    "mapFullyLit": "Kaart volledig verlicht",
    "mapName": "KAARTNAAM",
    "mapPermission": "Kaarttoegang",
    "mapTemplate": "Kaartsjabloon",
    "mapTitle": "Echo-kaart",
    "mapUnlocked": "Kaart ontgrendeld",
    "mapUpdatedSaved": "Opgeslagen. Dit is nu je huidige kaart voor levelselectie.",
    "modeGuide": "MODUSGIDS",
    "modeGuideExtreme": "Extreem: ongeveer 35% van de itemdropkans van Klassiek",
    "modeGuideHard": "Moeilijk: ongeveer 65% van de itemdropkans van Klassiek",
    "modeGuideSpeed": "Een snellere bal maakt besturing en voltooien moeilijker",
    "modeGuideStandard": "Klassiek: oorspronkelijke itemdropkans",
    "myCustomMaps": "Mijn Creaties",
    "myStarMap": "Mijn sterrenkaart",
    "myWorkshop": "Mijn werkplaats",
    "next": "Volgende",
    "noCustomMaps": "Nog geen aangepaste kaarten",
    "noCustomMapsHint": "Maak een sterrenkaart in de Werkplaats. Tot die tijd gebruikt SPELEN de systeemkaart.",
    "noCustomStarMap": "Nog geen aangepaste kaarten",
    "noMapDescription": "Geen kaartbeschrijving",
    "noMatches": "Geen resultaten",
    "normal": "NORMAAL",
    "off": "UIT",
    "on": "AAN",
    "openStarMapEditor": "KAART BEWERKEN",
    "originalImageTitle": "Originele afbeelding",
    "paddleGrow": "Peddel vergroot",
    "paddleSkins": "Paddle-skins",
    "play": "SPELEN",
    "playsLikesFormat": "{plays} keer gespeeld · {likes} likes",
    "previewHint": "Upload een afbeelding om {count} levelsterren te maken. Sleep ze om de route af te stemmen en sla de kaart op.",
    "previous": "Vorige",
    "privateMap": "Privé",
    "publicMap": "Openbaar",
    "visibilityChangedPublic": "Zichtbaarheid gewijzigd naar Openbaar.",
    "visibilityChangedPrivate": "Zichtbaarheid gewijzigd naar Privé.",
    "visibilityChangedShare": "Zichtbaarheid gewijzigd naar Alleen via deel-link.",
    "publicMaps": "Galerij",
    "publicMapsDescription": "Ontdek kaarten die door andere spelers zijn gemaakt",
    "regenerateByCount": "Trajectreconstructie",
    "regenerateNodesSuccess": "{count} levelpunten gemaakt. Klik eerst op Trajectreconstructie voordat je sleept.",
    "replayLevel": "Level opnieuw spelen",
    "restart": "Opnieuw starten",
    "result": "Resultaat",
    "returnHome": "Terug naar home",
    "runtimeBalls": "BALLEN",
    "runtimeHome": "START",
    "runtimeLives": "LEVENS",
    "runtimePaused": "GEPAUZEERD",
    "runtimeResume": "HERVATTEN",
    "runtimeTime": "TIJD",
    "save": "Opslaan",
    "saveAndBack": "OPSLAAN & TERUG",
    "saveFailed": "Opslaan mislukt",
    "saveMap": "Kaart opslaan",
    "saveMapAndCreateLevels": "OPSLAAN ALS HUIDIGE KAART",
    "saveSuccess": "Succesvol opgeslagen",
    "scenicFragment": "Bezienswaardigheidsfragment",
    "scenicFragmentObtained": "Fragment verkregen! Het wordt opgeslagen nadat het level is voltooid.",
    "scenicFragmentProgress": "Bezienswaardigheidsfragmenten {found}/{total}",
    "searchColor": "Zoek kleur of HEX-waarde",
    "searchMaps": "Kaarten zoeken",
    "selectMapTemplate": "Kaartsjabloon",
    "selectTemplateDescription": "Upload een afbeelding om automatisch {count} levelsterren te maken. Stem de route af en sla de kaart op.",
    "selectTemplateOrUpload": "AFBEELDING UPLOADEN",
    "selected": "Geselecteerd",
    "selectedChallenge": "GEKOZEN",
    "selectedMap": "GEKOZEN",
    "settings": "INSTELLINGEN",
    "settingsTitle": "SPELINSTELLINGEN",
    "share": "Delen",
    "shareAction": "Delen",
    "shareFailed": "Delen mislukt.",
    "shareInviteText": "Help mijn BricksBeater-sterrenkaart te verlichten!",
    "shareMap": "Alleen via link",
    "shareReady": "Deellink is klaar.",
    "shareSuccess": "Succesvol gedeeld",
    "shareUnavailable": "Privékaarten kunnen niet worden gedeeld. Wijzig eerst de zichtbaarheid.",
    "skill": "Vaardigheid",
    "skillCount": "Aantal vaardigheden",
    "skins": "SKINS",
    "skinsTitle": "Skinwinkel",
    "slow": "LANGZAAM",
    "sound": "GELUID",
    "specialLevel": "Speciaal level",
    "specialLevelLocked": "Speciaal level vergrendeld",
    "standard": "KLASSIEK",
    "starMap": "Sterrenkaart",
    "starMapClear": "KAART WISSEN",
    "starMapCleared": "De huidige aangepaste sterrenkaart is gewist.",
    "starMapCurrentBadge": "HUIDIGE STERRENKAART",
    "starMapDefaultName": "Voer kaartnaam in",
    "starMapEdit": "KAART BEWERKEN",
    "starMapEditorHint": "Upload een afbeelding om automatisch {count} levelsterren te maken. Stem de route af en sla de kaart op.",
    "starMapEditorTitle": "STERRENKAARTMAKER",
    "starMapGenerated": "{count} levelsterren gemaakt. Sleep ze om de kaart af te stemmen.",
    "starMapImageError": "Deze afbeelding kan niet worden verwerkt.",
    "starMapLightProgress": "VERLICHT {done} / {total}",
    "starMapName": "KAARTNAAM",
    "starMapPreviewInLevels": "LEVELSELECTIE OPENEN",
    "starMapProcessing": "Afbeelding analyseren...",
    "starMapRegenerate": "STERREN OPNIEUW MAKEN",
    "starMapRevealHint": "Voltooi levels om de oorspronkelijke afbeelding geleidelijk te onthullen.",
    "starMapSaveCurrent": "OPSLAAN ALS HUIDIGE KAART",
    "starMapSaveError": "De sterrenkaart kan niet lokaal worden opgeslagen.",
    "starMapSaved": "Opgeslagen. Dit is nu je huidige kaart voor levelselectie.",
    "starMapUpload": "AFBEELDING UPLOADEN",
    "starMapUploadEmptyHint": "De eerste versie gebruikt beeldanalyse en heeft geen AI nodig.",
    "starMapUploadEmptyTitle": "Upload een afbeelding om een sterrenkaart te maken",
    "startError": "Kan het spel niet starten.",
    "subtitle": "Breek de leegte · Laat de sterren stralen",
    "systemMapCurrentProgress": "Systeemkaart · Level {level} bereikt",
    "systemMapDescription": "Officiële campagne, level voor level.",
    "systemMapName": "ECHO-KAART",
    "templateLoadFailed": "Deze afbeelding kan niet worden verwerkt.",
    "title": "Echo's van de Aarde",
    "totalLevels": "{count} levels",
    "unavailable": "Levelgegevens niet beschikbaar",
    "uploadImage": "Afbeelding uploaden",
    "uploadMapImage": "Upload een afbeelding om een kaart te maken",
    "uploadMapImageHint": "Upload- en kaartgeneratielogica wordt in een latere ontwikkelfase toegevoegd.",
    "victory": "Overwinning",
    "viewCompletedMap": "KAART BEKIJKEN",
    "viewOriginal": "Origineel bekijken",
    "workshop": "Werkplaats",
    "workshopComingSoon": "WERKPLAATS VOOR AANGEPASTE KAARTEN",
    "workshopDescription": "Een creatieve ruimte waar spelers een afbeelding kunnen uploaden en omzetten in een aangepaste bakstenen kaart.",
    "workshopLevelMap": "Levelontwerp",
    "workshopStarMap": "Kaartontwerp",
    "workshopWorkbench": "Werkbank",
    "workshopToolSoon": "Editor binnenkort beschikbaar",
    "workshopTools": "Kaarttools",
    "worldMap": "Wereldkaart",
    "zh": "zh",
    "skin.ball_default": "Klassiek wit",
    "skin.ball_star": "Ster",
    "skin.ball_football": "Voetbal",
    "skin.ball_flower": "Bloem",
    "skin.ball_planet": "Planeet",
    "skin.ball_rainbow": "Regenboogsnoep",
    "skin.ball_diamond": "Diamant",
    "skin.paddle_default": "Klassiek",
    "skin.paddle_stool": "Kruk",
    "skin.paddle_grass": "Gras",
    "skin.paddle_rainbow": "Regenboogbrug",
    "skin.paddle_carpet": "Vliegend tapijt",
    "skin.paddle_wave": "Golf",
    "skin.paddle_mecha": "Mecha-platform",
    "imageFileRequired": "AFBEELDING UPLOADEN",
    "imageReadFailed": "De afbeelding kan niet worden gelezen.",
    "imageTooLarge": "De afbeelding is te groot.",
    "imageUnsupported": "Je browser ondersteunt Canvas niet.",
    "imageNoRegion": "Er is geen bruikbaar gebied in de afbeelding gevonden.",
    "mapNotFound": "Levelgegevens niet beschikbaar",
    "mapCategoryWorld": "Wereldkaart",
    "mapCategoryWorldShort": "Wereld",
    "mapCategoryCountry": "Landenkaart",
    "mapCategoryCountryShort": "Land",
    "mapCategoryRegion": "Regiokaart",
    "mapCategoryRegionShort": "Regio",
    "worldClassicName": "Klassieke wereldbezienswaardigheden",
    "worldClassicDesc": "Een ontdekkingskaart met beroemde bezienswaardigheden uit de hele wereld.",
    "worldRouteName": "Wereldreisroute",
    "worldRouteDesc": "Een kaart ontworpen als reisroute rond de wereld.",
    "worldContinentsName": "Verkenning van zeven continenten",
    "worldContinentsDesc": "Verken de zeven continenten.",
    "chinaClassicName": "Klassiek China",
    "chinaClassicDesc": "Beroemde Chinese steden en bezienswaardigheden.",
    "japanClassicName": "Klassiek Japan",
    "japanClassicDesc": "Kenmerkende locaties in Japan.",
    "franceClassicName": "Klassiek Frankrijk",
    "franceClassicDesc": "Franse steden en bezienswaardigheden.",
    "usaClassicName": "Klassieke Verenigde Staten",
    "usaClassicDesc": "Kenmerkende bezienswaardigheden van de Verenigde Staten.",
    "europeClassicName": "Klassieke steden van Europa",
    "europeClassicDesc": "Europese reissteden om te verkennen.",
    "eastAsiaName": "Reis door Oost-Azië",
    "eastAsiaDesc": "Steden en bezienswaardigheden in Oost-Azië.",
    "southEastAsiaName": "Reis door Zuidoost-Azië",
    "southEastAsiaDesc": "Een reisroute door Zuidoost-Azië.",
    "chinaRegionName": "Regio’s van China",
    "chinaRegionDesc": "Een thematische ontdekkingskaart van de regio’s van China.",
    "demoWorldDreamName": "Eerste halte rond de wereld",
    "demoWorldDreamDesc": "Maak van je droomsteden een sterrenkaart en verlicht je reisroute stap voor stap.",
    "demoJapanNightName": "Reisplan door Japan",
    "demoJapanNightDesc": "Een speelbare route langs Tokio, Kyoto, Osaka en de berg Fuji.",
    "demoChinaLandmarksName": "Klassieke bezienswaardigheden van China",
    "demoChinaLandmarksDesc": "Van de Chinese Muur naar Xi'an, Shanghai, Chengdu en Guangzhou.",
    "demoFranceTripName": "Romantische route door Frankrijk",
    "demoFranceTripDesc": "Parijs, Lyon, Nice en Straatsburg op een ontspannen reiskaart.",
    "demoSevenContinentsName": "Ontdek de zeven continenten",
    "demoSevenContinentsDesc": "Begin in Azië en maak van alle zeven continenten etappes van je avontuur.",
    "demoUsaRoadName": "Roadtrip door de VS",
    "demoUsaRoadDesc": "New York, Washington, Chicago, Los Angeles en de Grand Canyon tot aan de westkust.",
    "demoEuropeCityName": "Stedentrip door Europa",
    "demoEuropeCityDesc": "Een stedenkaart langs Londen, Parijs, Berlijn, Rome, Athene en Barcelona.",
    "scenicForbiddenCity": "Verboden Stad",
    "scenicTempleOfHeaven": "Tempel van de Hemel",
    "scenicGreatWall": "Chinese Muur",
    "scenicZhangjiajie": "Zhangjiajie"
  }),
  "pl": Object.freeze({
    "allFragmentsCollected": "Zebrano wszystkie fragmenty",
    "back": "Wstecz",
    "backHome": "WRÓĆ DO MENU",
    "backendReady": "Frontend Vue gotowy do współpracy z backendem",
    "ballSkins": "Skórki piłki",
    "ballSpeed": "PRĘDKOŚĆ PIŁKI",
    "breakableCount": "Zniszczalne cegły: {count}",
    "brickPalette": "Paleta bloków",
    "builtLevels": "Utworzono {done}/{total}",
    "levelBuilt": "Utworzono",
    "cancel": "Anuluj",
    "cannotReadTemplate": "Nie można przetworzyć tego obrazu.",
    "canvasUnsupported": "Twoja przeglądarka nie obsługuje Canvas.",
    "challengeMapHint": "Wybierz mapę dla przycisku GRAJ na stronie głównej. Oficjalna mapa jest zawsze dostępna.",
    "challengeMapTitle": "WYBIERZ MAPĘ",
    "chapter": "Rozdział",
    "chapterEndFragmentNotice": "fragmentów atrakcji.",
    "chapters": "Rozdziały",
    "chooseChallenge": "UŻYJ TEJ MAPY",
    "chooseForPlay": "UŻYJ TEJ MAPY",
    "chooseMapPlay": "Graj na tej mapie",
    "clearSearch": "Wyczyść wyszukiwanie",
    "cleared": "Ukończono",
    "close": "Zamknij",
    "collectFragment": "Zbierz fragment",
    "collectedFragments": "Zebrano fragmentów: {count}",
    "comingSoon": "WKRÓTCE",
    "completed": "Ukończono",
    "confirm": "Potwierdź",
    "confirmDeleteMap": "Usunąć „{name}”? Lokalne dane poziomów również zostaną usunięte.",
    "confirmOverwrite": "Generowanie z obrazu nadpisze bieżące płótno. Kontynuować?",
    "continueGame": "Kontynuuj",
    "countryCA": "Kanada",
    "countryCN": "Chiny",
    "countryDE": "Niemcy",
    "countryFR": "Francja",
    "countryGB": "Wielka Brytania",
    "countryJP": "Japonia",
    "countryKR": "Korea Południowa",
    "countryUS": "Stany Zjednoczone",
    "createLevel": "Utwórz poziom",
    "createStarMap": "Utwórz mapę gwiazd",
    "createStarMapFirst": "Utwórz mapę gwiazd w Warsztacie. Do tego czasu przycisk GRAJ będzie używać mapy systemowej.",
    "creativeWorkshop": "WARSZTAT",
    "creativeWorkshopHint": "Mapy niestandardowe",
    "currentLevel": "BIEŻĄCY POZIOM",
    "currentLevelFormat": "Osiągnięto poziom {level}",
    "currentProgress": "Kontynuuj od poziomu {level}",
    "currentReachedLevel": "Osiągnięto poziom {level}",
    "custom": "Niestandardowy",
    "customMapCompleteSubtitle": "Gratulacje! Ukończono wszystkie poziomy na mapie {name}.",
    "customMapCompleteTitle": "MAPA UKOŃCZONA!",
    "customMapCurrentProgress": "{name} · Osiągnięto poziom {level}",
    "customMapDescription": "Twoja niestandardowa mapa gwiazd i utworzone przez Ciebie poziomy.",
    "defeat": "Porażka",
    "deleteAction": "Usuń",
    "deleteMap": "Usuń mapę",
    "difficulty": "TRUDNOŚĆ",
    "dragNodesHint": "Prześlij obraz, aby automatycznie utworzyć {count} gwiazd poziomów. Przeciągaj gwiazdy, aby dostroić trasę, a następnie zapisz mapę jako bieżącą mapę wyboru poziomów.",
    "editMap": "Edytuj mapę",
    "en": "en",
    "energyFull": "Pełna",
    "energyInsufficient": "Za mało energii: {current}/{cost}. Poczekaj na regenerację.",
    "energyLabel": "ENERGIA",
    "energyLoading": "ŁADOWANIE",
    "enterMap": "Wejdź na mapę",
    "mapViews": "Wyświetlenia",
    "likeMap": "Lubię to",
    "unlikeMap": "Cofnij polubienie",
    "enterSpecialLevel": "Wejdź do poziomu specjalnego",
    "extreme": "EKSTREMALNY",
    "fast": "SZYBKA",
    "finalLevel": "Finał",
    "fr": "fr",
    "fragment": "Fragment",
    "fragmentCollected": "Fragment zebrany",
    "fragmentProgress": "Postęp fragmentów",
    "fragments": "Fragmenty",
    "gameHelp": "A/D lub ←/→ ruch · Spacja start · R restart",
    "gameFailedTitle": "Gra przegrana",
    "retryChallenge": "Spróbuj ponownie",
    "generateMap": "Generuj mapę",
    "goCreateStarMap": "UTWÓRZ MAPĘ GWIAZD",
    "hard": "TRUDNY",
    "highScore": "Najlepszy wynik",
    "ja": "ja",
    "ko": "ko",
    "laserMissile": "Pocisk laserowy",
    "leaderboard": "RANKING",
    "leaderboardFullTitle": "Pełny ranking krajów",
    "leaderboardHint": "Ranking",
    "leaderboardPageTitle": "🌍 GLOBALNY RANKING KRAJÓW",
    "leaderboardPlaceholderNote": "Trasa rankingu, magazyn stanu i warstwa API są gotowe. Obecny układ jest tymczasowy i można go zastąpić bez zmian w integracji z backendem.",
    "leaderboardScore": "Wynik",
    "resultLevelScore": "Wynik poziomu",
    "leaderboardCleared": "Postęp",
    "level": "Poziom",
    "levelCountFormat": "Liczba poziomów: {count}",
    "levelCountPoints": "Poziomy / Punkty",
    "levelLocked": "Poziom zablokowany",
    "levelMap": "Mapa poziomów",
    "levelPlaysFormat": "Poziomów: {count} · rozegrano: {plays}",
    "levelSelect": "WYBÓR POZIOMU",
    "litLevels": "Oświetlono {done}/{total}",
    "loading": "Ładowanie",
    "loadingLevel": "Ładowanie poziomu...",
    "loadingSharedMap": "Ładowanie udostępnionej mapy…",
    "loadingTemplate": "Analizowanie obrazu...",
    "locked": "Zablokowany",
    "mapCompleted": "Mapa ukończona",
    "mapDescription": "Opis mapy",
    "mapDescriptionPlaceholder": "Wpisz opis mapy",
    "mapFullyLit": "Mapa w pełni rozświetlona",
    "mapName": "NAZWA MAPY",
    "mapPermission": "Uprawnienia mapy",
    "mapTemplate": "Szablon mapy",
    "mapTitle": "Mapa Echa",
    "mapUnlocked": "Mapa odblokowana",
    "mapUpdatedSaved": "Zapisano. To jest teraz bieżąca mapa wyboru poziomów.",
    "modeGuide": "PRZEWODNIK PO TRYBACH",
    "modeGuideExtreme": "Ekstremalny: około 35% częstotliwości wypadania przedmiotów z trybu Klasycznego",
    "modeGuideHard": "Trudny: około 65% częstotliwości wypadania przedmiotów z trybu Klasycznego",
    "modeGuideSpeed": "Szybsza piłka zwiększa trudność sterowania i ukończenia poziomu",
    "modeGuideStandard": "Klasyczny: oryginalna częstotliwość wypadania przedmiotów",
    "myCustomMaps": "Moje Prace",
    "myStarMap": "Moja mapa gwiazd",
    "myWorkshop": "Mój warsztat",
    "next": "Dalej",
    "noCustomMaps": "Brak map niestandardowych",
    "noCustomMapsHint": "Utwórz mapę gwiazd w Warsztacie. Do tego czasu przycisk GRAJ będzie używać mapy systemowej.",
    "noCustomStarMap": "Brak map niestandardowych",
    "noMapDescription": "Brak opisu mapy",
    "noMatches": "Brak wyników",
    "normal": "NORMALNA",
    "off": "WYŁ.",
    "on": "WŁ.",
    "openStarMapEditor": "EDYTUJ MAPĘ",
    "originalImageTitle": "Oryginalny obraz",
    "paddleGrow": "Powiększona paletka",
    "paddleSkins": "Skórki platformy",
    "play": "GRAJ",
    "playsLikesFormat": "Rozegrano: {plays} · polubienia: {likes}",
    "previewHint": "Prześlij obraz, aby utworzyć {count} gwiazd poziomów. Przeciągaj je, aby dostroić trasę, a następnie zapisz mapę.",
    "previous": "Wstecz",
    "privateMap": "Prywatna",
    "publicMap": "Publiczna",
    "visibilityChangedPublic": "Widoczność zmieniono na Publiczną.",
    "visibilityChangedPrivate": "Widoczność zmieniono na Prywatną.",
    "visibilityChangedShare": "Widoczność zmieniono na Tylko przez link.",
    "publicMaps": "Galeria",
    "publicMapsDescription": "Odkrywaj mapy stworzone przez innych graczy",
    "regenerateByCount": "Przebudowa Trasy",
    "regenerateNodesSuccess": "Utworzono {count} punktów. Przed przeciąganiem kliknij Przebuduj trasę.",
    "replayLevel": "Zagraj poziom ponownie",
    "restart": "Uruchom ponownie",
    "result": "Wynik",
    "returnHome": "Wróć do strony głównej",
    "runtimeBalls": "PIŁKI",
    "runtimeHome": "START",
    "runtimeLives": "ŻYCIA",
    "runtimePaused": "PAUZA",
    "runtimeResume": "WZNÓW",
    "runtimeTime": "CZAS",
    "save": "Zapisz",
    "saveAndBack": "ZAPISZ I WRÓĆ",
    "saveFailed": "Nie udało się zapisać",
    "saveMap": "Zapisz mapę",
    "saveMapAndCreateLevels": "ZAPISZ JAKO BIEŻĄCĄ MAPĘ",
    "saveSuccess": "Zapisano pomyślnie",
    "scenicFragment": "Fragment atrakcji",
    "scenicFragmentObtained": "Zdobyto fragment! Zostanie zapisany po ukończeniu poziomu.",
    "scenicFragmentProgress": "Fragmenty atrakcji {found}/{total}",
    "searchColor": "Szukaj koloru lub wartości HEX",
    "searchMaps": "Szukaj map",
    "selectMapTemplate": "Szablon mapy",
    "selectTemplateDescription": "Prześlij obraz, aby automatycznie utworzyć {count} gwiazd poziomów. Dostosuj trasę i zapisz mapę.",
    "selectTemplateOrUpload": "PRZEŚLIJ OBRAZ",
    "selected": "Wybrano",
    "selectedChallenge": "WYBRANO",
    "selectedMap": "WYBRANO",
    "settings": "USTAWIENIA",
    "settingsTitle": "USTAWIENIA GRY",
    "share": "Udostępnij",
    "shareAction": "Udostępnij",
    "shareFailed": "Nie udało się udostępnić.",
    "shareInviteText": "Pomóż rozświetlić moją mapę gwiezdną BricksBeater!",
    "shareMap": "Tylko przez link",
    "shareReady": "Link do udostępnienia jest gotowy.",
    "shareSuccess": "Udostępniono pomyślnie",
    "shareUnavailable": "Prywatnych map nie można udostępniać. Najpierw zmień widoczność.",
    "skill": "Umiejętność",
    "skillCount": "Liczba umiejętności",
    "skins": "SKÓRKI",
    "skinsTitle": "Sklep ze skórkami",
    "slow": "WOLNA",
    "sound": "DŹWIĘK",
    "specialLevel": "Poziom specjalny",
    "specialLevelLocked": "Poziom specjalny zablokowany",
    "standard": "KLASYCZNY",
    "starMap": "Mapa gwiazd",
    "starMapClear": "WYCZYŚĆ MAPĘ",
    "starMapCleared": "Bieżąca niestandardowa mapa gwiazd została wyczyszczona.",
    "starMapCurrentBadge": "BIEŻĄCA MAPA GWIAZD",
    "starMapDefaultName": "Wpisz nazwę mapy",
    "starMapEdit": "EDYTUJ MAPĘ",
    "starMapEditorHint": "Prześlij obraz, aby automatycznie utworzyć {count} gwiazd poziomów. Dostosuj trasę i zapisz mapę.",
    "starMapEditorTitle": "KREATOR MAPY GWIAZD",
    "starMapGenerated": "Utworzono {count} gwiazd poziomów. Przeciągaj je, aby dostroić mapę.",
    "starMapImageError": "Nie można przetworzyć tego obrazu.",
    "starMapLightProgress": "OŚWIETLONO {done} / {total}",
    "starMapName": "NAZWA MAPY",
    "starMapPreviewInLevels": "OTWÓRZ WYBÓR POZIOMU",
    "starMapProcessing": "Analizowanie obrazu...",
    "starMapRegenerate": "UTWÓRZ GWIAZDY PONOWNIE",
    "starMapRevealHint": "Ukończ poziomy, aby stopniowo odsłaniać oryginalny obraz.",
    "starMapSaveCurrent": "ZAPISZ JAKO BIEŻĄCĄ MAPĘ",
    "starMapSaveError": "Nie można zapisać mapy gwiazd lokalnie.",
    "starMapSaved": "Zapisano. To jest teraz bieżąca mapa wyboru poziomów.",
    "starMapUpload": "PRZEŚLIJ OBRAZ",
    "starMapUploadEmptyHint": "Pierwsza wersja korzysta z analizy obrazu i nie wymaga AI.",
    "starMapUploadEmptyTitle": "Prześlij obraz, aby utworzyć mapę gwiazd",
    "startError": "Nie można uruchomić gry.",
    "subtitle": "Rozbij pustkę · Rozświetl gwiazdy",
    "systemMapCurrentProgress": "Mapa systemowa · Osiągnięto poziom {level}",
    "systemMapDescription": "Oficjalna kampania, poziom po poziomie.",
    "systemMapName": "MAPA ECHA",
    "templateLoadFailed": "Nie można przetworzyć tego obrazu.",
    "title": "Echa Ziemi",
    "totalLevels": "{count} poziomów",
    "unavailable": "Dane poziomu są niedostępne",
    "uploadImage": "Prześlij obraz",
    "uploadMapImage": "Prześlij obraz, aby utworzyć mapę",
    "uploadMapImageHint": "Logika przesyłania i generowania map zostanie dodana na późniejszym etapie rozwoju.",
    "victory": "Zwycięstwo",
    "viewCompletedMap": "ZOBACZ MAPĘ",
    "viewOriginal": "Zobacz oryginał",
    "workshop": "Warsztat",
    "workshopComingSoon": "WARSZTAT MAP NIESTANDARDOWYCH",
    "workshopDescription": "Przestrzeń kreatywna, w której gracze mogą przesłać obraz i zmienić go w niestandardową mapę cegieł.",
    "workshopLevelMap": "Projekt Poziomu",
    "workshopStarMap": "Projekt Mapy",
    "workshopWorkbench": "Stół Roboczy",
    "workshopToolSoon": "Edytor już wkrótce",
    "workshopTools": "Narzędzia mapy",
    "worldMap": "Mapa świata",
    "zh": "zh",
    "skin.ball_default": "Klasyczna biała",
    "skin.ball_star": "Gwiazda",
    "skin.ball_football": "Piłka nożna",
    "skin.ball_flower": "Kwiat",
    "skin.ball_planet": "Planeta",
    "skin.ball_rainbow": "Tęczowy cukierek",
    "skin.ball_diamond": "Diament",
    "skin.paddle_default": "Klasyczna",
    "skin.paddle_stool": "Stołek",
    "skin.paddle_grass": "Trawa",
    "skin.paddle_rainbow": "Tęczowy most",
    "skin.paddle_carpet": "Magiczny dywan",
    "skin.paddle_wave": "Fala",
    "skin.paddle_mecha": "Platforma mecha",
    "imageFileRequired": "PRZEŚLIJ OBRAZ",
    "imageReadFailed": "Nie można odczytać obrazu.",
    "imageTooLarge": "Obraz jest zbyt duży.",
    "imageUnsupported": "Twoja przeglądarka nie obsługuje Canvas.",
    "imageNoRegion": "Nie wykryto użytecznego obszaru na obrazie.",
    "mapNotFound": "Dane poziomu są niedostępne",
    "mapCategoryWorld": "Mapa świata",
    "mapCategoryWorldShort": "Świat",
    "mapCategoryCountry": "Mapa kraju",
    "mapCategoryCountryShort": "Kraj",
    "mapCategoryRegion": "Mapa regionu",
    "mapCategoryRegionShort": "Region",
    "worldClassicName": "Klasyczne atrakcje świata",
    "worldClassicDesc": "Mapa odkrywania złożona ze słynnych atrakcji z całego świata.",
    "worldRouteName": "Trasa podróży dookoła świata",
    "worldRouteDesc": "Mapa zaprojektowana jako trasa podróży dookoła świata.",
    "worldContinentsName": "Odkrywanie siedmiu kontynentów",
    "worldContinentsDesc": "Odkrywaj siedem kontynentów.",
    "chinaClassicName": "Klasyczne Chiny",
    "chinaClassicDesc": "Słynne miasta i atrakcje Chin.",
    "japanClassicName": "Klasyczna Japonia",
    "japanClassicDesc": "Charakterystyczne miejsca Japonii.",
    "franceClassicName": "Klasyczna Francja",
    "franceClassicDesc": "Miasta i atrakcje Francji.",
    "usaClassicName": "Klasyczne USA",
    "usaClassicDesc": "Charakterystyczne atrakcje Stanów Zjednoczonych.",
    "europeClassicName": "Klasyczne miasta Europy",
    "europeClassicDesc": "Europejskie miasta turystyczne.",
    "eastAsiaName": "Podróż po Azji Wschodniej",
    "eastAsiaDesc": "Miasta i atrakcje Azji Wschodniej.",
    "southEastAsiaName": "Podróż po Azji Południowo-Wschodniej",
    "southEastAsiaDesc": "Trasa podróży przez Azję Południowo-Wschodnią.",
    "chinaRegionName": "Regiony Chin",
    "chinaRegionDesc": "Tematyczna mapa odkrywania regionów Chin.",
    "demoWorldDreamName": "Pierwszy przystanek dookoła świata",
    "demoWorldDreamDesc": "Zamień wymarzone miasta w mapę gwiazd i rozświetlaj swoją listę podróży etap po etapie.",
    "demoJapanNightName": "Plan podróży po Japonii",
    "demoJapanNightDesc": "Grywalna trasa przez Tokio, Kioto, Osakę i górę Fuji.",
    "demoChinaLandmarksName": "Klasyczne zabytki Chin",
    "demoChinaLandmarksDesc": "Od Wielkiego Muru przez Xi'an, Szanghaj, Chengdu i Guangzhou.",
    "demoFranceTripName": "Romantyczna trasa po Francji",
    "demoFranceTripDesc": "Paryż, Lyon, Nicea i Strasburg na spokojnej mapie podróży.",
    "demoSevenContinentsName": "Odkrywanie siedmiu kontynentów",
    "demoSevenContinentsDesc": "Wyrusz z Azji i zamień siedem kontynentów w etapy swojej przygody.",
    "demoUsaRoadName": "Podróż samochodem po USA",
    "demoUsaRoadDesc": "Nowy Jork, Waszyngton, Chicago, Los Angeles i Wielki Kanion aż po zachodnie wybrzeże.",
    "demoEuropeCityName": "Podróż po miastach Europy",
    "demoEuropeCityDesc": "Mapa miast: Londyn, Paryż, Berlin, Rzym, Ateny i Barcelona.",
    "scenicForbiddenCity": "Zakazane Miasto",
    "scenicTempleOfHeaven": "Świątynia Nieba",
    "scenicGreatWall": "Wielki Mur Chiński",
    "scenicZhangjiajie": "Zhangjiajie"
  })
});


const SYSTEM_CONTENT = Object.freeze({
  "en": {
    "templateCategory.world": "World Map",
    "templateCategory.country": "Country Map",
    "templateCategory.region": "Regional Map",
    "template.worldClassic.name": "World Landmarks",
    "template.worldClassic.description": "A world star map built from landmarks in New York, Paris, Cairo, Beijing, Tokyo and Sydney.",
    "template.worldRoute.name": "World Travel Route",
    "template.worldRoute.description": "Nodes arranged along a round-the-world travel route for travel-themed levels.",
    "template.worldContinents.name": "Seven Continents",
    "template.worldContinents.description": "A world exploration map organized by the seven continents.",
    "template.chinaClassic.name": "Classic China",
    "template.chinaClassic.description": "A China-themed star map featuring the Great Wall, Xi'an, Shanghai, Chengdu and Guangzhou.",
    "template.japanClassic.name": "Classic Japan",
    "template.japanClassic.description": "A Japan-themed map featuring Mount Fuji, Tokyo, Kyoto, Osaka and Hokkaido.",
    "template.franceClassic.name": "Classic France",
    "template.franceClassic.description": "A France-themed map featuring Paris, Lyon, Nice and Strasbourg.",
    "template.usaClassic.name": "Classic USA",
    "template.usaClassic.description": "A United States-themed map featuring New York, Washington, Chicago, Los Angeles and the Grand Canyon.",
    "template.europeClassic.name": "Classic European Cities",
    "template.europeClassic.description": "A European travel map connecting London, Paris, Berlin, Rome, Athens and Barcelona.",
    "template.eastAsia.name": "East Asia Journey",
    "template.eastAsia.description": "A route through Beijing, Shanghai, Seoul, Tokyo and Taipei.",
    "template.southeastAsia.name": "Southeast Asia Journey",
    "template.southeastAsia.description": "A route through Bangkok, Hanoi, Singapore, Kuala Lumpur and Bali.",
    "template.chinaRegions.name": "Explore China Regions",
    "template.chinaRegions.description": "A domestic exploration route through Beijing, Xi'an, Chengdu, Hangzhou, Guangzhou and Shandong."
  },
  "zh-CN": {
    "templateCategory.world": "世界地图",
    "templateCategory.country": "国家地图",
    "templateCategory.region": "地区地图",
    "template.worldClassic.name": "世界经典地标",
    "template.worldClassic.description": "用纽约、巴黎、开罗、北京、东京、悉尼等世界地标组成的环球星图。",
    "template.worldRoute.name": "环球旅行路线",
    "template.worldRoute.description": "沿环球旅行线路布置节点，适合制作世界旅行主题关卡。",
    "template.worldContinents.name": "七大洲探索",
    "template.worldContinents.description": "按七大洲区域组织的世界探索地图。",
    "template.chinaClassic.name": "中国经典",
    "template.chinaClassic.description": "长城、西安、上海、成都、广州等代表地点组成的中国主题星图。",
    "template.japanClassic.name": "日本经典",
    "template.japanClassic.description": "富士山、东京、京都、大阪和北海道等日本代表地点。",
    "template.franceClassic.name": "法国经典",
    "template.franceClassic.description": "巴黎、里昂、尼斯、斯特拉斯堡等法国城市与地标。",
    "template.usaClassic.name": "美国经典",
    "template.usaClassic.description": "纽约、华盛顿、芝加哥、洛杉矶和大峡谷等美国代表地点。",
    "template.europeClassic.name": "欧洲经典城市",
    "template.europeClassic.description": "伦敦、巴黎、柏林、罗马、雅典、巴塞罗那组成的欧洲旅行地图。",
    "template.eastAsia.name": "东亚旅行",
    "template.eastAsia.description": "北京、上海、首尔、东京、台北等东亚城市节点。",
    "template.southeastAsia.name": "东南亚旅行",
    "template.southeastAsia.description": "曼谷、河内、新加坡、吉隆坡、巴厘岛等东南亚旅行节点。",
    "template.chinaRegions.name": "中国地区探索",
    "template.chinaRegions.description": "北京、西安、成都、杭州、广州、山东等地区组成的国内探索地图。"
  },
  "zh-TW": {
    "templateCategory.world": "世界地圖",
    "templateCategory.country": "國家地圖",
    "templateCategory.region": "地區地圖",
    "template.worldClassic.name": "世界經典地標",
    "template.worldClassic.description": "以紐約、巴黎、開羅、北京、東京、雪梨等世界地標組成的環球星圖。",
    "template.worldRoute.name": "環球旅行路線",
    "template.worldRoute.description": "沿環球旅行路線配置節點，適合製作世界旅行主題關卡。",
    "template.worldContinents.name": "七大洲探索",
    "template.worldContinents.description": "依七大洲區域組織的世界探索地圖。",
    "template.chinaClassic.name": "中國經典",
    "template.chinaClassic.description": "由長城、西安、上海、成都、廣州等代表地點組成的中國主題星圖。",
    "template.japanClassic.name": "日本經典",
    "template.japanClassic.description": "富士山、東京、京都、大阪與北海道等日本代表地點。",
    "template.franceClassic.name": "法國經典",
    "template.franceClassic.description": "巴黎、里昂、尼斯、史特拉斯堡等法國城市與地標。",
    "template.usaClassic.name": "美國經典",
    "template.usaClassic.description": "紐約、華盛頓、芝加哥、洛杉磯與大峽谷等美國代表地點。",
    "template.europeClassic.name": "歐洲經典城市",
    "template.europeClassic.description": "倫敦、巴黎、柏林、羅馬、雅典、巴塞隆納組成的歐洲旅行地圖。",
    "template.eastAsia.name": "東亞旅行",
    "template.eastAsia.description": "北京、上海、首爾、東京、台北等東亞城市節點。",
    "template.southeastAsia.name": "東南亞旅行",
    "template.southeastAsia.description": "曼谷、河內、新加坡、吉隆坡、峇里島等東南亞旅行節點。",
    "template.chinaRegions.name": "中國地區探索",
    "template.chinaRegions.description": "北京、西安、成都、杭州、廣州、山東等地區組成的國內探索地圖。"
  }
});

const SKIN_KEY_BY_ID = Object.freeze({
  "ball_default": "skin.ball_default",
  "ball_star": "skin.ball_star",
  "ball_football": "skin.ball_football",
  "ball_flower": "skin.ball_flower",
  "ball_planet": "skin.ball_planet",
  "ball_rainbow": "skin.ball_rainbow",
  "ball_diamond": "skin.ball_diamond",
  "paddle_default": "skin.paddle_default",
  "paddle_stool": "skin.paddle_stool",
  "paddle_grass": "skin.paddle_grass",
  "paddle_rainbow": "skin.paddle_rainbow",
  "paddle_carpet": "skin.paddle_carpet",
  "paddle_wave": "skin.paddle_wave",
  "paddle_mecha": "skin.paddle_mecha",
});


export function normalizeLanguage(value) {
  const raw = String(value || "").trim();
  if (raw === "zh") return "zh-CN";
  return SUPPORTED_LANGUAGES.includes(raw) ? raw : DEFAULT_LANGUAGE;
}

export function getLanguageOptions() { return LANGUAGE_OPTIONS; }
export function getHtmlLanguage(language) { return HTML_LANGUAGE[normalizeLanguage(language)] || DEFAULT_LANGUAGE; }
export function getLanguageButtonLabel(language) {
  return LANGUAGE_OPTIONS.find(option => option.code === normalizeLanguage(language))?.shortLabel || "EN";
}

function interpolate(text, params = {}) {
  let output = String(text);
  for (const [name, value] of Object.entries(params)) {
    output = output.replaceAll(`{${name}}`, String(value));
  }
  return output;
}

const SAFETY_MESSAGES = Object.freeze({
  "zh-TW": { reportMap:"檢舉", reportSubmitted:"檢舉已提交，感謝你協助維護社群安全。", alreadyReported:"你已經檢舉過這張地圖。", moderation_pending:"等待安全審核", moderation_approved:"安全審核已通過", moderation_rejected:"安全審核未通過", moderation_hidden:"因檢舉暫時隱藏", contentModeration:"內容審核", moderatorOnly:"只有審核員可以進入此頁面。", moderationQueueEmpty:"目前審核佇列中沒有作品。", moderationReasonPrompt:"填寫審核原因（選填）", reportPrompt:"請選擇最主要的檢舉原因。檢舉資訊將會保密。", selectReportReason:"請選擇檢舉原因。", reportDetails:"補充說明（選填，最多300字）", submitReport:"提交檢舉", reportReason_sexual:"色情或裸露內容", reportReason_violence:"暴力或危險行為", reportReason_hate:"仇恨或騷擾", reportReason_illegal:"違法商品或活動", reportReason_spam:"垃圾廣告或誤導內容", reportReason_copyright:"侵犯版權", reportReason_other:"其他不適當內容", reportCount:"收到 {count} 次檢舉", approve:"通過", reject:"拒絕", hide:"隱藏", refresh:"重新整理" },
  ja: { reportMap:"報告", reportSubmitted:"報告を送信しました。コミュニティの安全へのご協力ありがとうございます。", alreadyReported:"このマップはすでに報告済みです。", moderation_pending:"安全審査待ち", moderation_approved:"安全審査通過", moderation_rejected:"安全審査不合格", moderation_hidden:"報告により非表示", contentModeration:"コンテンツ審査", moderatorOnly:"モデレーターのみ利用できます。", moderationQueueEmpty:"この審査キューに作品はありません。", moderationReasonPrompt:"審査理由を入力（任意）", reportPrompt:"主な報告理由を選択してください。報告者の情報は非公開です。", selectReportReason:"報告理由を選択してください。", reportDetails:"詳細（任意、300文字以内）", submitReport:"報告を送信", reportReason_sexual:"性的または裸体のコンテンツ", reportReason_violence:"暴力または危険行為", reportReason_hate:"ヘイトまたは嫌がらせ", reportReason_illegal:"違法な商品または活動", reportReason_spam:"スパムまたは誤解を招く内容", reportReason_copyright:"著作権侵害", reportReason_other:"その他の不適切な内容", reportCount:"報告 {count} 件", approve:"承認", reject:"却下", hide:"非表示", refresh:"更新" },
  ko: { reportMap:"신고", reportSubmitted:"신고가 제출되었습니다. 커뮤니티 안전에 협조해 주셔서 감사합니다.", alreadyReported:"이미 신고한 지도입니다.", moderation_pending:"안전 검토 대기", moderation_approved:"안전 검토 통과", moderation_rejected:"안전 검토 거부", moderation_hidden:"신고로 숨김", contentModeration:"콘텐츠 검토", moderatorOnly:"검토자만 이용할 수 있습니다.", moderationQueueEmpty:"이 검토 대기열에 작품이 없습니다.", moderationReasonPrompt:"검토 사유 입력(선택)", reportPrompt:"주요 신고 사유를 선택하세요. 신고자 정보는 공개되지 않습니다.", selectReportReason:"신고 사유를 선택하세요.", reportDetails:"추가 설명(선택, 최대 300자)", submitReport:"신고 제출", reportReason_sexual:"성적 또는 노출 콘텐츠", reportReason_violence:"폭력 또는 위험 행동", reportReason_hate:"혐오 또는 괴롭힘", reportReason_illegal:"불법 상품 또는 활동", reportReason_spam:"스팸 또는 오해의 소지가 있는 콘텐츠", reportReason_copyright:"저작권 침해", reportReason_other:"기타 부적절한 콘텐츠", reportCount:"신고 {count}건", approve:"승인", reject:"거부", hide:"숨기기", refresh:"새로고침" },
  es: { reportMap:"Denunciar", reportSubmitted:"Denuncia enviada. Gracias por ayudar a mantener segura la comunidad.", alreadyReported:"Ya has denunciado este mapa.", moderation_pending:"Revisión de seguridad pendiente", moderation_approved:"Revisión aprobada", moderation_rejected:"Revisión rechazada", moderation_hidden:"Oculto tras denuncias", contentModeration:"Revisión de contenido", moderatorOnly:"Solo disponible para moderadores.", moderationQueueEmpty:"No hay obras en esta cola.", moderationReasonPrompt:"Motivo de la revisión (opcional)", reportPrompt:"Elige el motivo principal. La denuncia es confidencial.", selectReportReason:"Selecciona un motivo.", reportDetails:"Detalles adicionales (opcional, máx. 300 caracteres)", submitReport:"Enviar denuncia", reportReason_sexual:"Contenido sexual o desnudos", reportReason_violence:"Violencia o conducta peligrosa", reportReason_hate:"Odio o acoso", reportReason_illegal:"Bienes o actividades ilegales", reportReason_spam:"Spam o contenido engañoso", reportReason_copyright:"Infracción de derechos de autor", reportReason_other:"Otro contenido inapropiado", reportCount:"{count} denuncias", approve:"Aprobar", reject:"Rechazar", hide:"Ocultar", refresh:"Actualizar" },
  fr: { reportMap:"Signaler", reportSubmitted:"Signalement envoyé. Merci de contribuer à la sécurité de la communauté.", alreadyReported:"Vous avez déjà signalé cette carte.", moderation_pending:"En attente de vérification", moderation_approved:"Vérification réussie", moderation_rejected:"Vérification refusée", moderation_hidden:"Masqué après signalements", contentModeration:"Modération du contenu", moderatorOnly:"Page réservée aux modérateurs.", moderationQueueEmpty:"Aucune création dans cette file.", moderationReasonPrompt:"Motif de la décision (facultatif)", reportPrompt:"Choisissez le motif principal. Le signalement est confidentiel.", selectReportReason:"Choisissez un motif.", reportDetails:"Détails supplémentaires (facultatif, 300 caractères max.)", submitReport:"Envoyer le signalement", reportReason_sexual:"Contenu sexuel ou nudité", reportReason_violence:"Violence ou comportement dangereux", reportReason_hate:"Haine ou harcèlement", reportReason_illegal:"Biens ou activités illégaux", reportReason_spam:"Spam ou contenu trompeur", reportReason_copyright:"Violation du droit d’auteur", reportReason_other:"Autre contenu inapproprié", reportCount:"{count} signalements", approve:"Approuver", reject:"Refuser", hide:"Masquer", refresh:"Actualiser" },
  de: { reportMap:"Melden", reportSubmitted:"Meldung gesendet. Danke, dass du die Community schützt.", alreadyReported:"Du hast diese Karte bereits gemeldet.", moderation_pending:"Sicherheitsprüfung ausstehend", moderation_approved:"Sicherheitsprüfung bestanden", moderation_rejected:"Sicherheitsprüfung abgelehnt", moderation_hidden:"Nach Meldungen ausgeblendet", contentModeration:"Inhaltsprüfung", moderatorOnly:"Diese Seite ist nur für Moderatoren verfügbar.", moderationQueueEmpty:"Keine Werke in dieser Prüfliste.", moderationReasonPrompt:"Prüfgrund eingeben (optional)", reportPrompt:"Wähle den Hauptgrund. Meldungen sind vertraulich.", selectReportReason:"Bitte einen Meldegrund wählen.", reportDetails:"Weitere Angaben (optional, max. 300 Zeichen)", submitReport:"Meldung senden", reportReason_sexual:"Sexuelle Inhalte oder Nacktheit", reportReason_violence:"Gewalt oder gefährliches Verhalten", reportReason_hate:"Hass oder Belästigung", reportReason_illegal:"Illegale Waren oder Aktivitäten", reportReason_spam:"Spam oder irreführende Inhalte", reportReason_copyright:"Urheberrechtsverletzung", reportReason_other:"Andere unangemessene Inhalte", reportCount:"{count} Meldungen", approve:"Freigeben", reject:"Ablehnen", hide:"Ausblenden", refresh:"Aktualisieren" },
  "pt-BR": { reportMap:"Denunciar", reportSubmitted:"Denúncia enviada. Obrigado por ajudar a manter a comunidade segura.", alreadyReported:"Você já denunciou este mapa.", moderation_pending:"Aguardando revisão de segurança", moderation_approved:"Revisão aprovada", moderation_rejected:"Revisão rejeitada", moderation_hidden:"Oculto após denúncias", contentModeration:"Revisão de conteúdo", moderatorOnly:"Esta página é apenas para moderadores.", moderationQueueEmpty:"Não há obras nesta fila.", moderationReasonPrompt:"Informe o motivo (opcional)", reportPrompt:"Escolha o motivo principal. A denúncia é confidencial.", selectReportReason:"Escolha um motivo.", reportDetails:"Detalhes adicionais (opcional, até 300 caracteres)", submitReport:"Enviar denúncia", reportReason_sexual:"Conteúdo sexual ou nudez", reportReason_violence:"Violência ou comportamento perigoso", reportReason_hate:"Ódio ou assédio", reportReason_illegal:"Bens ou atividades ilegais", reportReason_spam:"Spam ou conteúdo enganoso", reportReason_copyright:"Violação de direitos autorais", reportReason_other:"Outro conteúdo impróprio", reportCount:"{count} denúncias", approve:"Aprovar", reject:"Rejeitar", hide:"Ocultar", refresh:"Atualizar" },
  "pt-PT": { reportMap:"Denunciar", reportSubmitted:"Denúncia enviada. Obrigado por ajudar a manter a comunidade segura.", alreadyReported:"Já denunciaste este mapa.", moderation_pending:"A aguardar revisão de segurança", moderation_approved:"Revisão aprovada", moderation_rejected:"Revisão rejeitada", moderation_hidden:"Oculto após denúncias", contentModeration:"Revisão de conteúdo", moderatorOnly:"Esta página destina-se apenas a moderadores.", moderationQueueEmpty:"Não há criações nesta fila.", moderationReasonPrompt:"Indica o motivo (opcional)", reportPrompt:"Escolhe o motivo principal. A denúncia é confidencial.", selectReportReason:"Escolhe um motivo.", reportDetails:"Detalhes adicionais (opcional, até 300 caracteres)", submitReport:"Enviar denúncia", reportReason_sexual:"Conteúdo sexual ou nudez", reportReason_violence:"Violência ou comportamento perigoso", reportReason_hate:"Ódio ou assédio", reportReason_illegal:"Bens ou atividades ilegais", reportReason_spam:"Spam ou conteúdo enganador", reportReason_copyright:"Violação de direitos de autor", reportReason_other:"Outro conteúdo impróprio", reportCount:"{count} denúncias", approve:"Aprovar", reject:"Rejeitar", hide:"Ocultar", refresh:"Atualizar" },
  it: { reportMap:"Segnala", reportSubmitted:"Segnalazione inviata. Grazie per contribuire alla sicurezza della community.", alreadyReported:"Hai già segnalato questa mappa.", moderation_pending:"Revisione di sicurezza in attesa", moderation_approved:"Revisione approvata", moderation_rejected:"Revisione rifiutata", moderation_hidden:"Nascosta dopo segnalazioni", contentModeration:"Revisione contenuti", moderatorOnly:"Pagina riservata ai moderatori.", moderationQueueEmpty:"Nessuna opera in questa coda.", moderationReasonPrompt:"Inserisci il motivo (facoltativo)", reportPrompt:"Scegli il motivo principale. La segnalazione è riservata.", selectReportReason:"Scegli un motivo.", reportDetails:"Dettagli aggiuntivi (facoltativi, max 300 caratteri)", submitReport:"Invia segnalazione", reportReason_sexual:"Contenuto sessuale o nudità", reportReason_violence:"Violenza o comportamento pericoloso", reportReason_hate:"Odio o molestie", reportReason_illegal:"Beni o attività illegali", reportReason_spam:"Spam o contenuto ingannevole", reportReason_copyright:"Violazione del copyright", reportReason_other:"Altro contenuto inappropriato", reportCount:"{count} segnalazioni", approve:"Approva", reject:"Rifiuta", hide:"Nascondi", refresh:"Aggiorna" },
  ru: { reportMap:"Пожаловаться", reportSubmitted:"Жалоба отправлена. Спасибо за помощь в защите сообщества.", alreadyReported:"Вы уже жаловались на эту карту.", moderation_pending:"Ожидает проверки", moderation_approved:"Проверка пройдена", moderation_rejected:"Проверка не пройдена", moderation_hidden:"Скрыто после жалоб", contentModeration:"Проверка контента", moderatorOnly:"Страница доступна только модераторам.", moderationQueueEmpty:"В этой очереди нет работ.", moderationReasonPrompt:"Укажите причину (необязательно)", reportPrompt:"Выберите основную причину. Жалоба конфиденциальна.", selectReportReason:"Выберите причину жалобы.", reportDetails:"Дополнительные сведения (до 300 символов)", submitReport:"Отправить жалобу", reportReason_sexual:"Сексуальный контент или нагота", reportReason_violence:"Насилие или опасное поведение", reportReason_hate:"Ненависть или травля", reportReason_illegal:"Незаконные товары или действия", reportReason_spam:"Спам или обман", reportReason_copyright:"Нарушение авторских прав", reportReason_other:"Другой неприемлемый контент", reportCount:"Жалоб: {count}", approve:"Одобрить", reject:"Отклонить", hide:"Скрыть", refresh:"Обновить" },
  ar: { reportMap:"إبلاغ", reportSubmitted:"تم إرسال البلاغ. شكرًا لمساعدتك في حماية المجتمع.", alreadyReported:"لقد أبلغت عن هذه الخريطة من قبل.", moderation_pending:"بانتظار مراجعة الأمان", moderation_approved:"اجتازت مراجعة الأمان", moderation_rejected:"رُفضت في مراجعة الأمان", moderation_hidden:"مخفية بعد البلاغات", contentModeration:"مراجعة المحتوى", moderatorOnly:"هذه الصفحة للمشرفين فقط.", moderationQueueEmpty:"لا توجد أعمال في قائمة المراجعة.", moderationReasonPrompt:"أدخل سبب القرار (اختياري)", reportPrompt:"اختر السبب الرئيسي. سيظل البلاغ سريًا.", selectReportReason:"يرجى اختيار سبب البلاغ.", reportDetails:"تفاصيل إضافية (اختياري، 300 حرف كحد أقصى)", submitReport:"إرسال البلاغ", reportReason_sexual:"محتوى جنسي أو عري", reportReason_violence:"عنف أو سلوك خطير", reportReason_hate:"كراهية أو مضايقة", reportReason_illegal:"سلع أو أنشطة غير قانونية", reportReason_spam:"رسائل مزعجة أو محتوى مضلل", reportReason_copyright:"انتهاك حقوق النشر", reportReason_other:"محتوى آخر غير لائق", reportCount:"{count} بلاغات", approve:"موافقة", reject:"رفض", hide:"إخفاء", refresh:"تحديث" },
  hi: { reportMap:"रिपोर्ट करें", reportSubmitted:"रिपोर्ट भेज दी गई। समुदाय को सुरक्षित रखने में मदद के लिए धन्यवाद।", alreadyReported:"आप इस मैप को पहले ही रिपोर्ट कर चुके हैं।", moderation_pending:"सुरक्षा समीक्षा लंबित", moderation_approved:"सुरक्षा समीक्षा पास", moderation_rejected:"सुरक्षा समीक्षा में अस्वीकृत", moderation_hidden:"रिपोर्ट के बाद छिपाया गया", contentModeration:"कंटेंट समीक्षा", moderatorOnly:"यह पेज केवल मॉडरेटर के लिए है।", moderationQueueEmpty:"इस समीक्षा सूची में कोई रचना नहीं है।", moderationReasonPrompt:"समीक्षा का कारण लिखें (वैकल्पिक)", reportPrompt:"मुख्य कारण चुनें। रिपोर्ट गोपनीय रहेगी।", selectReportReason:"रिपोर्ट का कारण चुनें।", reportDetails:"अतिरिक्त विवरण (वैकल्पिक, अधिकतम 300 अक्षर)", submitReport:"रिपोर्ट भेजें", reportReason_sexual:"यौन या नग्न सामग्री", reportReason_violence:"हिंसा या खतरनाक व्यवहार", reportReason_hate:"नफ़रत या उत्पीड़न", reportReason_illegal:"अवैध वस्तुएँ या गतिविधि", reportReason_spam:"स्पैम या भ्रामक सामग्री", reportReason_copyright:"कॉपीराइट उल्लंघन", reportReason_other:"अन्य अनुचित सामग्री", reportCount:"{count} रिपोर्ट", approve:"स्वीकार", reject:"अस्वीकार", hide:"छिपाएँ", refresh:"रीफ़्रेश" },
  th: { reportMap:"รายงาน", reportSubmitted:"ส่งรายงานแล้ว ขอบคุณที่ช่วยดูแลชุมชนให้ปลอดภัย", alreadyReported:"คุณรายงานแผนที่นี้แล้ว", moderation_pending:"รอตรวจสอบความปลอดภัย", moderation_approved:"ผ่านการตรวจสอบ", moderation_rejected:"ไม่ผ่านการตรวจสอบ", moderation_hidden:"ซ่อนหลังได้รับรายงาน", contentModeration:"ตรวจสอบเนื้อหา", moderatorOnly:"หน้านี้สำหรับผู้ตรวจสอบเท่านั้น", moderationQueueEmpty:"ไม่มีผลงานในคิวนี้", moderationReasonPrompt:"ระบุเหตุผล (ไม่บังคับ)", reportPrompt:"เลือกเหตุผลหลัก รายงานจะถูกเก็บเป็นความลับ", selectReportReason:"โปรดเลือกเหตุผล", reportDetails:"รายละเอียดเพิ่มเติม (ไม่บังคับ สูงสุด 300 ตัวอักษร)", submitReport:"ส่งรายงาน", reportReason_sexual:"เนื้อหาทางเพศหรือภาพเปลือย", reportReason_violence:"ความรุนแรงหรือพฤติกรรมอันตราย", reportReason_hate:"ความเกลียดชังหรือคุกคาม", reportReason_illegal:"สินค้าหรือกิจกรรมผิดกฎหมาย", reportReason_spam:"สแปมหรือเนื้อหาหลอกลวง", reportReason_copyright:"ละเมิดลิขสิทธิ์", reportReason_other:"เนื้อหาไม่เหมาะสมอื่น ๆ", reportCount:"รายงาน {count} ครั้ง", approve:"อนุมัติ", reject:"ปฏิเสธ", hide:"ซ่อน", refresh:"รีเฟรช" },
  vi: { reportMap:"Báo cáo", reportSubmitted:"Đã gửi báo cáo. Cảm ơn bạn đã giúp giữ an toàn cho cộng đồng.", alreadyReported:"Bạn đã báo cáo bản đồ này.", moderation_pending:"Đang chờ kiểm duyệt", moderation_approved:"Đã qua kiểm duyệt", moderation_rejected:"Không qua kiểm duyệt", moderation_hidden:"Đã ẩn sau báo cáo", contentModeration:"Kiểm duyệt nội dung", moderatorOnly:"Trang này chỉ dành cho kiểm duyệt viên.", moderationQueueEmpty:"Không có tác phẩm trong hàng chờ này.", moderationReasonPrompt:"Nhập lý do (không bắt buộc)", reportPrompt:"Chọn lý do chính. Báo cáo sẽ được bảo mật.", selectReportReason:"Vui lòng chọn lý do.", reportDetails:"Thông tin bổ sung (không bắt buộc, tối đa 300 ký tự)", submitReport:"Gửi báo cáo", reportReason_sexual:"Nội dung tình dục hoặc khỏa thân", reportReason_violence:"Bạo lực hoặc hành vi nguy hiểm", reportReason_hate:"Thù ghét hoặc quấy rối", reportReason_illegal:"Hàng hóa hoặc hoạt động bất hợp pháp", reportReason_spam:"Spam hoặc nội dung gây hiểu lầm", reportReason_copyright:"Vi phạm bản quyền", reportReason_other:"Nội dung không phù hợp khác", reportCount:"{count} báo cáo", approve:"Duyệt", reject:"Từ chối", hide:"Ẩn", refresh:"Làm mới" },
  id: { reportMap:"Laporkan", reportSubmitted:"Laporan dikirim. Terima kasih telah membantu menjaga keamanan komunitas.", alreadyReported:"Anda sudah melaporkan peta ini.", moderation_pending:"Menunggu tinjauan keamanan", moderation_approved:"Lolos tinjauan keamanan", moderation_rejected:"Ditolak dalam tinjauan", moderation_hidden:"Disembunyikan setelah laporan", contentModeration:"Tinjauan konten", moderatorOnly:"Halaman ini hanya untuk moderator.", moderationQueueEmpty:"Tidak ada karya dalam antrean ini.", moderationReasonPrompt:"Masukkan alasan (opsional)", reportPrompt:"Pilih alasan utama. Laporan bersifat rahasia.", selectReportReason:"Pilih alasan laporan.", reportDetails:"Detail tambahan (opsional, maks. 300 karakter)", submitReport:"Kirim laporan", reportReason_sexual:"Konten seksual atau ketelanjangan", reportReason_violence:"Kekerasan atau perilaku berbahaya", reportReason_hate:"Kebencian atau pelecehan", reportReason_illegal:"Barang atau aktivitas ilegal", reportReason_spam:"Spam atau konten menyesatkan", reportReason_copyright:"Pelanggaran hak cipta", reportReason_other:"Konten tidak pantas lainnya", reportCount:"{count} laporan", approve:"Setujui", reject:"Tolak", hide:"Sembunyikan", refresh:"Segarkan" },
  tr: { reportMap:"Bildir", reportSubmitted:"Bildirim gönderildi. Topluluğu güvenli tutmaya yardım ettiğiniz için teşekkürler.", alreadyReported:"Bu haritayı zaten bildirdiniz.", moderation_pending:"Güvenlik incelemesi bekliyor", moderation_approved:"Güvenlik incelemesi geçti", moderation_rejected:"Güvenlik incelemesi reddetti", moderation_hidden:"Bildirimlerden sonra gizlendi", contentModeration:"İçerik incelemesi", moderatorOnly:"Bu sayfa yalnızca moderatörlere açıktır.", moderationQueueEmpty:"Bu kuyrukta eser yok.", moderationReasonPrompt:"İnceleme nedenini girin (isteğe bağlı)", reportPrompt:"Ana nedeni seçin. Bildirim gizli tutulur.", selectReportReason:"Bir bildirim nedeni seçin.", reportDetails:"Ek ayrıntılar (isteğe bağlı, en fazla 300 karakter)", submitReport:"Bildirimi gönder", reportReason_sexual:"Cinsel içerik veya çıplaklık", reportReason_violence:"Şiddet veya tehlikeli davranış", reportReason_hate:"Nefret veya taciz", reportReason_illegal:"Yasa dışı ürün veya faaliyet", reportReason_spam:"Spam veya yanıltıcı içerik", reportReason_copyright:"Telif hakkı ihlali", reportReason_other:"Diğer uygunsuz içerik", reportCount:"{count} bildirim", approve:"Onayla", reject:"Reddet", hide:"Gizle", refresh:"Yenile" },
  nl: { reportMap:"Melden", reportSubmitted:"Melding verzonden. Bedankt dat je de community veilig helpt houden.", alreadyReported:"Je hebt deze kaart al gemeld.", moderation_pending:"Wacht op veiligheidscontrole", moderation_approved:"Veiligheidscontrole geslaagd", moderation_rejected:"Veiligheidscontrole afgewezen", moderation_hidden:"Verborgen na meldingen", contentModeration:"Contentcontrole", moderatorOnly:"Deze pagina is alleen voor moderators.", moderationQueueEmpty:"Geen werken in deze wachtrij.", moderationReasonPrompt:"Voer de reden in (optioneel)", reportPrompt:"Kies de belangrijkste reden. De melding blijft vertrouwelijk.", selectReportReason:"Kies een meldingsreden.", reportDetails:"Aanvullende details (optioneel, max. 300 tekens)", submitReport:"Melding verzenden", reportReason_sexual:"Seksuele inhoud of naaktheid", reportReason_violence:"Geweld of gevaarlijk gedrag", reportReason_hate:"Haat of intimidatie", reportReason_illegal:"Illegale goederen of activiteiten", reportReason_spam:"Spam of misleidende inhoud", reportReason_copyright:"Auteursrechtenschending", reportReason_other:"Andere ongepaste inhoud", reportCount:"{count} meldingen", approve:"Goedkeuren", reject:"Afwijzen", hide:"Verbergen", refresh:"Vernieuwen" },
  pl: { reportMap:"Zgłoś", reportSubmitted:"Zgłoszenie wysłane. Dziękujemy za pomoc w ochronie społeczności.", alreadyReported:"Ta mapa została już przez Ciebie zgłoszona.", moderation_pending:"Oczekuje na kontrolę bezpieczeństwa", moderation_approved:"Kontrola bezpieczeństwa zaliczona", moderation_rejected:"Odrzucono podczas kontroli", moderation_hidden:"Ukryto po zgłoszeniach", contentModeration:"Weryfikacja treści", moderatorOnly:"Ta strona jest dostępna tylko dla moderatorów.", moderationQueueEmpty:"Brak prac w tej kolejce.", moderationReasonPrompt:"Podaj powód decyzji (opcjonalnie)", reportPrompt:"Wybierz główny powód. Zgłoszenie jest poufne.", selectReportReason:"Wybierz powód zgłoszenia.", reportDetails:"Dodatkowe informacje (opcjonalnie, maks. 300 znaków)", submitReport:"Wyślij zgłoszenie", reportReason_sexual:"Treści seksualne lub nagość", reportReason_violence:"Przemoc lub niebezpieczne zachowanie", reportReason_hate:"Nienawiść lub nękanie", reportReason_illegal:"Nielegalne towary lub działania", reportReason_spam:"Spam lub treści wprowadzające w błąd", reportReason_copyright:"Naruszenie praw autorskich", reportReason_other:"Inne nieodpowiednie treści", reportCount:"Zgłoszenia: {count}", approve:"Zatwierdź", reject:"Odrzuć", hide:"Ukryj", refresh:"Odśwież" }
});

const OWN_MAP_REPORT_MESSAGES = Object.freeze({
  "zh-TW":"不能檢舉自己建立的地圖。", ja:"自分のマップは報告できません。", ko:"자신이 만든 지도는 신고할 수 없습니다.",
  es:"No puedes denunciar tu propio mapa.", fr:"Vous ne pouvez pas signaler votre propre carte.", de:"Du kannst deine eigene Karte nicht melden.",
  "pt-BR":"Você não pode denunciar seu próprio mapa.", "pt-PT":"Não podes denunciar o teu próprio mapa.", it:"Non puoi segnalare la tua mappa.",
  ru:"Нельзя пожаловаться на собственную карту.", ar:"لا يمكنك الإبلاغ عن خريطتك.", hi:"आप अपने मैप की रिपोर्ट नहीं कर सकते।",
  th:"คุณไม่สามารถรายงานแผนที่ของตนเองได้", vi:"Bạn không thể báo cáo bản đồ của mình.", id:"Anda tidak dapat melaporkan peta sendiri.",
  tr:"Kendi haritanızı bildiremezsiniz.", nl:"Je kunt je eigen kaart niet melden.", pl:"Nie możesz zgłosić własnej mapy."
});

const WORKSHOP_CONTROL_MESSAGES = Object.freeze({
  "zh-TW": { dailyPublishRemaining:"今天還可以發佈 {count} 次", publishLimitReached:"今天的發佈次數已用完，請明天再試。", workshopBanned:"你的創意工坊發佈權限已被暫停。", submitAppeal:"提交申訴", appealPlaceholder:"請說明為什麼需要重新審核這項作品", appealTooShort:"申訴內容至少填寫 5 個字。", appealSubmitted:"申訴已提交。", pendingAppeals:"待處理申訴", appealAvailabilityHint:"作品被拒絕或隱藏後，這裡會顯示提交申訴按鈕。" },
  ja: { dailyPublishRemaining:"本日あと {count} 回公開できます", publishLimitReached:"本日の公開上限に達しました。明日もう一度お試しください。", workshopBanned:"ワークショップへの公開権限が一時停止されています。", submitAppeal:"異議申し立て", appealPlaceholder:"再審査が必要な理由を説明してください", appealTooShort:"5文字以上入力してください。", appealSubmitted:"異議申し立てを送信しました。", pendingAppeals:"審査待ちの異議申し立て", appealAvailabilityHint:"作品が却下または非表示になると、ここに異議申し立てボタンが表示されます。" },
  ko: { dailyPublishRemaining:"오늘 {count}회 더 공개할 수 있습니다", publishLimitReached:"오늘의 공개 한도에 도달했습니다. 내일 다시 시도하세요.", workshopBanned:"워크숍 게시 권한이 일시 중지되었습니다.", submitAppeal:"이의 제기", appealPlaceholder:"이 작품을 다시 검토해야 하는 이유를 설명하세요", appealTooShort:"5자 이상 입력하세요.", appealSubmitted:"이의 제기가 제출되었습니다.", pendingAppeals:"대기 중인 이의 제기", appealAvailabilityHint:"작품이 거부되거나 숨겨지면 여기에 이의 제기 버튼이 표시됩니다." },
  es: { dailyPublishRemaining:"Publicaciones disponibles hoy: {count}", publishLimitReached:"Has alcanzado el límite de publicaciones de hoy.", workshopBanned:"Tu permiso para publicar en el Taller está suspendido.", submitAppeal:"Enviar apelación", appealPlaceholder:"Explica por qué se debe revisar de nuevo esta obra", appealTooShort:"Introduce al menos 5 caracteres.", appealSubmitted:"Apelación enviada.", pendingAppeals:"Apelaciones pendientes", appealAvailabilityHint:"El botón de apelación aparecerá aquí si se rechaza u oculta una obra." },
  fr: { dailyPublishRemaining:"Publications restantes aujourd’hui : {count}", publishLimitReached:"La limite de publications du jour est atteinte.", workshopBanned:"Votre droit de publier dans l’Atelier est suspendu.", submitAppeal:"Faire appel", appealPlaceholder:"Expliquez pourquoi cette création doit être réexaminée", appealTooShort:"Saisissez au moins 5 caractères.", appealSubmitted:"Appel envoyé.", pendingAppeals:"Appels en attente", appealAvailabilityHint:"Le bouton d’appel apparaît ici après le refus ou le masquage d’une création." },
  de: { dailyPublishRemaining:"Heute noch {count} Veröffentlichungen verfügbar", publishLimitReached:"Das heutige Veröffentlichungslimit ist erreicht.", workshopBanned:"Deine Veröffentlichungsberechtigung für die Werkstatt ist ausgesetzt.", submitAppeal:"Einspruch senden", appealPlaceholder:"Erkläre, warum dieses Werk erneut geprüft werden soll", appealTooShort:"Bitte mindestens 5 Zeichen eingeben.", appealSubmitted:"Einspruch gesendet.", pendingAppeals:"Offene Einsprüche", appealAvailabilityHint:"Nach Ablehnung oder Ausblendung erscheint hier die Einspruchsschaltfläche." },
  "pt-BR": { dailyPublishRemaining:"Publicações restantes hoje: {count}", publishLimitReached:"O limite de publicações de hoje foi atingido.", workshopBanned:"Sua permissão para publicar no Workshop está suspensa.", submitAppeal:"Enviar recurso", appealPlaceholder:"Explique por que esta obra deve ser analisada novamente", appealTooShort:"Digite pelo menos 5 caracteres.", appealSubmitted:"Recurso enviado.", pendingAppeals:"Recursos pendentes", appealAvailabilityHint:"O botão de recurso aparece aqui após uma obra ser rejeitada ou ocultada." },
  "pt-PT": { dailyPublishRemaining:"Publicações restantes hoje: {count}", publishLimitReached:"O limite de publicações de hoje foi atingido.", workshopBanned:"A tua permissão para publicar no Workshop está suspensa.", submitAppeal:"Enviar recurso", appealPlaceholder:"Explica por que esta obra deve ser revista novamente", appealTooShort:"Introduz pelo menos 5 caracteres.", appealSubmitted:"Recurso enviado.", pendingAppeals:"Recursos pendentes", appealAvailabilityHint:"O botão de recurso aparece aqui depois de uma obra ser rejeitada ou ocultada." },
  it: { dailyPublishRemaining:"Pubblicazioni rimaste oggi: {count}", publishLimitReached:"Hai raggiunto il limite di pubblicazioni di oggi.", workshopBanned:"Il tuo permesso di pubblicare nel Laboratorio è sospeso.", submitAppeal:"Invia ricorso", appealPlaceholder:"Spiega perché quest’opera deve essere riesaminata", appealTooShort:"Inserisci almeno 5 caratteri.", appealSubmitted:"Ricorso inviato.", pendingAppeals:"Ricorsi in attesa", appealAvailabilityHint:"Il pulsante di ricorso appare qui dopo il rifiuto o l’occultamento di un’opera." },
  ru: { dailyPublishRemaining:"Сегодня можно опубликовать ещё: {count}", publishLimitReached:"Дневной лимит публикаций исчерпан.", workshopBanned:"Ваше право публикации в мастерской приостановлено.", submitAppeal:"Подать апелляцию", appealPlaceholder:"Объясните, почему работу следует проверить повторно", appealTooShort:"Введите не менее 5 символов.", appealSubmitted:"Апелляция отправлена.", pendingAppeals:"Ожидающие апелляции", appealAvailabilityHint:"После отклонения или скрытия работы здесь появится кнопка апелляции." },
  ar: { dailyPublishRemaining:"عمليات النشر المتبقية اليوم: {count}", publishLimitReached:"تم بلوغ حد النشر اليومي.", workshopBanned:"تم تعليق صلاحية النشر في ورشة الإبداع.", submitAppeal:"إرسال اعتراض", appealPlaceholder:"اشرح سبب ضرورة إعادة مراجعة هذا العمل", appealTooShort:"أدخل 5 أحرف على الأقل.", appealSubmitted:"تم إرسال الاعتراض.", pendingAppeals:"الاعتراضات المعلقة", appealAvailabilityHint:"يظهر زر الاعتراض هنا بعد رفض العمل أو إخفائه." },
  hi: { dailyPublishRemaining:"आज शेष प्रकाशन: {count}", publishLimitReached:"आज की प्रकाशन सीमा पूरी हो गई है।", workshopBanned:"वर्कशॉप में प्रकाशित करने की अनुमति निलंबित है।", submitAppeal:"अपील भेजें", appealPlaceholder:"बताएँ कि इस रचना की दोबारा समीक्षा क्यों होनी चाहिए", appealTooShort:"कम से कम 5 अक्षर दर्ज करें।", appealSubmitted:"अपील भेज दी गई।", pendingAppeals:"लंबित अपील", appealAvailabilityHint:"रचना अस्वीकार या छिपाए जाने पर यहाँ अपील बटन दिखाई देगा।" },
  th: { dailyPublishRemaining:"วันนี้เผยแพร่ได้อีก {count} ครั้ง", publishLimitReached:"ถึงขีดจำกัดการเผยแพร่วันนี้แล้ว", workshopBanned:"สิทธิ์เผยแพร่ในเวิร์กช็อปถูกระงับ", submitAppeal:"ส่งคำอุทธรณ์", appealPlaceholder:"อธิบายเหตุผลที่ควรตรวจสอบผลงานนี้อีกครั้ง", appealTooShort:"กรอกอย่างน้อย 5 ตัวอักษร", appealSubmitted:"ส่งคำอุทธรณ์แล้ว", pendingAppeals:"คำอุทธรณ์ที่รอดำเนินการ", appealAvailabilityHint:"ปุ่มอุทธรณ์จะแสดงที่นี่เมื่อผลงานถูกปฏิเสธหรือซ่อน" },
  vi: { dailyPublishRemaining:"Lượt xuất bản còn lại hôm nay: {count}", publishLimitReached:"Đã đạt giới hạn xuất bản hôm nay.", workshopBanned:"Quyền xuất bản trong Xưởng của bạn đã bị tạm ngưng.", submitAppeal:"Gửi khiếu nại", appealPlaceholder:"Giải thích lý do tác phẩm này cần được xem xét lại", appealTooShort:"Vui lòng nhập ít nhất 5 ký tự.", appealSubmitted:"Đã gửi khiếu nại.", pendingAppeals:"Khiếu nại đang chờ", appealAvailabilityHint:"Nút khiếu nại sẽ xuất hiện ở đây khi tác phẩm bị từ chối hoặc ẩn." },
  id: { dailyPublishRemaining:"Sisa publikasi hari ini: {count}", publishLimitReached:"Batas publikasi hari ini telah tercapai.", workshopBanned:"Izin publikasi Workshop Anda ditangguhkan.", submitAppeal:"Kirim banding", appealPlaceholder:"Jelaskan mengapa karya ini perlu ditinjau ulang", appealTooShort:"Masukkan sedikitnya 5 karakter.", appealSubmitted:"Banding dikirim.", pendingAppeals:"Banding tertunda", appealAvailabilityHint:"Tombol banding muncul di sini setelah karya ditolak atau disembunyikan." },
  tr: { dailyPublishRemaining:"Bugün kalan yayın hakkı: {count}", publishLimitReached:"Bugünkü yayın sınırına ulaşıldı.", workshopBanned:"Atölye yayınlama izniniz askıya alındı.", submitAppeal:"İtiraz gönder", appealPlaceholder:"Bu eserin neden yeniden incelenmesi gerektiğini açıklayın", appealTooShort:"En az 5 karakter girin.", appealSubmitted:"İtiraz gönderildi.", pendingAppeals:"Bekleyen itirazlar", appealAvailabilityHint:"Eser reddedilir veya gizlenirse itiraz düğmesi burada görünür." },
  nl: { dailyPublishRemaining:"Publicaties over voor vandaag: {count}", publishLimitReached:"De publicatielimiet voor vandaag is bereikt.", workshopBanned:"Je publicatierecht voor de Werkplaats is opgeschort.", submitAppeal:"Bezwaar indienen", appealPlaceholder:"Leg uit waarom dit werk opnieuw moet worden beoordeeld", appealTooShort:"Voer minstens 5 tekens in.", appealSubmitted:"Bezwaar ingediend.", pendingAppeals:"Openstaande bezwaren", appealAvailabilityHint:"De bezwaarknop verschijnt hier nadat een werk is afgewezen of verborgen." },
  pl: { dailyPublishRemaining:"Pozostałe publikacje na dziś: {count}", publishLimitReached:"Dzisiejszy limit publikacji został osiągnięty.", workshopBanned:"Twoje uprawnienie do publikowania w Warsztacie zostało zawieszone.", submitAppeal:"Wyślij odwołanie", appealPlaceholder:"Wyjaśnij, dlaczego ta praca powinna zostać ponownie sprawdzona", appealTooShort:"Wpisz co najmniej 5 znaków.", appealSubmitted:"Odwołanie wysłane.", pendingAppeals:"Oczekujące odwołania", appealAvailabilityHint:"Przycisk odwołania pojawi się tutaj po odrzuceniu lub ukryciu pracy." }
});

export function translate(language, key, params = {}) {
  const lang = normalizeLanguage(language);
  if (key === "builtCountFormat") {
    return translate(lang, "builtLevels", { done: params.current, total: params.total });
  }
  if (key === "litCountFormat") {
    return translate(lang, "litLevels", { done: params.current, total: params.total });
  }
  const pack = MESSAGES[lang] || MESSAGES.en;
  const contentPack = SYSTEM_CONTENT[lang] || SYSTEM_CONTENT.en;
  const text =
    pack?.[key]
    ?? contentPack?.[key]
    ?? SAFETY_MESSAGES[lang]?.[key]
    ?? WORKSHOP_CONTROL_MESSAGES[lang]?.[key]
    ?? (key === "cannotReportOwnMap" ? OWN_MAP_REPORT_MESSAGES[lang] : undefined)
    ?? MESSAGES.en?.[key]
    ?? SYSTEM_CONTENT.en?.[key]
    ?? key;
  return interpolate(text, params);
}

export function getSkinName(id, language = DEFAULT_LANGUAGE) {
  const key = SKIN_KEY_BY_ID[id];
  return key ? translate(language, key) : String(id || "");
}

export class I18nError extends Error {
  constructor(key, params = {}) {
    super(key);
    this.name = "I18nError";
    this.i18nKey = key;
    this.i18nParams = params;
  }
}

export function translateError(language, error, fallbackKey = "starMapImageError") {
  if (error?.i18nKey) return translate(language, error.i18nKey, error.i18nParams || {});
  return translate(language, fallbackKey);
}
